
/*
 * @license @wistia/ui v0.8.26
 *
 * Copyright (c) 2024-2025, Wistia, Inc. and its affiliates.
 *
 * This source code is unlicensed, all rights reserved.
 */


// src/providers/UIProvider/UIProvider.tsx
import { Provider as TooltipProvider } from "@radix-ui/react-tooltip";
import { createContext as createContext2, useContext } from "react";

// src/css/GlobalStyle.tsx
import { createGlobalStyle } from "styled-components";

// src/css/globalStyleAdjustmentsCss.tsx
import { css } from "styled-components";
var globalStyleAdjustmentsCss = css`
  /* stylelint-disable property-no-vendor-prefix */

  /**
  * 1. Change from 'content-box' so that 'width' calculations are unaffected by 'padding' or 'border'
       See: https://css-tricks.com/inheriting-box-sizing-probably-slightly-better-best-practice/
  * 2. Ensure the iOS tap highlight is transparent
  * 3. Prevent font size inflation
  * 4. Fix for 100vh in mobile WebKit
  *    See: https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/
  */
  html {
    box-sizing: border-box; /* 1 */
    -webkit-tap-highlight-color: rgb(0 0 0 / 0%); /* 2 */
    -moz-text-size-adjust: none; /* 3 */
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
    height: -webkit-fill-available; /* 4 */
  }

  *,
  *::before,
  *::after {
    box-sizing: inherit; /* 1 */
  }

  /**
  * 1. Without this fonts are too heavy weight in OS X Firefox
  * 2. Design decision
    3. See: https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/
  */
  body {
    -moz-osx-font-smoothing: grayscale; /* 1 */
    -webkit-font-smoothing: antialiased; /* 2 */
    line-height: 1.5; /* 2 */
    min-height: 100vh; /* 3 */
    min-height: -webkit-fill-available; /* 3 */
  }

  /* Remove default margin in favour of better control in authored CSS */
  body,
  h1,
  h2,
  h3,
  h4,
  p,
  figure,
  blockquote,
  dl,
  dd {
    margin-block-end: 0;
  }

  /* Remove list styles on ul, ol elements with a list role, which suggests default styling will be removed */
  ul[role='list'],
  ol[role='list'] {
    list-style: none;
  }

  /* Set shorter line heights on headings and interactive elements */
  h1,
  h2,
  h3,
  h4,
  button,
  input,
  label {
    line-height: 1.1;
  }

  /* Balance text wrapping on headings */

  /* h1,
  h2,
  h3,
  h4 {
    text-wrap: balance;
  } */

  /* A elements that don't have a class get default styles */
  a:not([class]) {
    text-decoration-skip-ink: auto;
    color: currentcolor;
  }

  /* Make images easier to work with */
  img,
  picture {
    max-width: 100%;
    display: block;
  }

  /* Inherit fonts for inputs and buttons */
  input,
  button,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
  }

  /* Make sure textareas without a rows attribute are not tiny */
  textarea:not([rows]) {
    min-height: 10em;
  }

  /* Anything that has been anchored to should have extra scroll margin */
  :target {
    scroll-margin-block: 5ex;
  }

  /* Remove all animations and transitions for people that prefer not to see them */
  @media (prefers-reduced-motion: reduce) {
    html:focus-within {
      scroll-behavior: auto;
    }

    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

// src/css/designTokens/index.tsx
import { css as css10 } from "styled-components";

// src/css/designTokens/colorGlobal.tsx
import { css as css2 } from "styled-components";
var lightGlobalTokens = css2`
  --wui-app-background: #ffffff;
  --wui-blue-surface: #f4f8ffcc;
  --wui-blue-contrast: #ffffff;
  --wui-purple-surface: #fdf5ffcc;
  --wui-purple-contrast: #ffffff;
  --wui-green-surface: #f3faf1cc;
  --wui-green-contrast: #ffffff;
  --wui-pink-surface: #fff5f9cc;
  --wui-pink-contrast: #ffffff;
  --wui-red-surface: #fff5f5cc;
  --wui-red-contrast: #ffffff;
  --wui-yellow-surface: #fafbe5cc;
  --wui-yellow-contrast: #22230b;
  --wui-orange-surface: #fff5f3cc;
  --wui-orange-contrast: #ffffff;
  --wui-error-surface: #fff5f5cc;
  --wui-error-contrast: #ffffff;
  --wui-success-surface: #f1faf5cc;
  --wui-success-contrast: #ffffff;
  --wui-warning-surface: #fefbe6cc;
  --wui-warning-contrast: #262209;
  --wui-gray-surface: #ffffffcc;
  --wui-gray-contrast: #ffffff;
  --wui-blue-1: #fdfdfe;
  --wui-blue-2: #f6f9ff;
  --wui-blue-3: #ecf2ff;
  --wui-blue-4: #ddeaff;
  --wui-blue-5: #cddfff;
  --wui-blue-6: #b8d1ff;
  --wui-blue-7: #a1beff;
  --wui-blue-8: #81a4fe;
  --wui-blue-9: #2949e5;
  --wui-blue-10: #1f36d3;
  --wui-blue-11: #3154dc;
  --wui-blue-12: #192b65;
  --wui-blue-alpha-1: #00008003;
  --wui-blue-alpha-2: #0055ff0a;
  --wui-blue-alpha-3: #0051ff12;
  --wui-blue-alpha-4: #0062ff21;
  --wui-blue-alpha-5: #005cff33;
  --wui-blue-alpha-6: #005aff47;
  --wui-blue-alpha-7: #004fff5e;
  --wui-blue-alpha-8: #0047fd7d;
  --wui-blue-alpha-9: #0026e0d6;
  --wui-blue-alpha-10: #001acde0;
  --wui-blue-alpha-11: #002bd4cf;
  --wui-blue-alpha-12: #001454e5;
  --wui-purple-1: #fefcff;
  --wui-purple-2: #fdf7ff;
  --wui-purple-3: #fbeaff;
  --wui-purple-4: #f7ddff;
  --wui-purple-5: #f2ceff;
  --wui-purple-6: #eabdff;
  --wui-purple-7: #e0a5fc;
  --wui-purple-8: #d285f7;
  --wui-purple-9: #ac12e0;
  --wui-purple-10: #9a09c9;
  --wui-purple-11: #9b0bca;
  --wui-purple-12: #550072;
  --wui-purple-alpha-1: #aa00ff03;
  --wui-purple-alpha-2: #c000ff08;
  --wui-purple-alpha-3: #cf00ff14;
  --wui-purple-alpha-4: #c300ff21;
  --wui-purple-alpha-5: #bc00ff30;
  --wui-purple-alpha-6: #ae00ff42;
  --wui-purple-alpha-7: #a801f759;
  --wui-purple-alpha-8: #a101ef7a;
  --wui-purple-alpha-9: #a600deed;
  --wui-purple-alpha-10: #9600c7f5;
  --wui-purple-alpha-11: #9700c8f5;
  --wui-purple-alpha-12: #550072;
  --wui-green-1: #fbfefa;
  --wui-green-2: #f5fbf4;
  --wui-green-3: #e7f8e5;
  --wui-green-4: #d8f2d4;
  --wui-green-5: #c6ebc0;
  --wui-green-6: #afe0a6;
  --wui-green-7: #8fd185;
  --wui-green-8: #5fbd51;
  --wui-green-9: #268713;
  --wui-green-10: #117900;
  --wui-green-11: #1e8106;
  --wui-green-12: #1e3d19;
  --wui-green-alpha-1: #33cc0005;
  --wui-green-alpha-2: #18a3000b;
  --wui-green-alpha-3: #14bb001a;
  --wui-green-alpha-4: #18b2012b;
  --wui-green-alpha-5: #19af003f;
  --wui-green-alpha-6: #1aa70059;
  --wui-green-alpha-7: #159f017a;
  --wui-green-alpha-8: #189f00b0;
  --wui-green-alpha-9: #159f00ae;
  --wui-green-alpha-10: #157d00ec;
  --wui-green-alpha-11: #117900;
  --wui-green-alpha-12: #197e00f9;
  --wui-pink-1: #fffcfd;
  --wui-pink-2: #fff7fa;
  --wui-pink-3: #ffe8f3;
  --wui-pink-4: #ffdaeb;
  --wui-pink-5: #fdcbe2;
  --wui-pink-6: #f7bcd8;
  --wui-pink-7: #efa8ca;
  --wui-pink-8: #e68eba;
  --wui-pink-9: #ff40b3;
  --wui-pink-10: #f12fa7;
  --wui-pink-11: #cd0089;
  --wui-pink-12: #680445;
  --wui-pink-alpha-1: #ff005503;
  --wui-pink-alpha-2: #ff006008;
  --wui-pink-alpha-3: #ff007a17;
  --wui-pink-alpha-4: #ff007626;
  --wui-pink-alpha-5: #f6007133;
  --wui-pink-alpha-6: #e1006b42;
  --wui-pink-alpha-7: #d1006457;
  --wui-pink-alpha-8: #c7006470;
  --wui-pink-alpha-9: #ff009abf;
  --wui-pink-alpha-10: #ee0093d1;
  --wui-pink-alpha-11: #cd0089;
  --wui-pink-alpha-12: #660042fa;
  --wui-red-1: #fffcfc;
  --wui-red-2: #fff7f7;
  --wui-red-3: #ffe9e9;
  --wui-red-4: #ffd9d9;
  --wui-red-5: #ffcaca;
  --wui-red-6: #ffbaba;
  --wui-red-7: #fca5a6;
  --wui-red-8: #f5898c;
  --wui-red-9: #ff2b51;
  --wui-red-10: #f10845;
  --wui-red-11: #db0035;
  --wui-red-12: #6d021a;
  --wui-red-alpha-1: #ff000003;
  --wui-red-alpha-2: #ff000008;
  --wui-red-alpha-3: #ff000017;
  --wui-red-alpha-4: #ff000026;
  --wui-red-alpha-5: #ff000036;
  --wui-red-alpha-6: #ff000045;
  --wui-red-alpha-7: #f7010359;
  --wui-red-alpha-8: #ea000775;
  --wui-red-alpha-9: #ff002ed4;
  --wui-red-alpha-10: #f1003ff7;
  --wui-red-alpha-11: #db0035;
  --wui-red-alpha-12: #6c0018fc;
  --wui-yellow-1: #fdfdf8;
  --wui-yellow-2: #fbfcea;
  --wui-yellow-3: #f6fbb8;
  --wui-yellow-4: #f0f491;
  --wui-yellow-5: #e7eb6d;
  --wui-yellow-6: #d8dd5d;
  --wui-yellow-7: #c8cc52;
  --wui-yellow-8: #b3b600;
  --wui-yellow-9: #bcbf19;
  --wui-yellow-10: #b1b400;
  --wui-yellow-11: #7a7b00;
  --wui-yellow-12: #3e3f14;
  --wui-yellow-alpha-1: #b7b70008;
  --wui-yellow-alpha-2: #cfdb0014;
  --wui-yellow-alpha-3: #dff10047;
  --wui-yellow-alpha-4: #dde6006e;
  --wui-yellow-alpha-5: #d5dc0091;
  --wui-yellow-alpha-6: #c2ca00a3;
  --wui-yellow-alpha-7: #aeb400ad;
  --wui-yellow-alpha-8: #b3b600;
  --wui-yellow-alpha-9: #b5b800e5;
  --wui-yellow-alpha-10: #b1b400;
  --wui-yellow-alpha-11: #7a7b00;
  --wui-yellow-alpha-12: #2e2f00eb;
  --wui-orange-1: #fffcfb;
  --wui-orange-2: #fff7f5;
  --wui-orange-3: #ffebe6;
  --wui-orange-4: #ffdbce;
  --wui-orange-5: #fcbfcb;
  --wui-orange-6: #ffbdaa;
  --wui-orange-7: #f4aa95;
  --wui-orange-8: #ea9178;
  --wui-orange-9: #d1451a;
  --wui-orange-10: #c23600;
  --wui-orange-11: #ce4216;
  --wui-orange-12: #592b1e;
  --wui-orange-alpha-1: #ff400005;
  --wui-orange-alpha-2: #ff33000a;
  --wui-orange-alpha-3: #ff33001a;
  --wui-orange-alpha-4: #ff440030;
  --wui-orange-alpha-5: #ff400045;
  --wui-orange-alpha-6: #ff390054;
  --wui-orange-alpha-7: #e533016b;
  --wui-orange-alpha-8: #d8300087;
  --wui-orange-alpha-9: #cc3000e5;
  --wui-orange-alpha-10: #c23600;
  --wui-orange-alpha-11: #c93000e8;
  --wui-orange-alpha-12: #430f00e0;
  --wui-error-1: #fffcfc;
  --wui-error-2: #fff7f7;
  --wui-error-3: #ffebea;
  --wui-error-4: #ffdcd9;
  --wui-error-5: #ffcdca;
  --wui-error-6: #febdb9;
  --wui-error-7: #f5a9a5;
  --wui-error-8: #eb8f8b;
  --wui-error-9: #e5484d;
  --wui-error-10: #d73840;
  --wui-error-11: #cc2e39;
  --wui-error-12: #64181b;
  --wui-error-alpha-1: #ff000003;
  --wui-error-alpha-2: #ff000008;
  --wui-error-alpha-3: #ff0d0014;
  --wui-error-alpha-4: #ff150026;
  --wui-error-alpha-5: #ff0f0036;
  --wui-error-alpha-6: #fc0f0045;
  --wui-error-alpha-7: #e30c0159;
  --wui-error-alpha-8: #d4090073;
  --wui-error-alpha-9: #db0007b8;
  --wui-error-alpha-10: #cc000ac7;
  --wui-error-alpha-11: #c1000ed1;
  --wui-error-alpha-12: #540003e8;
  --wui-success-1: #fbfefc;
  --wui-success-2: #f4fbf7;
  --wui-success-3: #e5f6eb;
  --wui-success-4: #d6f1df;
  --wui-success-5: #c4e8d1;
  --wui-success-6: #adddc0;
  --wui-success-7: #8fcea8;
  --wui-success-8: #60b887;
  --wui-success-9: #30a46c;
  --wui-success-10: #289662;
  --wui-success-11: #00814c;
  --wui-success-12: #1d3b2a;
  --wui-success-alpha-1: #00c04005;
  --wui-success-alpha-2: #00a3460a;
  --wui-success-alpha-3: #00a73b1a;
  --wui-success-alpha-4: #00a83829;
  --wui-success-alpha-5: #019c393b;
  --wui-success-alpha-6: #00963c52;
  --wui-success-alpha-7: #00903970;
  --wui-success-alpha-8: #008d3f9e;
  --wui-success-alpha-9: #008f4acf;
  --wui-success-alpha-10: #008345d6;
  --wui-success-alpha-11: #00814c;
  --wui-success-alpha-12: #00220fe3;
  --wui-warning-1: #fdfdfa;
  --wui-warning-2: #fefceb;
  --wui-warning-3: #fff9bf;
  --wui-warning-4: #fef29f;
  --wui-warning-5: #f8e981;
  --wui-warning-6: #e9da79;
  --wui-warning-7: #d8cb75;
  --wui-warning-8: #c4b54f;
  --wui-warning-9: #ffe62a;
  --wui-warning-10: #f3dd45;
  --wui-warning-11: #897a00;
  --wui-warning-12: #413d23;
  --wui-warning-alpha-1: #99990005;
  --wui-warning-alpha-2: #f3d90014;
  --wui-warning-alpha-3: #ffe80040;
  --wui-warning-alpha-4: #fddd0061;
  --wui-warning-alpha-5: #f1d3007d;
  --wui-warning-alpha-6: #d6b90087;
  --wui-warning-alpha-7: #b79f008a;
  --wui-warning-alpha-8: #aa9400b0;
  --wui-warning-alpha-9: #ffe100d6;
  --wui-warning-alpha-10: #efd100ba;
  --wui-warning-alpha-11: #897a00;
  --wui-warning-alpha-12: #231e00db;
  --wui-gray-1: #fcfcfd;
  --wui-gray-2: #f9f9fb;
  --wui-gray-3: #f0f0f3;
  --wui-gray-4: #e8e8ec;
  --wui-gray-5: #e0e0e5;
  --wui-gray-6: #d9d9de;
  --wui-gray-7: #ceced5;
  --wui-gray-8: #bbbbc4;
  --wui-gray-9: #8d8d95;
  --wui-gray-10: #82828a;
  --wui-gray-11: #64646a;
  --wui-gray-12: #202024;
  --wui-gray-alpha-1: #00005503;
  --wui-gray-alpha-2: #00005505;
  --wui-gray-alpha-3: #0000330f;
  --wui-gray-alpha-4: #00002d17;
  --wui-gray-alpha-5: #00002a1f;
  --wui-gray-alpha-6: #00002226;
  --wui-gray-alpha-7: #00002530;
  --wui-gray-alpha-8: #00002245;
  --wui-gray-alpha-9: #00001273;
  --wui-gray-alpha-10: #0000117d;
  --wui-gray-alpha-11: #00000a9c;
  --wui-gray-alpha-12: #000005de;
  --wui-vendor-hubspot-1: #fefcfb;
  --wui-vendor-hubspot-2: #fff5f2;
  --wui-vendor-hubspot-3: #ffe9e2;
  --wui-vendor-hubspot-4: #ffd7c9;
  --wui-vendor-hubspot-5: #ffc8b7;
  --wui-vendor-hubspot-6: #ffb8a3;
  --wui-vendor-hubspot-7: #ffa48d;
  --wui-vendor-hubspot-8: #f68a70;
  --wui-vendor-hubspot-9: #ff5c36;
  --wui-vendor-hubspot-10: #f24d25;
  --wui-vendor-hubspot-11: #d93a0f;
  --wui-vendor-hubspot-12: #5b2a1e;
  --wui-vendor-hubspot-surface: #fff3efcc;
  --wui-vendor-hubspot-contrast: #ffffff;
  --wui-vendor-marketo-1: #fdfdff;
  --wui-vendor-marketo-2: #f7f8ff;
  --wui-vendor-marketo-3: #eff2ff;
  --wui-vendor-marketo-4: #e3e8ff;
  --wui-vendor-marketo-5: #d5ddff;
  --wui-vendor-marketo-6: #c5cfff;
  --wui-vendor-marketo-7: #b0bcfa;
  --wui-vendor-marketo-8: #93a0f2;
  --wui-vendor-marketo-9: #4c50cc;
  --wui-vendor-marketo-10: #4040bb;
  --wui-vendor-marketo-11: #4f55c7;
  --wui-vendor-marketo-12: #252a62;
  --wui-vendor-marketo-surface: #f5f6ffcc;
  --wui-vendor-marketo-contrast: #ffffff;
  --wui-vendor-pardot-1: #fafdff;
  --wui-vendor-pardot-2: #f3fafe;
  --wui-vendor-pardot-3: #e1f5ff;
  --wui-vendor-pardot-4: #ceeeff;
  --wui-vendor-pardot-5: #bae5ff;
  --wui-vendor-pardot-6: #a4d9fa;
  --wui-vendor-pardot-7: #85c9f1;
  --wui-vendor-pardot-8: #4fb3e8;
  --wui-vendor-pardot-9: #00a1e0;
  --wui-vendor-pardot-10: #0094cf;
  --wui-vendor-pardot-11: #0079b5;
  --wui-vendor-pardot-12: #0c3a51;
  --wui-vendor-pardot-surface: #f0f9fecc;
  --wui-vendor-pardot-contrast: #ffffff;
`;
var darkGlobalTokens = css2`
  --wui-app-background: #101629;
  --wui-blue-surface: #141c2d80;
  --wui-blue-contrast: #ffffff;
  --wui-purple-surface: #34132980;
  --wui-purple-contrast: #ffffff;
  --wui-green-surface: #1c240280;
  --wui-green-contrast: #ffffff;
  --wui-pink-surface: #3c0f1080;
  --wui-pink-contrast: #ffffff;
  --wui-red-surface: #38160480;
  --wui-red-contrast: #ffffff;
  --wui-yellow-surface: #28220080;
  --wui-yellow-contrast: #22230b;
  --wui-orange-surface: #321a0480;
  --wui-orange-contrast: #ffffff;
  --wui-error-surface: #36160280;
  --wui-error-contrast: #ffffff;
  --wui-success-surface: #1a260880;
  --wui-success-contrast: #ffffff;
  --wui-warning-surface: #2c240080;
  --wui-warning-contrast: #262209;
  --wui-gray-surface: #0000000d;
  --wui-gray-contrast: #ffffff;
  --wui-blue-1: #111725;
  --wui-blue-2: #12192b;
  --wui-blue-3: #152452;
  --wui-blue-4: #192c6d;
  --wui-blue-5: #203681;
  --wui-blue-6: #284191;
  --wui-blue-7: #314da7;
  --wui-blue-8: #395ac3;
  --wui-blue-9: #2949e5;
  --wui-blue-10: #1f36d3;
  --wui-blue-11: #90b3ff;
  --wui-blue-12: #d2e1ff;
  --wui-blue-alpha-1: #90960003;
  --wui-blue-alpha-2: #90d6a905;
  --wui-blue-alpha-3: #2b5fff30;
  --wui-blue-alpha-4: #2c5bfd52;
  --wui-blue-alpha-5: #3764ff69;
  --wui-blue-alpha-6: #426fff7d;
  --wui-blue-alpha-7: #4773fe96;
  --wui-blue-alpha-8: #4975ffb8;
  --wui-blue-alpha-9: #2c4ffee0;
  --wui-blue-alpha-10: #233fffcc;
  --wui-blue-alpha-11: #90b3ff;
  --wui-blue-alpha-12: #d2e1ff;
  --wui-purple-1: #1c1320;
  --wui-purple-2: #221429;
  --wui-purple-3: #361544;
  --wui-purple-4: #46135b;
  --wui-purple-5: #521969;
  --wui-purple-6: #5f2578;
  --wui-purple-7: #753491;
  --wui-purple-8: #9643ba;
  --wui-purple-9: #ac12e0;
  --wui-purple-10: #9d00d0;
  --wui-purple-11: #e590ff;
  --wui-purple-12: #f4d2ff;
  --wui-purple-alpha-1: #fc00000d;
  --wui-purple-alpha-2: #f6002914;
  --wui-purple-alpha-3: #fd10d129;
  --wui-purple-alpha-4: #f60afe3d;
  --wui-purple-alpha-5: #eb20fd4d;
  --wui-purple-alpha-6: #e53ffd5e;
  --wui-purple-alpha-7: #e054ff7d;
  --wui-purple-alpha-8: #d659ffad;
  --wui-purple-alpha-9: #c612fedb;
  --wui-purple-alpha-10: #c400ffc7;
  --wui-purple-alpha-11: #e590ff;
  --wui-purple-alpha-12: #f4d2ff;
  --wui-green-1: #111a0f;
  --wui-green-2: #151e13;
  --wui-green-3: #182e14;
  --wui-green-4: #133e0c;
  --wui-green-5: #174c0d;
  --wui-green-6: #1a5c0e;
  --wui-green-7: #1d6d0d;
  --wui-green-8: #1d8004;
  --wui-green-9: #268713;
  --wui-green-10: #117900;
  --wui-green-11: #76d467;
  --wui-green-12: #aff9a3;
  --wui-green-alpha-1: #44e30005;
  --wui-green-alpha-2: #9ef90009;
  --wui-green-alpha-3: #5cf9001b;
  --wui-green-alpha-4: #22fe002c;
  --wui-green-alpha-5: #2efc003c;
  --wui-green-alpha-6: #32fe004d;
  --wui-green-alpha-7: #33fd0060;
  --wui-green-alpha-8: #2cfd0075;
  --wui-green-alpha-9: #3eff007c;
  --wui-green-alpha-10: #13fd006d;
  --wui-green-alpha-11: #8dff75d0;
  --wui-green-alpha-12: #b3fea6f9;
  --wui-pink-1: #1f1319;
  --wui-pink-2: #26121c;
  --wui-pink-3: #3f122b;
  --wui-pink-4: #550438;
  --wui-pink-5: #630b42;
  --wui-pink-6: #741b50;
  --wui-pink-7: #8f2965;
  --wui-pink-8: #b73581;
  --wui-pink-9: #ff40b3;
  --wui-pink-10: #f12fa7;
  --wui-pink-11: #ff86cc;
  --wui-pink-12: #ffcde8;
  --wui-pink-alpha-1: #f1000012;
  --wui-pink-alpha-2: #fa000017;
  --wui-pink-alpha-3: #fb023333;
  --wui-pink-alpha-4: #fe005d4a;
  --wui-pink-alpha-5: #fe007159;
  --wui-pink-alpha-6: #ff22866b;
  --wui-pink-alpha-7: #ff3a9a87;
  --wui-pink-alpha-8: #fe42a7b2;
  --wui-pink-alpha-9: #ff40b3;
  --wui-pink-alpha-10: #fe31aff2;
  --wui-pink-alpha-11: #ff86cc;
  --wui-pink-alpha-12: #ffcde8;
  --wui-red-1: #1e1414;
  --wui-red-2: #241616;
  --wui-red-3: #411014;
  --wui-red-4: #580314;
  --wui-red-5: #68071b;
  --wui-red-6: #7a1725;
  --wui-red-7: #952634;
  --wui-red-8: #c23244;
  --wui-red-9: #ff2b51;
  --wui-red-10: #f00e45;
  --wui-red-11: #ff8e93;
  --wui-red-12: #ffcfce;
  --wui-red-alpha-1: #fe00000f;
  --wui-red-alpha-2: #f8170017;
  --wui-red-alpha-3: #fc000036;
  --wui-red-alpha-4: #ff00004d;
  --wui-red-alpha-5: #ff00045e;
  --wui-red-alpha-6: #fd192173;
  --wui-red-alpha-7: #ff333d8f;
  --wui-red-alpha-8: #ff3b4ebf;
  --wui-red-alpha-9: #ff2b51;
  --wui-red-alpha-10: #ff0e46f0;
  --wui-red-alpha-11: #ff8e93;
  --wui-red-alpha-12: #ffcfce;
  --wui-yellow-1: #171811;
  --wui-yellow-2: #1c1c13;
  --wui-yellow-3: #292a16;
  --wui-yellow-4: #353615;
  --wui-yellow-5: #414218;
  --wui-yellow-6: #4e4f1c;
  --wui-yellow-7: #5c5e20;
  --wui-yellow-8: #6d6f22;
  --wui-yellow-9: #bcbf19;
  --wui-yellow-10: #b1b400;
  --wui-yellow-11: #cbcf4b;
  --wui-yellow-12: #eaeeae;
  --wui-yellow-alpha-1: #f0560008;
  --wui-yellow-alpha-2: #fc8c000d;
  --wui-yellow-alpha-3: #fdd3001c;
  --wui-yellow-alpha-4: #fce20029;
  --wui-yellow-alpha-5: #fcea0036;
  --wui-yellow-alpha-6: #fcef0042;
  --wui-yellow-alpha-7: #fdf60e52;
  --wui-yellow-alpha-8: #fdf91863;
  --wui-yellow-alpha-9: #feff13ba;
  --wui-yellow-alpha-10: #feff00ad;
  --wui-yellow-alpha-11: #fbff54cc;
  --wui-yellow-alpha-12: #fbfeb8ed;
  --wui-orange-1: #1c1514;
  --wui-orange-2: #211816;
  --wui-orange-3: #391a12;
  --wui-orange-4: #4e190b;
  --wui-orange-5: #5c2010;
  --wui-orange-6: #6c2d1c;
  --wui-orange-7: #833d2a;
  --wui-orange-8: #a95038;
  --wui-orange-9: #d1451a;
  --wui-orange-10: #c23600;
  --wui-orange-11: #ff9271;
  --wui-orange-12: #f9d4ca;
  --wui-orange-alpha-1: #fc03000d;
  --wui-orange-alpha-2: #f5310012;
  --wui-orange-alpha-3: #fe2e002b;
  --wui-orange-alpha-4: #fc220042;
  --wui-orange-alpha-5: #fd360052;
  --wui-orange-alpha-6: #fd520863;
  --wui-orange-alpha-7: #ff672c7a;
  --wui-orange-alpha-8: #fe7040a3;
  --wui-orange-alpha-9: #ff5117cf;
  --wui-orange-alpha-10: #ff4100bf;
  --wui-orange-alpha-11: #ff9271;
  --wui-orange-alpha-12: #ffd8cefa;
  --wui-error-1: #1d1514;
  --wui-error-2: #231615;
  --wui-error-3: #3d1414;
  --wui-error-4: #521013;
  --wui-error-5: #62171a;
  --wui-error-6: #732426;
  --wui-error-7: #8c3434;
  --wui-error-8: #b54546;
  --wui-error-9: #e5484d;
  --wui-error-10: #d63941;
  --wui-error-11: #ff8f8b;
  --wui-error-12: #ffd2ce;
  --wui-error-alpha-1: #fd04000d;
  --wui-error-alpha-2: #f7170014;
  --wui-error-alpha-3: #fb0c0030;
  --wui-error-alpha-4: #fd010047;
  --wui-error-alpha-5: #fe190059;
  --wui-error-alpha-6: #ff38226b;
  --wui-error-alpha-7: #fe4f3e85;
  --wui-error-alpha-8: #fe5a52b0;
  --wui-error-alpha-9: #fe4e52e3;
  --wui-error-alpha-10: #fe4046d4;
  --wui-error-alpha-11: #ff8f8b;
  --wui-error-alpha-12: #ffd2ce;
  --wui-success-1: #121915;
  --wui-success-2: #151e18;
  --wui-success-3: #172e21;
  --wui-success-4: #153c27;
  --wui-success-5: #1b4931;
  --wui-success-6: #23573c;
  --wui-success-7: #2b6848;
  --wui-success-8: #327c55;
  --wui-success-9: #30a46c;
  --wui-success-10: #1d9760;
  --wui-success-11: #63d196;
  --wui-success-12: #b2f1cb;
  --wui-success-alpha-1: #90d60005;
  --wui-success-alpha-2: #9ef9000a;
  --wui-success-alpha-3: #53f9001c;
  --wui-success-alpha-4: #2ffd1d29;
  --wui-success-alpha-5: #43ff4e38;
  --wui-success-alpha-6: #54fd6d47;
  --wui-success-alpha-7: #5dff8059;
  --wui-success-alpha-8: #5eff8e70;
  --wui-success-alpha-9: #45fe979c;
  --wui-success-alpha-10: #28fe8c8f;
  --wui-success-alpha-11: #78ffb1cc;
  --wui-success-alpha-12: #bcffd6f0;
  --wui-warning-1: #181711;
  --wui-warning-2: #1e1d14;
  --wui-warning-3: #2b270c;
  --wui-warning-4: #352f00;
  --wui-warning-5: #403800;
  --wui-warning-6: #4d450e;
  --wui-warning-7: #5f5720;
  --wui-warning-8: #796f2b;
  --wui-warning-9: #ffe629;
  --wui-warning-10: #f1dd4e;
  --wui-warning-11: #f2dd4f;
  --wui-warning-12: #f4edb8;
  --wui-warning-alpha-1: #f333000a;
  --wui-warning-alpha-2: #fe8d000f;
  --wui-warning-alpha-3: #feac001c;
  --wui-warning-alpha-4: #fcb50029;
  --wui-warning-alpha-5: #fcbc0033;
  --wui-warning-alpha-6: #fccc0042;
  --wui-warning-alpha-7: #fdd90e54;
  --wui-warning-alpha-8: #fddf2e70;
  --wui-warning-alpha-9: #ffe629;
  --wui-warning-alpha-10: #fee950f2;
  --wui-warning-alpha-11: #fee851f2;
  --wui-warning-alpha-12: #fef7bef5;
  --wui-gray-1: #0d162d;
  --wui-gray-2: #131c35;
  --wui-gray-3: #172448;
  --wui-gray-4: #1c2a50;
  --wui-gray-5: #223057;
  --wui-gray-6: #293860;
  --wui-gray-7: #36466f;
  --wui-gray-8: #4e5f8a;
  --wui-gray-9: #5b6d98;
  --wui-gray-10: #687aa7;
  --wui-gray-11: #9fb3e3;
  --wui-gray-12: #e4eeff;
  --wui-gray-alpha-1: #0017f605;
  --wui-gray-alpha-2: #437cf60f;
  --wui-gray-alpha-3: #4177ff26;
  --wui-gray-alpha-4: #5283fd2e;
  --wui-gray-alpha-5: #638fff38;
  --wui-gray-alpha-6: #719afe42;
  --wui-gray-alpha-7: #84a8fe54;
  --wui-gray-alpha-8: #98b7ff73;
  --wui-gray-alpha-9: #a0bcfe85;
  --wui-gray-alpha-10: #a4bffe96;
  --wui-gray-alpha-11: #b4caffde;
  --wui-gray-alpha-12: #e4eeff;
  --wui-vendor-hubspot-1: #1d1513;
  --wui-vendor-hubspot-2: #231815;
  --wui-vendor-hubspot-3: #3b1a12;
  --wui-vendor-hubspot-4: #511609;
  --wui-vendor-hubspot-5: #601d0d;
  --wui-vendor-hubspot-6: #702a1a;
  --wui-vendor-hubspot-7: #883a27;
  --wui-vendor-hubspot-8: #af4a33;
  --wui-vendor-hubspot-9: #ff5c36;
  --wui-vendor-hubspot-10: #f14f28;
  --wui-vendor-hubspot-11: #ff9578;
  --wui-vendor-hubspot-12: #ffd5ca;
  --wui-vendor-hubspot-surface: #361a0280;
  --wui-vendor-hubspot-contrast: #ffffff;
  --wui-vendor-marketo-1: #141621;
  --wui-vendor-marketo-2: #161827;
  --wui-vendor-marketo-3: #1f234a;
  --wui-vendor-marketo-4: #262a66;
  --wui-vendor-marketo-5: #2f3476;
  --wui-vendor-marketo-6: #383f83;
  --wui-vendor-marketo-7: #444c97;
  --wui-vendor-marketo-8: #515ab4;
  --wui-vendor-marketo-9: #4c50cc;
  --wui-vendor-marketo-10: #4040bb;
  --wui-vendor-marketo-11: #9faeff;
  --wui-vendor-marketo-12: #dae1ff;
  --wui-vendor-marketo-surface: #1c1a2680;
  --wui-vendor-marketo-contrast: #ffffff;
  --wui-vendor-pardot-1: #0e191f;
  --wui-vendor-pardot-2: #111d24;
  --wui-vendor-pardot-3: #092c3d;
  --wui-vendor-pardot-4: #003752;
  --wui-vendor-pardot-5: #004463;
  --wui-vendor-pardot-6: #055274;
  --wui-vendor-pardot-7: #15648a;
  --wui-vendor-pardot-8: #197ba9;
  --wui-vendor-pardot-9: #00a1e0;
  --wui-vendor-pardot-10: #0095d3;
  --wui-vendor-pardot-11: #62c3f8;
  --wui-vendor-pardot-12: #bfe7ff;
  --wui-vendor-pardot-surface: #12242080;
  --wui-vendor-pardot-contrast: #ffffff;
`;

// src/css/designTokens/colorAlias.tsx
import { css as css3 } from "styled-components";
var colorAliasTokens = css3`
  --wui-color-bg-app: var(--wui-app-background);

  /* --- default --- */
  --wui-color-bg-fill: var(--wui-blue-9);
  --wui-color-bg-fill-active: var(--wui-blue-11);
  --wui-color-bg-fill-hover: var(--wui-blue-10);
  --wui-color-bg-surface: var(--wui-gray-1);
  --wui-color-bg-surface-active: var(--wui-gray-4);
  --wui-color-bg-surface-hover: var(--wui-gray-3);
  --wui-color-bg-surface-secondary: var(--wui-gray-3);
  --wui-color-bg-surface-secondary-active: var(--wui-gray-5);
  --wui-color-bg-surface-secondary-hover: var(--wui-gray-4);
  --wui-color-bg-surface-tertiary: var(--wui-gray-5);
  --wui-color-border: var(--wui-gray-6);
  --wui-color-border-hover: var(--wui-gray-8);
  --wui-color-border-active: var(--wui-gray-9);
  --wui-color-border-secondary: var(--wui-gray-8);
  --wui-color-border-secondary-hover: var(--wui-gray-10);
  --wui-color-border-secondary-active: var(--wui-gray-11);
  --wui-color-icon: var(--wui-gray-11);
  --wui-color-icon-on-fill: var(--wui-gray-contrast);
  --wui-color-text: var(--wui-gray-12);
  --wui-color-text-button: var(--wui-gray-12);
  --wui-color-text-link: var(--wui-blue-9);
  --wui-color-text-on-fill: var(--wui-gray-contrast);
  --wui-color-text-secondary: var(--wui-gray-11);
  --wui-color-focus-ring: var(--wui-blue-9);

  /* --- Semantic --- */

  /* Error */
  --wui-color-bg-fill-active-error: var(--wui-error-11);
  --wui-color-bg-fill-error: var(--wui-error-9);
  --wui-color-bg-fill-hover-error: var(--wui-error-10);
  --wui-color-bg-surface-active-error: var(--wui-error-4);
  --wui-color-bg-surface-error: var(--wui-error-2);
  --wui-color-bg-surface-hover-error: var(--wui-error-3);
  --wui-color-bg-surface-secondary-active-error: var(--wui-error-5);
  --wui-color-bg-surface-secondary-error: var(--wui-error-3);
  --wui-color-bg-surface-secondary-hover-error: var(--wui-error-4);
  --wui-color-bg-surface-tertiary-error: var(--wui-error-5);
  --wui-color-border-error: var(--wui-error-6);
  --wui-color-border-hover-errror: var(--wui-error-8);
  --wui-color-border-active-error: var(--wui-error-9);
  --wui-color-border-secondary-error: var(--wui-error-8);
  --wui-color-border-secondary-hover-error: var(--wui-error-10);
  --wui-color-border-secondary-active-error: var(--wui-error-11);
  --wui-color-icon-error: var(--wui-error-11);
  --wui-color-icon-on-fill-error: var(--wui-error-contrast);
  --wui-color-text-error: var(--wui-error-11);
  --wui-color-text-button-error: var(--wui-error-11);
  --wui-color-text-link-error: var(--wui-error-11);
  --wui-color-text-on-fill-error: var(--wui-error-contrast);
  --wui-color-text-secondary-error: var(--wui-error-11);
  --wui-color-focus-ring-error: var(--wui-error-9);

  /* Info */
  --wui-color-bg-fill-active-info: var(--wui-blue-11);
  --wui-color-bg-fill-hover-info: var(--wui-blue-10);
  --wui-color-bg-fill-info: var(--wui-blue-9);
  --wui-color-bg-surface-active-info: var(--wui-blue-4);
  --wui-color-bg-surface-hover-info: var(--wui-blue-3);
  --wui-color-bg-surface-info: var(--wui-blue-2);
  --wui-color-bg-surface-secondary-active-info: var(--wui-blue-5);
  --wui-color-bg-surface-secondary-hover-info: var(--wui-blue-4);
  --wui-color-bg-surface-secondary-info: var(--wui-blue-3);
  --wui-color-bg-surface-tertiary-info: var(--wui-blue-6);
  --wui-color-border-info: var(--wui-blue-6);
  --wui-color-border-hover-info: var(--wui-blue-8);
  --wui-color-border-active-info: var(--wui-blue-9);
  --wui-color-border-secondary-info: var(--wui-blue-8);
  --wui-color-border-secondary-hover-info: var(--wui-blue-10);
  --wui-color-border-secondary-active-info: var(--wui-blue-11);
  --wui-color-icon-info: var(--wui-blue-11);
  --wui-color-icon-on-fill-info: var(--wui-blue-contrast);
  --wui-color-text-info: var(--wui-blue-12);
  --wui-color-text-button-info: var(--wui-blue-11);
  --wui-color-text-link-info: var(--wui-blue-9);
  --wui-color-text-on-fill-info: var(--wui-blue-contrast);
  --wui-color-text-secondary-info: var(--wui-blue-11);
  --wui-color-focus-ring-info: var(--wui-blue-9);

  /* Success */
  --wui-color-bg-fill-active-success: var(--wui-success-11);
  --wui-color-bg-fill-hover-success: var(--wui-success-10);
  --wui-color-bg-fill-success: var(--wui-success-9);
  --wui-color-bg-surface-active-success: var(--wui-success-4);
  --wui-color-bg-surface-hover-success: var(--wui-success-3);
  --wui-color-bg-surface-secondary-active-success: var(--wui-success-5);
  --wui-color-bg-surface-secondary-hover-success: var(--wui-success-4);
  --wui-color-bg-surface-secondary-success: var(--wui-success-3);
  --wui-color-bg-surface-success: var(--wui-success-2);
  --wui-color-bg-surface-tertiary-success: var(--wui-success-5);
  --wui-color-border-success: var(--wui-success-6);
  --wui-color-border-hover-success: var(--wui-success-8);
  --wui-color-border-active-success: var(--wui-success-9);
  --wui-color-border-secondary-success: var(--wui-success-8);
  --wui-color-border-secondary-hover-success: var(--wui-success-10);
  --wui-color-border-secondary-active-success: var(--wui-success-11);
  --wui-color-icon-on-fill-success: var(--wui-success-contrast);
  --wui-color-icon-success: var(--wui-success-11);
  --wui-color-text-link-success: var(--wui-success-9);
  --wui-color-text-on-fill-success: var(--wui-success-contrast);
  --wui-color-text-secondary-success: var(--wui-success-11);
  --wui-color-text-success: var(--wui-success-12);
  --wui-color-text-button-success: var(--wui-success-11);
  --wui-color-focus-ring-success: var(--wui-success-9);

  /* Warning */
  --wui-color-bg-fill-active-warning: var(--wui-warning-11);
  --wui-color-bg-fill-hover-warning: var(--wui-warning-10);
  --wui-color-bg-fill-warning: var(--wui-warning-9);
  --wui-color-bg-surface-active-warning: var(--wui-warning-4);
  --wui-color-bg-surface-hover-warning: var(--wui-warning-3);
  --wui-color-bg-surface-secondary-active-warning: var(--wui-warning-5);
  --wui-color-bg-surface-secondary-hover-warning: var(--wui-warning-4);
  --wui-color-bg-surface-secondary-warning: var(--wui-warning-3);
  --wui-color-bg-surface-tertiary-warning: var(--wui-warning-5);
  --wui-color-bg-surface-warning: var(--wui-warning-2);
  --wui-color-border-warning: var(--wui-warning-6);
  --wui-color-border-hover-warning: var(--wui-warning-8);
  --wui-color-border-active-warning: var(--wui-warning-9);
  --wui-color-border-secondary-warning: var(--wui-warning-8);
  --wui-color-border-secondary-hover-warning: var(--wui-warning-10);
  --wui-color-border-secondary-active-warning: var(--wui-warning-11);
  --wui-color-icon-on-fill-warning: var(--wui-warning-2);
  --wui-color-icon-warning: var(--wui-warning-11);
  --wui-color-text-link-warning: var(--wui-warning-11);
  --wui-color-text-on-fill-warning: var(--wui-warning-contrast);
  --wui-color-text-secondary-warning: var(--wui-warning-11);
  --wui-color-text-button-warning: var(--wui-warning-11);
  --wui-color-text-warning: var(--wui-warning-12);
  --wui-color-focus-ring-warning: var(--wui-warning-9);

  /* --- Accent --- */

  /* Blue */
  --wui-color-bg-fill-active-blue: var(--wui-blue-11);
  --wui-color-bg-fill-blue: var(--wui-blue-9);
  --wui-color-bg-fill-hover-blue: var(--wui-blue-10);
  --wui-color-bg-surface-active-blue: var(--wui-blue-4);
  --wui-color-bg-surface-blue: var(--wui-blue-2);
  --wui-color-bg-surface-hover-blue: var(--wui-blue-3);
  --wui-color-bg-surface-secondary-active-blue: var(--wui-blue-5);
  --wui-color-bg-surface-secondary-blue: var(--wui-blue-3);
  --wui-color-bg-surface-secondary-hover-blue: var(--wui-blue-4);
  --wui-color-bg-surface-tertiary-blue: var(--wui-blue-5);
  --wui-color-border-blue: var(--wui-blue-6);
  --wui-color-border-hover-blue: var(--wui-blue-8);
  --wui-color-border-active-blue: var(--wui-blue-9);
  --wui-color-border-secondary-blue: var(--wui-blue-8);
  --wui-color-border-secondary-hover-blue: var(--wui-blue-10);
  --wui-color-border-secondary-active-blue: var(--wui-blue-11);
  --wui-color-icon-blue: var(--wui-blue-11);
  --wui-color-icon-on-fill-blue: var(--wui-blue-contrast);
  --wui-color-text-blue: var(--wui-blue-12);
  --wui-color-text-button-blue: var(--wui-blue-11);
  --wui-color-text-link-blue: var(--wui-blue-9);
  --wui-color-text-on-fill-blue: var(--wui-blue-2);
  --wui-color-text-secondary-blue: var(--wui-blue-5);
  --wui-color-focus-ring-blue: var(--wui-blue-9);

  /* Green */
  --wui-color-bg-fill-active-green: var(--wui-green-11);
  --wui-color-bg-fill-green: var(--wui-green-9);
  --wui-color-bg-fill-hover-green: var(--wui-green-10);
  --wui-color-bg-surface-active-green: var(--wui-green-4);
  --wui-color-bg-surface-green: var(--wui-green-2);
  --wui-color-bg-surface-hover-green: var(--wui-green-3);
  --wui-color-bg-surface-secondary-active-green: var(--wui-green-5);
  --wui-color-bg-surface-secondary-green: var(--wui-green-3);
  --wui-color-bg-surface-secondary-hover-green: var(--wui-green-4);
  --wui-color-bg-surface-tertiary-green: var(--wui-green-5);
  --wui-color-border-green: var(--wui-green-6);
  --wui-color-border-hover-green: var(--wui-green-8);
  --wui-color-border-active-green: var(--wui-green-9);
  --wui-color-border-secondary-green: var(--wui-green-8);
  --wui-color-border-secondary-hover-green: var(--wui-green-10);
  --wui-color-border-secondary-active-green: var(--wui-green-11);
  --wui-color-icon-green: var(--wui-green-11);
  --wui-color-icon-on-fill-green: var(--wui-green-contrast);
  --wui-color-text-green: var(--wui-green-12);
  --wui-color-text-button-green: var(--wui-green-11);
  --wui-color-text-link-green: var(--wui-green-11);
  --wui-color-text-on-fill-green: var(--wui-green-contrast);
  --wui-color-text-secondary-green: var(--wui-green-10);
  --wui-color-focus-ring-green: var(--wui-green-9);

  /* Orange */
  --wui-color-bg-fill-active-orange: var(--wui-orange-11);
  --wui-color-bg-fill-hover-orange: var(--wui-orange-10);
  --wui-color-bg-fill-orange: var(--wui-orange-9);
  --wui-color-bg-surface-active-orange: var(--wui-orange-4);
  --wui-color-bg-surface-hover-orange: var(--wui-orange-3);
  --wui-color-bg-surface-orange: var(--wui-orange-2);
  --wui-color-bg-surface-secondary-active-orange: var(--wui-orange-5);
  --wui-color-bg-surface-secondary-hover-orange: var(--wui-orange-4);
  --wui-color-bg-surface-secondary-orange: var(--wui-orange-3);
  --wui-color-bg-surface-tertiary-orange: var(--wui-orange-5);
  --wui-color-border-orange: var(--wui-orange-6);
  --wui-color-border-hover-orange: var(--wui-orange-8);
  --wui-color-border-active-orange: var(--wui-orange-9);
  --wui-color-border-secondary-orange: var(--wui-orange-8);
  --wui-color-border-secondary-hover-orange: var(--wui-orange-10);
  --wui-color-border-secondary-active-orange: var(--wui-orange-11);
  --wui-color-icon-on-fill-orange: var(--wui-orange-contrast);
  --wui-color-icon-orange: var(--wui-orange-11);
  --wui-color-text-link-orange: var(--wui-orange-11);
  --wui-color-text-on-fill-orange: var(--wui-orange-contrast);
  --wui-color-text-orange: var(--wui-orange-12);
  --wui-color-text-button-orange: var(--wui-orange-11);
  --wui-color-text-secondary-orange: var(--wui-orange-10);
  --wui-color-focus-ring-orange: var(--wui-orange-9);

  /* Pink */
  --wui-color-bg-fill-active-pink: var(--wui-pink-11);
  --wui-color-bg-fill-hover-pink: var(--wui-pink-10);
  --wui-color-bg-fill-pink: var(--wui-pink-9);
  --wui-color-bg-surface-active-pink: var(--wui-pink-4);
  --wui-color-bg-surface-hover-pink: var(--wui-pink-3);
  --wui-color-bg-surface-pink: var(--wui-pink-2);
  --wui-color-bg-surface-secondary-active-pink: var(--wui-pink-5);
  --wui-color-bg-surface-secondary-hover-pink: var(--wui-pink-4);
  --wui-color-bg-surface-secondary-pink: var(--wui-pink-3);
  --wui-color-bg-surface-tertiary-pink: var(--wui-pink-5);
  --wui-color-border-pink: var(--wui-pink-6);
  --wui-color-border-hover-pink: var(--wui-pink-8);
  --wui-color-border-active-pink: var(--wui-pink-9);
  --wui-color-border-secondary-pink: var(--wui-pink-8);
  --wui-color-border-secondary-hover-pink: var(--wui-pink-10);
  --wui-color-border-secondary-active-pink: var(--wui-pink-11);
  --wui-color-icon-on-fill-pink: var(--wui-pink-contrast);
  --wui-color-icon-pink: var(--wui-pink-11);
  --wui-color-text-link-pink: var(--wui-pink-11);
  --wui-color-text-on-fill-pink: var(--wui-pink-contrast);
  --wui-color-text-pink: var(--wui-pink-12);
  --wui-color-text-button-pink: var(--wui-pink-11);
  --wui-color-text-secondary-pink: var(--wui-pink-10);
  --wui-color-focus-ring-pink: var(--wui-pink-9);

  /* Purple */
  --wui-color-bg-fill-active-purple: var(--wui-purple-11);
  --wui-color-bg-fill-hover-purple: var(--wui-purple-10);
  --wui-color-bg-fill-purple: var(--wui-purple-9);
  --wui-color-bg-surface-active-purple: var(--wui-purple-4);
  --wui-color-bg-surface-hover-purple: var(--wui-purple-3);
  --wui-color-bg-surface-purple: var(--wui-purple-2);
  --wui-color-bg-surface-secondary-active-purple: var(--wui-purple-5);
  --wui-color-bg-surface-secondary-hover-purple: var(--wui-purple-4);
  --wui-color-bg-surface-secondary-purple: var(--wui-purple-3);
  --wui-color-bg-surface-tertiary-purple: var(--wui-purple-5);
  --wui-color-border-purple: var(--wui-purple-6);
  --wui-color-border-hover-purple: var(--wui-purple-8);
  --wui-color-border-active-purple: var(--wui-purple-9);
  --wui-color-border-secondary-purple: var(--wui-purple-8);
  --wui-color-border-secondary-hover-purple: var(--wui-purple-10);
  --wui-color-border-secondary-active-purple: var(--wui-purple-11);
  --wui-color-icon-on-fill-purple: var(--wui-purple-contrast);
  --wui-color-icon-purple: var(--wui-purple-11);
  --wui-color-text-link-purple: var(--wui-purple-11);
  --wui-color-text-on-fill-purple: var(--wui-purple-contrast);
  --wui-color-text-button-purple: var(--wui-purple-11);
  --wui-color-text-purple: var(--wui-purple-12);
  --wui-color-text-secondary-purple: var(--wui-purple-11);
  --wui-color-focus-ring-purple: var(--wui-purple-9);

  /* Yellow */
  --wui-color-bg-fill-active-yellow: var(--wui-yellow-11);
  --wui-color-bg-fill-hover-yellow: var(--wui-yellow-10);
  --wui-color-bg-fill-yellow: var(--wui-yellow-9);
  --wui-color-bg-surface-active-yellow: var(--wui-yellow-4);
  --wui-color-bg-surface-hover-yellow: var(--wui-yellow-3);
  --wui-color-bg-surface-secondary-active-yellow: var(--wui-yellow-5);
  --wui-color-bg-surface-secondary-hover-yellow: var(--wui-yellow-4);
  --wui-color-bg-surface-secondary-yellow: var(--wui-yellow-3);
  --wui-color-bg-surface-tertiary-yellow: var(--wui-yellow-5);
  --wui-color-bg-surface-yellow: var(--wui-yellow-2);
  --wui-color-border-yellow: var(--wui-yellow-6);
  --wui-color-border-hover-yellow: var(--wui-yellow-8);
  --wui-color-border-active-yellow: var(--wui-yellow-9);
  --wui-color-border-secondary-yellow: var(--wui-yellow-8);
  --wui-color-border-secondary-hover-yellow: var(--wui-yellow-10);
  --wui-color-border-secondary-active-yellow: var(--wui-yellow-11);
  --wui-color-icon-on-fill-yellow: var(--wui-yellow-contrast);
  --wui-color-icon-yellow: var(--wui-yellow-11);
  --wui-color-text-link-yellow: var(--wui-yellow-11);
  --wui-color-text-on-fill-yellow: var(--wui-yellow-contrast);
  --wui-color-text-secondary-yellow: var(--wui-yellow-10);
  --wui-color-text-yellow: var(--wui-yellow-11);
  --wui-color-text-button-yellow: var(--wui-yellow-12);
  --wui-color-focus-ring-yellow: var(--wui-yellow-9);

  /* --- Misc --- */
  --wui-color-bg-fill-white: #ffffff;
  --wui-color-focus-color: var(--wui-blue-9);
  --wui-color-invalid-indicator: var(--wui-error-9);
  --wui-color-notification-pill-color: var(--wui-pink-9);
  --wui-color-backdrop: var(--wui-gray-alpha-9);
  --wui-color-segmented-control-checked-background: var(--wui-bg-app);
  --wui-color-text-on-fill-white-selected: var(--wui-blue-9);
  --wui-color-bg-tooltip: #242528;
  --wui-color-drop-shadow: rgb(31 51 153 / 10%);

  /* --- State --- */

  /* Disabled */
  --wui-color-bg-surface-disabled: var(--wui-gray-3);
  --wui-color-border-disabled: var(--wui-gray-4);
  --wui-color-icon-disabled: var(--wui-gray-8);
  --wui-color-text-disabled: var(--wui-gray-10);
  --wui-color-focus-ring-disabled: var(--wui-gray-6);

  /* Selected */
  --wui-color-bg-surface-selected: var(--wui-blue-3);
  --wui-color-bg-surface-selected-active: var(--wui-blue-5);
  --wui-color-bg-surface-selected-hover: var(--wui-blue-4);
  --wui-color-icon-selected: var(--wui-blue-9);
  --wui-color-text-selected: var(--wui-blue-9);
  --wui-color-border-selected: var(--wui-blue-6);
  --wui-color-border-hover-selected: var(--wui-blue-8);
  --wui-color-border-active-selected: var(--wui-blue-9);
`;

// src/css/designTokens/borderRadius.tsx
import { css as css4 } from "styled-components";
var borderRadiusTokens = css4`
  --wui-border-radius-00: 0;
  --wui-border-radius-01: 4px;
  --wui-border-radius-02: 8px;
  --wui-border-radius-03: 16px;
  --wui-border-radius-04: 20px;
  --wui-border-radius-05: 24px;
  --wui-border-radius-rounded: 1000px;
`;

// src/css/designTokens/spacing.tsx
import { css as css5 } from "styled-components";
var spacingTokens = css5`
  --wui-space-00: 0;
  --wui-space-01: 4px;
  --wui-space-02: 8px;
  --wui-space-03: 12px;
  --wui-space-04: 16px;
  --wui-space-05: 24px;
  --wui-space-06: 32px;
  --wui-space-07: 40px;
  --wui-space-08: 48px;
  --wui-space-09: 64px;
`;

// src/css/designTokens/typography.tsx
import { css as css6 } from "styled-components";
var typographyTokens = css6`
  --wui-typography-family-default: intervariable, sans-serif;
  --wui-typography-family-brand: 'GT Walsheim', sans-serif;
  --wui-typography-family-mono: 'IBM Plex Mono', monospace;

  /* Brand Weights */
  --wui-typography-weight-brand-bold: 600;
  --wui-typography-weight-brand-black: 700;

  /* Heading Hero */
  --wui-typography-heading-hero-family: var(--wui-typography-family-brand);
  --wui-typography-heading-hero-line-height: 58px;
  --wui-typography-heading-hero-size: 48px;
  --wui-typography-heading-hero-weight: var(--wui-typography-weight-brand-black);

  /* Heading 1 */
  --wui-typography-heading-1-family: var(--wui-typography-family-brand);
  --wui-typography-heading-1-line-height: 44px;
  --wui-typography-heading-1-size: 36px;
  --wui-typography-heading-1-weight: var(--wui-typography-weight-brand-black);

  /* Heading 2 */
  --wui-typography-heading-2-family: var(--wui-typography-family-brand);
  --wui-typography-heading-2-line-height: 38px;
  --wui-typography-heading-2-size: 32px;
  --wui-typography-heading-2-weight: var(--wui-typography-weight-brand-black);

  /* Heading 3 */
  --wui-typography-heading-3-family: var(--wui-typography-family-brand);
  --wui-typography-heading-3-line-height: 30px;
  --wui-typography-heading-3-size: 24px;
  --wui-typography-heading-3-weight: var(--wui-typography-weight-brand-bold);

  /* Heading 4 */
  --wui-typography-heading-4-family: var(--wui-typography-family-brand);
  --wui-typography-heading-4-line-height: 24px;
  --wui-typography-heading-4-size: 18px;
  --wui-typography-heading-4-weight: var(--wui-typography-weight-brand-bold);

  /* Heading 5 */
  --wui-typography-heading-5-family: var(--wui-typography-family-brand);
  --wui-typography-heading-5-line-height: 22px;
  --wui-typography-heading-5-size: 16px;
  --wui-typography-heading-5-weight: var(--wui-typography-weight-brand-bold);

  /* Heading 6 */
  --wui-typography-heading-6-family: var(--wui-typography-family-brand);
  --wui-typography-heading-6-line-height: 18px;
  --wui-typography-heading-6-size: 14px;
  --wui-typography-heading-6-weight: var(--wui-typography-weight-brand-bold);

  /* Body Weight */
  --wui-typography-weight-body: 425;
  --wui-typography-weight-body-bold: 700;

  /* Body 1 */
  --wui-typography-body-1-family: var(--wui-typography-family-default);
  --wui-typography-body-1-line-height: 30px;
  --wui-typography-body-1-size: 20px;
  --wui-typography-body-1-weight: var(--wui-typography-weight-body);
  --wui-typography-body-1-paragraph-spacing: 16px;

  /* Body 2 */
  --wui-typography-body-2-family: var(--wui-typography-family-default);
  --wui-typography-body-2-line-height: 26px;
  --wui-typography-body-2-size: 16px;
  --wui-typography-body-2-weight: var(--wui-typography-weight-body);
  --wui-typography-body-2-paragraph-spacing: 12px;

  /* Body 3 */
  --wui-typography-body-3-family: var(--wui-typography-family-default);
  --wui-typography-body-3-line-height: 22px;
  --wui-typography-body-3-size: 14px;
  --wui-typography-body-3-weight: var(--wui-typography-weight-body);
  --wui-typography-body-3-paragraph-spacing: 8px;

  /* Body 4 */
  --wui-typography-body-4-family: var(--wui-typography-family-default);
  --wui-typography-body-4-line-height: 18px;
  --wui-typography-body-4-size: 12px;
  --wui-typography-body-4-weight: var(--wui-typography-weight-body);
  --wui-typography-body-4-paragraph-spacing: 8px;

  /* Label Weight */
  --wui-typography-weight-label: 475;
  --wui-typography-weight-label-bold: 600;

  /* Label 1 */
  --wui-typography-label-1-family: var(--wui-typography-family-default);
  --wui-typography-label-1-line-height: 24px;
  --wui-typography-label-1-size: 18px;
  --wui-typography-label-1-weight: var(--wui-typography-weight-label);

  /* Label 2 */
  --wui-typography-label-2-family: var(--wui-typography-family-default);
  --wui-typography-label-2-line-height: 18px;
  --wui-typography-label-2-size: 16px;
  --wui-typography-label-2-weight: var(--wui-typography-weight-label);

  /* Label 3 */
  --wui-typography-label-3-family: var(--wui-typography-family-default);
  --wui-typography-label-3-line-height: 16px;
  --wui-typography-label-3-size: 14px;
  --wui-typography-label-3-weight: var(--wui-typography-weight-label);

  /* Label 4 */
  --wui-typography-label-4-family: var(--wui-typography-family-default);
  --wui-typography-label-4-line-height: 14px;
  --wui-typography-label-4-size: 12px;
  --wui-typography-label-4-weight: var(--wui-typography-weight-label);
`;

// src/css/designTokens/zIndex.tsx
import { css as css7 } from "styled-components";
var zIndexTokens = css7`
  --wui-zindex-under: -1;
  --wui-zindex-backdrop: 500;
  --wui-zindex-modal: 500;
  --wui-zindex-menu: 500;
  --wui-zindex-select: 500;
  --wui-zindex-tooltip: 500;
`;

// src/css/designTokens/elevation.tsx
import { css as css8 } from "styled-components";
var elevationTokens = css8`
  --wui-elevation-01: 0px 4px 8px 0px var(--wui-color-drop-shadow);
  --wui-elevation-02: 0px 4px 24px 0px var(--wui-color-drop-shadow);
`;

// src/css/designTokens/motion.tsx
import { css as css9 } from "styled-components";
var motionTokens = css9`
  /* Durations */
  --wui-motion-duration-01: 70ms;
  --wui-motion-duration-02: 110ms;
  --wui-motion-duration-03: 150ms;
  --wui-motion-duration-04: 240ms;
  --wui-motion-duration-05: 400ms;
  --wui-motion-duration-06: 700ms;

  @media (prefers-reduced-motion: reduce) {
    --wui-motion-duration-01: 0.01ms;
    --wui-motion-duration-02: 0.01ms;
    --wui-motion-duration-03: 0.01ms;
    --wui-motion-duration-04: 0.01ms;
    --wui-motion-duration-05: 0.01ms;
    --wui-motion-duration-06: 0.01ms;
  }

  /* Easings */

  /* Taken from: https://polaris.shopify.com/tokens/motion */
  --wui-motion-ease: cubic-bezier(0.25, 0.1, 0.25, 1);
  --wui-motion-ease-in: cubic-bezier(0.42, 0, 1, 1);
  --wui-motion-ease-out: cubic-bezier(0.19, 0.91, 0.38, 1);
  --wui-motion-ease-in-out: cubic-bezier(0.42, 0, 0.58, 1);
  --wui-motion-linear: cubic-bezier(0, 0, 1, 1);
`;

// src/css/designTokens/index.tsx
var colorTokens = css10`
  ${lightGlobalTokens}
  /* Dark mode is disabled until we have a more robust plan for supporting it */

  /* @media (prefers-color-scheme: dark) {
    ${darkGlobalTokens}
  } */
  ${colorAliasTokens}
`;
var designTokens = css10`
  :root {
    ${borderRadiusTokens}
    ${colorTokens}
    ${elevationTokens}
    ${spacingTokens}
    ${typographyTokens}
    ${zIndexTokens}
    ${motionTokens}
  }
`;

// src/components/ClickRegion/globalStyles.tsx
import { css as css11 } from "styled-components";
var clickRegionGlobalStyles = css11`
  [data-click-region] {
    position: relative;
    cursor: pointer;
    z-index: 1;
  }

  [data-click-region] a:not([data-click-region-target-link]),
  [data-click-region] button:not([data-click-region-target-button]) {
    z-index: 2;
  }

  [data-click-region-target-link]::before {
    content: '';
    cursor: inherit;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
  }
`;

// src/css/GlobalStyle.tsx
var GlobalStyle = createGlobalStyle`
  ${globalStyleAdjustmentsCss}
  ${designTokens}
  ${clickRegionGlobalStyles}
`;

// src/providers/AriaLiveProvider/AriaLiveProvider.tsx
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { isNonEmptyString } from "@wistia/type-guards";

// src/providers/AriaLiveProvider/AriaLiveContext.tsx
import { createContext } from "react";
var AriaLiveContext = createContext(void 0);

// src/css/visuallyHiddenStyle.tsx
var visuallyHiddenStyle = {
  border: "0",
  clip: "rect(1px, 1px, 1px, 1px)",
  clipPath: "inset(50%)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: "0",
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px"
};

// src/providers/AriaLiveProvider/AriaLiveProvider.tsx
import { jsx } from "react/jsx-runtime";
var debugStyle = {
  position: "absolute",
  border: "3px dashed red",
  backgroundColor: "white",
  padding: "10px",
  margin: "5px",
  minHeight: "50px",
  width: "calc(100% - 10px)"
};
var AriaLiveProvider = ({ children, debug = false }) => {
  const [message, setMessage] = useState(null);
  const liveRegionRef = useRef(null);
  useEffect(() => {
    const liveRegion = document.createElement("div");
    liveRegion.setAttribute("role", "alert");
    liveRegion.setAttribute("aria-live", "polite");
    liveRegion.setAttribute("aria-atomic", "true");
    Object.assign(liveRegion.style, debug ? debugStyle : visuallyHiddenStyle);
    document.body.appendChild(liveRegion);
    liveRegionRef.current = liveRegion;
    return () => {
      if (liveRegionRef.current) {
        document.body.removeChild(liveRegionRef.current);
      }
    };
  }, [debug]);
  useEffect(() => {
    if (liveRegionRef.current && isNonEmptyString(message)) {
      liveRegionRef.current.textContent = message;
    }
  }, [message]);
  const announce = useCallback(
    (announcement, politeness = "polite") => {
      if (liveRegionRef.current) {
        liveRegionRef.current.setAttribute("aria-live", politeness);
        setMessage(announcement);
        setTimeout(() => setMessage(null), 1e3);
      }
    },
    []
  );
  const value = useMemo(() => ({ announce }), [announce]);
  return /* @__PURE__ */ jsx(AriaLiveContext.Provider, { value, children });
};

// src/providers/UIProvider/ToastProvider.tsx
import { Toaster } from "sonner";
import { jsx as jsx2 } from "react/jsx-runtime";
var toastConfig = {
  expand: false,
  // ensure the Toasts "stack" when there are multiple visible
  gap: 24,
  // space between Toasts when expanded (--wui-space-05)
  position: "bottom-left",
  // ensure this value matches the Position type
  visibleToasts: 3
  // number of Toasts to show at a time
};
var ToastProvider = () => /* @__PURE__ */ jsx2(Toaster, { ...toastConfig });

// src/providers/UIProvider/UIProvider.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var UIProviderNestingContext = createContext2(false);
var UIProvider = ({ children }) => {
  const isNested = useContext(UIProviderNestingContext);
  if (isNested) {
    console.warn("UIProvider is nested. Only one instance of UIProvider.");
  }
  return /* @__PURE__ */ jsx3(UIProviderNestingContext.Provider, { value: true, children: /* @__PURE__ */ jsx3(AriaLiveProvider, { children: /* @__PURE__ */ jsxs(TooltipProvider, { children: [
    /* @__PURE__ */ jsx3(GlobalStyle, {}),
    children,
    /* @__PURE__ */ jsx3(ToastProvider, {})
  ] }) }) });
};

// src/helpers/copyToClipboard/copyToClipboard.ts
import { isNil } from "@wistia/type-guards";
var copyToClipboard = async (textToCopy) => {
  if (isNil(navigator.clipboard)) {
    throw new DOMException("The clipboard is not accessible", "NotAllowedError");
  }
  return navigator.clipboard.writeText(textToCopy).catch((error) => {
    if (error instanceof Error) {
      throw error;
    }
    throw new DOMException("The clipboard is not accessible", "NotAllowedError");
  });
};

// src/helpers/dateTime/constants.ts
var defaultLocales = ["en-US"];
var defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
var secondsInMinute = 60;
var minutesInHour = 60;
var halfAnHourInMinutes = 30;
var hoursInDay = 24;
var milisecondsInSecond = 1e3;
var millisecondsInDay = hoursInDay * minutesInHour * secondsInMinute * milisecondsInSecond;

// src/helpers/dateTime/buildTimeDuration.ts
var buildTimeDuration = (numberOfMilliseconds) => {
  const numberOfSeconds = Math.floor(numberOfMilliseconds / milisecondsInSecond);
  const numberOfMinutes = Math.floor(numberOfSeconds / secondsInMinute);
  const numberOfHours = Math.floor(numberOfMinutes / minutesInHour);
  const seconds = numberOfSeconds - numberOfMinutes * secondsInMinute;
  const minutes = numberOfMinutes - numberOfHours * minutesInHour;
  return { seconds, minutes, hours: numberOfHours };
};

// src/helpers/dateTime/isDate.ts
var isDate = (date) => date instanceof Date;

// src/helpers/dateTime/isInvalidDate.ts
var isInvalidDate = (date) => {
  if (!isDate(date)) {
    return true;
  }
  const time = date.getTime();
  return time === 0 || Number.isNaN(time);
};

// src/helpers/dateTime/dateOnlyISOString.ts
var dateOnlyISOString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    const formatter = new Intl.DateTimeFormat(defaultLocales, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone
    });
    const parts = formatter.formatToParts(date);
    const partsAsObject = parts.reduce(
      (map, obj) => {
        map[obj.type] = obj.value;
        return map;
      },
      {}
    );
    return `${partsAsObject.year}-${partsAsObject.month}-${partsAsObject.day}`;
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateOnlyString.ts
var dateOnlyString = (date, { timeZone = defaultTimeZone, omitYear = false } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  const formatOptions = omitYear ? { month: "short", day: "numeric", timeZone } : { dateStyle: "medium", timeZone };
  try {
    return new Intl.DateTimeFormat(defaultLocales, formatOptions).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateOnlyStringForSentence.ts
var dateOnlyStringForSentence = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      dateStyle: "long",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateOnlyStringNumeric.ts
var dateOnlyStringNumeric = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateTimeToDate.ts
var dateTimeToDate = (dateTime2) => {
  const { year, month, dayOfMonth, hours, minutes } = dateTime2 ?? {};
  if (year !== void 0 && month !== void 0 && dayOfMonth !== void 0) {
    return new Date(year, month, dayOfMonth, hours ?? 0, minutes ?? 0);
  }
  return null;
};

// src/helpers/dateTime/dateToDateTime.ts
var dateToDateTime = (date) => {
  if (!isDate(date)) {
    return null;
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    dayOfMonth: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes()
  };
};

// src/helpers/dateTime/dateTimeToISO.ts
var dateTimeToISO = (dateTime2) => dateTimeToDate(dateTime2)?.toISOString() ?? null;

// src/helpers/dateTime/dateTimeRounded.ts
var dateTimeRounded = (dateTime2, toISOString = true) => {
  const first30Min = dateTime2.getMinutes() <= halfAnHourInMinutes;
  const hours = first30Min ? dateTime2.getHours() : dateTime2.getHours() + 1;
  const minutes = first30Min ? halfAnHourInMinutes : 0;
  const dateWithTime = {
    ...dateToDateTime(dateTime2),
    hours,
    minutes
  };
  if (toISOString) {
    return dateTimeToISO(dateWithTime);
  }
  return dateTimeToDate(dateWithTime);
};

// src/helpers/dateTime/dateTimeString.ts
import { isNil as isNil2 } from "@wistia/type-guards";
var dateTimeString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (isNil2(date)) {
    return "";
  }
  const formattedDate = new Date(date);
  if (isInvalidDate(formattedDate)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone
    }).format(formattedDate);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateTimeStringForSentence.ts
var dateTimeStringForSentence = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    const { year, month, day, hour, minute, dayPeriod } = new Intl.DateTimeFormat(defaultLocales, {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone
    }).formatToParts(date).reduce((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
    return `${month} ${day}, ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/dateUTCOffset.ts
var dateUTCOffset = (date) => {
  const offsetInHours = date.getTimezoneOffset() / minutesInHour * -1;
  const hours = Math.round(offsetInHours);
  const minutes = (offsetInHours - hours) * minutesInHour;
  const prefix = hours >= 0 ? "+" : "";
  const hoursString = `${hours}`.padStart(2, "0");
  const minutesString = `${minutes}`.padStart(2, "0");
  return `${prefix}${hoursString}:${minutesString}`;
};

// src/helpers/dateTime/dayOfWeekString.ts
var dayOfWeekString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (date === null) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      weekday: "long",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/padTimeInteger.ts
var padTimeInteger = (num) => num.toString().padStart(2, "0");

// src/helpers/dateTime/mediaDurationString.ts
var mediaDurationString = (numberOfMilliseconds) => {
  const { hours, minutes, seconds } = buildTimeDuration(numberOfMilliseconds);
  if (hours < 1) {
    return `${minutes}:${padTimeInteger(seconds)}`;
  }
  return `${hours}:${padTimeInteger(minutes)}:${padTimeInteger(seconds)}`;
};

// src/helpers/dateTime/millisecondsToDurationISOString.ts
var millisecondsToDurationISOString = (numberOfMilliseconds) => {
  const { seconds, minutes, hours } = buildTimeDuration(numberOfMilliseconds);
  let string = "PT";
  if (hours) {
    string += `${hours}H`;
  }
  if (minutes) {
    string += `${minutes}M`;
  }
  if (seconds) {
    string += `${seconds}S`;
  }
  if (!(seconds || minutes || hours)) {
    string += "0S";
  }
  return string;
};

// src/helpers/dateTime/monthDayStringNumeric.ts
var monthDayStringNumeric = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      month: "2-digit",
      day: "2-digit",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/sessionDurationString.ts
var sessionDurationString = (numberOfMilliseconds) => {
  const { hours, minutes, seconds } = buildTimeDuration(numberOfMilliseconds);
  return `${hours}:${padTimeInteger(minutes)}:${padTimeInteger(seconds)}`;
};

// src/helpers/dateTime/getLocalDateParts.ts
import { isNil as isNil3 } from "@wistia/type-guards";
var getLocalDateParts = (date) => {
  const parts = new Intl.DateTimeFormat(defaultLocales, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: defaultTimeZone
  }).formatToParts(date);
  const yearPart = parts.find((part) => part.type === "year");
  const monthPart = parts.find((part) => part.type === "month");
  const dayPart = parts.find((part) => part.type === "day");
  if (isNil3(yearPart) || isNil3(monthPart) || isNil3(dayPart)) {
    throw new Error("Failed to parse date parts");
  }
  const year = parseInt(yearPart.value, 10);
  const month = parseInt(monthPart.value, 10);
  const day = parseInt(dayPart.value, 10);
  return { year, month, day };
};

// src/helpers/dateTime/differenceInCalendarDays.ts
var differenceInCalendarDays = (dateLeft, dateRight) => {
  const leftDate = isDate(dateLeft) ? dateLeft : new Date(dateLeft);
  const rightDate = isDate(dateRight) ? dateRight : new Date(dateRight);
  if (Number.isNaN(leftDate.getTime()) || Number.isNaN(rightDate.getTime())) {
    return NaN;
  }
  const { year: y1, month: m1, day: d1 } = getLocalDateParts(leftDate);
  const { year: y2, month: m2, day: d2 } = getLocalDateParts(rightDate);
  const startOfDayLeft = new Date(y1, m1 - 1, d1).getTime();
  const startOfDayRight = new Date(y2, m2 - 1, d2).getTime();
  const msDiff = startOfDayLeft - startOfDayRight;
  return Math.round(msDiff / millisecondsInDay);
};

// src/helpers/dateTime/timeAgoString.ts
var timeAgoString = (date, { nowAnchor = /* @__PURE__ */ new Date() } = {}) => {
  if (isInvalidDate(date)) {
    return "";
  }
  const minutesAgo = (nowAnchor.valueOf() - date.valueOf()) / (secondsInMinute * milisecondsInSecond);
  const minutesAgoRounded = Math.round(minutesAgo);
  const differenceInDays = differenceInCalendarDays(nowAnchor, date);
  if (minutesAgo < 0) {
    return dateTimeString(date);
  }
  if (minutesAgo < 1) {
    return "< 1 minute ago";
  }
  if (minutesAgoRounded < 2) {
    return "1 minute ago";
  }
  if (minutesAgoRounded <= minutesInHour) {
    return `${minutesAgoRounded} minutes ago`;
  }
  if (differenceInDays === 0) {
    return `Today, ${Intl.DateTimeFormat(defaultLocales, { timeStyle: "short" }).format(date)}`;
  }
  if (differenceInDays === 1) {
    return `Yesterday, ${Intl.DateTimeFormat(defaultLocales, { timeStyle: "short" }).format(date)}`;
  }
  if (date.getFullYear() === nowAnchor.getFullYear()) {
    return Intl.DateTimeFormat(defaultLocales, {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit"
    }).format(date);
  }
  return dateTimeString(date);
};

// src/helpers/dateTime/timeOnlyString.ts
var timeOnlyString = (date, { timeZone = defaultTimeZone } = {}) => {
  if (!isDate(date) || isInvalidDate(date)) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(defaultLocales, {
      timeStyle: "short",
      timeZone
    }).format(date);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// src/helpers/dateTime/index.ts
var dateTime = {
  buildTimeDuration,
  dateOnlyISOString,
  dateOnlyString,
  dateOnlyStringForSentence,
  dateOnlyStringNumeric,
  dateTimeRounded,
  dateTimeString,
  dateTimeStringForSentence,
  dateTimeToDate,
  dateTimeToISO,
  dateToDateTime,
  dateUTCOffset,
  dayOfWeekString,
  mediaDurationString,
  millisecondsToDurationISOString,
  monthDayStringNumeric,
  sessionDurationString,
  timeAgoString,
  timeOnlyString
};

// src/helpers/mergeRefs/mergeRefs.ts
import { isNotNil, isFunction } from "@wistia/type-guards";
var mergeRefs = (refs) => (value) => {
  refs.forEach((ref) => {
    if (isFunction(ref)) {
      ref(value);
      return;
    }
    if (isNotNil(ref)) {
      ref.current = value;
    }
  });
};

// src/helpers/mq/mq.ts
import { getValueAndUnit } from "polished";

// src/css/breakpoints.ts
var breakpoints = {
  xs: "0em",
  // 0px
  sm: "37.5em",
  // 600px
  md: "56.5625em",
  // ~904px
  lg: "77.5em",
  // 1240px
  xl: "90em"
  // 1440px
};

// src/helpers/mq/mq.ts
var getMaxWidth = (breakpoint) => {
  const breakpointBuffer = 25e-4;
  const [value, unit] = getValueAndUnit(breakpoint);
  return `@media (max-width: ${Math.max(value - breakpointBuffer, 0)}${unit})`;
};
var xsAndUp = () => `@media (min-width: ${breakpoints.xs})`;
var smAndUp = () => `@media (min-width: ${breakpoints.sm})`;
var mdAndUp = () => `@media (min-width: ${breakpoints.md})`;
var lgAndUp = () => `@media (min-width: ${breakpoints.lg})`;
var xlAndUp = () => `@media (min-width: ${breakpoints.xl})`;
var xsAndDown = () => getMaxWidth(breakpoints.xs);
var smAndDown = () => getMaxWidth(breakpoints.sm);
var mdAndDown = () => getMaxWidth(breakpoints.md);
var lgAndDown = () => getMaxWidth(breakpoints.lg);
var xlAndDown = () => getMaxWidth(breakpoints.xl);
var mq = {
  xsAndUp,
  smAndUp,
  mdAndUp,
  lgAndUp,
  xlAndUp,
  xsAndDown,
  smAndDown,
  mdAndDown,
  lgAndDown,
  xlAndDown
};

// src/hooks/useAriaLive/useAriaLive.tsx
import { useContext as useContext2 } from "react";
import { isNil as isNil4 } from "@wistia/type-guards";
var useAriaLive = () => {
  const context = useContext2(AriaLiveContext);
  if (isNil4(context)) {
    throw new Error("useAriaLive must be used within an AriaLiveProvider");
  }
  return context;
};

// src/hooks/useBoolean/useBoolean.ts
import { useState as useState2, useCallback as useCallback2 } from "react";
var useBoolean = (initialValue = false) => {
  const [value, setValue] = useState2(initialValue);
  const toggle = useCallback2(() => setValue((val) => !val), []);
  const setTrue = useCallback2(() => setValue(true), []);
  const setFalse = useCallback2(() => setValue(false), []);
  return [value, toggle, setTrue, setFalse, setValue];
};

// src/hooks/useClipboard/useClipboard.ts
import { useCallback as useCallback3 } from "react";

// src/private/hooks/useTimedToggle/useTimedToggle.ts
import { useEffect as useEffect2, useRef as useRef2, useState as useState3 } from "react";
var useTimedToggle = (initialValue) => {
  const [value, setValue] = useState3(false);
  const timeoutRef = useRef2();
  const initialValueRef = useRef2(initialValue);
  const toggleValue = (timeout) => {
    clearTimeout(timeoutRef.current);
    setValue(!initialValueRef.current);
    timeoutRef.current = window.setTimeout(() => setValue(initialValueRef.current), timeout);
  };
  useEffect2(() => () => clearTimeout(timeoutRef.current), []);
  return [value, toggleValue];
};

// src/hooks/useClipboard/useClipboard.ts
var useClipboard = (textToCopy, timeout = 1500) => {
  const [hasCopied, toggleHasCopied] = useTimedToggle(false);
  const [failedToCopy, toggleFailedToCopy] = useTimedToggle(false);
  const onCopy = useCallback3(async () => {
    try {
      await copyToClipboard(textToCopy);
      if (timeout && timeout > 0) {
        toggleHasCopied(timeout);
      }
    } catch (error) {
      if (error instanceof Error) {
        toggleFailedToCopy(timeout);
      }
    }
  }, [textToCopy, timeout, toggleHasCopied, toggleFailedToCopy]);
  return [onCopy, hasCopied, failedToCopy];
};

// src/hooks/useFilePicker/useFilePicker.tsx
import { useFilePicker, useImperativeFilePicker } from "use-file-picker";
import {
  FileAmountLimitValidator,
  FileTypeValidator,
  FileSizeValidator,
  ImageDimensionsValidator,
  PersistentFileAmountLimitValidator
} from "use-file-picker/validators";

// src/hooks/useFocusTrap/useFocusTrap.ts
import { useRef as useRef3, useCallback as useCallback4, useEffect as useEffect3 } from "react";
import { isNotUndefined } from "@wistia/type-guards";

// src/hooks/useFocusTrap/helpers.ts
var FOCUSABLE_ELEMENT_SELECTORS = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]';
var coerceToString = (value) => value === null || value === void 0 ? "" : String(value);
var isHiddenElement = (element) => {
  const { display, visibility } = window.getComputedStyle(element);
  const isHidden = display === "none" || element.style.display === "none" || visibility === "none" || element.style.visibility === "hidden";
  return element.offsetWidth <= 0 && element.offsetHeight <= 0 || isHidden;
};
var isVisibleElement = (element) => {
  let parentElement = element;
  while (parentElement) {
    if (parentElement === document.body) {
      break;
    }
    if (isHiddenElement(parentElement)) {
      return false;
    }
    parentElement = parentElement.parentNode;
  }
  return true;
};
var getElementTabIndex = (element) => {
  const tabIndex = element.getAttribute("tabindex");
  return Number.parseInt(tabIndex ?? void 0, 10);
};
var isTabIndexNaN = (element) => {
  const tabIndex = getElementTabIndex(element);
  return Number.isNaN(tabIndex);
};
var isFocusableElement = (element) => {
  const tabbableNodeRegEx = /input|select|textarea|button|object/;
  const nodeName = element.nodeName.toLowerCase();
  const isTabIndexNotNaN = !isTabIndexNaN(element);
  const isFocusable = (
    // @ts-expect-error - Disabled is specific to buttons and inputs, but we could be dealing with any number of types here.  Disabled would be undefined for those, so ignoring.
    tabbableNodeRegEx.test(nodeName) && !element.disabled || (element instanceof HTMLAnchorElement ? element.href || isTabIndexNotNaN : isTabIndexNotNaN)
  );
  return Boolean(isFocusable) && isVisibleElement(element);
};
var isTabbableElement = (element) => {
  const tabIndex = getElementTabIndex(element);
  return (isTabIndexNaN(element) || tabIndex >= 0) && isFocusableElement(element);
};
var findTabbableDescendants = (element) => Array.from(element.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)).filter(
  isTabbableElement
);
var focusLaterElements = [];
var focusElement = null;
var needToFocus = false;
var handleBlur = () => {
  needToFocus = true;
};
var handleFocus = () => {
  if (needToFocus) {
    needToFocus = false;
    if (!focusElement) {
      return;
    }
    if (focusElement.contains(document.activeElement)) {
      return;
    }
    const element = findTabbableDescendants(focusElement)[0] ?? focusElement;
    element.focus();
  }
};
var markForFocusLater = () => {
  const element = document.activeElement;
  if (element !== null) {
    focusLaterElements.push(element);
  }
};
var returnFocus = () => {
  let toFocus = null;
  try {
    toFocus = focusLaterElements.pop();
    if (toFocus) {
      toFocus.focus();
    }
  } catch {
    console.warn(
      `You tried to return focus to ${coerceToString(toFocus)} but it is not in the DOM anymore`
    );
  }
};
var setupScopedFocus = (element) => {
  focusElement = element;
  document.addEventListener("focusout", handleBlur, false);
  document.addEventListener("focusin", handleFocus, true);
};
var teardownScopedFocus = () => {
  focusElement = null;
  document.removeEventListener("focusout", handleBlur);
  document.removeEventListener("focusin", handleFocus);
};
var scopeTab = (node, event) => {
  const tabbable = findTabbableDescendants(node);
  if (!tabbable.length) {
    event.preventDefault();
    return;
  }
  const finalTabbable = tabbable[event.shiftKey ? 0 : tabbable.length - 1];
  const leavingFinalTabbable = finalTabbable === document.activeElement || node === document.activeElement;
  if (!leavingFinalTabbable) {
    return;
  }
  event.preventDefault();
  const target = tabbable[event.shiftKey ? tabbable.length - 1 : 0];
  if (target) {
    target.focus();
  }
};
var createAriaHider = (containerNode, selector) => {
  selector ??= "body > :not(script)";
  const rootNodes = Array.from(document.querySelectorAll(selector)).map((node) => {
    if (node.contains(containerNode)) {
      return void 0;
    }
    const ariaHidden = node.getAttribute("aria-hidden");
    if (ariaHidden === null || ariaHidden === "false") {
      node.setAttribute("aria-hidden", "true");
    }
    return {
      node,
      ariaHidden
    };
  });
  return () => {
    rootNodes.forEach((item) => {
      if (!item) {
        return;
      }
      if (item.ariaHidden === null) {
        item.node.removeAttribute("aria-hidden");
      } else {
        item.node.setAttribute("aria-hidden", item.ariaHidden);
      }
    });
  };
};

// src/hooks/useFocusTrap/useFocusTrap.ts
var isRef = (val) => {
  return val !== null && typeof val === "object" && "current" in val;
};
var useFocusTrap = (active = true, options = {}) => {
  const ref = useRef3(null);
  const restoreAriaRef = useRef3(null);
  const setRef = useCallback4(
    (node) => {
      if (restoreAriaRef.current !== null) {
        restoreAriaRef.current();
      }
      if (ref.current) {
        returnFocus();
        teardownScopedFocus();
      }
      if (active && node !== null && node !== void 0) {
        setupScopedFocus(node);
        markForFocusLater();
        const processNode = (node2) => {
          restoreAriaRef.current = !(options.disableAriaHider ?? false) ? createAriaHider(node2) : null;
          let focusElement2 = null;
          if (isNotUndefined(options.focusSelector)) {
            if (isRef(options.focusSelector)) {
              focusElement2 = options.focusSelector.current;
            } else {
              focusElement2 = typeof options.focusSelector === "string" ? node2.querySelector(options.focusSelector) : options.focusSelector;
            }
          }
          if (!focusElement2) {
            const children = Array.from(
              node2.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)
            );
            focusElement2 = // Prefer tabbable elements, But fallback to any focusable element
            children.find(isTabbableElement) ?? // But fallback to any focusable element
            children.find(isFocusableElement) ?? // Nothing found
            null;
            if (!focusElement2 && isFocusableElement(node2)) {
              focusElement2 = node2;
            }
          }
          if (focusElement2) {
            focusElement2.focus();
          }
          if (!focusElement2 && process.env["NODE_ENV"] === "development") {
            console.warn(
              '[useFocusTrap]: Failed to find a focusable element after activating the focus trap. Make sure to include at an element that can recieve focus. As a fallback, you can also set "tabIndex={-1}" on the focus trap node.',
              node2
            );
          }
        };
        setTimeout(() => {
          if (node.ownerDocument) {
            processNode(node);
          }
          if (!node.ownerDocument && process.env["NODE_ENV"] === "development") {
            console.warn(
              "[useFocusTrap]: The focus trap is not part of the DOM yet, so it is unable to correctly set focus. Make sure to render the ref node.",
              node
            );
          }
        });
        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active, options.focusSelector, options.disableAriaHider]
  );
  useEffect3(() => {
    if (!active) {
      return void 0;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Tab" && ref.current) {
        scopeTab(ref.current, event);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active]);
  return setRef;
};

// src/hooks/useForceUpdate/useForceUpdate.ts
import { useCallback as useCallback5, useState as useState4 } from "react";
var useForceUpdate = () => {
  const [, dispatch] = useState4(/* @__PURE__ */ Object.create(null));
  return useCallback5(() => {
    dispatch(/* @__PURE__ */ Object.create(null));
  }, [dispatch]);
};

// src/hooks/useIsHovered/useIsHovered.ts
import { useState as useState5 } from "react";
var useIsHovered = () => {
  const [isHovered, setIsHovered] = useState5(false);
  const onFocus = () => setIsHovered(true);
  const onMouseEnter = () => setIsHovered(true);
  const onMouseMove = () => setIsHovered(true);
  const onBlur = () => setIsHovered(false);
  const onMouseLeave = () => setIsHovered(false);
  const hoverProps = {
    onFocus,
    onMouseEnter,
    onBlur,
    onMouseLeave
  };
  if (!isHovered) {
    hoverProps.onMouseMove = onMouseMove;
  }
  return [isHovered, hoverProps, setIsHovered];
};

// src/hooks/useKey/useKey.ts
import { useCallback as useCallback6 } from "react";

// src/private/hooks/useEvent/useEvent.ts
import { useEffect as useEffect4, useRef as useRef4 } from "react";

// src/private/helpers/isValidRef/isValidRef.ts
import { isNotNil as isNotNil2, isRecord } from "@wistia/type-guards";
var isValidRef = (value) => {
  return isNotNil2(value) && isRecord(value) && "current" in value;
};

// src/private/helpers/noOpFn/noOpFn.ts
var noOpFn = () => {
};

// src/private/hooks/useEvent/useEvent.ts
var isEventTargetSupported = (eventTarget) => (
  // @ts-expect-error the compiler thinks that addEventListener may not exist on eventTarget, but we still need to check it.
  Boolean(typeof eventTarget === "object" && eventTarget?.addEventListener)
);
var useEvent = (eventName, eventHandler, eventTarget = window, eventOptions = {}) => {
  const savedEventHandler = useRef4();
  const savedEventOptions = useRef4();
  useEffect4(() => {
    savedEventHandler.current = eventHandler;
  }, [eventHandler]);
  useEffect4(() => {
    savedEventOptions.current = eventOptions;
  }, [eventOptions]);
  useEffect4(() => {
    const target = isValidRef(eventTarget) ? eventTarget.current : eventTarget;
    if (!eventName || !isEventTargetSupported(target)) {
      return;
    }
    const eventListener = (event) => {
      if (savedEventHandler.current !== void 0) {
        return savedEventHandler.current(event);
      }
      return noOpFn;
    };
    target.addEventListener(eventName, eventListener, savedEventOptions.current);
    return () => {
      target.removeEventListener(eventName, eventListener, savedEventOptions.current);
    };
  }, [eventName, eventTarget, savedEventOptions]);
};

// src/hooks/useKey/useKey.ts
var useKey = (key, eventHandler, { eventName = "keydown", eventTarget, eventOptions } = {}) => {
  const memoizedEventHandler = useCallback6(
    (handlerEvent) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.nodeName ?? "") || document.activeElement?.isContentEditable) {
        return;
      }
      let matchesKey = false;
      if (typeof key === "function") {
        matchesKey = key(handlerEvent);
      } else if (typeof key === "string") {
        matchesKey = handlerEvent.key === key;
      } else if (key) {
        matchesKey = true;
      } else {
        matchesKey = false;
      }
      if (matchesKey) {
        eventHandler(handlerEvent);
      }
    },
    [key, eventHandler]
  );
  useEvent(eventName, memoizedEventHandler, eventTarget, eventOptions);
};

// src/hooks/useLocalStorage/useLocalStorage.ts
import { useState as useState6 } from "react";
var useLocalStorage = (key, initialValue, storage = window.localStorage) => {
  const [storedValue, setStoredValue] = useState6(() => {
    try {
      const item = storage.getItem(key);
      return item !== null && Boolean(item) ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      storage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };
  const removeValue = () => {
    storage.removeItem(key);
    setStoredValue(void 0);
  };
  return [storedValue, setValue, removeValue];
};

// src/hooks/useLockBodyScroll/useLockBodyScroll.ts
import { useLayoutEffect } from "react";
var useLockBodyScroll = (locked) => {
  useLayoutEffect(() => {
    if (locked) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
    return noOpFn;
  }, [locked]);
};

// src/hooks/useMq/useMq.ts
import { isBoolean } from "@wistia/type-guards";

// src/hooks/useWindowSize/useWindowSize.ts
import { useState as useState7, useLayoutEffect as useLayoutEffect2 } from "react";
import { debounce } from "throttle-debounce";

// src/private/helpers/isClient/isClient.ts
var isClient = () => typeof window !== "undefined" && typeof document !== "undefined";

// src/hooks/useWindowSize/useWindowSize.ts
var useWindowSize = (interval = 0) => {
  const [dimensions, setDimensions] = useState7({
    width: isClient() ? window.innerWidth : 0,
    height: isClient() ? window.innerHeight : 0
  });
  useLayoutEffect2(() => {
    const handleResize = debounce(
      interval,
      () => setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })
    );
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [interval]);
  return dimensions;
};

// src/hooks/useMq/useMq.ts
var REM_SIZE = 16;
var useMq = () => {
  const { width } = useWindowSize();
  const viewportWidth = width / REM_SIZE;
  const isXsAndUp = true;
  const isSmAndUp = viewportWidth >= Number.parseFloat(breakpoints.sm);
  const isMdAndUp = viewportWidth >= Number.parseFloat(breakpoints.md);
  const isLgAndUp = viewportWidth >= Number.parseFloat(breakpoints.lg);
  const isXlAndUp = viewportWidth >= Number.parseFloat(breakpoints.xl);
  const isXsAndDown = viewportWidth < Number.parseFloat(breakpoints.sm);
  const isSmAndDown = viewportWidth < Number.parseFloat(breakpoints.md);
  const isMdAndDown = viewportWidth < Number.parseFloat(breakpoints.lg);
  const isLgAndDown = viewportWidth < Number.parseFloat(breakpoints.xl);
  const isXlAndDown = true;
  return {
    isXlAndUp,
    isLgAndUp,
    isMdAndUp,
    isSmAndUp,
    isXsAndUp,
    isXsAndDown,
    isSmAndDown,
    isMdAndDown,
    isLgAndDown,
    isXlAndDown
  };
};
var useActiveMq = () => {
  const mq2 = useMq();
  const keys = Object.keys(mq2);
  return keys.filter((key) => {
    const value = mq2[key];
    return isBoolean(value) && value;
  });
};

// src/hooks/useOnClickOutside/useOnClickOutside.ts
import { useEffect as useEffect5 } from "react";
var useOnClickOutside = (ref, handler, eventTypes = ["mousedown", "touchend"]) => {
  useEffect5(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    if (Array.isArray(eventTypes)) {
      eventTypes.forEach((eventType) => {
        document.addEventListener(eventType, listener);
      });
    } else {
      document.addEventListener(eventTypes, listener);
    }
    return () => {
      if (Array.isArray(eventTypes)) {
        eventTypes.forEach((eventType) => {
          document.removeEventListener(eventType, listener);
        });
      } else {
        document.removeEventListener(eventTypes, listener);
      }
    };
  }, [ref, handler, eventTypes]);
};

// src/hooks/usePreviousValue/usePreviousValue.ts
import { useEffect as useEffect6, useRef as useRef5 } from "react";
var usePreviousValue = (value) => {
  const ref = useRef5();
  useEffect6(() => {
    ref.current = value;
  });
  return ref.current;
};

// src/hooks/useToast/useToast.tsx
import { useCallback as useCallback7 } from "react";
import { toast as sonnerToast } from "sonner";

// src/private/components/Toast/Toast.tsx
import { cloneElement, isValidElement } from "react";
import styled2 from "styled-components";
import { isNotNil as isNotNil4 } from "@wistia/type-guards";

// src/components/Ellipsis/Ellipsis.tsx
import { isNotNil as isNotNil3 } from "@wistia/type-guards";
import styled, { css as css13 } from "styled-components";

// src/css/lineClampCss.tsx
import { css as css12 } from "styled-components";
var lineClampCss = ($lines = 1) => css12`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${$lines};
  display: -webkit-box !important;
  white-space: initial;
`;

// src/components/Ellipsis/Ellipsis.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var ellipsisStyle = css13`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;

  /*
    Using feature detection (i.e., -webkit-named-image) we only target Safari browsers
    with this additional styling to prevent a bug that's exclusive to Safari.

    Note that Safari does not properly hide the truncated text for non-inline/inline-block
    elements so we need to apply display: inline to all the paragraphs in the description.
    see https://stackoverflow.com/a/70683084
  */
  @supports (background: - webkit-named-image(i)) {
    & > div > div:has(p) {
      margin-top: 16px;
    }

    & > div > div > p {
      display: inline;
    }
  }
`;
var ellipsisFlexParentStyle = css13`
  min-width: 0;
`;
var EllipsisComponent = styled.span`
  ${ellipsisStyle};
  ${({ $lines }) => {
  if (isNotNil3($lines)) {
    return lineClampCss($lines);
  }
  return void 0;
}}
`;
var Ellipsis = ({ children, lines, ...props }) => /* @__PURE__ */ jsx4(
  EllipsisComponent,
  {
    $lines: lines,
    ...props,
    children
  }
);
Ellipsis.displayName = "Ellipsis";

// src/private/helpers/getColorScheme/getColorScheme.ts
import { css as css14 } from "styled-components";
var colorSchemeSchema = [
  { token: "bg-surface", step: "2" },
  { token: "bg-surface-hover", step: "3" },
  { token: "bg-surface-active", step: "4" },
  { token: "bg-surface-secondary", step: "3" },
  { token: "bg-surface-secondary-hover", step: "4" },
  { token: "bg-surface-secondary-active", step: "5" },
  { token: "bg-surface-tertiary", step: "5" },
  { token: "bg-fill", step: "9" },
  { token: "bg-fill-hover", step: "10" },
  { token: "bg-fill-active", step: "11" },
  { token: "text", step: "12" },
  { token: "text-link", step: "9" },
  { token: "text-secondary", step: "11" },
  { token: "text-button", step: "11" },
  { token: "text-on-fill", step: "contrast" },
  { token: "border", step: "6" },
  { token: "border-hover", step: "8" },
  { token: "border-active", step: "9" },
  { token: "border-secondary", step: "8" },
  { token: "border-secondary-hover", step: "10" },
  { token: "border-secondary-active", step: "11" },
  { token: "icon", step: "11" },
  { token: "icon-on-fill", step: "contrast" },
  { token: "focus-ring", step: "9" }
];
var defaultScheme = css14`
  --wui-color-bg-surface: var(--wui-gray-1);
  --wui-color-bg-surface-hover: var(--wui-gray-3);
  --wui-color-bg-surface-active: var(--wui-gray-4);
  --wui-color-bg-surface-secondary: var(--wui-gray-3);
  --wui-color-bg-surface-secondary-hover: var(--wui-gray-4);
  --wui-color-bg-surface-secondary-active: var(--wui-gray-5);
  --wui-color-bg-surface-tertiary: var(--wui-gray-5);
  --wui-color-bg-fill: var(--wui-blue-9);
  --wui-color-bg-fill-hover: var(--wui-blue-10);
  --wui-color-bg-fill-active: var(--wui-blue-11);
  --wui-color-text: var(--wui-gray-12);
  --wui-color-text-button: var(--wui-gray-12);
  --wui-color-text-link: var(--wui-blue-9);
  --wui-color-text-secondary: var(--wui-gray-11);
  --wui-color-text-on-fill: var(--wui-gray-contrast);
  --wui-color-border: var(--wui-gray-6);
  --wui-color-border-hover: var(--wui-gray-8);
  --wui-color-border-active: var(--wui-gray-9);
  --wui-color-border-secondary: var(--wui-gray-8);
  --wui-color-border-secondary-hover: var(--wui-gray-10);
  --wui-color-border-secondary-active: var(--wui-gray-11);
  --wui-color-icon: var(--wui-gray-11);
  --wui-color-icon-on-fill: var(--wui-gray-contrast);
  --wui-color-focus-ring: var(--wui-blue-9);
`;
var navScheme = css14`
  --wui-color-bg-app: #1e3399;

  --wui-color-bg-fill: var(--wui-blue-1);
  --wui-color-bg-fill-hover: #e7e9f4;
  --wui-color-bg-fill-active: #dcdfef;

  --wui-color-bg-surface: transparent;
  --wui-color-bg-surface-hover: #15267b;
  --wui-color-bg-surface-active: #122271;

  --wui-color-bg-surface-secondary: #15267b;
  --wui-color-bg-surface-secondary-hover: #122271;
  --wui-color-bg-surface-secondary-active: #101e67;

  --wui-color-bg-surface-tertiary: #0d1a5c;

  --wui-color-border: #101e67;
  --wui-color-border-hover: #0e1c61;
  --wui-color-border-active: #0d1a5c;

  --wui-color-border-secondary: var(--wui-color-bg-fill);
  --wui-color-border-secondary-hover: var(--wui-color-bg-fill-hover);
  --wui-color-border-secondary-active: var(--wui-color-bg-fill-active);

  --wui-color-icon: var(--wui-blue-1);
  --wui-color-icon-on-fill: var(--wui-color-bg-app);

  --wui-color-text: var(--wui-blue-1);
  --wui-color-text-on-fill: var(--wui-color-bg-app);
  --wui-color-text-link: var(--wui-blue-4);
  --wui-color-text-secondary: var(--wui-blue-6);
  --wui-color-text-button: var(--wui-blue-1);

  --wui-color-focus-ring: var(--wui-blue-1);

  /* Nav - Disabled */
  --wui-color-bg-surface-disabled: transparent;
  --wui-color-icon-disabled: var(--wui-blue-8);
  --wui-color-text-disabled: var(--wui-blue-8);

  /* Nav - Selected */
  --wui-color-bg-surface-selected: var(--wui-color-bg-fill);
  --wui-color-bg-surface-selected-active: var(--wui-color-bg-fill-active);
  --wui-color-bg-surface-selected-hover: var(--wui-color-bg-fill-hover);
  --wui-color-icon-selected: var(--wui-color-bg-app);
  --wui-color-text-selected: var(--wui-color-bg-app);
  --wui-color-border-selected: transparent;
  --wui-color-border-hover-selected: transparent;
  --wui-color-border-active-selected: transparent;
`;
var getColorScheme = (colorScheme) => {
  if (colorScheme === "nav") {
    return navScheme;
  }
  if (colorScheme === "inherit") {
    return null;
  }
  if (colorScheme === "default") {
    return defaultScheme;
  }
  return css14`
    ${colorSchemeSchema.map(({ token, step }) => {
    return `--wui-color-${token}: var(--wui-${colorScheme === "info" ? "blue" : colorScheme}-${step});`;
  }).join("")}
  `;
};

// src/private/components/Toast/Toast.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var Message = styled2(Ellipsis)`
  line-height: var(--wui-typography-label-3-line-height);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);

  ${lineClampCss(3)}
`;
var MessageWrapper = styled2.div`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  margin-right: var(--wui-space-03);

  svg {
    align-self: center;
    min-width: 16px;
    margin-right: var(--wui-space-02); /* space between icon & message */
  }
`;
var ActionWrapper = styled2.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  margin-right: var(--wui-space-03);
`;
var StyledToast = styled2.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  width: 356px;
  border-radius: var(--wui-border-radius-01);
  background-color: var(--wui-color-bg-surface);
  border: 1px solid var(--wui-color-border);
  color: var(--wui-color-text);
  padding-top: var(--wui-space-02);
  padding-right: 0; /* padding here is on the left/right slots */
  padding-bottom: var(--wui-space-02);
  padding-left: var(--wui-space-03);
  box-shadow: var(--wui-elevation-02);

  svg {
    height: 16px;
    width: 16px;
  }
`;
var Action = ({ actionButton }) => {
  if (isNotNil4(actionButton) && isValidElement(actionButton)) {
    return /* @__PURE__ */ jsx5(ActionWrapper, { children: cloneElement(actionButton, {
      variant: "soft",
      // force Button variant
      size: "sm"
      // force Button size
    }) });
  }
  return null;
};
var Toast = ({
  action,
  message,
  colorScheme = "inherit",
  icon,
  ...props
}) => {
  return /* @__PURE__ */ jsxs2(
    StyledToast,
    {
      ...props,
      $colorScheme: colorScheme,
      children: [
        /* @__PURE__ */ jsxs2(MessageWrapper, { children: [
          isNotNil4(icon) ? icon : null,
          /* @__PURE__ */ jsx5(Message, { lines: 3, children: message })
        ] }),
        /* @__PURE__ */ jsx5(Action, { actionButton: action })
      ]
    }
  );
};
Toast.displayName = "Toast";

// src/hooks/useToast/useToast.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var useToast = () => {
  return useCallback7(
    ({ message, action, colorScheme, icon, position = "bottom-left", duration = 3e3 }) => {
      sonnerToast.custom(
        () => {
          return /* @__PURE__ */ jsx6(
            Toast,
            {
              action,
              colorScheme,
              icon,
              message
            }
          );
        },
        { position, duration }
      );
    },
    []
  );
};

// src/components/ActionButton/ActionButton.tsx
import { forwardRef as forwardRef3 } from "react";
import styled6, { css as css17 } from "styled-components";

// src/components/Button/Button.tsx
import { forwardRef as forwardRef2 } from "react";
import styled5 from "styled-components";
import { isNotNil as isNotNil6, isNotUndefined as isNotUndefined4 } from "@wistia/type-guards";

// src/css/buttonResetCss.tsx
import { css as css15 } from "styled-components";
var buttonResetCss = css15`
  align-items: center;
  background-color: transparent;
  background-image: none;
  border: none;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  justify-content: center;
  line-height: 1;
  padding: 0;
  text-align: left;
  text-decoration: none;
  vertical-align: middle;

  /*
  * Fixes input types in iOS. Higher specificity for Safari to deal with
  * Normalize defaults
  */
  &,
  &[type='button'],
  &[type='reset'],
  &[type='submit'] {
    appearance: none;
  }

  /* Normalize focus behavior of Firefox */
  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  /* add some default affordance for a focused button */
  &:focus {
    outline: thin dotted #999999;
  }

  &[aria-disabled] {
    cursor: not-allowed;
  }
`;

// src/components/Button/buttonStyles.tsx
import { css as css16 } from "styled-components";
var buttonSmallStyles = css16`
  --button-padding-vertical: var(--wui-space-01);
  --button-padding-horizontal: var(--wui-space-02);
  --button-label-padding-horizontal: var(--wui-space-01);
  --button-typography-family: var(--wui-typography-family-default);
  --button-typography-size: var(--wui-typography-label-4-size);
  --button-typography-weight: var(--wui-typography-label-4-weight);
  --button-typography-line-height: 16px;
  --button-icon-size: 12px;
  --button-label-gap: 4px;
`;
var buttonMediumStyles = css16`
  --button-padding-vertical: var(--wui-space-02);
  --button-padding-horizontal: var(--wui-space-02);
  --button-label-padding-horizontal: 6px;
  --button-typography-family: var(--wui-typography-family-default);
  --button-typography-size: var(--wui-typography-label-3-size);
  --button-typography-weight: var(--wui-typography-label-3-weight);
  --button-typography-line-height: 16px;
  --button-icon-size: 16px;
  --button-label-gap: 6px;
`;
var buttonLargeStyles = css16`
  --button-padding-vertical: var(--wui-space-02);
  --button-padding-horizontal: var(--wui-space-02);
  --button-label-padding-horizontal: var(--wui-space-02);
  --button-typography-family: var(--wui-typography-family-default);
  --button-typography-size: var(--wui-typography-label-1-size);
  --button-typography-weight: var(--wui-typography-label-1-weight);
  --button-typography-line-height: 24px;
  --button-icon-size: 24px;
  --button-label-gap: 8px;
`;
var buttonExtraLargeStyles = css16`
  --button-padding-vertical: var(--wui-space-03);
  --button-padding-horizontal: var(--wui-space-03);
  --button-label-padding-horizontal: var(--wui-space-02);
  --button-typography-family: var(--wui-typography-family-brand);
  --button-typography-size: 18px;
  --button-typography-weight: var(--wui-typography-weight-brand-bold);
  --button-typography-line-height: 24px;
  --button-icon-size: 24px;
  --button-label-gap: 8px;
`;
var ghostButtonVariant = css16`
  --button-color-bg: transparent;
  --button-color-bg-hover: var(--wui-color-bg-surface-hover);
  --button-color-bg-active: var(--wui-color-bg-surface-active);
  --button-color-text: var(--wui-color-text-button);
  --button-color-icon: var(--wui-color-icon);
`;
var outlineButtonVariant = css16`
  ${ghostButtonVariant};
  --button-color-border: var(--wui-color-border);

  box-shadow: 0 0 0 2px var(--button-color-border) inset;
`;
var softButtonVariant = css16`
  --button-color-bg: var(--wui-color-bg-surface-secondary);
  --button-color-bg-hover: var(--wui-color-bg-surface-secondary-hover);
  --button-color-bg-active: var(--wui-color-bg-surface-secondary-active);
  --button-color-text: var(--wui-color-text-button);
  --button-color-icon: var(--wui-color-icon);
`;
var solidButtonVariant = css16`
  --button-color-bg: var(--wui-color-bg-fill);
  --button-color-bg-hover: var(--wui-color-bg-fill-hover);
  --button-color-bg-active: var(--wui-color-bg-fill-active);
  --button-color-text: var(--wui-color-text-on-fill);
  --button-color-icon: var(--wui-color-icon-on-fill);
`;
var disabledButtonVariant = css16`
  --button-color-bg: var(--wui-color-bg-surface-disabled);
  --button-color-bg-hover: var(--wui-color-bg-surface-disabled);
  --button-color-bg-active: var(--wui-color-bg-surface-disabled);
  --button-color-text: var(--wui-color-text-disabled);
  --button-color-icon: var(--wui-color-icon-disabled);
  --button-color-border: var(--wui-color-border-disabled);
`;
var pressedButtonVariant = css16`
  --button-color-bg: var(--wui-color-bg-surface-selected);
  --button-color-bg-hover: var(--wui-color-bg-surface-selected-hover);
  --button-color-bg-active: var(--wui-color-bg-surface-selected-active);
  --button-color-text: var(--wui-color-text-selected);
  --button-color-icon: var(--wui-color-icon-selected);
  --button-color-border: var(--wui-color-border-selected);
`;
var buttonBaseStyles = css16`
  border-radius: var(--wui-border-radius-rounded);
  font-family: var(--button-typography-family);
  font-size: var(--button-typography-size);
  font-weight: var(--button-typography-weight);
  line-height: var(--button-typography-line-height);
  padding: var(--button-padding-vertical) var(--button-padding-horizontal);
  background: var(--button-color-bg);
  color: var(--button-color-text);
  transition: background-color var(--wui-motion-duration-01) var(--wui-motion-ease);

  /* This ensure that the icon is the corect color when the style is solid or the button is disabled */
  && {
    svg {
      color: var(--button-color-icon);
      width: var(--button-icon-size);
      height: var(--button-icon-size);
    }
  }

  &:hover,
  &.force-state-hover {
    background: var(--button-color-bg-hover);
  }

  &:active,
  &.force-state-active {
    background: var(--button-color-bg-active);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible,
  &.force-state-focus {
    outline: 2px solid var(--wui-color-focus-ring);
    outline-offset: 2px;
    background: var(--button-color-bg-hover);

    &[aria-disabled='true'] {
      outline-color: var(--wui-color-border-disabled);
    }
  }

  &[aria-disabled='true'] {
    ${disabledButtonVariant};
  }

  &[aria-pressed='true'] {
    ${pressedButtonVariant};
  }
`;
var buttonVariantsStyles = {
  ghost: ghostButtonVariant,
  outline: outlineButtonVariant,
  soft: softButtonVariant,
  solid: solidButtonVariant
};
var buttonSizeStyles = {
  sm: buttonSmallStyles,
  md: buttonMediumStyles,
  lg: buttonLargeStyles,
  xl: buttonExtraLargeStyles
};

// src/components/Icon/Icon.tsx
import { isNil as isNil5 } from "@wistia/type-guards";
import styled3 from "styled-components";

// src/components/Icon/icons/AbTestIcon.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var AbTestIcon = (props) => /* @__PURE__ */ jsx7(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M20.0599 18.296L15.3332 9.09199V3.33334H16.3332C16.8852 3.33334 17.3332 2.88534 17.3332 2.33334C17.3332 1.78134 16.8852 1.33334 16.3332 1.33334H7.66659C7.11459 1.33334 6.66659 1.78134 6.66659 2.33334C6.66659 2.88534 7.11459 3.33334 7.66659 3.33334H8.66658V9.09199L3.93993 18.296C3.4586 19.2333 3.4986 20.3293 4.04793 21.2293C4.59726 22.1293 5.55459 22.6666 6.60792 22.6666H17.3906C18.4452 22.6666 19.4026 22.1293 19.9506 21.2293C20.4999 20.3293 20.5399 19.2333 20.0586 18.296H20.0599ZM10.5559 9.79066C10.6292 9.64932 10.6666 9.49199 10.6666 9.33332V3.33334H13.3332V9.33332C13.3332 9.49199 13.3706 9.64932 13.4439 9.79066L15.6052 14H8.39458L10.5559 9.79066Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/AccessibilityIcon.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var AccessibilityIcon = (props) => /* @__PURE__ */ jsx8(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx8(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9998 1.33334C6.13319 1.33334 1.33321 6.13333 1.33321 12C1.33321 17.8666 6.13319 22.6666 11.9998 22.6666C17.8665 22.6666 22.6665 17.8666 22.6665 12C22.6665 6.13333 17.8665 1.33334 11.9998 1.33334ZM11.9998 4.32C12.9598 4.32 13.8132 5.17333 13.8132 6.13333C13.8132 7.09333 12.9598 7.94666 11.9998 7.94666C11.0398 7.94666 10.1865 7.09333 10.1865 6.13333C10.1865 5.17333 11.0398 4.32 11.9998 4.32ZM9.33319 19.68C9.22652 19.68 9.11985 19.68 8.90652 19.5733C8.37319 19.36 8.26652 18.8266 8.47985 18.2933C8.47985 18.2933 9.97318 14.24 10.2932 12.7466C10.3998 12.2133 10.5065 11.7867 10.5065 11.1467C10.5065 10.9333 10.3998 10.72 10.1865 10.6133L6.55986 9.54666C6.02653 9.33332 5.70653 8.79999 5.91986 8.37333C6.02653 7.83999 6.55986 7.62666 7.09319 7.73333C7.09319 7.73333 10.7198 8.90666 11.9998 8.90666C13.2798 8.90666 17.0132 7.73333 17.0132 7.73333C17.5465 7.62666 18.0798 7.94666 18.1865 8.37333C18.2932 8.90666 17.9732 9.43999 17.5465 9.54666L13.9198 10.72C13.7065 10.8267 13.5998 10.9333 13.5998 11.2533C13.5998 11.8933 13.7065 12.32 13.8132 12.8533C14.1332 14.3466 15.6265 18.4 15.6265 18.4C15.8398 18.9333 15.6265 19.4666 15.1998 19.68C15.0932 19.7866 14.9865 19.7866 14.7732 19.7866C14.4532 19.7866 14.0265 19.5733 13.9198 19.2533L11.9998 14.3466L10.1865 19.2533C10.0799 19.5733 9.65318 19.68 9.33319 19.68Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ActivityIcon.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var ActivityIcon = (props) => /* @__PURE__ */ jsx9(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx9(
      "path",
      {
        d: "M11.9998 1.33334C6.11847 1.33334 1.33315 6.11867 1.33315 12C1.33315 17.8813 6.11847 22.6666 11.9998 22.6666C17.8811 22.6666 22.6664 17.8813 22.6664 12C22.6664 6.11867 17.8811 1.33334 11.9998 1.33334ZM18.3731 10.3733L14.8451 13.9013C14.1971 14.5506 13.1371 14.5506 12.4878 13.9013L10.3331 11.7467L7.0398 15.04C6.84513 15.2346 6.58913 15.3333 6.33313 15.3333C6.07713 15.3333 5.82113 15.236 5.62647 15.04C5.2358 14.6493 5.2358 14.016 5.62647 13.6253L9.15446 10.0973C9.80246 9.44799 10.8625 9.44799 11.5118 10.0973L13.6664 12.252L16.9598 8.95866C17.3504 8.56799 17.9838 8.56799 18.3744 8.95866C18.7651 9.34932 18.7651 9.98266 18.3744 10.3733H18.3731Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/AddLayoutIcon.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var AddLayoutIcon = (props) => /* @__PURE__ */ jsx10(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx10(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.0004 2.66699H5.00041C2.97774 2.66699 1.33374 4.31099 1.33374 6.33366V17.667C1.33374 19.6897 2.97774 21.3337 5.00041 21.3337H13.8369C13.5179 20.857 13.3318 20.2838 13.3318 19.6673C13.3318 19.5545 13.338 19.4432 13.3501 19.3337H9.33374V4.66699H19.0004C19.9191 4.66699 20.6671 5.41499 20.6671 6.33366V13.5055C21.8311 13.9179 22.6651 15.0288 22.6651 16.334V16.6673H22.6671V6.33366C22.6671 4.31099 21.0231 2.66699 19.0004 2.66699ZM20.6651 18.6673H22.9984C23.5504 18.6673 23.9984 19.1153 23.9984 19.6673C23.9984 20.2193 23.5504 20.6673 22.9984 20.6673H20.6651V23.0006C20.6651 23.5526 20.2171 24.0006 19.6651 24.0006C19.1131 24.0006 18.6651 23.5526 18.6651 23.0006V20.6673H16.3318C15.7798 20.6673 15.3318 20.2193 15.3318 19.6673C15.3318 19.1153 15.7798 18.6673 16.3318 18.6673H18.6651V16.334C18.6651 15.782 19.1131 15.334 19.6651 15.334C20.2171 15.334 20.6651 15.782 20.6651 16.334V18.6673Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AddMediaIcon.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var AddMediaIcon = (props) => /* @__PURE__ */ jsx11(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx11(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.6664 2.66666H6.33307C4.31041 2.66666 2.66641 4.31065 2.66641 6.33331V17.6666C2.66641 19.6893 4.31041 21.3333 6.33307 21.3333H13.8384C13.5192 20.8565 13.3331 20.2833 13.3331 19.6666C13.3331 18.01 14.6765 16.6666 16.333 16.6666H16.6664V16.3333C16.6664 14.6767 18.0098 13.3333 19.6664 13.3333C20.283 13.3333 20.8563 13.5195 21.333 13.8386V6.33331C21.333 4.31065 19.689 2.66666 17.6664 2.66666ZM15.3382 19.7688C15.3895 20.2729 15.8155 20.6666 16.333 20.6666H18.6664V21.1948L18.6664 22.9999C18.6664 23.5519 19.1144 23.9999 19.6664 23.9999C20.2184 23.9999 20.6664 23.5519 20.6664 22.9999V20.6666H22.9997C23.5517 20.6666 23.9997 20.2186 23.9997 19.6666C23.9997 19.1146 23.5517 18.6666 22.9997 18.6666L21.1946 18.6666H20.6664V16.3333C20.6664 15.7813 20.2184 15.3333 19.6664 15.3333C19.1144 15.3333 18.6664 15.7813 18.6664 16.3333V18.6666H16.333C15.781 18.6666 15.3331 19.1146 15.3331 19.6666C15.3331 19.7011 15.3348 19.7352 15.3382 19.7688ZM15.037 12.864L10.1704 15.7026C10.0131 15.7946 9.83973 15.8413 9.6664 15.8413C9.49573 15.8413 9.32506 15.796 9.16773 15.7066C8.85306 15.5266 8.6664 15.2026 8.6664 14.84V9.16131C8.6664 8.79864 8.8544 8.47464 9.16773 8.29464C9.4824 8.11331 9.85706 8.11598 10.1691 8.29731L15.0357 11.136C15.3464 11.3173 15.533 11.64 15.533 12C15.533 12.36 15.349 12.6826 15.037 12.864Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AnnotationLinkIcon.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var AnnotationLinkIcon = (props) => /* @__PURE__ */ jsx12(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx12(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.333 8.66666H16.333C16.885 8.66666 17.333 8.21866 17.333 7.66666C17.333 7.11466 16.885 6.66666 16.333 6.66666H15.333C14.5983 6.66666 13.9997 6.068 13.9997 5.33333V4.66667C13.9997 3.932 14.5983 3.33334 15.333 3.33334H16.333C16.885 3.33334 17.333 2.88534 17.333 2.33334C17.333 1.78134 16.885 1.33334 16.333 1.33334H15.333C13.4957 1.33334 11.9997 2.82934 11.9997 4.66667V5.33333C11.9997 7.17066 13.4957 8.66666 15.333 8.66666ZM19.6663 8.66666H20.6663C22.5036 8.66666 23.9996 7.17066 23.9996 5.33333V4.66667C23.9996 2.82934 22.5036 1.33334 20.6663 1.33334H19.6663C19.1143 1.33334 18.6663 1.78134 18.6663 2.33334C18.6663 2.88534 19.1143 3.33334 19.6663 3.33334H20.6663C21.401 3.33334 21.9996 3.932 21.9996 4.66667V5.33333C21.9996 6.068 21.401 6.66666 20.6663 6.66666H19.6663C19.1143 6.66666 18.6663 7.11466 18.6663 7.66666C18.6663 8.21866 19.1143 8.66666 19.6663 8.66666ZM20.6663 10.6667H15.333C12.3917 10.6667 9.99967 8.27466 9.99967 5.33333V4.66667C9.99967 4.49165 10.0232 4.32293 10.0469 4.15385L10.0469 4.15383L10.0469 4.15377C10.054 4.1026 10.0612 4.05141 10.0677 4H4.99968C2.97702 4 1.33302 5.644 1.33302 7.66666V16.3333C1.33302 18.356 2.97702 20 4.99968 20H18.9996C21.0223 20 22.6663 18.356 22.6663 16.3333V10.2693C22.0476 10.5213 21.3743 10.6667 20.6663 10.6667ZM16.333 6H19.6663C20.2183 6 20.6663 5.552 20.6663 5C20.6663 4.448 20.2183 4 19.6663 4H16.333C15.781 4 15.333 4.448 15.333 5C15.333 5.552 15.781 6 16.333 6Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AppearanceIcon.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var AppearanceIcon = (props) => /* @__PURE__ */ jsx13(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx13(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.33295 2.66666H4.99962C3.71296 2.66666 2.66629 3.71332 2.66629 4.99998V17.3333C2.66629 19.5386 4.46096 21.3333 6.66628 21.3333C8.87161 21.3333 10.6663 19.5386 10.6663 17.3333V4.99998C10.6663 3.71332 9.61961 2.66666 8.33295 2.66666ZM6.66628 18.3333C6.11429 18.3333 5.66629 17.8853 5.66629 17.3333C5.66629 16.7813 6.11429 16.3333 6.66628 16.3333C7.21828 16.3333 7.66628 16.7813 7.66628 17.3333C7.66628 17.8853 7.21828 18.3333 6.66628 18.3333ZM18.2156 8.14131L15.8583 5.78398C15.0116 4.93598 13.5609 4.91198 12.6649 5.69731V16.9893L18.2142 11.44C18.6556 10.9986 18.8982 10.4133 18.8982 9.79064C18.8982 9.16797 18.6569 8.58131 18.2156 8.14131ZM11.1503 21.3333L19.1369 13.3466C20.3583 13.42 21.3329 14.428 21.3329 15.6666V19C21.3329 20.2866 20.2863 21.3333 18.9996 21.3333H11.1503Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ArchiveIcon.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var ArchiveIcon = (props) => /* @__PURE__ */ jsx14(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx14(
      "path",
      {
        d: "M20.3329 2.66666H3.66623C2.37957 2.66666 1.3329 3.71332 1.3329 4.99998V6.99998C1.3329 8.17198 2.2049 9.13597 3.3329 9.29997V17.6666C3.3329 19.6893 4.97689 21.3333 6.99955 21.3333H16.9995C19.0222 21.3333 20.6662 19.6893 20.6662 17.6666V9.29997C21.7942 9.13597 22.6662 8.17331 22.6662 6.99998V4.99998C22.6662 3.71332 21.6195 2.66666 20.3329 2.66666ZM14.6662 13.3333H9.33288C8.78088 13.3333 8.33288 12.8853 8.33288 12.3333C8.33288 11.7813 8.78088 11.3333 9.33288 11.3333H14.6662C15.2182 11.3333 15.6662 11.7813 15.6662 12.3333C15.6662 12.8853 15.2182 13.3333 14.6662 13.3333ZM20.6662 6.99998C20.6662 7.18398 20.5169 7.33331 20.3329 7.33331H3.66623C3.48223 7.33331 3.3329 7.18398 3.3329 6.99998V4.99998C3.3329 4.81598 3.48223 4.66665 3.66623 4.66665H20.3329C20.5169 4.66665 20.6662 4.81598 20.6662 4.99998V6.99998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ArrowDownIcon.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var ArrowDownIcon = (props) => /* @__PURE__ */ jsx15(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx15(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.9995 17.9199L16.9608 13.9586C17.3515 13.5679 17.9848 13.5679 18.3755 13.9586C18.7661 14.3493 18.7661 14.9826 18.3755 15.3733L12.7088 21.0399C12.5141 21.2346 12.2582 21.3333 12.0022 21.3333H11.9995C11.7435 21.3333 11.4875 21.2359 11.2928 21.0399L5.62617 15.3733C5.2355 14.9826 5.2355 14.3493 5.62617 13.9586C6.01683 13.5679 6.65017 13.5679 7.04083 13.9586L10.9995 17.9173V3.66665C10.9995 3.11466 11.4475 2.66666 11.9995 2.66666C12.5515 2.66666 12.9995 3.11466 12.9995 3.66665V17.9199Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ArrowLeftIcon.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var ArrowLeftIcon = (props) => /* @__PURE__ */ jsx16(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx16(
      "path",
      {
        clipRule: "evenodd",
        d: "M6.08344 10.9986L20.3341 10.9986C20.8861 10.9986 21.3341 11.4466 21.3341 11.9986C21.3341 12.5506 20.8861 12.9986 20.3341 12.9986L6.08079 12.9986L10.0421 16.9599C10.4328 17.3506 10.4328 17.9839 10.0421 18.3746C9.65144 18.7653 9.01811 18.7653 8.62744 18.3746L2.96079 12.708C2.76612 12.5133 2.66746 12.2573 2.66746 12.0013L2.66746 11.9986C2.66746 11.7426 2.76479 11.4866 2.96079 11.292L8.62744 5.6253C9.01811 5.23464 9.65144 5.23464 10.0421 5.6253C10.4328 6.01597 10.4328 6.6493 10.0421 7.03997L6.08344 10.9986Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ArrowRightIcon.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var ArrowRightIcon = (props) => /* @__PURE__ */ jsx17(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx17(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.9213 11.0012L13.96 7.0399C13.5693 6.64924 13.5693 6.01591 13.96 5.62524C14.3506 5.23457 14.984 5.23457 15.3746 5.62524L21.0413 11.2919C21.2359 11.4866 21.3346 11.7426 21.3346 11.9986L21.3346 12.0012C21.3346 12.2572 21.2373 12.5132 21.0413 12.7079L15.3746 18.3745C14.984 18.7652 14.3506 18.7652 13.96 18.3745C13.5693 17.9839 13.5693 17.3505 13.96 16.9599L17.9186 13.0012L3.668 13.0012C3.116 13.0012 2.668 12.5532 2.668 12.0012C2.668 11.4492 3.116 11.0012 3.668 11.0012L17.9213 11.0012Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ArrowUpIcon.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var ArrowUpIcon = (props) => /* @__PURE__ */ jsx18(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx18(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.0026 6.08258L13.0026 20.3332C13.0026 20.8852 12.5546 21.3332 12.0026 21.3332C11.4506 21.3332 11.0026 20.8852 11.0026 20.3332L11.0026 6.07991L7.04126 10.0412C6.6506 10.4319 6.01727 10.4319 5.6266 10.0412C5.23593 9.65057 5.23593 9.01724 5.6266 8.62657L11.2933 2.95992C11.4879 2.76525 11.7439 2.66659 11.9999 2.66659L12.0026 2.66659C12.2586 2.66659 12.5146 2.76392 12.7092 2.95992L18.3759 8.62657C18.7666 9.01724 18.7666 9.65057 18.3759 10.0412C17.9852 10.4319 17.3519 10.4319 16.9612 10.0412L13.0026 6.08258Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ArrowUpRightIcon.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var ArrowUpRightIcon = (props) => /* @__PURE__ */ jsx19(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx19(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.9998 13.0132C17.9998 13.5652 18.4478 14.0132 18.9998 14.0132C19.5518 14.0132 19.9998 13.5652 19.9998 13.0132V4.99994C19.9998 4.44794 19.5518 3.99994 18.9998 3.99994H10.9865C10.4345 3.99994 9.98651 4.44794 9.98651 4.99994C9.98651 5.55193 10.4345 5.99993 10.9865 5.99993H16.5864L4.2932 18.2919C3.90253 18.6826 3.90253 19.3159 4.2932 19.7066C4.48786 19.9026 4.74386 19.9999 4.99986 19.9999C5.25586 19.9999 5.51186 19.9012 5.70653 19.7066L17.9998 7.41567V13.0132Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AsteriskIcon.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
var AsteriskIcon = (props) => /* @__PURE__ */ jsx20(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx20(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9998 21.9999C11.4478 21.9999 10.9998 21.5519 10.9998 20.9999V13.7312L4.70514 17.3652C4.54781 17.4559 4.37581 17.4999 4.20647 17.4999C3.85981 17.4999 3.52381 17.3212 3.33981 16.9999C3.06381 16.5212 3.22781 15.9106 3.70514 15.6346L10.0009 11.9999L3.70514 8.36525C3.22781 8.08925 3.06381 7.47725 3.33981 6.99992C3.61448 6.52259 4.22647 6.35592 4.70514 6.63459L10.9998 10.2689V2.99994C10.9998 2.44794 11.4478 1.99994 11.9998 1.99994C12.5518 1.99994 12.9998 2.44794 12.9998 2.99994V10.2686L19.2944 6.6346C19.7744 6.35727 20.3838 6.52393 20.6598 6.99993C20.9358 7.4786 20.7718 8.08926 20.2944 8.36526L13.9983 12.0001L20.2931 15.6346C20.7704 15.9106 20.9344 16.5226 20.6584 16.9999C20.4732 17.321 20.1375 17.4997 19.7924 17.4999C19.6233 17.4998 19.4516 17.4571 19.2944 17.3652L12.9998 13.7312V20.9999C12.9998 21.5519 12.5518 21.9999 11.9998 21.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AudioDescriptionIcon.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var AudioDescriptionIcon = (props) => /* @__PURE__ */ jsx21(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx21(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99974 1.99994H18.9997C21.0224 1.99994 22.6664 3.64393 22.6664 5.6666V14.9999C22.6664 17.0226 21.0224 18.6666 18.9997 18.6666H13.0171L8.29173 22.4466C8.1104 22.5919 7.88907 22.6666 7.6664 22.6666C7.5184 22.6666 7.3704 22.6346 7.23307 22.5679C6.8864 22.4012 6.6664 22.0506 6.6664 21.6666V18.6666H4.99974C2.97708 18.6666 1.33308 17.0226 1.33308 14.9999V5.6666C1.33308 3.64393 2.97708 1.99994 4.99974 1.99994ZM10.9157 14.3332C11.0424 14.3332 11.1717 14.3092 11.2957 14.2586C11.8077 14.0479 12.0504 13.4639 11.8411 12.9532L9.23706 6.61993C9.0824 6.24526 8.71707 5.99993 8.31173 5.99993H7.6864C7.28107 5.99993 6.91574 6.24526 6.76107 6.61993L4.15708 12.9532C3.94774 13.4639 4.19174 14.0479 4.70241 14.2586C5.21574 14.4679 5.79841 14.2239 6.00774 13.7132L6.43841 12.6666H9.55973L9.9904 13.7132C10.1491 14.0999 10.5224 14.3332 10.9157 14.3332ZM15.9997 14.3332C17.837 14.3332 19.333 12.8372 19.333 10.9999V9.33325C19.333 7.49592 17.837 5.99993 15.9997 5.99993H13.6664C13.1144 5.99993 12.6664 6.44793 12.6664 6.99993V13.3332C12.6664 13.8852 13.1144 14.3332 13.6664 14.3332H15.9997ZM15.9997 7.99992H14.6664V12.3332H15.9997C16.7344 12.3332 17.333 11.7346 17.333 10.9999V9.33325C17.333 8.59859 16.7344 7.99992 15.9997 7.99992ZM8.7384 10.6666L7.99973 8.86926L7.26107 10.6666H8.7384Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/AutoScrollIcon.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var AutoScrollIcon = (props) => /* @__PURE__ */ jsx22(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx22(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.99968 3.59993C5.99968 2.93719 6.53694 2.39993 7.19968 2.39993H16.7997C17.4624 2.39993 17.9997 2.93719 17.9997 3.59993C17.9997 4.26267 17.4624 4.79993 16.7997 4.79993H7.19968C6.53694 4.79993 5.99968 4.26267 5.99968 3.59993ZM5.99968 20.3999C5.99968 19.7371 6.53694 19.1999 7.19968 19.1999H16.7997C17.4624 19.1999 17.9997 19.7371 17.9997 20.3999C17.9997 21.0626 17.4624 21.5999 16.7997 21.5999H7.19968C6.53694 21.5999 5.99968 21.0626 5.99968 20.3999ZM5.99968 7.19992H17.9997C19.9844 7.19992 21.5996 8.81511 21.5996 10.7999V13.1999C21.5996 15.1847 19.9844 16.7999 17.9997 16.7999H5.99968C4.01489 16.7999 2.39969 15.1847 2.39969 13.1999V10.7999C2.39969 8.81511 4.01489 7.19992 5.99968 7.19992ZM7.19968 13.1999H16.7997C17.4633 13.1999 17.9997 12.6623 17.9997 11.9999C17.9997 11.3375 17.4633 10.7999 16.7997 10.7999H7.19968C6.53608 10.7999 5.99968 11.3375 5.99968 11.9999C5.99968 12.6623 6.53608 13.1999 7.19968 13.1999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/BackgroundIcon.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var BackgroundIcon = (props) => /* @__PURE__ */ jsx23(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx23(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.95963 7.70658C3.1543 7.90258 3.41029 7.99991 3.66629 7.99991C3.92229 7.99991 4.17963 7.90124 4.37429 7.70658L7.70762 4.37325C8.09828 3.98258 8.09828 3.34925 7.70762 2.95859C7.31695 2.56792 6.68362 2.56792 6.29295 2.95859L2.95963 6.29191C2.56896 6.68258 2.56896 7.31591 2.95963 7.70658ZM3.6663 21.3332C3.4103 21.3332 3.1543 21.2359 2.95963 21.0399C2.56897 20.6492 2.56897 20.0159 2.95963 19.6252L19.6263 2.95992C20.0169 2.56925 20.6503 2.56925 21.0409 2.95992C21.4316 3.35059 21.4316 3.98392 21.0409 4.37458L4.37296 21.0399C4.1783 21.2345 3.9223 21.3332 3.6663 21.3332ZM3.6663 14.6666C3.4103 14.6666 3.1543 14.5692 2.95963 14.3732C2.56897 13.9826 2.56897 13.3492 2.95963 12.9586L12.9596 2.95992C13.3503 2.56925 13.9836 2.56925 14.3743 2.95992C14.7649 3.35059 14.7649 3.98392 14.3743 4.37458L4.37296 14.3732C4.1783 14.5679 3.9223 14.6666 3.6663 14.6666ZM10.3329 21.3332C10.0769 21.3332 9.82095 21.2359 9.62628 21.0399C9.23562 20.6492 9.23562 20.0159 9.62628 19.6252L19.6263 9.62524C20.0169 9.23457 20.6503 9.23457 21.0409 9.62524C21.4316 10.0159 21.4316 10.6492 21.0409 11.0399L11.0409 21.0399C10.8463 21.2345 10.5889 21.3332 10.3329 21.3332ZM16.2929 21.0399C16.4876 21.2359 16.7436 21.3332 16.9996 21.3332H17.0009C17.2569 21.3332 17.5129 21.2345 17.7076 21.0399L21.0409 17.7066C21.4316 17.3159 21.4316 16.6826 21.0409 16.2919C20.6503 15.9012 20.0169 15.9012 19.6263 16.2919L16.2929 19.6252C15.9023 20.0159 15.9023 20.6492 16.2929 21.0399Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/BellIcon.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var BellIcon = (props) => /* @__PURE__ */ jsx24(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx24(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.3329 14.9999C19.3329 15.9186 20.0809 16.6666 20.9995 16.6666C21.5515 16.6666 21.9995 17.1146 21.9995 17.6666C21.9995 18.2186 21.5515 18.6666 20.9995 18.6666H2.99957C2.44757 18.6666 1.99957 18.2186 1.99957 17.6666C1.99957 17.1146 2.44757 16.6666 2.99957 16.6666C3.91823 16.6666 4.66623 15.9186 4.66623 14.9999V8.6666C4.66623 4.62261 7.95555 1.33328 11.9995 1.33328C16.0435 1.33328 19.3329 4.62261 19.3329 8.6666V14.9999ZM10.4009 19.9999H13.5996H13.6009C13.8022 19.9999 13.9915 20.0906 14.1182 20.2466C14.2449 20.4026 14.2942 20.6079 14.2529 20.8039C14.0222 21.8999 13.0956 22.6666 12.0009 22.6666C10.9062 22.6666 9.97956 21.8999 9.74889 20.8039C9.70756 20.6079 9.75689 20.4026 9.88356 20.2466C10.0102 20.0906 10.1996 19.9999 10.4009 19.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/BoltIcon.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var BoltIcon = (props) => /* @__PURE__ */ jsx25(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M20.2048 10.0546C19.9755 9.61061 19.5208 9.33328 19.0208 9.33328H13.4395L14.1288 2.5013C14.2155 1.89063 13.8768 1.30397 13.3021 1.07463C12.7301 0.8453 12.0781 1.03863 11.7208 1.5413L3.89017 12.5613C3.60084 12.9706 3.56484 13.5013 3.79417 13.9453C4.0235 14.3893 4.47817 14.6666 4.97817 14.6666H10.5595L9.87015 21.4986C9.78349 22.1092 10.1222 22.6959 10.6968 22.9252C10.8582 22.9892 11.0262 23.0212 11.1928 23.0212C11.6142 23.0212 12.0221 22.8199 12.2781 22.4586L20.1101 11.4386C20.3995 11.0293 20.4341 10.4986 20.2048 10.0546Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CalendarIcon.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var CalendarIcon = (props) => /* @__PURE__ */ jsx26(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx26(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.66609 0.999936V2.6666H15.3327V0.999936C15.3327 0.447938 15.7807 -6.10352e-05 16.3327 -6.10352e-05C16.8847 -6.10352e-05 17.3327 0.447938 17.3327 0.999936V2.6666H18.3327C20.3554 2.6666 21.9994 4.3106 21.9994 6.33326V17.6666C21.9994 19.6892 20.3554 21.3332 18.3327 21.3332H5.6661C3.64344 21.3332 1.99944 19.6892 1.99944 17.6666V6.33326C1.99944 4.3106 3.64344 2.6666 5.6661 2.6666H6.6661V0.999936C6.6661 0.447938 7.1141 -6.10352e-05 7.6661 -6.10352e-05C8.21809 -6.10352e-05 8.66609 0.447938 8.66609 0.999936ZM19.9994 17.6666C19.9994 18.5852 19.2514 19.3332 18.3327 19.3332H5.6661C4.74744 19.3332 3.99944 18.5852 3.99944 17.6666V9.33325H19.9994V17.6666ZM10.6661 12.3332C10.6661 11.5986 11.2648 10.9999 11.9994 10.9999C12.7341 10.9999 13.3327 11.5986 13.3327 12.3332C13.3327 13.0679 12.7341 13.6666 11.9994 13.6666C11.2648 13.6666 10.6661 13.0679 10.6661 12.3332ZM16.6661 13.6666C17.4007 13.6666 17.9994 13.0679 17.9994 12.3332C17.9994 11.5986 17.4007 10.9999 16.6661 10.9999C15.9314 10.9999 15.3327 11.5986 15.3327 12.3332C15.3327 13.0679 15.9314 13.6666 16.6661 13.6666ZM10.6661 16.3332C10.6661 15.5986 11.2648 14.9999 11.9994 14.9999C12.7341 14.9999 13.3327 15.5986 13.3327 16.3332C13.3327 17.0679 12.7341 17.6666 11.9994 17.6666C11.2648 17.6666 10.6661 17.0679 10.6661 16.3332ZM7.33276 14.9999C6.5981 14.9999 5.99943 15.5986 5.99943 16.3332C5.99943 17.0679 6.5981 17.6666 7.33276 17.6666C8.06743 17.6666 8.66609 17.0679 8.66609 16.3332C8.66609 15.5986 8.06743 14.9999 7.33276 14.9999ZM15.3327 16.3332C15.3327 15.5986 15.9314 14.9999 16.6661 14.9999C17.4007 14.9999 17.9994 15.5986 17.9994 16.3332C17.9994 17.0679 17.4007 17.6666 16.6661 17.6666C15.9314 17.6666 15.3327 17.0679 15.3327 16.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CallToActionIcon.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var CallToActionIcon = (props) => /* @__PURE__ */ jsx27(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx27(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.9987 2.66653C17.6217 2.66679 17.201 2.78399 16.9106 2.89853C16.62 3.0132 12.2413 4.74653 10.0013 5.65319C7.77198 6.55586 5.54132 7.45852 3.12133 8.39585C2.528 8.62519 2.044 9.09985 1.79467 9.69852C1.492 10.4225 1.332 11.2185 1.332 11.9998C1.332 12.8012 1.492 13.5838 1.80533 14.3278C2.056 14.9185 2.52266 15.3718 3.108 15.5985L4.00133 15.9546V18.8798C4.00133 19.8412 4.57866 20.6932 5.47332 21.0478L8.06665 22.0785C8.34665 22.1905 8.63865 22.2452 8.92798 22.2452C9.59998 22.2452 10.2573 21.9518 10.7106 21.4145L12.4671 19.3293L16.8986 21.0958C17.1693 21.2038 17.6173 21.3332 17.9973 21.3332C20.6573 21.3332 22.6639 17.3212 22.6639 11.9998C22.6639 6.6795 20.658 2.668 17.9987 2.66653ZM17.9987 2.66653C17.9992 2.66653 17.9996 2.66653 18 2.66653H17.9973C17.9978 2.66653 17.9983 2.66653 17.9987 2.66653ZM6.00132 18.8798V16.7519L10.5101 18.5492L9.18265 20.1252C9.08931 20.2358 8.93731 20.2718 8.80531 20.2198L6.21199 19.1892C6.08399 19.1385 6.00132 19.0172 6.00132 18.8798ZM18 19.3332C16.9133 19.3332 15.3333 16.4758 15.3333 11.9998C15.3333 7.52386 16.9133 4.66653 18 4.66653C19.0866 4.66653 20.6666 7.52386 20.6666 11.9998C20.6666 16.4758 19.0866 19.3332 18 19.3332ZM17.3333 9.99985C18.4373 9.99985 19.3333 10.8958 19.3333 11.9998C19.3333 13.1038 18.4373 13.9998 17.3333 13.9998C17.2877 13.9998 17.2438 13.9937 17.1997 13.9876C17.1755 13.9842 17.1512 13.9809 17.1266 13.9785C17.0493 13.3758 17 12.7185 17 11.9998C17 11.2812 17.0493 10.6238 17.1266 10.0212C17.1468 10.0189 17.1668 10.0161 17.1867 10.0133C17.2351 10.0065 17.2832 9.99985 17.3333 9.99985Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CameraIcon.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var CameraIcon = (props) => /* @__PURE__ */ jsx28(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx28(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99993 3.99988H12.9999C15.0226 3.99988 16.6666 5.64388 16.6666 7.66654V16.3332C16.6666 18.3559 15.0226 19.9999 12.9999 19.9999H4.99993C2.97727 19.9999 1.33327 18.3559 1.33327 16.3332V7.66654C1.33327 5.64388 2.97727 3.99988 4.99993 3.99988ZM5.33327 9.33321C5.33327 10.0692 5.9306 10.6665 6.6666 10.6665C7.4026 10.6665 7.99993 10.0692 7.99993 9.33321C7.99993 8.59721 7.4026 7.99988 6.6666 7.99988C5.9306 7.99988 5.33327 8.59721 5.33327 9.33321ZM21.5306 6.66655C22.0506 6.37988 22.6679 6.38921 23.1813 6.69188C23.6946 6.99588 23.9999 7.53188 23.9999 8.12788V15.8719C23.9999 16.4679 23.6946 17.0039 23.1813 17.3079C22.9186 17.4625 22.6266 17.5412 22.3346 17.5412C22.0586 17.5412 21.7826 17.4719 21.5293 17.3319L18.6666 15.7572V8.24121L21.5306 6.66655Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CameraOffIcon.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var CameraOffIcon = (props) => /* @__PURE__ */ jsx29(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx29(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.95993 22.0399C2.1546 22.2359 2.4106 22.3332 2.6666 22.3332C2.9226 22.3332 3.1786 22.2345 3.37326 22.0399L22.0413 3.37454C22.4319 2.98387 22.4319 2.35054 22.0413 1.95987C21.6506 1.5692 21.0173 1.5692 20.6266 1.95987L16.3558 6.23037C15.7997 4.92119 14.511 3.99988 12.9999 3.99988H4.99993C2.97727 3.99988 1.33327 5.64388 1.33327 7.66654V16.3332C1.33327 17.6663 2.05078 18.8283 3.11781 19.4674L1.95993 20.6252C1.56926 21.0159 1.56926 21.6492 1.95993 22.0399ZM7.99993 9.33321C7.99993 8.59721 7.4026 7.99988 6.6666 7.99988C5.9306 7.99988 5.33327 8.59721 5.33327 9.33321C5.33327 10.0692 5.9306 10.6665 6.6666 10.6665C7.4026 10.6665 7.99993 10.0692 7.99993 9.33321ZM12.9999 19.9999H8.24261L16.6666 11.5759V16.3332C16.6666 18.3559 15.0226 19.9999 12.9999 19.9999ZM23.1813 6.69187C22.6906 6.40254 22.1079 6.38921 21.6039 6.63854L18.6666 9.57587V15.7585L21.5293 17.3332C21.7826 17.4732 22.0586 17.5425 22.3346 17.5425C22.6266 17.5425 22.9186 17.4639 23.1813 17.3092C23.6946 17.0052 23.9999 16.4692 23.9999 15.8732V8.12787C23.9999 7.53187 23.6946 6.99587 23.1813 6.69187Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CaretDownIcon.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var CaretDownIcon = (props) => /* @__PURE__ */ jsx30(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M11.9999 19.3331C11.7439 19.3331 11.4879 19.2358 11.2932 19.0398L2.95992 10.7065C2.56926 10.3158 2.56926 9.68251 2.95992 9.29184C3.35059 8.90117 3.98392 8.90117 4.37459 9.29184L12.0012 16.9185L19.6279 9.29184C20.0185 8.90117 20.6519 8.90117 21.0425 9.29184C21.4332 9.68251 21.4332 10.3158 21.0425 10.7065L12.7092 19.0398C12.5146 19.2345 12.2586 19.3331 12.0026 19.3331H11.9999Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretDownStrongIcon.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
var CaretDownStrongIcon = (props) => /* @__PURE__ */ jsx31(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M18.6985 5.33321H5.30121C4.45054 5.33321 3.66654 5.79588 3.25721 6.54121C2.84788 7.28655 2.87454 8.19721 3.33054 8.91588L10.0292 19.4799C10.4599 20.1585 11.1959 20.5625 11.9999 20.5625C12.8039 20.5625 13.5399 20.1572 13.9705 19.4799L20.6705 8.91455C21.1252 8.19588 21.1532 7.28521 20.7439 6.53988C20.3345 5.79455 19.5505 5.33188 18.6999 5.33188L18.6985 5.33321Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretLeftIcon.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var CaretLeftIcon = (props) => /* @__PURE__ */ jsx32(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx32(
      "path",
      {
        d: "M15.3332 21.3332C15.0772 21.3332 14.8212 21.2358 14.6265 21.0398L6.29319 12.7065C5.90252 12.3158 5.90252 11.6825 6.29319 11.2918L14.6265 2.95987C15.0172 2.5692 15.6505 2.5692 16.0412 2.95987C16.4318 3.35054 16.4318 3.98387 16.0412 4.37453L8.41451 12.0012L16.0412 19.6278C16.4318 20.0185 16.4318 20.6518 16.0412 21.0425C15.8465 21.2372 15.5905 21.3358 15.3345 21.3358L15.3332 21.3332Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretLeftStrongIcon.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var CaretLeftStrongIcon = (props) => /* @__PURE__ */ jsx33(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx33(
      "path",
      {
        d: "M4.51848 10.0292L15.0838 3.32921C15.8038 2.87321 16.7145 2.84521 17.4585 3.25587C18.2038 3.66654 18.6665 4.44921 18.6665 5.29987V18.6985C18.6665 19.5492 18.2038 20.3332 17.4585 20.7425C17.1078 20.9359 16.7198 21.0319 16.3331 21.0319C15.8985 21.0319 15.4638 20.9105 15.0825 20.6692L4.51848 13.9705C3.83981 13.5399 3.43581 12.8039 3.43581 11.9999C3.43581 11.1959 3.84115 10.4599 4.51848 10.0292Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretRightIcon.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var CaretRightIcon = (props) => /* @__PURE__ */ jsx34(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx34(
      "path",
      {
        d: "M17.7064 11.2932L9.37311 2.95987C8.98244 2.5692 8.34911 2.5692 7.95845 2.95987C7.56778 3.35054 7.56778 3.98387 7.95845 4.37453L15.5851 12.0012L7.95845 19.6278C7.56778 20.0185 7.56778 20.6518 7.95845 21.0425C8.15311 21.2372 8.40911 21.3358 8.66511 21.3358C8.92111 21.3358 9.17711 21.2385 9.37178 21.0425L17.7051 12.7092C18.0958 12.3185 18.0958 11.6852 17.7051 11.2945L17.7064 11.2932Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretRightStrongIcon.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var CaretRightStrongIcon = (props) => /* @__PURE__ */ jsx35(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx35(
      "path",
      {
        d: "M19.4811 10.0292L8.91576 3.32921C8.19576 2.87321 7.28509 2.84521 6.54109 3.25587C5.79576 3.66654 5.33309 4.44921 5.33309 5.29987V18.6985C5.33309 19.5492 5.79576 20.3332 6.54109 20.7425C6.89176 20.9359 7.27976 21.0319 7.66643 21.0319C8.10109 21.0319 8.53576 20.9105 8.91709 20.6692L19.4811 13.9705C20.1598 13.5399 20.5638 12.8039 20.5638 11.9999C20.5638 11.1959 20.1584 10.4599 19.4811 10.0292Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretUpIcon.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
var CaretUpIcon = (props) => /* @__PURE__ */ jsx36(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M12.7064 6.29318C12.3157 5.90252 11.6824 5.90252 11.2917 6.29318L2.95974 14.6265C2.56907 15.0172 2.56907 15.6505 2.95974 16.0412C3.35041 16.4318 3.98374 16.4318 4.3744 16.0412L12.0011 8.41451L19.6277 16.0412C19.8224 16.2358 20.0784 16.3345 20.3344 16.3345C20.5904 16.3345 20.8464 16.2372 21.041 16.0412C21.4317 15.6505 21.4317 15.0172 21.041 14.6265L12.7077 6.29318H12.7064Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CaretUpStrongIcon.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var CaretUpStrongIcon = (props) => /* @__PURE__ */ jsx37(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx37(
      "path",
      {
        d: "M18.6987 18.6665H5.30133C4.45066 18.6665 3.66666 18.2039 3.25733 17.4585C2.848 16.7132 2.87466 15.8025 3.33066 15.0839L10.0293 4.51988C10.46 3.84121 11.196 3.43721 12 3.43721C12.804 3.43721 13.54 3.84254 13.9707 4.51988L20.6707 15.0852C21.1253 15.8039 21.1533 16.7145 20.744 17.4599C20.3347 18.2052 19.5507 18.6679 18.7 18.6679L18.6987 18.6665Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ChannelIcon.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var ChannelIcon = (props) => /* @__PURE__ */ jsx38(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx38(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99999 2.66653H19C21.025 2.66653 22.6666 4.30815 22.6666 6.33319V14.3332C22.6666 16.3582 21.025 17.9998 19 17.9998H4.99999C2.97495 17.9998 1.33333 16.3582 1.33333 14.3332V6.33319C1.33333 4.30815 2.97495 2.66653 4.99999 2.66653ZM12 22.9998C12.9204 22.9998 13.6666 22.2536 13.6666 21.3332C13.6666 20.4127 12.9204 19.6665 12 19.6665C11.0795 19.6665 10.3333 20.4127 10.3333 21.3332C10.3333 22.2536 11.0795 22.9998 12 22.9998ZM7.99998 21.3332C7.99998 22.0695 7.40303 22.6665 6.66665 22.6665C5.93027 22.6665 5.33332 22.0695 5.33332 21.3332C5.33332 20.5968 5.93027 19.9998 6.66665 19.9998C7.40303 19.9998 7.99998 20.5968 7.99998 21.3332ZM18.6666 21.3332C18.6666 22.0695 18.0697 22.6665 17.3333 22.6665C16.5969 22.6665 16 22.0695 16 21.3332C16 20.5968 16.5969 19.9998 17.3333 19.9998C18.0697 19.9998 18.6666 20.5968 18.6666 21.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ChaptersIcon.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var ChaptersIcon = (props) => /* @__PURE__ */ jsx39(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M17.6666 2.66653H6.33325C4.31059 2.66653 2.6666 4.31053 2.6666 6.33319V17.6665C2.6666 19.6892 4.31059 21.3332 6.33325 21.3332H17.6666C19.6892 21.3332 21.3332 19.6892 21.3332 17.6665V6.33319C21.3332 4.31053 19.6892 2.66653 17.6666 2.66653ZM7.66658 17.3332C6.93058 17.3332 6.33325 16.7358 6.33325 15.9998C6.33325 15.2638 6.93058 14.6665 7.66658 14.6665C8.40258 14.6665 8.99991 15.2638 8.99991 15.9998C8.99991 16.7358 8.40258 17.3332 7.66658 17.3332ZM7.66658 13.3332C6.93058 13.3332 6.33325 12.7358 6.33325 11.9998C6.33325 11.2638 6.93058 10.6665 7.66658 10.6665C8.40258 10.6665 8.99991 11.2638 8.99991 11.9998C8.99991 12.7358 8.40258 13.3332 7.66658 13.3332ZM7.66658 9.66652C6.93058 9.66652 6.33325 9.06919 6.33325 8.33319C6.33325 7.59719 6.93058 6.99986 7.66658 6.99986C8.40258 6.99986 8.99991 7.59719 8.99991 8.33319C8.99991 9.06919 8.40258 9.66652 7.66658 9.66652ZM16.6666 16.6665H11.6666C11.1146 16.6665 10.6666 16.2185 10.6666 15.6665C10.6666 15.1145 11.1146 14.6665 11.6666 14.6665H16.6666C17.2186 14.6665 17.6666 15.1145 17.6666 15.6665C17.6666 16.2185 17.2186 16.6665 16.6666 16.6665ZM16.6666 12.9998H11.6666C11.1146 12.9998 10.6666 12.5518 10.6666 11.9998C10.6666 11.4478 11.1146 10.9998 11.6666 10.9998H16.6666C17.2186 10.9998 17.6666 11.4478 17.6666 11.9998C17.6666 12.5518 17.2186 12.9998 16.6666 12.9998ZM16.6666 9.33318H11.6666C11.1146 9.33318 10.6666 8.88519 10.6666 8.33319C10.6666 7.78119 11.1146 7.33319 11.6666 7.33319H16.6666C17.2186 7.33319 17.6666 7.78119 17.6666 8.33319C17.6666 8.88519 17.2186 9.33318 16.6666 9.33318Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CheckmarkIcon.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
var CheckmarkIcon = (props) => /* @__PURE__ */ jsx40(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx40(
      "path",
      {
        d: "M8.99985 19.9998H8.99718C8.69451 19.9998 8.40785 19.8612 8.21985 19.6252L2.88653 12.9585C2.54119 12.5265 2.61053 11.8985 3.04253 11.5532C3.47586 11.2078 4.10386 11.2785 4.44786 11.7092L9.00518 17.4052L19.5558 4.37053C19.9038 3.9412 20.5318 3.8732 20.9625 4.22253C21.3918 4.57053 21.4585 5.19986 21.1105 5.62919L9.77718 19.6292C9.58651 19.8638 9.30118 19.9998 8.99985 19.9998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CheckmarkCircleIcon.tsx
import { jsx as jsx41 } from "react/jsx-runtime";
var CheckmarkCircleIcon = (props) => /* @__PURE__ */ jsx41(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx41(
      "path",
      {
        d: "M11.9998 1.33322C6.11847 1.33322 1.33315 6.11854 1.33315 11.9999C1.33315 17.8812 6.11847 22.6665 11.9998 22.6665C17.8811 22.6665 22.6664 17.8812 22.6664 11.9999C22.6664 6.11854 17.8811 1.33322 11.9998 1.33322ZM17.1238 8.94387L11.4571 16.2772C11.2758 16.5119 10.9998 16.6545 10.7038 16.6652C10.6905 16.6652 10.6785 16.6652 10.6665 16.6652C10.3838 16.6652 10.1131 16.5452 9.92246 16.3345L6.92246 13.0012C6.55313 12.5905 6.58647 11.9585 6.99713 11.5879C7.40913 11.2199 8.03846 11.2519 8.41046 11.6625L10.6078 14.1052L15.5424 7.71987C15.8798 7.28254 16.5091 7.20254 16.9451 7.53987C17.3824 7.87721 17.4624 8.5052 17.1251 8.94254L17.1238 8.94387Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CheckmarkCircleOutlineIcon.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var CheckmarkCircleOutlineIcon = (props) => /* @__PURE__ */ jsx42(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx42(
      "path",
      {
        clipRule: "evenodd",
        d: "M3.33308 11.9999C3.33308 7.2134 7.21327 3.33322 11.9997 3.33322C16.7862 3.33322 20.6664 7.2134 20.6664 11.9999C20.6664 16.7863 16.7862 20.6665 11.9997 20.6665C7.21327 20.6665 3.33308 16.7863 3.33308 11.9999ZM11.9997 1.33322C6.1087 1.33322 1.33308 6.10884 1.33308 11.9999C1.33308 17.8909 6.1087 22.6665 11.9997 22.6665C17.8907 22.6665 22.6664 17.8909 22.6664 11.9999C22.6664 6.10884 17.8907 1.33322 11.9997 1.33322ZM17.1243 8.94465C17.462 8.50764 17.3815 7.87961 16.9445 7.54192C16.5075 7.20423 15.8795 7.28474 15.5418 7.72176L10.6079 14.1067L8.40969 11.6642C8.04024 11.2537 7.40795 11.2204 6.99744 11.5899C6.58693 11.9594 6.55365 12.5916 6.92311 13.0022L9.9231 16.3355C10.1215 16.5559 10.4075 16.6769 10.7038 16.6658C11.0002 16.6547 11.2763 16.5126 11.4577 16.278L17.1243 8.94465Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CheckmarkThreeQuatersCircleIcon.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var CheckmarkThreeQuatersCircleIcon = (props) => /* @__PURE__ */ jsx43(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx43(
      "path",
      {
        clipRule: "evenodd",
        d: "M21.1219 4.28103C21.4613 3.84531 21.3832 3.21698 20.9475 2.87762C20.5117 2.53825 19.8834 2.61635 19.544 3.05207L11.9697 12.7768L9.07634 9.56161C8.7069 9.15108 8.07461 9.11777 7.66408 9.4872C7.25356 9.85664 7.22024 10.4889 7.58968 10.8995L11.2817 15.0021C11.4796 15.2221 11.7648 15.3431 12.0605 15.3326C12.3562 15.322 12.6321 15.1811 12.8139 14.9477L21.1219 4.28103ZM11.9997 2.66655C6.84473 2.66655 2.66636 6.84492 2.66636 11.9999C2.66636 17.1548 6.84473 21.3332 11.9997 21.3332C17.1546 21.3332 21.333 17.1548 21.333 11.9999C21.333 11.0484 21.188 10.1313 20.924 9.26752C20.7625 8.73937 20.2035 8.4421 19.6753 8.60355C19.1471 8.76501 18.8499 9.32405 19.0113 9.85221C19.2193 10.5325 19.333 11.2527 19.333 11.9999C19.333 16.0502 16.05 19.3332 11.9997 19.3332C7.94929 19.3332 4.66635 16.0502 4.66635 11.9999C4.66635 7.94949 7.94929 4.66654 11.9997 4.66654C13.0985 4.66654 14.1393 4.91549 15.0798 5.3522C15.5808 5.58479 16.1754 5.36727 16.408 4.86636C16.6406 4.36544 16.423 3.77081 15.9221 3.53822C14.732 2.9856 13.4048 2.66655 11.9997 2.66655Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CircleSlashedIcon.tsx
import { jsx as jsx44 } from "react/jsx-runtime";
var CircleSlashedIcon = (props) => /* @__PURE__ */ jsx44(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx44(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.95962 22.0398C2.15429 22.2358 2.41029 22.3332 2.66629 22.3332C2.92229 22.3332 3.17829 22.2345 3.37295 22.0398L22.0409 3.37453C22.4316 2.98387 22.4316 2.35054 22.0409 1.95987C21.6502 1.5692 21.0169 1.5692 20.6262 1.95987L17.8512 4.73468C16.2489 3.44142 14.2126 2.66653 11.9996 2.66653C6.85295 2.66653 2.6663 6.85319 2.6663 11.9998C2.6663 14.2136 3.44093 16.2498 4.73382 17.8512L1.95962 20.6252C1.56896 21.0158 1.56896 21.6492 1.95962 22.0398ZM7.8796 20.3612C9.1236 20.9772 10.5196 21.3332 11.9996 21.3332C17.1462 21.3332 21.3329 17.1465 21.3329 11.9998C21.3329 10.5198 20.9769 9.12519 20.3622 7.87986L7.8796 20.3612Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ClapboardIcon.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
var ClapboardIcon = (props) => /* @__PURE__ */ jsx45(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx45(
      "path",
      {
        d: "M19 2.66653H4.99999C2.97733 2.66653 1.33333 4.31053 1.33333 6.3332V17.6665C1.33333 19.6892 2.97733 21.3332 4.99999 21.3332H19C21.0227 21.3332 22.6667 19.6892 22.6667 17.6665V6.3332C22.6667 4.31053 21.0227 2.66653 19 2.66653ZM20.6667 6.3332V7.99987H17.2853L18.952 4.66653H19C19.9187 4.66653 20.6667 5.41453 20.6667 6.3332ZM7.28533 7.99987L8.952 4.66653H11.716L10.0493 7.99987H7.28533ZM13.952 4.66653H16.716L15.0493 7.99987H12.2853L13.952 4.66653ZM4.99999 4.66653H6.71466L5.048 7.99987H3.33333V6.3332C3.33333 5.41453 4.08133 4.66653 4.99999 4.66653Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CloseIcon.tsx
import { jsx as jsx46 } from "react/jsx-runtime";
var CloseIcon = (props) => /* @__PURE__ */ jsx46(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx46(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.3738 6.04035C19.7644 5.64983 19.7644 5.01667 19.3738 4.62614C18.9833 4.23562 18.3502 4.23562 17.9596 4.62614L12.0001 10.5857L6.04055 4.62614C5.65002 4.23562 5.01686 4.23562 4.62634 4.62614C4.23581 5.01667 4.23581 5.64983 4.62634 6.04035L10.5859 11.9999L4.62634 17.9594C4.23581 18.35 4.23581 18.9831 4.62634 19.3737C5.01686 19.7642 5.65002 19.7642 6.04055 19.3737L12.0001 13.4141L17.9596 19.3737C18.3502 19.7642 18.9833 19.7642 19.3738 19.3737C19.7644 18.9831 19.7644 18.35 19.3738 17.9594L13.4143 11.9999L19.3738 6.04035Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CloseOctagonIcon.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var CloseOctagonIcon = (props) => /* @__PURE__ */ jsx47(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx47(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.216 3.07467L20.9253 6.784C21.6187 7.47733 22 8.39733 22 9.37733V14.624C22 15.604 21.6187 16.524 20.9253 17.2173L17.216 20.9267C16.5227 21.62 15.6027 22.0013 14.6227 22.0013H9.37599C8.39599 22.0013 7.47599 21.62 6.78266 20.9267L3.07332 17.2173C2.37999 16.524 1.99866 15.604 1.99866 14.624V9.37733C1.99866 8.39733 2.37999 7.47733 3.07332 6.784L6.78266 3.07467C7.47599 2.38133 8.39599 2 9.37599 2H14.6227C15.6027 2 16.5227 2.38133 17.216 3.07467ZM16.3397 16.3423C16.7303 15.9517 16.7303 15.3183 16.3397 14.9277H16.3423L13.3823 11.9677L16.3423 9.00767C16.733 8.617 16.733 7.98367 16.3423 7.593C15.9517 7.20234 15.3183 7.20234 14.9277 7.593L11.9677 10.553L9.00766 7.593C8.61699 7.20234 7.98365 7.20234 7.59299 7.593C7.20232 7.98367 7.20232 8.617 7.59299 9.00767L10.553 11.9677L7.59299 14.9277C7.20232 15.3183 7.20232 15.9517 7.59299 16.3423C7.78765 16.5383 8.04365 16.6357 8.29965 16.6357C8.55566 16.6357 8.81166 16.537 9.00632 16.3423L11.9663 13.3823L14.9263 16.3423C15.121 16.5383 15.377 16.6357 15.633 16.6357C15.889 16.6357 16.145 16.537 16.3397 16.3423Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ClosedCaptionsIcon.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var ClosedCaptionsIcon = (props) => /* @__PURE__ */ jsx48(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M19 2.66653H4.99999C2.97732 2.66653 1.33333 4.31053 1.33333 6.33319V17.6665C1.33333 19.6892 2.97732 21.3332 4.99999 21.3332H19C21.0226 21.3332 22.6666 19.6892 22.6666 17.6665V6.33319C22.6666 4.31053 21.0226 2.66653 19 2.66653ZM6.33332 11.3332H13C13.552 11.3332 14 11.7812 14 12.3332C14 12.8852 13.552 13.3332 13 13.3332H6.33332C5.78132 13.3332 5.33332 12.8852 5.33332 12.3332C5.33332 11.7812 5.78132 11.3332 6.33332 11.3332ZM7.66665 17.3332H6.33332C5.78132 17.3332 5.33332 16.8852 5.33332 16.3332C5.33332 15.7812 5.78132 15.3332 6.33332 15.3332H7.66665C8.21864 15.3332 8.66664 15.7812 8.66664 16.3332C8.66664 16.8852 8.21864 17.3332 7.66665 17.3332ZM17.6666 17.3332H11C10.448 17.3332 9.99997 16.8852 9.99997 16.3332C9.99997 15.7812 10.448 15.3332 11 15.3332H17.6666C18.2186 15.3332 18.6666 15.7812 18.6666 16.3332C18.6666 16.8852 18.2186 17.3332 17.6666 17.3332ZM17.6666 13.3332H16.3333C15.7813 13.3332 15.3333 12.8852 15.3333 12.3332C15.3333 11.7812 15.7813 11.3332 16.3333 11.3332H17.6666C18.2186 11.3332 18.6666 11.7812 18.6666 12.3332C18.6666 12.8852 18.2186 13.3332 17.6666 13.3332Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CollapseIcon.tsx
import { jsx as jsx49 } from "react/jsx-runtime";
var CollapseIcon = (props) => /* @__PURE__ */ jsx49(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx49(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9999 7.91987L15.9599 3.95987H15.9572C16.3479 3.5692 16.9812 3.5692 17.3719 3.95987C17.7625 4.35054 17.7625 4.98387 17.3719 5.37454L12.7052 10.0412C12.5105 10.2372 12.2545 10.3345 11.9985 10.3345C11.7425 10.3345 11.4865 10.2359 11.2919 10.0412L6.62521 5.37454C6.23455 4.98387 6.23455 4.35054 6.62521 3.95987C7.01588 3.5692 7.64921 3.5692 8.03988 3.95987L11.9999 7.91987ZM11.2919 13.9599C11.6825 13.5692 12.3159 13.5692 12.7065 13.9599L17.3732 18.6265C17.7639 19.0172 17.7639 19.6505 17.3732 20.0412C17.1785 20.2372 16.9225 20.3345 16.6665 20.3345C16.4105 20.3345 16.1545 20.2359 15.9599 20.0412L11.9999 16.0812L8.03988 20.0412C7.64921 20.4319 7.01588 20.4319 6.62521 20.0412C6.23455 19.6505 6.23455 19.0172 6.62521 18.6265L11.2919 13.9599Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CollapseDiagonalIcon.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
var CollapseDiagonalIcon = (props) => /* @__PURE__ */ jsx50(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx50(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.6253 2.95987C20.0159 2.5692 20.6493 2.5692 21.0399 2.95987L21.0386 2.95854C21.4293 3.3492 21.4293 3.98254 21.0386 4.3732L18.9119 6.49987L20.8026 8.39054C21.1853 8.77454 21.2986 9.34387 21.0919 9.84387C20.8839 10.3439 20.3999 10.6665 19.8599 10.6665H14.6653C13.9293 10.6665 13.3319 10.0679 13.3319 9.3332V4.13854C13.3319 3.5972 13.6546 3.1132 14.1559 2.90654C14.6559 2.69854 15.2279 2.81454 15.6093 3.1972L17.4986 5.08654L19.6253 2.95987ZM4.1386 13.3332H9.33326V13.3345C10.0693 13.3345 10.6666 13.9332 10.6666 14.6679V19.8625C10.6666 20.4039 10.3439 20.8879 9.8426 21.0945C9.67726 21.1625 9.50393 21.1959 9.33193 21.1959C8.98393 21.1959 8.64393 21.0599 8.38926 20.8039L6.49993 18.9145L4.37326 21.0412C4.1786 21.2372 3.9226 21.3345 3.6666 21.3345C3.4106 21.3345 3.1546 21.2359 2.95993 21.0412C2.56926 20.6505 2.56926 20.0172 2.95993 19.6265L5.0866 17.4999L3.19593 15.6092C2.81326 15.2252 2.69993 14.6559 2.9066 14.1559C3.1146 13.6559 3.5986 13.3332 4.1386 13.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CommentsIcon.tsx
import { jsx as jsx51 } from "react/jsx-runtime";
var CommentsIcon = (props) => /* @__PURE__ */ jsx51(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx51(
      "path",
      {
        d: "M11.9999 1.33322C6.11859 1.33322 1.33327 6.11854 1.33327 11.9999C1.33327 13.8612 1.82793 15.7012 2.74926 17.2945C3.0666 17.8892 2.6226 19.9638 1.6266 20.9598C1.34793 21.2385 1.2586 21.6558 1.39993 22.0238C1.54127 22.3918 1.8866 22.6438 2.27993 22.6652C2.38526 22.6705 2.49326 22.6732 2.60126 22.6732C4.12793 22.6732 5.98126 22.1305 7.18792 21.5118C8.08792 21.9692 9.04125 22.2945 10.0292 22.4825C10.6746 22.6052 11.3386 22.6665 11.9999 22.6665C17.8812 22.6665 22.6665 17.8812 22.6665 11.9999C22.6665 6.11854 17.8812 1.33322 11.9999 1.33322ZM11.3332 14.3332C11.3332 14.8852 10.8852 15.3332 10.3332 15.3332H7.66658C7.11459 15.3332 6.66659 14.8852 6.66659 14.3332V12.3332C6.66659 9.7292 7.57859 8.16254 9.53591 7.40121C10.0506 7.20121 10.6292 7.45721 10.8306 7.97054C11.0306 8.4852 10.7746 9.0652 10.2612 9.2652C9.59058 9.5252 8.93325 9.91187 8.73058 11.3332H10.3332C10.8852 11.3332 11.3332 11.7812 11.3332 12.3332V14.3332ZM17.3332 14.3332C17.3332 14.8852 16.8852 15.3332 16.3332 15.3332H13.6666C13.1146 15.3332 12.6666 14.8852 12.6666 14.3332V12.3332C12.6666 9.7292 13.5786 8.16254 15.5359 7.40121C16.0506 7.20121 16.6306 7.45721 16.8306 7.97054C17.0306 8.4852 16.7746 9.0652 16.2612 9.2652C15.5906 9.5252 14.9332 9.91187 14.7306 11.3332H16.3332C16.8852 11.3332 17.3332 11.7812 17.3332 12.3332V14.3332Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/CompanyIcon.tsx
import { jsx as jsx52 } from "react/jsx-runtime";
var CompanyIcon = (props) => /* @__PURE__ */ jsx52(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx52(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.99986 8.33319C5.99986 8.88519 6.44786 9.33319 6.99986 9.33319C7.55186 9.33319 7.99853 8.88519 7.99853 8.33319V5.93053C7.99853 5.79187 8.08519 5.66653 8.21719 5.61853L15.5505 2.92921C15.6918 2.87987 15.8025 2.93187 15.8558 2.96921C15.9092 3.00654 15.9985 3.09187 15.9985 3.24254V4.99987C15.9985 5.55187 16.4465 5.99986 16.9985 5.99986C17.5505 5.99986 17.9985 5.55187 17.9985 4.99987V3.24254C17.9985 2.47987 17.6252 1.76521 17.0012 1.32921C16.3772 0.893211 15.5785 0.789211 14.8625 1.05188L7.53053 3.73987C6.61453 4.07454 5.99986 4.95587 5.99986 5.93053V8.33319ZM14.9998 7.3332H20.3332C21.6198 7.3332 22.6665 8.37986 22.6665 9.66652V20.3332C22.6665 21.6198 21.6198 22.6665 20.3332 22.6665H3.66654C2.37987 22.6665 1.33321 21.6198 1.33321 20.3332V12.9998C1.33321 11.7132 2.37987 10.6665 3.66654 10.6665H8.99986C10.2865 10.6665 11.3332 11.7132 11.3332 12.9998V20.6665H12.6665V9.66652C12.6665 8.37986 13.7132 7.3332 14.9998 7.3332ZM5.66653 19.3332H6.99986C7.55186 19.3332 7.99986 18.8852 7.99986 18.3332C7.99986 17.7812 7.55186 17.3332 6.99986 17.3332H5.66653C5.11453 17.3332 4.66653 17.7812 4.66653 18.3332C4.66653 18.8852 5.11453 19.3332 5.66653 19.3332ZM5.66653 15.9998H6.99986C7.55186 15.9998 7.99986 15.5518 7.99986 14.9998C7.99986 14.4478 7.55186 13.9998 6.99986 13.9998H5.66653C5.11453 13.9998 4.66653 14.4478 4.66653 14.9998C4.66653 15.5518 5.11453 15.9998 5.66653 15.9998ZM16.9998 19.3332H18.3332C18.8852 19.3332 19.3332 18.8852 19.3332 18.3332C19.3332 17.7812 18.8852 17.3332 18.3332 17.3332H16.9998C16.4478 17.3332 15.9998 17.7812 15.9998 18.3332C15.9998 18.8852 16.4478 19.3332 16.9998 19.3332ZM16.9998 15.9998H18.3332C18.8852 15.9998 19.3332 15.5518 19.3332 14.9998C19.3332 14.4478 18.8852 13.9998 18.3332 13.9998H16.9998C16.4478 13.9998 15.9998 14.4478 15.9998 14.9998C15.9998 15.5518 16.4478 15.9998 16.9998 15.9998ZM16.9998 12.6665H18.3332C18.8852 12.6665 19.3332 12.2185 19.3332 11.6665C19.3332 11.1145 18.8852 10.6665 18.3332 10.6665H16.9998C16.4478 10.6665 15.9998 11.1145 15.9998 11.6665C15.9998 12.2185 16.4478 12.6665 16.9998 12.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CompareIcon.tsx
import { jsx as jsx53 } from "react/jsx-runtime";
var CompareIcon = (props) => /* @__PURE__ */ jsx53(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx53(
      "path",
      {
        d: "M16.9878 7.01321C15.9904 3.73055 12.9384 1.33322 9.33313 1.33322C4.92114 1.33322 1.33315 4.92121 1.33315 9.3332C1.33315 12.9372 3.72914 15.9905 7.0118 16.9865C8.00913 20.2692 11.0611 22.6665 14.6664 22.6665C19.0784 22.6665 22.6664 19.0785 22.6664 14.6665C22.6664 11.0625 20.2704 8.00921 16.9878 7.01321ZM3.33314 9.3332C3.33314 6.02521 6.02513 3.33322 9.33313 3.33322C11.6798 3.33322 13.6944 4.69988 14.6798 6.66654C14.6758 6.66654 14.6704 6.66654 14.6664 6.66654C10.2545 6.66654 6.66647 10.2545 6.66647 14.6665C6.66647 14.6705 6.66647 14.6759 6.66647 14.6799C4.69847 13.6945 3.33314 11.6799 3.33314 9.3332ZM14.6664 20.6665C12.3198 20.6665 10.3051 19.2998 9.31979 17.3332C9.32379 17.3332 9.32913 17.3332 9.33313 17.3332C13.7451 17.3332 17.3331 13.7452 17.3331 9.3332C17.3331 9.3292 17.3331 9.32387 17.3331 9.31987C19.3011 10.3052 20.6664 12.3199 20.6664 14.6665C20.6664 17.9745 17.9744 20.6665 14.6664 20.6665Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ConnectionOkayIcon.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var ConnectionOkayIcon = (props) => /* @__PURE__ */ jsx54(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx54(
      "path",
      {
        clipRule: "evenodd",
        d: "M13 10.3332C13 9.78092 12.5523 9.33321 12 9.33321C11.4477 9.33321 11 9.78092 11 10.3332V18.9999C11 19.5522 11.4477 19.9999 12 19.9999C12.5523 19.9999 13 19.5522 13 18.9999V10.3332ZM7.66667 12.6665C8.21896 12.6665 8.66667 13.1143 8.66667 13.6665V18.9999C8.66667 19.5522 8.21896 19.9999 7.66667 19.9999C7.11439 19.9999 6.66667 19.5522 6.66667 18.9999V13.6665C6.66667 13.1143 7.11439 12.6665 7.66667 12.6665ZM3.66667 15.9999C4.21896 15.9999 4.66667 16.4476 4.66667 16.9999V18.9999C4.66667 19.5522 4.21896 19.9999 3.66667 19.9999C3.11439 19.9999 2.66667 19.5522 2.66667 18.9999V16.9999C2.66667 16.4476 3.11439 15.9999 3.66667 15.9999ZM16.3333 19.9999C16.8856 19.9999 17.3333 19.5522 17.3333 18.9999C17.3333 18.4476 16.8856 17.9999 16.3333 17.9999C15.7811 17.9999 15.3333 18.4476 15.3333 18.9999C15.3333 19.5522 15.7811 19.9999 16.3333 19.9999ZM21.3333 18.9999C21.3333 19.5522 20.8856 19.9999 20.3333 19.9999C19.7811 19.9999 19.3333 19.5522 19.3333 18.9999C19.3333 18.4476 19.7811 17.9999 20.3333 17.9999C20.8856 17.9999 21.3333 18.4476 21.3333 18.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ConnectionPoorIcon.tsx
import { jsx as jsx55 } from "react/jsx-runtime";
var ConnectionPoorIcon = (props) => /* @__PURE__ */ jsx55(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx55(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.2929 2.95944C15.6834 2.56892 16.3166 2.56892 16.7071 2.95944L18.6667 4.919L20.6262 2.95944C21.0168 2.56892 21.6499 2.56892 22.0404 2.95944C22.431 3.34997 22.431 3.98313 22.0404 4.37366L20.0809 6.33322L22.0404 8.29278C22.431 8.6833 22.431 9.31647 22.0404 9.70699C21.6499 10.0975 21.0168 10.0975 20.6262 9.70699L18.6667 7.74743L16.7071 9.70699C16.3166 10.0975 15.6834 10.0975 15.2929 9.70699C14.9024 9.31647 14.9024 8.6833 15.2929 8.29278L17.2525 6.33322L15.2929 4.37366C14.9024 3.98313 14.9024 3.34997 15.2929 2.95944ZM7.66667 12.6665C8.21896 12.6665 8.66667 13.1143 8.66667 13.6665V18.9999C8.66667 19.5522 8.21896 19.9999 7.66667 19.9999C7.11439 19.9999 6.66667 19.5522 6.66667 18.9999V13.6665C6.66667 13.1143 7.11439 12.6665 7.66667 12.6665ZM3.66667 15.9999C4.21896 15.9999 4.66667 16.4476 4.66667 16.9999V18.9999C4.66667 19.5522 4.21896 19.9999 3.66667 19.9999C3.11439 19.9999 2.66667 19.5522 2.66667 18.9999V16.9999C2.66667 16.4476 3.11439 15.9999 3.66667 15.9999ZM17.3333 18.9999C17.3333 19.5522 16.8856 19.9999 16.3333 19.9999C15.7811 19.9999 15.3333 19.5522 15.3333 18.9999C15.3333 18.4476 15.7811 17.9999 16.3333 17.9999C16.8856 17.9999 17.3333 18.4476 17.3333 18.9999ZM12 19.9999C12.5523 19.9999 13 19.5522 13 18.9999C13 18.4476 12.5523 17.9999 12 17.9999C11.4477 17.9999 11 18.4476 11 18.9999C11 19.5522 11.4477 19.9999 12 19.9999ZM21.3333 18.9999C21.3333 19.5522 20.8856 19.9999 20.3333 19.9999C19.7811 19.9999 19.3333 19.5522 19.3333 18.9999C19.3333 18.4476 19.7811 17.9999 20.3333 17.9999C20.8856 17.9999 21.3333 18.4476 21.3333 18.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ConnectionStrongIcon.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var ConnectionStrongIcon = (props) => /* @__PURE__ */ jsx56(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx56(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.3333 18.9999C19.3333 19.5519 19.7813 19.9999 20.3333 19.9999C20.8853 19.9999 21.3333 19.5519 21.3333 18.9999V3.66655C21.3333 3.11455 20.8853 2.66655 20.3333 2.66655C19.7813 2.66655 19.3333 3.11455 19.3333 3.66655V18.9999ZM16.3333 19.9999C15.7813 19.9999 15.3333 19.5519 15.3333 18.9999V6.99988C15.3333 6.44788 15.7813 5.99988 16.3333 5.99988C16.8853 5.99988 17.3333 6.44788 17.3333 6.99988V18.9999C17.3333 19.5519 16.8853 19.9999 16.3333 19.9999ZM11 18.9999C11 19.5519 11.448 19.9999 12 19.9999C12.552 19.9999 13 19.5519 13 18.9999V10.3332C13 9.78122 12.552 9.33322 12 9.33322C11.448 9.33322 11 9.78122 11 10.3332V18.9999ZM7.66667 19.9999C7.11467 19.9999 6.66667 19.5519 6.66667 18.9999V13.6665C6.66667 13.1145 7.11467 12.6665 7.66667 12.6665C8.21867 12.6665 8.66667 13.1145 8.66667 13.6665V18.9999C8.66667 19.5519 8.21867 19.9999 7.66667 19.9999ZM3.66667 19.9999C3.11467 19.9999 2.66667 19.5519 2.66667 18.9999V16.9999C2.66667 16.4479 3.11467 15.9999 3.66667 15.9999C4.21867 15.9999 4.66667 16.4479 4.66667 16.9999V18.9999C4.66667 19.5519 4.21867 19.9999 3.66667 19.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ContentLibraryIcon.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var ContentLibraryIcon = (props) => /* @__PURE__ */ jsx57(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx57(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.99973 2.66653H18.9997C21.0224 2.66653 22.6664 4.31053 22.6664 6.33319V13.6665C22.6664 15.6892 21.0224 17.3332 18.9997 17.3332H8.99973C6.97707 17.3332 5.33308 15.6892 5.33308 13.6665V6.33319C5.33308 4.31053 6.97707 2.66653 8.99973 2.66653ZM13.2197 12.5358L16.281 10.6892V10.6865C16.7997 10.3745 16.7997 9.62252 16.281 9.30918L13.2197 7.46252C12.6837 7.13986 11.9997 7.52652 11.9997 8.15185V11.8465C11.9997 12.4732 12.6837 12.8585 13.2197 12.5358ZM4.99974 21.3332H16.333C16.885 21.3332 17.333 20.8852 17.333 20.3332C17.333 19.7812 16.885 19.3332 16.333 19.3332H4.99974C4.08108 19.3332 3.33308 18.5852 3.33308 17.6665V8.99985C3.33308 8.44785 2.88508 7.99985 2.33308 7.99985C1.78108 7.99985 1.33308 8.44785 1.33308 8.99985V17.6665C1.33308 19.6892 2.97708 21.3332 4.99974 21.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ControlsIcon.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var ControlsIcon = (props) => /* @__PURE__ */ jsx58(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx58(
      "path",
      {
        clipRule: "evenodd",
        d: "M7.99967 2.33322V5.47454C9.71967 5.92254 10.9997 7.47454 10.9997 9.3332C10.9997 11.1919 9.71967 12.7452 7.99967 13.1919V21.6665C7.99967 22.2185 7.55168 22.6665 6.99968 22.6665C6.44768 22.6665 5.99968 22.2185 5.99968 21.6665V13.1919C4.27968 12.7439 2.99969 11.1919 2.99969 9.3332C2.99969 7.47454 4.27968 5.92121 5.99968 5.47454V2.33322C5.99968 1.78122 6.44768 1.33322 6.99968 1.33322C7.55168 1.33322 7.99967 1.78122 7.99967 2.33322ZM4.99968 9.3332C4.99968 10.4359 5.89701 11.3332 6.99968 11.3332C8.10234 11.3332 8.99967 10.4359 8.99967 9.3332C8.99967 8.23054 8.10234 7.33321 6.99968 7.33321C5.89701 7.33321 4.99968 8.23054 4.99968 9.3332ZM17.9997 10.8079C19.7196 11.2559 20.9996 12.8079 20.9996 14.6665C20.9996 16.5252 19.7196 18.0785 17.9997 18.5252V21.6665C17.9997 22.2185 17.5517 22.6665 16.9997 22.6665C16.4477 22.6665 15.9997 22.2185 15.9997 21.6665V18.5252C14.2797 18.0772 12.9997 16.5252 12.9997 14.6665C12.9997 12.8079 14.2797 11.2545 15.9997 10.8079V2.33322C15.9997 1.78122 16.4477 1.33322 16.9997 1.33322C17.5517 1.33322 17.9997 1.78122 17.9997 2.33322V10.8079Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/CursorClickIcon.tsx
import { jsx as jsx59 } from "react/jsx-runtime";
var CursorClickIcon = (props) => /* @__PURE__ */ jsx59(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx59(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.333 4.99986C11.333 5.55186 10.885 5.99986 10.333 5.99986C9.78096 5.99986 9.33296 5.55186 9.33296 4.99986V2.33319C9.33296 1.78119 9.78096 1.33319 10.333 1.33319C10.885 1.33319 11.333 1.78119 11.333 2.33319V4.99986ZM17.845 16.4305L22.3636 20.9505L22.365 20.9492C22.7556 21.3399 22.7556 21.9732 22.365 22.3639C22.1703 22.5599 21.9143 22.6572 21.6583 22.6572C21.4023 22.6572 21.1463 22.5585 20.9516 22.3639L16.433 17.8439L15.281 20.9052C15.0836 21.4319 14.5956 21.7692 14.033 21.7692H14.0116C13.4396 21.7599 12.953 21.4052 12.7703 20.8652L9.42363 11.1172C9.25697 10.6305 9.37963 10.1039 9.7423 9.74119C10.105 9.37853 10.6316 9.25586 11.1183 9.42386L20.8676 12.7692C21.4076 12.9545 21.7596 13.4412 21.769 14.0105C21.777 14.5799 21.4383 15.0785 20.9063 15.2785L17.845 16.4305ZM14.1036 7.5612C14.3596 7.5612 14.6156 7.46387 14.8103 7.26787L16.6956 5.38253C17.0863 4.99187 17.0863 4.35853 16.6956 3.96787C16.305 3.5772 15.6716 3.5772 15.281 3.96787L13.3956 5.8532C13.005 6.24387 13.005 6.8772 13.3956 7.26787C13.5903 7.46253 13.8463 7.5612 14.1023 7.5612H14.1036ZM3.97029 15.2825L5.85563 13.3972H5.85429C6.24496 13.0065 6.87829 13.0065 7.26896 13.3972C7.65963 13.7879 7.65963 14.4212 7.26896 14.8119L5.38363 16.6972C5.18896 16.8932 4.93296 16.9905 4.67696 16.9905C4.42096 16.9905 4.16496 16.8919 3.97029 16.6972C3.57963 16.3065 3.57963 15.6732 3.97029 15.2825ZM5.99963 10.3332C5.99963 9.78119 5.55163 9.33319 4.99963 9.33319H2.33296C1.78096 9.33319 1.33296 9.78119 1.33296 10.3332C1.33296 10.8852 1.78096 11.3332 2.33296 11.3332H4.99963C5.55163 11.3332 5.99963 10.8852 5.99963 10.3332ZM3.96896 3.96919C4.35963 3.57853 4.99296 3.57853 5.38363 3.96919L7.26763 5.85453C7.6583 6.24519 7.6583 6.87853 7.26763 7.26919C7.07296 7.46519 6.81696 7.56253 6.56096 7.56253C6.30496 7.56253 6.04896 7.46386 5.8543 7.26919L3.96896 5.38386C3.5783 4.99319 3.5783 4.35986 3.96896 3.96919Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/DeleteIcon.tsx
import { jsx as jsx60 } from "react/jsx-runtime";
var DeleteIcon = (props) => /* @__PURE__ */ jsx60(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx60(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.9996 5.33321H20.3329C20.8849 5.33321 21.3329 5.78121 21.3329 6.33321C21.3329 6.88521 20.8849 7.33321 20.3329 7.33321H19.0884L18.4636 19.1932C18.3609 21.1398 16.7529 22.6665 14.8023 22.6665H9.1956C7.24494 22.6665 5.63695 21.1398 5.53428 19.1918L4.90949 7.33321H3.66629C3.11429 7.33321 2.66629 6.88521 2.66629 6.33321C2.66629 5.78121 3.11429 5.33321 3.66629 5.33321H7.99961V3.66655C7.99961 2.37989 9.04627 1.33322 10.3329 1.33322H13.6663C14.9529 1.33322 15.9996 2.37989 15.9996 3.66655V5.33321ZM10.3329 3.33322C10.1489 3.33322 9.99961 3.48255 9.99961 3.66655V5.33321H13.9996V3.66655C13.9996 3.48255 13.8503 3.33322 13.6663 3.33322H10.3329Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/DesktopIcon.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
var DesktopIcon = (props) => /* @__PURE__ */ jsx61(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx61(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.9995 18.6665H18.9995C21.0222 18.6665 22.6662 17.0225 22.6662 14.9998V6.33319C22.6662 4.31053 21.0222 2.66653 18.9995 2.66653H4.99956C2.9769 2.66653 1.3329 4.31053 1.3329 6.33319V14.9998C1.3329 17.0225 2.9769 18.6665 4.99956 18.6665H10.9995V20.0412C9.76888 20.1212 8.54754 20.3412 7.36488 20.7132C6.83822 20.8798 6.54622 21.4398 6.71288 21.9678C6.87822 22.4932 7.43421 22.7838 7.96621 22.6212C10.5769 21.7985 13.4155 21.7972 16.0315 22.6212C16.1315 22.6518 16.2329 22.6678 16.3329 22.6678C16.7582 22.6678 17.1515 22.3945 17.2875 21.9678C17.4529 21.4412 17.1595 20.8798 16.6342 20.7145V20.7132C15.4515 20.3412 14.2302 20.1198 12.9995 20.0412V18.6665ZM12.0009 16.6665H18.9995C19.9182 16.6665 20.6662 15.9185 20.6662 14.9998V6.33319C20.6662 5.41453 19.9182 4.66653 18.9995 4.66653H4.99956C4.08089 4.66653 3.3329 5.41453 3.3329 6.33319V14.9998C3.3329 15.9185 4.08089 16.6665 4.99956 16.6665H11.9981C11.9986 16.6665 11.9991 16.6665 11.9995 16.6665C12 16.6665 12.0005 16.6665 12.0009 16.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/DotsHorizontalIcon.tsx
import { jsx as jsx62 } from "react/jsx-runtime";
var DotsHorizontalIcon = (props) => /* @__PURE__ */ jsx62(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx62(
      "path",
      {
        clipRule: "evenodd",
        d: "M12 13.6665C12.9204 13.6665 13.6666 12.9203 13.6666 11.9999C13.6666 11.0794 12.9204 10.3332 12 10.3332C11.0795 10.3332 10.3333 11.0794 10.3333 11.9999C10.3333 12.9203 11.0795 13.6665 12 13.6665ZM4.33332 13.6665C5.25379 13.6665 5.99998 12.9203 5.99998 11.9999C5.99998 11.0794 5.25379 10.3332 4.33332 10.3332C3.41285 10.3332 2.66666 11.0794 2.66666 11.9999C2.66666 12.9203 3.41285 13.6665 4.33332 13.6665ZM21.3333 11.9999C21.3333 12.9203 20.5871 13.6665 19.6666 13.6665C18.7461 13.6665 18 12.9203 18 11.9999C18 11.0794 18.7461 10.3332 19.6666 10.3332C20.5871 10.3332 21.3333 11.0794 21.3333 11.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/DownloadIcon.tsx
import { jsx as jsx63 } from "react/jsx-runtime";
var DownloadIcon = (props) => /* @__PURE__ */ jsx63(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx63(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.9999 10.5852L15.6266 7.95853H15.6239C16.0146 7.56786 16.6479 7.56786 17.0386 7.95853C17.4292 8.3492 17.4292 8.98253 17.0386 9.37319L12.7053 13.7065C12.5106 13.9025 12.2546 13.9998 11.9986 13.9998C11.7426 13.9998 11.4866 13.9012 11.2919 13.7065L6.95861 9.37319C6.56794 8.98253 6.56794 8.3492 6.95861 7.95853C7.34927 7.56786 7.98261 7.56786 8.37327 7.95853L10.9999 10.5852V2.99988C10.9999 2.44788 11.4479 1.99988 11.9999 1.99988C12.5519 1.99988 12.9999 2.44788 12.9999 2.99988V10.5852ZM4.99992 15.9998H18.9999C20.2885 15.9998 21.3332 17.0445 21.3332 18.3332V19.6665C21.3332 20.9552 20.2885 21.9998 18.9999 21.9998H4.99992C3.71126 21.9998 2.6666 20.9552 2.6666 19.6665V18.3332C2.6666 17.0445 3.71126 15.9998 4.99992 15.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/EditTranscriptIcon.tsx
import { jsx as jsx64 } from "react/jsx-runtime";
var EditTranscriptIcon = (props) => /* @__PURE__ */ jsx64(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.8344 8.85282C16.0211 8.87183 16.223 8.83389 16.3459 8.69219C17.6571 7.18091 20.3164 4.47683 20.6789 4.21587C20.9813 3.99815 21.3177 3.92374 21.4454 3.90212C21.4807 3.89615 21.5165 3.89661 21.5505 3.90758C21.6756 3.94791 21.9654 4.09866 21.7172 4.59505C21.4636 5.10221 19.9604 7.34328 19.423 8.14019C19.3118 8.30505 19.3328 8.52457 19.4725 8.66607L19.5901 8.78525C19.7133 8.91004 19.9004 8.939 20.0701 8.89503C20.1998 8.86144 20.3358 8.84345 20.4773 8.84345C21.2852 8.75813 22.253 9.77264 22.1492 10.4611C22.1446 10.4917 22.1409 10.5225 22.14 10.5534C22.1153 11.4508 21.3807 12.1701 20.4773 12.1701C19.3173 12.2201 18.814 11.4257 18.814 10.5067C18.814 10.3645 18.8322 10.2278 18.8661 10.0976C18.91 9.92889 18.8818 9.74296 18.7585 9.61966L18.2769 9.13808C18.1142 8.97536 17.8504 8.97536 17.6877 9.13808L17.2061 9.61966C17.0828 9.74296 17.0546 9.92889 17.0986 10.0976C17.1325 10.2278 17.1506 10.3645 17.1506 10.5067C17.1506 11.4257 16.6278 12.0884 15.4873 12.1701C14.3468 12.2517 13.8065 11.3033 13.824 10.5067C13.8414 9.71022 14.5683 8.84345 15.4873 8.84345C15.502 8.84345 15.5166 8.84342 15.5311 8.8434C15.6391 8.84323 15.7386 8.84307 15.8344 8.85282ZM15.4738 11.4552C16.1781 11.4171 16.319 10.9642 16.319 10.5067C16.319 10.0493 15.9242 9.76651 15.4598 9.78058C14.9954 9.79466 14.6436 9.96352 14.6556 10.5067C14.6677 11.05 14.7696 11.4932 15.4738 11.4552ZM20.4488 11.204C20.9104 11.204 21.1647 10.9225 21.1647 10.4651C21.1647 10.0076 20.8873 9.8185 20.4257 9.8185C19.9642 9.8185 19.756 10.0769 19.756 10.5343C19.756 10.9918 19.9873 11.204 20.4488 11.204ZM16.6382 7.29752C16.324 7.14622 14.6489 5.03354 14.4976 4.77749C14.3918 4.59837 14.3087 4.33951 14.2684 4.20013C14.2498 4.13609 14.2476 4.0671 14.2805 4.0091C14.3314 3.91941 14.4406 3.80421 14.6556 3.85346C14.9924 3.93057 17.5675 5.66511 17.7304 5.99098C17.8934 6.31686 16.9525 7.44883 16.6382 7.29752ZM4.24987 6.00025C3.55952 6.00025 2.99988 6.55989 2.99988 7.25025C2.99988 7.9406 3.55952 8.50025 4.24987 8.50025H11.7499C12.4403 8.50025 12.9999 7.9406 12.9999 7.25025C12.9999 6.55989 12.4403 6.00025 11.7499 6.00025H4.24987ZM4.24987 11.0002C3.55952 11.0002 2.99988 11.5598 2.99988 12.2502C2.99988 12.9406 3.55952 13.5002 4.24987 13.5002H10.7499C11.4403 13.5002 11.9999 12.9406 11.9999 12.2502C11.9999 11.5598 11.4403 11.0002 10.7499 11.0002H4.24987ZM2.99988 17.2502C2.99988 16.5598 3.55952 16.0002 4.24987 16.0002H15.7498C16.4402 16.0002 16.9998 16.5598 16.9998 17.2502C16.9998 17.9406 16.4402 18.5002 15.7498 18.5002H4.24987C3.55952 18.5002 2.99988 17.9406 2.99988 17.2502Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/EllipsesVerticalIcon.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var EllipsesVerticalIcon = (props) => /* @__PURE__ */ jsx65(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx65(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9998 5.99986C12.9203 5.99986 13.6665 5.25367 13.6665 4.3332C13.6665 3.41272 12.9203 2.66653 11.9998 2.66653C11.0793 2.66653 10.3331 3.41272 10.3331 4.3332C10.3331 5.25367 11.0793 5.99986 11.9998 5.99986ZM11.9998 13.6665C12.9203 13.6665 13.6664 12.9203 13.6664 11.9998C13.6664 11.0794 12.9203 10.3332 11.9998 10.3332C11.0793 10.3332 10.3331 11.0794 10.3331 11.9998C10.3331 12.9203 11.0793 13.6665 11.9998 13.6665ZM13.6664 19.6665C13.6664 20.587 12.9203 21.3332 11.9998 21.3332C11.0793 21.3332 10.3331 20.587 10.3331 19.6665C10.3331 18.746 11.0793 17.9998 11.9998 17.9998C12.9203 17.9998 13.6664 18.746 13.6664 19.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/EmbedIcon.tsx
import { jsx as jsx66 } from "react/jsx-runtime";
var EmbedIcon = (props) => /* @__PURE__ */ jsx66(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx66(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.6664 9.33318C18.4104 9.33318 18.1544 9.23585 17.9597 9.03985C17.569 8.64919 17.569 8.01585 17.9597 7.62519L20.253 5.33186L17.9597 3.03853C17.569 2.64787 17.569 2.01454 17.9597 1.62387C18.3504 1.23321 18.9837 1.23321 19.3744 1.62387L22.3744 4.62386C22.765 5.01453 22.765 5.64786 22.3744 6.03853L19.3744 9.03852C19.1797 9.23319 18.9237 9.33185 18.6677 9.33185L18.6664 9.33318ZM20.7877 10.4545C20.221 11.0212 19.4677 11.3332 18.6664 11.3332H14.6664C13.8651 11.3332 13.1117 11.0212 12.5451 10.4545L9.54506 7.45453C8.9784 6.88786 8.6664 6.13453 8.6664 5.3332C8.6664 4.53187 8.9784 3.77854 9.54506 3.21187L10.0904 2.66654H6.33307C4.31041 2.66654 2.66641 4.31054 2.66641 6.3332V17.6665C2.66641 19.6892 4.31041 21.3332 6.33307 21.3332H17.6664C19.689 21.3332 21.333 19.6892 21.333 17.6665V9.90919L20.7877 10.4545ZM13.9597 9.03986C14.1544 9.23586 14.4104 9.33319 14.6664 9.33319L14.6677 9.33453C14.9237 9.33453 15.1797 9.23586 15.3744 9.0412C15.765 8.65053 15.765 8.0172 15.3744 7.62653L13.081 5.3332L15.3744 3.03988C15.765 2.64921 15.765 2.01588 15.3744 1.62521C14.9837 1.23455 14.3504 1.23455 13.9597 1.62521L10.9597 4.62521C10.5691 5.01587 10.5691 5.6492 10.9597 6.03987L13.9597 9.03986Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/EnvelopeIcon.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var EnvelopeIcon = (props) => /* @__PURE__ */ jsx67(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx67(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.1597 11.7692C12.061 11.8252 11.9383 11.8252 11.8397 11.7692L1.47569 6.04387C1.89836 4.48654 3.31036 3.33321 4.99969 3.33321H18.9997C20.6943 3.33321 22.1103 4.49454 22.529 6.05853L12.1597 11.7692ZM12.001 13.8118C12.3877 13.8118 12.7743 13.7145 13.1263 13.5198L22.6663 8.26651V16.9998C22.6663 19.0225 21.0223 20.6665 18.9996 20.6665H4.99968C2.97702 20.6665 1.33302 19.0225 1.33302 16.9998V8.25051L10.873 13.5185C11.225 13.7145 11.6143 13.8118 12.001 13.8118Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ErrorIcon.tsx
import { jsx as jsx68 } from "react/jsx-runtime";
var ErrorIcon = (props) => /* @__PURE__ */ jsx68(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx68(
      "path",
      {
        d: "M21.9156 16.6878L14.6823 4.15987C14.1223 3.19054 13.1196 2.61188 11.9996 2.61188C10.8796 2.61188 9.87694 3.19054 9.31695 4.15987L2.08363 16.6878C1.52363 17.6572 1.52363 18.8145 2.08363 19.7838C2.64363 20.7545 3.64629 21.3332 4.76629 21.3332H19.2329C20.3529 21.3332 21.3556 20.7545 21.9156 19.7838C22.4756 18.8145 22.4756 17.6572 21.9156 16.6878ZM10.9996 8.66653C10.9996 8.11453 11.4476 7.66653 11.9996 7.66653C12.5516 7.66653 12.9996 8.11453 12.9996 8.66653V13.3332C12.9996 13.8852 12.5516 14.3332 11.9996 14.3332C11.4476 14.3332 10.9996 13.8852 10.9996 13.3332V8.66653ZM11.9996 18.0918C11.2636 18.0918 10.6663 17.4932 10.6663 16.7585C10.6663 16.0238 11.2636 15.4252 11.9996 15.4252C12.7356 15.4252 13.3329 16.0238 13.3329 16.7585C13.3329 17.4932 12.7356 18.0918 11.9996 18.0918Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ExpandIcon.tsx
import { jsx as jsx69 } from "react/jsx-runtime";
var ExpandIcon = (props) => /* @__PURE__ */ jsx69(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx69(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.2915 2.95987C11.6822 2.5692 12.3155 2.5692 12.7062 2.95987L17.3729 7.62652C17.7635 8.01719 17.7635 8.65052 17.3729 9.04119C17.1782 9.23719 16.9222 9.33452 16.6662 9.33452C16.4102 9.33452 16.1542 9.23585 15.9595 9.04119L11.9995 5.0812L8.03956 9.04119C7.64889 9.43185 7.01556 9.43185 6.62489 9.04119C6.23423 8.65052 6.23423 8.01719 6.62489 7.62652L11.2915 2.95987ZM11.9995 18.9198L15.9595 14.9598H15.9569C16.3475 14.5692 16.9809 14.5692 17.3715 14.9598C17.7622 15.3505 17.7622 15.9838 17.3715 16.3745L12.7049 21.0412C12.5102 21.2372 12.2542 21.3345 11.9982 21.3345C11.7422 21.3345 11.4862 21.2358 11.2915 21.0412L6.62489 16.3745C6.23423 15.9838 6.23423 15.3505 6.62489 14.9598C7.01556 14.5692 7.64889 14.5692 8.03956 14.9598L11.9995 18.9198Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ExpandDiagonalIcon.tsx
import { jsx as jsx70 } from "react/jsx-runtime";
var ExpandDiagonalIcon = (props) => /* @__PURE__ */ jsx70(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx70(
      "path",
      {
        clipRule: "evenodd",
        d: "M14.8048 2.66653H19.9995C20.7355 2.66653 21.3328 3.2652 21.3328 3.99987V9.19587C21.3328 9.7372 21.0102 10.2212 20.5088 10.4279C20.3435 10.4959 20.1702 10.5292 19.9982 10.5292C19.6502 10.5292 19.3102 10.3932 19.0555 10.1372L17.1662 8.24787L15.0395 10.3745C14.8448 10.5705 14.5888 10.6679 14.3328 10.6679C14.0768 10.6679 13.8208 10.5692 13.6262 10.3745C13.2355 9.98387 13.2355 9.35053 13.6262 8.95987L15.7528 6.8332L13.8622 4.94253C13.4795 4.55853 13.3662 3.9892 13.5728 3.4892C13.7808 2.9892 14.2648 2.66653 14.8048 2.66653ZM8.95817 13.6265C9.34866 13.236 9.98159 13.2359 10.3723 13.626C10.7622 14.0167 10.7619 14.6495 10.3715 15.0399L8.24484 17.1665L10.1355 19.0572C10.5182 19.4412 10.6315 20.0105 10.4248 20.5105C10.2168 21.0105 9.73284 21.3332 9.19284 21.3332H3.99817C3.26217 21.3332 2.66484 20.7345 2.66484 19.9999V14.8052C2.66484 14.2639 2.98751 13.7799 3.48884 13.5732C3.98751 13.3652 4.56084 13.4812 4.94217 13.8639L6.83151 15.7532L8.95817 13.6265ZM10.3723 13.626C10.3725 13.6262 10.3727 13.6263 10.3728 13.6265L10.3718 13.6255C10.3719 13.6256 10.3721 13.6258 10.3723 13.626Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ExpandHorizontalIcon.tsx
import { jsx as jsx71 } from "react/jsx-runtime";
var ExpandHorizontalIcon = (props) => /* @__PURE__ */ jsx71(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx71(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.74764 10.9999H9.6663L9.66497 10.9986C10.217 10.9986 10.665 11.4466 10.665 11.9986C10.665 12.5506 10.217 12.9986 9.66497 12.9986H4.7463L7.37297 15.6252C7.76364 16.0159 7.76364 16.6492 7.37297 17.0399C7.1783 17.2359 6.9223 17.3332 6.6663 17.3332C6.4103 17.3332 6.1543 17.2346 5.95964 17.0399L1.6263 12.7066C1.23564 12.3159 1.23564 11.6826 1.6263 11.2919L5.95964 6.95857C6.3503 6.56791 6.98364 6.56791 7.3743 6.95857C7.76497 7.34924 7.76497 7.98257 7.3743 8.37324L4.74764 10.9999ZM16.625 6.95988C17.0156 6.56921 17.649 6.56921 18.0396 6.95988L18.0383 6.96121L22.3716 11.2945C22.7623 11.6852 22.7623 12.3185 22.3716 12.7092L18.0383 17.0425C17.8436 17.2385 17.5876 17.3359 17.3316 17.3359C17.0756 17.3359 16.8196 17.2372 16.625 17.0425C16.2343 16.6519 16.2343 16.0185 16.625 15.6279L19.2516 13.0012H14.333C13.781 13.0012 13.333 12.5532 13.333 12.0012C13.333 11.4492 13.781 11.0012 14.333 11.0012H19.2516L16.625 8.37454C16.2343 7.98388 16.2343 7.35054 16.625 6.95988Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FastForwardIcon.tsx
import { jsx as jsx72 } from "react/jsx-runtime";
var FastForwardIcon = (props) => /* @__PURE__ */ jsx72(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx72(
      "path",
      {
        d: "M23.1396 10.5412L13.1396 4.99988C12.6183 4.71188 11.9996 4.71988 11.4876 5.02255C10.973 5.32522 10.6663 5.86255 10.6663 6.45855V9.53988L2.47297 5.00122C1.9503 4.71188 1.33297 4.72122 0.819632 5.02255C0.304965 5.32522 -0.00170135 5.86255 -0.00170135 6.45855V17.5412C-0.00170135 18.1372 0.304965 18.6745 0.819632 18.9772C1.0823 19.1319 1.3743 19.2105 1.66497 19.2105C1.9423 19.2105 2.2183 19.1399 2.47163 18.9999L10.665 14.4599V17.5412C10.665 18.1372 10.9716 18.6745 11.4863 18.9772C11.749 19.1319 12.041 19.2105 12.3316 19.2105C12.609 19.2105 12.885 19.1399 13.1383 18.9999L23.1383 13.4585C23.6676 13.1652 23.9983 12.6065 23.9983 11.9999C23.9983 11.3932 23.6703 10.8345 23.1396 10.5412Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/FavoriteIcon.tsx
import { jsx as jsx73 } from "react/jsx-runtime";
var FavoriteIcon = (props) => /* @__PURE__ */ jsx73(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx73(
      "path",
      {
        d: "M22.6168 9.04785C22.4995 8.68652 22.1861 8.42252 21.8101 8.36786L15.6501 7.47319L12.8955 1.89054C12.5595 1.20787 11.4382 1.20787 11.1022 1.89054L8.34749 7.47186L2.18751 8.36652C1.81151 8.42119 1.49818 8.68519 1.38084 9.04652C1.26351 9.40785 1.36084 9.80652 1.63418 10.0719L6.0915 14.4158L5.03817 20.5505C4.97417 20.9265 5.1275 21.3052 5.4355 21.5292C5.7435 21.7518 6.15283 21.7812 6.48883 21.6052L11.9981 18.7078L17.5075 21.6052C17.6541 21.6825 17.8128 21.7198 17.9728 21.7198C18.1795 21.7198 18.3861 21.6558 18.5608 21.5292C18.8688 21.3052 19.0235 20.9265 18.9581 20.5505L17.9048 14.4158L22.3621 10.0719C22.6355 9.80652 22.7328 9.40785 22.6155 9.04652L22.6168 9.04785Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/FavoriteOutlineIcon.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var FavoriteOutlineIcon = (props) => /* @__PURE__ */ jsx74(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx74(
      "path",
      {
        d: "M12 1.33321C12.3806 1.33321 12.7283 1.54931 12.8967 1.89066L15.6507 7.47119L21.8104 8.36625C22.1871 8.42098 22.5 8.68481 22.6177 9.0468C22.7353 9.4088 22.6372 9.80619 22.3647 10.0719L17.9078 14.4169L18.9602 20.5507C19.0246 20.9259 18.8704 21.305 18.5625 21.5288C18.2546 21.7525 17.8463 21.7821 17.5094 21.605L12 18.7095L6.49053 21.605C6.1536 21.7821 5.74536 21.7525 5.43745 21.5288C5.12954 21.305 4.97535 20.9259 5.03972 20.5507L6.09215 14.4169L1.63526 10.0719C1.36272 9.80619 1.26465 9.4088 1.38228 9.0468C1.49991 8.68481 1.81285 8.42098 2.18953 8.36625L8.34923 7.47119L11.1032 1.89066C11.2717 1.54931 11.6193 1.33321 12 1.33321ZM12 4.59286L9.91005 8.82773C9.76439 9.12289 9.48282 9.32746 9.15711 9.37479L4.4821 10.0541L7.86471 13.3518C8.10037 13.5816 8.2079 13.9126 8.15224 14.2369L7.35352 18.8921L11.5348 16.6946C11.826 16.5416 12.1739 16.5416 12.4652 16.6946L16.6464 18.8921L15.8477 14.2369C15.792 13.9126 15.8996 13.5816 16.1352 13.3518L19.5178 10.0541L14.8428 9.37479C14.5171 9.32746 14.2355 9.12289 14.0899 8.82773L12 4.59286Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/FileTreeIcon.tsx
import { jsx as jsx75 } from "react/jsx-runtime";
var FileTreeIcon = (props) => /* @__PURE__ */ jsx75(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx75(
      "path",
      {
        clipRule: "evenodd",
        d: "M9.66658 7.99986H6.99992C6.64792 7.99986 6.31458 7.92786 5.99992 7.81586V15.6665C5.99992 16.2185 6.44925 16.6665 6.99992 16.6665H9.66658C10.2186 16.6665 10.6666 17.1145 10.6666 17.6665C10.6666 18.2185 10.2186 18.6665 9.66658 18.6665H6.99992C5.34659 18.6665 3.99992 17.3212 3.99992 15.6665V1.66655C3.99992 1.11455 4.44792 0.66655 4.99992 0.66655C5.55192 0.66655 5.99992 1.11455 5.99992 1.66655V4.99987C5.99992 5.55187 6.44925 5.99987 6.99992 5.99987H9.66658C10.2186 5.99987 10.6666 6.44787 10.6666 6.99987C10.6666 7.55187 10.2186 7.99986 9.66658 7.99986ZM17.6666 2.66655H14.3332C13.0446 2.66655 11.9999 3.71121 11.9999 4.99987V8.3332C11.9999 9.62186 13.0446 10.6665 14.3332 10.6665H17.6666C18.9552 10.6665 19.9999 9.62186 19.9999 8.3332V4.99987C19.9999 3.71121 18.9552 2.66655 17.6666 2.66655ZM17.6666 13.3332H14.3332C13.0446 13.3332 11.9999 14.3779 11.9999 15.6665V18.9998C11.9999 20.2885 13.0446 21.3332 14.3332 21.3332H17.6666C18.9552 21.3332 19.9999 20.2885 19.9999 18.9998V15.6665C19.9999 14.3779 18.9552 13.3332 17.6666 13.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FillIcon.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var FillIcon = (props) => /* @__PURE__ */ jsx76(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx76(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.34363 3.34363H18.6561C19.7607 3.34363 20.6561 4.23906 20.6561 5.34363V18.6561C20.6561 19.7607 19.7607 20.6561 18.6561 20.6561H5.34363C4.23906 20.6561 3.34363 19.7607 3.34363 18.6561V5.34363C3.34363 4.23906 4.23906 3.34363 5.34363 3.34363ZM1.34363 5.34363C1.34363 3.13449 3.13449 1.34363 5.34363 1.34363H18.6561C20.8653 1.34363 22.6561 3.13449 22.6561 5.34363V18.6561C22.6561 20.8653 20.8653 22.6561 18.6561 22.6561H5.34363C3.13449 22.6561 1.34363 20.8653 1.34363 18.6561V5.34363ZM7.06238 5.03113C5.95781 5.03113 5.06238 5.92656 5.06238 7.03113V17.0624C5.06238 18.1669 5.95781 19.0624 7.06238 19.0624H16.9999C18.1044 19.0624 18.9999 18.1669 18.9999 17.0624V7.03113C18.9999 5.92656 18.1044 5.03113 16.9999 5.03113H7.06238Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FilmStripIcon.tsx
import { jsx as jsx77 } from "react/jsx-runtime";
var FilmStripIcon = (props) => /* @__PURE__ */ jsx77(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx77(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.0021 2.66661C12.0013 2.66661 12.0006 2.66661 11.9999 2.66661C11.9991 2.66661 11.9984 2.66661 11.9977 2.66661H7.00208C7.00135 2.66661 7.00061 2.66661 6.99987 2.66661C6.99914 2.66661 6.9984 2.66661 6.99767 2.66661H4.99987C2.97483 2.66661 1.33321 4.30823 1.33321 6.33328V8.33328V15.6666V17.6666C1.33321 19.6917 2.97483 21.3333 4.99987 21.3333L6.99987 21.3333H11.9999H16.9999L18.9999 21.3333C21.0249 21.3333 22.6665 19.6917 22.6665 17.6666V15.6686C22.6665 15.6679 22.6665 15.6673 22.6665 15.6666C22.6665 15.666 22.6665 15.6653 22.6665 15.6646V8.33525C22.6665 8.33459 22.6665 8.33394 22.6665 8.33328C22.6665 8.33262 22.6665 8.33196 22.6665 8.3313V6.33328C22.6665 4.30823 21.0249 2.66661 18.9999 2.66661H17.0021C17.0013 2.66661 17.0006 2.66661 16.9999 2.66661C16.9991 2.66661 16.9984 2.66661 16.9977 2.66661H12.0021ZM15.9999 4.66661H12.9999V7.33328H15.9999V4.66661ZM17.9999 4.66661V7.33328H20.6665V6.33328C20.6665 5.4128 19.9203 4.66661 18.9999 4.66661H17.9999ZM20.6665 9.33328H12.9999V14.6666H16.9999H20.6665V9.33328ZM20.6665 16.6666H17.9999V19.3333H18.9999C19.9203 19.3333 20.6665 18.5871 20.6665 17.6666V16.6666ZM15.9999 19.3333V16.6666H12.9999V19.3333H15.9999ZM10.9999 19.3333V16.6666H7.99987V19.3333H10.9999ZM5.99987 19.3333V16.6666H3.33321V17.6666C3.33321 18.5871 4.0794 19.3333 4.99987 19.3333H5.99987ZM3.33321 14.6666H6.99987H10.9999V9.33328H3.33321V14.6666ZM3.33321 7.33328H5.99987V4.66661H4.99987C4.0794 4.66661 3.33321 5.4128 3.33321 6.33328V7.33328ZM7.99987 4.66661V7.33328H10.9999V4.66661H7.99987Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FilterIcon.tsx
import { jsx as jsx78 } from "react/jsx-runtime";
var FilterIcon = (props) => /* @__PURE__ */ jsx78(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx78(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.66653 5.99987C1.66653 5.0794 2.41272 4.33321 3.3332 4.33321H20.6665C21.587 4.33321 22.3331 5.0794 22.3331 5.99987C22.3331 6.92034 21.587 7.66653 20.6665 7.66653H3.3332C2.41272 7.66653 1.66653 6.92034 1.66653 5.99987ZM4.3332 11.9999C4.3332 11.0794 5.07939 10.3332 5.99986 10.3332H17.9998C18.9203 10.3332 19.6665 11.0794 19.6665 11.9999C19.6665 12.9203 18.9203 13.6665 17.9998 13.6665H5.99986C5.07939 13.6665 4.3332 12.9203 4.3332 11.9999ZM8.66652 16.3332C7.74605 16.3332 6.99986 17.0794 6.99986 17.9998C6.99986 18.9203 7.74605 19.6665 8.66652 19.6665H15.3332C16.2536 19.6665 16.9998 18.9203 16.9998 17.9998C16.9998 17.0794 16.2536 16.3332 15.3332 16.3332H8.66652Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FitIcon.tsx
import { jsx as jsx79 } from "react/jsx-runtime";
var FitIcon = (props) => /* @__PURE__ */ jsx79(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx79(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.34357 3.34363H18.6561C19.7606 3.34363 20.6561 4.23906 20.6561 5.34363V18.6561C20.6561 19.7607 19.7606 20.6561 18.6561 20.6561H5.34357C4.239 20.6561 3.34357 19.7607 3.34357 18.6561V5.34363C3.34357 4.23906 4.239 3.34363 5.34357 3.34363ZM1.34357 5.34363C1.34357 3.13449 3.13443 1.34363 5.34357 1.34363H18.6561C20.8652 1.34363 22.6561 3.13449 22.6561 5.34363V18.6561C22.6561 20.8653 20.8652 22.6561 18.6561 22.6561H5.34357C3.13443 22.6561 1.34357 20.8653 1.34357 18.6561V5.34363ZM7.06232 7.06238C5.95775 7.06238 5.06232 7.95781 5.06232 9.06238V15.0311C5.06232 16.1357 5.95775 17.0311 7.06232 17.0311H16.9998C18.1044 17.0311 18.9998 16.1357 18.9998 15.0311V9.06238C18.9998 7.95781 18.1044 7.06238 16.9998 7.06238H7.06232Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FontsIcon.tsx
import { jsx as jsx80 } from "react/jsx-runtime";
var FontsIcon = (props) => /* @__PURE__ */ jsx80(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx80(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.3998 2.39987C10.9064 2.39987 10.4633 2.70187 10.2829 3.16109L5.10656 16.3372C5.0999 16.3531 5.09357 16.3692 5.08758 16.3855L3.6829 19.961C3.44057 20.5779 3.74417 21.2744 4.36102 21.5167C4.97786 21.7591 5.67436 21.4555 5.9167 20.8386L7.03193 17.9998H16.9676L18.0829 20.8386C18.3252 21.4555 19.0217 21.7591 19.6385 21.5167C20.2554 21.2744 20.559 20.5779 20.3167 19.961L18.9122 16.386C18.9061 16.3694 18.8996 16.353 18.8928 16.3367L13.7167 3.16109C13.5363 2.70187 13.0932 2.39987 12.5998 2.39987H11.9998H11.3998ZM16.0248 15.5998L11.9998 5.3544L7.97478 15.5998H16.0248Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/FullScreenIcon.tsx
import { jsx as jsx81 } from "react/jsx-runtime";
var FullScreenIcon = (props) => /* @__PURE__ */ jsx81(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx81(
      "path",
      {
        clipRule: "evenodd",
        d: "M14.8051 2.66721H19.9998C20.7345 2.66721 21.3331 3.26587 21.3331 4.00054V9.19521C21.3331 9.73654 21.0105 10.2205 20.5105 10.4272C20.3438 10.4965 20.1705 10.5299 19.9985 10.5299C19.6518 10.5299 19.3131 10.3952 19.0571 10.1392L17.1665 8.24854L15.0398 10.3752C14.8451 10.5712 14.5891 10.6685 14.3331 10.6685C14.0771 10.6685 13.8211 10.5699 13.6265 10.3752C13.2358 9.98454 13.2358 9.35121 13.6265 8.96054L15.7531 6.83387L13.8625 4.94321C13.4798 4.56054 13.3665 3.98987 13.5731 3.48987C13.7798 2.98987 14.2638 2.66721 14.8051 2.66721ZM8.95848 13.6272C9.34897 13.2367 9.9819 13.2365 10.3726 13.6267C10.7625 14.0174 10.7622 14.6501 10.3718 15.0405L8.24515 17.1672L10.1358 19.0579C10.5185 19.4405 10.6318 20.0112 10.4251 20.5112C10.2185 21.0112 9.73448 21.3339 9.19315 21.3339H3.99848C3.26381 21.3339 2.66515 20.7352 2.66515 20.0005V14.8059C2.66515 14.2645 2.98781 13.7805 3.48781 13.5739C3.98915 13.3672 4.55848 13.4805 4.94115 13.8632L6.83181 15.7539L8.95848 13.6272ZM20.5105 13.5739C20.0105 13.3659 19.4398 13.4805 19.0571 13.8632L17.1665 15.7539L15.0398 13.6272C14.6491 13.2365 14.0158 13.2365 13.6251 13.6272C13.2345 14.0179 13.2345 14.6512 13.6251 15.0419L15.7518 17.1685L13.8611 19.0592C13.4785 19.4419 13.3651 20.0125 13.5718 20.5125C13.7785 21.0125 14.2625 21.3352 14.8038 21.3352H19.9985C20.7331 21.3352 21.3318 20.7365 21.3318 20.0019V14.8072C21.3318 14.2659 21.0091 13.7819 20.5091 13.5752L20.5105 13.5739ZM10.1385 4.94321L8.24781 6.83387L10.3731 8.96054C10.7638 9.35121 10.7638 9.98454 10.3731 10.3752C10.1785 10.5712 9.92248 10.6685 9.66648 10.6685C9.41048 10.6685 9.15448 10.5699 8.95981 10.3752L6.83315 8.24854L4.94248 10.1392C4.68648 10.3939 4.34781 10.5299 4.00115 10.5299C3.82915 10.5299 3.65581 10.4965 3.48915 10.4272C2.98915 10.2205 2.66648 9.73654 2.66648 9.19521V4.00054C2.66648 3.26587 3.26515 2.66721 3.99981 2.66721H9.19581C9.73714 2.66721 10.2211 2.98987 10.4278 3.48987C10.6345 3.98987 10.5211 4.56054 10.1385 4.94321Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/GearIcon.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
var GearIcon = (props) => /* @__PURE__ */ jsx82(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx82(
      "path",
      {
        d: "M21.253 9.92118L19.837 9.70384C19.6717 9.13851 19.4477 8.59585 19.1637 8.07985L20.0117 6.92385C20.5024 6.25585 20.433 5.34385 19.8464 4.75985L19.2384 4.15186C18.6544 3.56652 17.7437 3.49719 17.073 3.98652L15.917 4.83585C15.401 4.55186 14.8584 4.32786 14.2944 4.16252L14.0757 2.74519C13.9491 1.92653 13.2571 1.33186 12.4291 1.33186H11.5691C10.7411 1.33186 10.0477 1.92653 9.9224 2.74653L9.70506 4.16252C9.13973 4.32786 8.59707 4.55186 8.08107 4.83585L6.92507 3.98786C6.25574 3.49852 5.34507 3.56786 4.76108 4.15452L4.15308 4.76252C3.56774 5.34785 3.49841 6.25852 3.98774 6.92785L4.83708 8.08385C4.55441 8.59985 4.32908 9.14251 4.16374 9.70651L2.74641 9.92517C1.92775 10.0518 1.33308 10.7438 1.33308 11.5718V12.4318C1.33308 13.2598 1.92775 13.9532 2.74775 14.0785L4.16374 14.2958C4.32908 14.8612 4.55308 15.4038 4.83708 15.9198L3.98908 17.0758C3.49975 17.7438 3.56908 18.6545 4.15574 19.2398L4.76374 19.8478C5.34907 20.4345 6.26107 20.5038 6.92907 20.0132L8.08507 19.1638C8.60107 19.4465 9.14373 19.6718 9.70773 19.8372L9.9264 21.2545C10.0531 22.0731 10.7451 22.6678 11.5731 22.6678H12.4331C13.2611 22.6678 13.9544 22.0731 14.0797 21.2531L14.2971 19.8372C14.8624 19.6718 15.405 19.4478 15.921 19.1638L17.077 20.0118C17.745 20.5025 18.6557 20.4318 19.241 19.8465L19.849 19.2385C20.4344 18.6532 20.5037 17.7425 20.0144 17.0732L19.165 15.9172C19.449 15.4012 19.673 14.8585 19.8384 14.2945L21.2557 14.0758C22.0744 13.9492 22.669 13.2572 22.669 12.4292V11.5692C22.669 10.7412 22.0744 10.0478 21.2557 9.92251L21.253 9.92118ZM11.9997 14.9998C10.3424 14.9998 8.99973 13.6572 8.99973 11.9998C8.99973 10.3425 10.3424 8.99984 11.9997 8.99984C13.6571 8.99984 14.9997 10.3425 14.9997 11.9998C14.9997 13.6572 13.6571 14.9998 11.9997 14.9998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/GettingStartedIcon.tsx
import { jsx as jsx83 } from "react/jsx-runtime";
var GettingStartedIcon = (props) => /* @__PURE__ */ jsx83(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx83(
      "path",
      {
        clipRule: "evenodd",
        d: "M3.99968 21.3332C3.99968 21.8851 4.44768 22.3331 4.99968 22.3331C5.55168 22.3331 5.99968 21.8851 5.99968 21.3332V13.6092C7.16809 12.8868 8.25303 12.5953 9.22234 12.7452C10.097 12.8799 10.697 13.3359 11.3917 13.8639L11.3922 13.8642C12.1894 14.4708 13.0933 15.1586 14.4743 15.3692C14.7423 15.4105 15.013 15.4305 15.2877 15.4305C16.6597 15.4305 18.097 14.9132 19.5716 13.8892C19.8396 13.7025 19.9996 13.3959 19.9996 13.0679V4.31988C19.9996 3.94788 19.793 3.60521 19.4636 3.43321C19.1343 3.26121 18.737 3.28654 18.429 3.49854C17.0956 4.42521 15.8583 4.81054 14.7783 4.64387C13.901 4.50921 13.2997 4.05188 12.6037 3.52254L12.6012 3.52066C11.8045 2.91446 10.9036 2.229 9.52767 2.01855C8.395 1.84368 7.2127 2.05732 5.99853 2.61827C5.97333 2.08866 5.5355 1.66653 4.99968 1.66653C4.44768 1.66653 3.99968 2.11453 3.99968 2.66653V21.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/GridIcon.tsx
import { jsx as jsx84 } from "react/jsx-runtime";
var GridIcon = (props) => /* @__PURE__ */ jsx84(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx84(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99962 2.66653H8.33295C9.62161 2.66653 10.6663 3.7112 10.6663 4.99986V8.33319C10.6663 9.62185 9.62161 10.6665 8.33295 10.6665H4.99962C3.71096 10.6665 2.66629 9.62185 2.66629 8.33319V4.99986C2.66629 3.7112 3.71096 2.66653 4.99962 2.66653ZM15.6663 2.66654H18.9996C20.2882 2.66654 21.3329 3.7112 21.3329 4.99987V8.33319C21.3329 9.62185 20.2882 10.6665 18.9996 10.6665H15.6663C14.3776 10.6665 13.3329 9.62185 13.3329 8.33319V4.99987C13.3329 3.7112 14.3776 2.66654 15.6663 2.66654ZM8.33295 13.3332H4.99963C3.71096 13.3332 2.6663 14.3778 2.6663 15.6665V18.9998C2.6663 20.2885 3.71096 21.3332 4.99963 21.3332H8.33295C9.62161 21.3332 10.6663 20.2885 10.6663 18.9998V15.6665C10.6663 14.3778 9.62161 13.3332 8.33295 13.3332ZM15.6663 13.3332H18.9996C20.2882 13.3332 21.3329 14.3778 21.3329 15.6665V18.9998C21.3329 20.2885 20.2882 21.3332 18.9996 21.3332H15.6663C14.3776 21.3332 13.3329 20.2885 13.3329 18.9998V15.6665C13.3329 14.3778 14.3776 13.3332 15.6663 13.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/GripDotsVerticalIcon.tsx
import { jsx as jsx85 } from "react/jsx-runtime";
var GripDotsVerticalIcon = (props) => /* @__PURE__ */ jsx85(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx85(
      "path",
      {
        clipRule: "evenodd",
        d: "M14.9995 6.66653C15.92 6.66653 16.6662 5.92034 16.6662 4.99987C16.6662 4.0794 15.92 3.33321 14.9995 3.33321C14.0791 3.33321 13.3329 4.0794 13.3329 4.99987C13.3329 5.92034 14.0791 6.66653 14.9995 6.66653ZM10.6662 4.99987C10.6662 5.92034 9.92002 6.66653 8.99955 6.66653C8.07908 6.66653 7.33289 5.92034 7.33289 4.99987C7.33289 4.0794 8.07908 3.33321 8.99955 3.33321C9.92002 3.33321 10.6662 4.0794 10.6662 4.99987ZM10.6662 11.9999C10.6662 12.9203 9.92002 13.6665 8.99955 13.6665C8.07908 13.6665 7.33289 12.9203 7.33289 11.9999C7.33289 11.0794 8.07908 10.3332 8.99955 10.3332C9.92002 10.3332 10.6662 11.0794 10.6662 11.9999ZM8.99955 20.6665C9.92002 20.6665 10.6662 19.9203 10.6662 18.9998C10.6662 18.0794 9.92002 17.3332 8.99955 17.3332C8.07908 17.3332 7.33289 18.0794 7.33289 18.9998C7.33289 19.9203 8.07908 20.6665 8.99955 20.6665ZM16.6662 11.9999C16.6662 12.9203 15.92 13.6665 14.9995 13.6665C14.0791 13.6665 13.3329 12.9203 13.3329 11.9999C13.3329 11.0794 14.0791 10.3332 14.9995 10.3332C15.92 10.3332 16.6662 11.0794 16.6662 11.9999ZM16.6662 18.9998C16.6662 19.9203 15.92 20.6665 14.9995 20.6665C14.0791 20.6665 13.3329 19.9203 13.3329 18.9998C13.3329 18.0794 14.0791 17.3332 14.9995 17.3332C15.92 17.3332 16.6662 18.0794 16.6662 18.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/HelpCenterIcon.tsx
import { jsx as jsx86 } from "react/jsx-runtime";
var HelpCenterIcon = (props) => /* @__PURE__ */ jsx86(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx86(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.3981 8.25319C13.4149 8.25934 13.4317 8.26502 13.4485 8.27024C14.5007 8.6801 15.3296 9.51393 15.7359 10.5674C15.74 10.5796 15.7443 10.5918 15.7488 10.6038C15.9155 11.0478 15.9995 11.5172 15.9995 11.9998C15.9995 12.4798 15.9155 12.9505 15.7475 13.3985C15.7401 13.4182 15.7334 13.438 15.7274 13.4578C15.3186 14.4976 14.4967 15.3185 13.4582 15.7261C13.438 15.7323 13.418 15.7391 13.3981 15.7465C12.5061 16.0812 11.5008 16.0812 10.6008 15.7465C10.5812 15.7392 10.5615 15.7325 10.5417 15.7265C9.49942 15.3181 8.67709 14.4933 8.26912 13.4514C8.26336 13.4328 8.25704 13.4142 8.25016 13.3958C8.08349 12.9518 7.99949 12.4825 7.99949 11.9998C7.99949 11.5198 8.08349 11.0492 8.25149 10.6012C8.25666 10.5874 8.26149 10.5735 8.26601 10.5596C8.67352 9.50835 9.50251 8.67871 10.5512 8.26998C10.5678 8.26482 10.5844 8.25922 10.6008 8.25319C11.4928 7.91852 12.4981 7.91852 13.3981 8.25319ZM6.8063 8.99414C7.33146 8.08655 8.08522 7.33243 8.99251 6.80684L8.05069 4.28298C6.42881 5.11236 5.11047 6.43064 4.28113 8.05183L6.8063 8.99414ZM2.37995 7.38837C3.43026 5.19658 5.19608 3.42984 7.38681 2.38011C7.37661 2.21714 7.40617 2.05237 7.47549 1.90121C7.58616 1.65987 7.78749 1.47187 8.03549 1.37987C10.5715 0.43321 13.4288 0.43321 15.9648 1.37987C16.3875 1.53783 16.6402 1.95124 16.613 2.37939C18.8044 3.42993 20.5708 5.1965 21.6205 7.38795C22.0518 7.35963 22.4624 7.61321 22.6208 8.03586C23.0941 9.30119 23.3341 10.6358 23.3341 11.9998C23.3341 13.3638 23.0941 14.6985 22.6208 15.9638C22.4714 16.3652 22.0888 16.6132 21.6835 16.6132C21.6623 16.6132 21.641 16.6125 21.6197 16.6112C20.5695 18.8036 18.8034 20.5709 16.6122 21.6209C16.6222 21.7834 16.5926 21.9477 16.5235 22.0985C16.4128 22.3398 16.2115 22.5278 15.9635 22.6198C14.6957 23.0931 13.3626 23.3331 11.9988 23.3331C10.6364 23.3331 9.30328 23.0931 8.03416 22.6198C7.61144 22.4618 7.35876 22.0484 7.38595 21.6202C5.19491 20.5695 3.42832 18.8031 2.37888 16.6111C2.35747 16.6125 2.33611 16.6131 2.31484 16.6132C1.90976 16.6129 1.52876 16.3649 1.37951 15.9638C0.906175 14.6985 0.666176 13.3638 0.666176 11.9998C0.666176 10.6358 0.906175 9.30118 1.37951 8.03585C1.53776 7.61348 1.94973 7.36084 2.37995 7.38837ZM2.31484 16.6132C2.31462 16.6132 2.31439 16.6132 2.31417 16.6132H2.31551C2.31528 16.6132 2.31506 16.6132 2.31484 16.6132ZM4.28037 15.9476C5.10969 17.5694 6.42863 18.8887 8.05008 19.7179L8.99194 17.1933C8.0843 16.6677 7.33028 15.9134 6.80497 15.0055L4.28037 15.9476ZM15.9487 19.7178L15.0067 17.1935C15.9143 16.6679 16.6684 15.9137 17.1938 15.006L19.7184 15.9481C18.8892 17.5696 17.5708 18.8882 15.9487 19.7178ZM19.7177 8.05239C18.8897 6.43086 17.5709 5.1117 15.9488 4.28198L15.0068 6.80704C15.9141 7.3327 16.6678 8.08689 17.1929 8.99455L19.7177 8.05239Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/HideIcon.tsx
import { jsx as jsx87 } from "react/jsx-runtime";
var HideIcon = (props) => /* @__PURE__ */ jsx87(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx87(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.95999 22.0398C2.15466 22.2358 2.41066 22.3332 2.66666 22.3332C2.92265 22.3332 3.17865 22.2345 3.37332 22.0398L7.2781 18.1356C7.30273 18.1133 7.3264 18.0897 7.34901 18.0647L9.72523 15.6888C10.3864 16.0974 11.1657 16.3332 12 16.3332C14.3932 16.3332 16.3333 14.3931 16.3333 11.9999C16.3333 11.1658 16.0976 10.3867 15.6893 9.72564L18.0819 7.33337C18.0939 7.32191 18.1057 7.31011 18.1173 7.29798L22.0413 3.37453C22.4319 2.98387 22.4319 2.35054 22.0413 1.95987C21.6506 1.5692 21.0173 1.5692 20.6266 1.95987L17.2367 5.34956C15.6115 4.45332 13.8535 3.99987 12 3.99987C6.66267 3.99987 3.41201 7.7212 1.94934 9.93986C1.12268 11.1919 1.12268 12.8092 1.94934 14.0598C2.59984 15.0476 3.63261 16.3709 5.06982 17.5156L1.95999 20.6252C1.56932 21.0158 1.56932 21.6492 1.95999 22.0398ZM6.49439 16.0911L8.31101 14.2746C7.90244 13.6134 7.66665 12.8342 7.66665 11.9999C7.66665 9.60662 9.60674 7.66653 12 7.66653C12.8344 7.66653 13.6137 7.90238 14.275 8.31105L15.7502 6.83593C14.5713 6.28139 13.3155 6.0012 12.0013 6.0012C7.6 6.0012 4.86134 9.15986 3.62001 11.0412C3.24134 11.6159 3.24134 12.3865 3.62001 12.9599C4.21416 13.8603 5.16828 15.0789 6.49439 16.0911ZM9.72 19.7612C10.4573 19.9185 11.2253 19.9998 12 19.9998C17.3373 19.9998 20.588 16.2785 22.0506 14.0598C22.876 12.8079 22.876 11.1905 22.0506 9.93986C21.6306 9.30253 21.164 8.69453 20.664 8.13186C20.2973 7.71986 19.6653 7.68253 19.252 8.0492C18.8386 8.41586 18.8026 9.04786 19.1693 9.46119C19.6066 9.95186 20.0146 10.4839 20.3813 11.0412C20.76 11.6145 20.76 12.3852 20.3813 12.9599C19.14 14.8412 16.4013 17.9998 12 17.9998C11.3653 17.9998 10.74 17.9345 10.14 17.8052C9.60134 17.6905 9.068 18.0332 8.952 18.5732C8.83601 19.1132 9.18 19.6452 9.72 19.7612Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/HomeIcon.tsx
import { jsx as jsx88 } from "react/jsx-runtime";
var HomeIcon = (props) => /* @__PURE__ */ jsx88(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx88(
      "path",
      {
        d: "M20.4119 7.47053L13.4119 2.15054C12.5799 1.51721 11.4186 1.51854 10.5879 2.15054L3.58793 7.47053C3.01059 7.90919 2.6666 8.60386 2.6666 9.32786V18.9998C2.6666 21.0225 4.31059 22.6665 6.33325 22.6665H17.6666C19.6892 22.6665 21.3332 21.0225 21.3332 18.9998V9.32786C21.3332 8.60386 20.9892 7.91053 20.4119 7.47053Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/HourglassIcon.tsx
import { jsx as jsx89 } from "react/jsx-runtime";
var HourglassIcon = (props) => /* @__PURE__ */ jsx89(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx89(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.6518 19.9998H18.9998C19.5518 19.9998 19.9998 20.4478 19.9998 20.9998C19.9998 21.5518 19.5518 21.9998 18.9998 21.9998H4.99986C4.44786 21.9998 3.99986 21.5518 3.99986 20.9998C3.99986 20.4478 4.44786 19.9998 4.99986 19.9998H5.34786C5.42386 16.3318 5.95719 13.8758 7.99985 11.9999C5.95719 10.1239 5.42253 7.66786 5.34786 3.99987H4.99986C4.44786 3.99987 3.99986 3.55187 3.99986 2.99988C3.99986 2.44788 4.44786 1.99988 4.99986 1.99988H18.9998C19.5518 1.99988 19.9998 2.44788 19.9998 2.99988C19.9998 3.55187 19.5518 3.99987 18.9998 3.99987H18.6518C18.5758 7.66786 18.0425 10.1239 15.9998 11.9999C18.0425 13.8758 18.5772 16.3318 18.6518 19.9998ZM10.6118 11.9999C10.6118 12.3305 10.4478 12.6399 10.1745 12.8265C8.13452 14.2145 7.44385 15.9198 7.35185 19.9998H16.6478C16.5558 15.9198 15.8652 14.2145 13.8252 12.8265C13.5518 12.6399 13.3878 12.3305 13.3878 11.9999C13.3878 11.6692 13.5518 11.3599 13.8252 11.1732C15.8652 9.78519 16.5558 8.07986 16.6478 3.99987H7.35185C7.44385 8.07986 8.13452 9.78519 10.1745 11.1732C10.4478 11.3599 10.6118 11.6692 10.6118 11.9999ZM14.6559 5.56386C14.7825 5.7092 14.8439 5.87186 14.8119 6.09453H14.8105C14.3852 9.28652 12.9865 9.99185 11.9985 9.99185C11.0105 9.99185 9.61187 9.28652 9.18653 6.09453C9.15453 5.87186 9.21587 5.7092 9.34253 5.56386C9.4692 5.41586 9.6532 5.3332 9.84653 5.3332H14.1519C14.3452 5.3332 14.5292 5.4172 14.6559 5.56386Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ImportIcon.tsx
import { jsx as jsx90 } from "react/jsx-runtime";
var ImportIcon = (props) => /* @__PURE__ */ jsx90(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx90(
      "path",
      {
        clipRule: "evenodd",
        d: "M3.81096 10.4875C4.32408 6.86526 7.60391 3.99994 11.5417 3.99994C15.4337 3.99994 18.5871 6.71644 19.2331 10.2468C21.3693 10.6197 23 12.4277 23 14.5882C23 17.0167 20.9396 18.9999 18.4167 18.9999H5.58333C3.06041 18.9999 1 17.0167 1 14.5882C1 12.7444 2.16281 11.1385 3.81096 10.4875ZM12.9168 12.3823V8.4117H11.0834V12.3823H7.41667L12 17.6764L16.5833 12.3823H12.9168Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/InfoIcon.tsx
import { jsx as jsx91 } from "react/jsx-runtime";
var InfoIcon = (props) => /* @__PURE__ */ jsx91(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx91(
      "path",
      {
        d: "M11.9998 1.33321C6.11847 1.33321 1.33315 6.11853 1.33315 11.9998C1.33315 17.8812 6.11847 22.6665 11.9998 22.6665C17.8811 22.6665 22.6664 17.8812 22.6664 11.9998C22.6664 6.11853 17.8811 1.33321 11.9998 1.33321ZM12.9998 17.0918C12.9998 17.6438 12.5518 18.0918 11.9998 18.0918C11.4478 18.0918 10.9998 17.6438 10.9998 17.0918V10.9998C10.9998 10.4479 11.4478 9.99985 11.9998 9.99985C12.5518 9.99985 12.9998 10.4479 12.9998 10.9998V17.0918ZM11.9998 8.99985C11.2638 8.99985 10.6665 8.40119 10.6665 7.66652C10.6665 6.93186 11.2638 6.33319 11.9998 6.33319C12.7358 6.33319 13.3331 6.93186 13.3331 7.66652C13.3331 8.40119 12.7358 8.99985 11.9998 8.99985Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/IntegrationsIcon.tsx
import { jsx as jsx92 } from "react/jsx-runtime";
var IntegrationsIcon = (props) => /* @__PURE__ */ jsx92(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx92(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.9997 6.66653C10.9997 9.05976 9.05963 10.9999 6.66641 10.9999C4.27318 10.9999 2.33308 9.05976 2.33308 6.66653C2.33308 4.2733 4.27318 2.33321 6.66641 2.33321C9.05963 2.33321 10.9997 4.2733 10.9997 6.66653ZM18.333 15.9998H20.6664C21.2184 15.9998 21.6664 16.4478 21.6664 16.9998C21.6664 17.5518 21.2184 17.9998 20.6664 17.9998H18.333V20.3332C18.333 20.8852 17.885 21.3332 17.333 21.3332C16.781 21.3332 16.333 20.8852 16.333 20.3332V17.9998H13.9997C13.4477 17.9998 12.9997 17.5518 12.9997 16.9998C12.9997 16.4478 13.4477 15.9998 13.9997 15.9998H16.333V13.6665C16.333 13.1145 16.781 12.6665 17.333 12.6665C17.885 12.6665 18.333 13.1145 18.333 13.6665V15.9998ZM17.333 10.9999C19.7263 10.9999 21.6664 9.05976 21.6664 6.66653C21.6664 4.2733 19.7263 2.33321 17.333 2.33321C14.9398 2.33321 12.9997 4.2733 12.9997 6.66653C12.9997 9.05976 14.9398 10.9999 17.333 10.9999ZM10.9997 17.3332C10.9997 19.7264 9.05963 21.6665 6.66641 21.6665C4.27318 21.6665 2.33308 19.7264 2.33308 17.3332C2.33308 14.9399 4.27318 12.9998 6.66641 12.9998C9.05963 12.9998 10.9997 14.9399 10.9997 17.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/LeaveIcon.tsx
import { jsx as jsx93 } from "react/jsx-runtime";
var LeaveIcon = (props) => /* @__PURE__ */ jsx93(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx93(
      "path",
      {
        clipRule: "evenodd",
        d: "M9.66636 1.99988H18.333C19.6197 1.99988 20.6664 3.04654 20.6664 4.33321V19.6652C20.6664 20.9519 19.6197 21.9985 18.333 21.9985H9.66636C8.37969 21.9985 7.33303 20.9519 7.33303 19.6652V16.3319C7.33303 15.7799 7.78103 15.3319 8.33303 15.3319C8.88503 15.3319 9.33303 15.7799 9.33303 16.3319V19.6652C9.33303 19.8492 9.48236 19.9985 9.66636 19.9985H16.7357L14.437 18.5732C13.7464 18.1452 13.333 17.4039 13.333 16.5905V7.40788C13.333 6.59454 13.7464 5.85321 14.437 5.42521L16.7357 3.99988H9.66636C9.48236 3.99988 9.33303 4.14921 9.33303 4.33321V7.66654C9.33303 8.21854 8.88503 8.66654 8.33303 8.66654C7.78103 8.66654 7.33303 8.21854 7.33303 7.66654V4.33321C7.33303 3.04654 8.37969 1.99988 9.66636 1.99988ZM8.99969 12.9999H3.41435H3.41302L5.37302 14.9599C5.76369 15.3505 5.76369 15.9839 5.37302 16.3745C5.17835 16.5705 4.92235 16.6679 4.66635 16.6679C4.41035 16.6679 4.15435 16.5692 3.95969 16.3745L0.293019 12.7079C-0.0976476 12.3172 -0.0976476 11.6839 0.293019 11.2932L3.95969 7.62521C4.35035 7.23455 4.98369 7.23455 5.37435 7.62521C5.76502 8.01588 5.76502 8.64921 5.37435 9.03988L3.41435 10.9999H8.99969C9.55169 10.9999 9.99969 11.4479 9.99969 11.9999C9.99969 12.5519 9.55169 12.9999 8.99969 12.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/LinkIcon.tsx
import { jsx as jsx94 } from "react/jsx-runtime";
var LinkIcon = (props) => /* @__PURE__ */ jsx94(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx94(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.765 15.3278C11.937 15.6878 12.2943 15.8972 12.6677 15.8972C12.813 15.8972 12.9597 15.8665 13.0983 15.7998C13.681 15.5212 14.2103 15.1465 14.685 14.6718C16.913 12.4439 16.913 8.81853 14.685 6.59053L11.785 3.69054C9.55833 1.46121 5.93434 1.46255 3.69168 3.70387C1.46369 5.93187 1.46369 9.55586 3.69168 11.7839L4.93301 13.0252C5.32368 13.4158 5.95701 13.4158 6.34768 13.0252C6.73834 12.6345 6.73834 12.0012 6.34768 11.6105L5.10635 10.3692C3.65835 8.92119 3.65835 6.5652 5.11968 5.10387C6.56767 3.65587 8.925 3.65721 10.3717 5.10387L13.2717 8.00386C14.7197 9.45319 14.7197 11.8092 13.2583 13.2705C12.9583 13.5705 12.6143 13.8145 12.237 13.9945C11.737 14.2332 11.5263 14.8292 11.765 15.3278ZM12.2157 20.3092C13.3277 21.4225 14.7877 21.9785 16.2503 21.9785C17.717 21.9785 19.1863 21.4185 20.309 20.2958C22.537 18.0678 22.537 14.4438 20.309 12.2159L19.0676 10.9745C18.677 10.5839 18.0437 10.5839 17.653 10.9745C17.2623 11.3652 17.2623 11.9985 17.653 12.3892L18.8943 13.6305C20.3423 15.0785 20.3423 17.4345 18.881 18.8958C17.433 20.3438 15.077 20.3425 13.629 18.8958L10.729 15.9958C9.28101 14.5465 9.28101 12.1905 10.7423 10.7292C11.0423 10.4292 11.3863 10.1852 11.7637 10.0052C12.2637 9.76653 12.4743 9.17053 12.2357 8.67186C11.9957 8.17186 11.397 7.95986 10.9023 8.19986C10.3197 8.47853 9.79034 8.85319 9.31567 9.32786C7.08768 11.5559 7.08768 15.1812 9.31567 17.4092L12.2157 20.3092Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ListIcon.tsx
import { jsx as jsx95 } from "react/jsx-runtime";
var ListIcon = (props) => /* @__PURE__ */ jsx95(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx95(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.33295 2.66653H4.99962C3.71096 2.66653 2.66629 3.7112 2.66629 4.99986V8.33319C2.66629 9.62185 3.71096 10.6665 4.99962 10.6665H8.33295C9.62161 10.6665 10.6663 9.62185 10.6663 8.33319V4.99986C10.6663 3.7112 9.62161 2.66653 8.33295 2.66653ZM13.6663 3.99987H20.3329C20.8849 3.99987 21.3329 4.44787 21.3329 4.99987C21.3329 5.55186 20.8849 5.99986 20.3329 5.99986H13.6663C13.1143 5.99986 12.6663 5.55186 12.6663 4.99987C12.6663 4.44787 13.1143 3.99987 13.6663 3.99987ZM13.6663 7.99986H20.3329C20.8849 7.99986 21.3329 8.44786 21.3329 8.99986C21.3329 9.55185 20.8849 9.99985 20.3329 9.99985H13.6663C13.1143 9.99985 12.6663 9.55185 12.6663 8.99986C12.6663 8.44786 13.1143 7.99986 13.6663 7.99986ZM20.3329 13.9998H13.6663C13.1143 13.9998 12.6663 14.4478 12.6663 14.9998C12.6663 15.5518 13.1143 15.9998 13.6663 15.9998H20.3329C20.8849 15.9998 21.3329 15.5518 21.3329 14.9998C21.3329 14.4478 20.8849 13.9998 20.3329 13.9998ZM13.6663 17.9998H20.3329C20.8849 17.9998 21.3329 18.4478 21.3329 18.9998C21.3329 19.5518 20.8849 19.9998 20.3329 19.9998H13.6663C13.1143 19.9998 12.6663 19.5518 12.6663 18.9998C12.6663 18.4478 13.1143 17.9998 13.6663 17.9998ZM4.99962 13.3332H8.33295C9.62161 13.3332 10.6663 14.3778 10.6663 15.6665V18.9998C10.6663 20.2885 9.62161 21.3332 8.33295 21.3332H4.99962C3.71096 21.3332 2.66629 20.2885 2.66629 18.9998V15.6665C2.66629 14.3778 3.71096 13.3332 4.99962 13.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/LiveIcon.tsx
import { jsx as jsx96 } from "react/jsx-runtime";
var LiveIcon = (props) => /* @__PURE__ */ jsx96(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx96(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.39956 3.98521C5.79022 4.37588 5.79022 5.00921 5.39956 5.39988H5.39822C1.75957 9.03854 1.75957 14.9599 5.39822 18.5985C5.78889 18.9892 5.78889 19.6225 5.39822 20.0132C5.20356 20.2092 4.94756 20.3065 4.69156 20.3065C4.43556 20.3065 4.17956 20.2078 3.9849 20.0132C1.8449 17.8732 0.666237 15.0279 0.666237 11.9999C0.666237 8.97187 1.8449 6.12654 3.9849 3.98521C4.37556 3.59455 5.00889 3.59455 5.39956 3.98521ZM8.22888 6.81453C7.83955 6.4252 7.20622 6.42253 6.81422 6.81453C5.42889 8.19986 4.66623 10.0412 4.66623 11.9998C4.66623 13.9585 5.42889 15.8012 6.81422 17.1852C7.00889 17.3798 7.26489 17.4785 7.52089 17.4785C7.77689 17.4785 8.03289 17.3812 8.22755 17.1852C8.61822 16.7945 8.61822 16.1612 8.22755 15.7705C7.21955 14.7638 6.66489 13.4238 6.66489 11.9985C6.66489 10.5732 7.21955 9.23452 8.22755 8.22653C8.61822 7.83586 8.61822 7.20253 8.22755 6.81186L8.22888 6.81453ZM11.9995 15.3332C13.8405 15.3332 15.3329 13.8408 15.3329 11.9998C15.3329 10.1589 13.8405 8.66652 11.9995 8.66652C10.1586 8.66652 8.66622 10.1589 8.66622 11.9998C8.66622 13.8408 10.1586 15.3332 11.9995 15.3332ZM17.1849 6.81453C16.7942 6.42386 16.1609 6.42386 15.7702 6.81453C15.3795 7.20519 15.3795 7.83853 15.7702 8.22919C16.7782 9.23586 17.3329 10.5759 17.3329 12.0012C17.3329 13.4265 16.7782 14.7652 15.7702 15.7732C15.3795 16.1638 15.3795 16.7972 15.7702 17.1878C15.9649 17.3825 16.2209 17.4812 16.4769 17.4812C16.7315 17.4812 16.9889 17.3838 17.1835 17.1878C18.5689 15.8025 19.3315 13.9612 19.3315 12.0025C19.3315 10.0439 18.5689 8.20119 17.1835 6.81719L17.1849 6.81453ZM18.5995 3.98655C18.9902 3.59589 19.6235 3.59589 20.0142 3.98655L20.0129 3.98789C22.1529 6.12788 23.3315 8.97454 23.3315 12.0012C23.3315 15.0279 22.1529 17.8745 20.0129 20.0145C19.8182 20.2105 19.5622 20.3078 19.3062 20.3078C19.0502 20.3078 18.7942 20.2092 18.5995 20.0145C18.2089 19.6238 18.2089 18.9905 18.5995 18.5998C22.2382 14.9612 22.2382 9.03987 18.5995 5.40122C18.2089 5.01055 18.2089 4.37722 18.5995 3.98655Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/LocalizationIcon.tsx
import { jsx as jsx97, jsxs as jsxs3 } from "react/jsx-runtime";
var LocalizationIcon = (props) => /* @__PURE__ */ jsxs3(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx97(
        "path",
        {
          d: "M11.0535 13.1579C11.1195 13.1759 11.1855 13.1839 11.2505 13.1839C11.5805 13.1839 11.8825 12.9649 11.9735 12.6309C12.0825 12.2319 11.8465 11.8189 11.4465 11.7109C10.8675 11.5529 10.3345 11.3049 9.8575 10.9849C10.6085 10.1129 11.1865 8.96988 11.4125 7.49988H12.7505C13.1645 7.49988 13.5005 7.16388 13.5005 6.74988C13.5005 6.33588 13.1645 5.99988 12.7505 5.99988H9.5005V4.74988C9.5005 4.33588 9.1645 3.99988 8.7505 3.99988C8.3365 3.99988 8.0005 4.33588 8.0005 4.74988V5.99988H4.7505C4.3365 5.99988 4.0005 6.33588 4.0005 6.74988C4.0005 7.16388 4.3365 7.49988 4.7505 7.49988H6.0835C6.2665 8.80788 6.8045 9.99988 7.6255 10.9689C6.2645 11.8649 4.8085 11.9939 4.6995 12.0019C4.2865 12.0299 3.9735 12.3869 4.0015 12.8009C4.0285 13.1969 4.3575 13.5009 4.7495 13.5009C4.7665 13.5009 4.7835 13.5009 4.8005 13.4999C5.8605 13.4289 7.4045 13.0079 8.7465 12.0159C9.4255 12.5229 10.1945 12.9249 11.0535 13.1589V13.1579ZM9.8955 7.49988C9.6965 8.55288 9.2715 9.35788 8.7475 9.97688C8.1605 9.27588 7.7555 8.42988 7.5885 7.49988H9.8955Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx97(
        "path",
        {
          clipRule: "evenodd",
          d: "M7.80049 4.74988C7.80049 4.22542 8.22604 3.79988 8.75049 3.79988C9.27495 3.79988 9.70049 4.22542 9.70049 4.74988V5.79988H12.7505C13.2749 5.79988 13.7005 6.22542 13.7005 6.74988C13.7005 7.27434 13.2749 7.69988 12.7505 7.69988H11.5823C11.3498 9.03093 10.8307 10.0979 10.156 10.9386C10.5662 11.1892 11.0159 11.3861 11.4989 11.5179C12.0057 11.6549 12.3045 12.178 12.1664 12.6836C12.051 13.1068 11.6683 13.3839 11.2505 13.3839C11.1759 13.3839 11.0996 13.3755 11.0226 13.3565C11.0153 13.3554 11.0081 13.3538 11.0009 13.3518C10.1648 13.1241 9.41304 12.7424 8.74582 12.2633C7.39798 13.2189 5.87543 13.6283 4.81386 13.6994L4.81224 13.6995C4.78928 13.7009 4.76722 13.7009 4.75131 13.7009H4.74949C4.25269 13.7009 3.83614 13.3158 3.80196 12.8145L3.80195 12.8144C3.76649 12.2901 4.16278 11.8381 4.68539 11.8024C4.78142 11.7953 6.06773 11.6803 7.32732 10.9204C6.59839 9.99977 6.10876 8.89933 5.91163 7.69988H4.75049C4.22604 7.69988 3.80049 7.27434 3.80049 6.74988C3.80049 6.22542 4.22604 5.79988 4.75049 5.79988H7.80049V4.74988ZM11.0871 12.9607C11.0935 12.9618 11.0998 12.9632 11.1061 12.9649C11.1538 12.9779 11.202 12.9839 11.2505 12.9839C11.4926 12.9839 11.7138 12.8231 11.7805 12.5783L11.7805 12.5782C11.8604 12.2858 11.6873 11.9831 11.3943 11.904L11.3938 11.9038C10.7935 11.74 10.2407 11.4828 9.74607 11.151C9.69855 11.1191 9.66697 11.0684 9.6593 11.0117C9.65163 10.955 9.66861 10.8977 9.70595 10.8544C10.4325 10.0107 10.9945 8.90242 11.2148 7.46949C11.2298 7.37191 11.3137 7.29988 11.4125 7.29988H12.7505C13.054 7.29988 13.3005 7.05342 13.3005 6.74988C13.3005 6.44634 13.054 6.19988 12.7505 6.19988H9.50049C9.39004 6.19988 9.30049 6.11034 9.30049 5.99988V4.74988C9.30049 4.44634 9.05404 4.19988 8.75049 4.19988C8.44695 4.19988 8.20049 4.44634 8.20049 4.74988V5.99988C8.20049 6.11034 8.11095 6.19988 8.00049 6.19988H4.75049C4.44695 6.19988 4.20049 6.44634 4.20049 6.74988C4.20049 7.05342 4.44695 7.29988 4.75049 7.29988H6.08349C6.18324 7.29988 6.26774 7.37338 6.28156 7.47217C6.45937 8.74303 6.98177 9.89972 7.77809 10.8396C7.81509 10.8833 7.8316 10.9407 7.82346 10.9974C7.81531 11.054 7.78328 11.1045 7.73547 11.1359C6.33229 12.0597 4.83586 12.1924 4.71413 12.2013L4.71302 12.2014C4.40995 12.222 4.18057 12.4838 4.20103 12.7873C4.22085 13.078 4.4623 13.3009 4.74949 13.3009M4.78805 13.3003C5.81494 13.2313 7.32059 12.8212 8.62761 11.8551C8.69855 11.8026 8.79547 11.8028 8.86615 11.8556C9.52268 12.3458 10.2629 12.7334 11.0871 12.9607M7.43508 7.37156C7.47308 7.32613 7.52927 7.29988 7.58849 7.29988H9.89549C9.95504 7.29988 10.0115 7.32641 10.0495 7.37226C10.0875 7.41811 10.1031 7.47851 10.092 7.53702C9.88648 8.62461 9.44607 9.4612 8.90014 10.1061C8.86202 10.1511 8.80596 10.177 8.74696 10.1769C8.68796 10.1767 8.63203 10.1505 8.59415 10.1053C7.98524 9.37811 7.56492 8.50018 7.39164 7.53523C7.38117 7.47693 7.39708 7.417 7.43508 7.37156ZM7.83328 7.69988C7.99768 8.42068 8.31241 9.08372 8.74808 9.65704C9.14153 9.14128 9.46459 8.50065 9.64928 7.69988H7.83328Z",
          fill: "currentColor",
          fillRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx97(
        "path",
        {
          d: "M19.9525 18.9869L16.9525 10.9869C16.8425 10.6939 16.5625 10.4999 16.2505 10.4999H15.7505C15.4385 10.4999 15.1585 10.6939 15.0485 10.9869L12.0485 18.9869C11.9025 19.3749 12.0995 19.8069 12.4875 19.9519C12.8735 20.0969 13.3075 19.9009 13.4525 19.5129L14.2075 17.4999H17.7935L18.5485 19.5129C18.6615 19.8139 18.9465 19.9999 19.2505 19.9999C19.3375 19.9999 19.4275 19.9849 19.5135 19.9519C19.9015 19.8059 20.0975 19.3739 19.9525 18.9869ZM14.7705 15.9999L16.0005 12.7189L17.2305 15.9999H14.7695H14.7705Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx97(
        "path",
        {
          clipRule: "evenodd",
          d: "M14.8612 10.9166C15.0004 10.5457 15.3549 10.2999 15.7505 10.2999H16.2505C16.646 10.2999 17.0005 10.5457 17.1397 10.9166L20.1397 18.9167C20.3234 19.4069 20.0752 19.9538 19.5845 20.1388C19.4742 20.1811 19.3599 20.1999 19.2505 20.1999C18.8655 20.1999 18.5043 19.9644 18.3612 19.5832L17.6549 17.6999H14.346L13.6398 19.5829C13.4559 20.0746 12.9064 20.3228 12.4173 20.1392C11.926 19.9555 11.6762 19.4082 11.8613 18.9164L14.8612 10.9166ZM15.2357 11.0572L12.2357 19.0571C12.1288 19.3413 12.2729 19.6582 12.5575 19.7645C12.8402 19.8708 13.1589 19.727 13.2651 19.4429L14.0689 17.2999H17.932L18.7357 19.4426C18.7357 19.4426 18.7357 19.4426 18.7357 19.4426C18.8186 19.6633 19.0274 19.7999 19.2505 19.7999C19.3148 19.7999 19.3803 19.7887 19.4418 19.7652L19.443 19.7647C19.7279 19.6575 19.8714 19.3406 19.7652 19.0571C19.7652 19.0571 19.7652 19.0571 19.7652 19.0571L16.7652 11.0572C16.6844 10.842 16.4789 10.6999 16.2505 10.6999H15.7505C15.522 10.6999 15.3165 10.842 15.2357 11.0572ZM16.0005 12.1491L17.519 16.1999H14.7695V15.9995L14.5832 15.9297L16.0005 12.1491ZM15.059 15.7999H16.9419L16.0005 13.2886L15.059 15.7999Z",
          fill: "currentColor",
          fillRule: "evenodd"
        }
      )
    ]
  }
);

// src/components/Icon/icons/LockIcon.tsx
import { jsx as jsx98 } from "react/jsx-runtime";
var LockIcon = (props) => /* @__PURE__ */ jsx98(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx98(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.3328 9.99985V6.66653C15.3328 4.8292 13.8368 3.3332 11.9995 3.3332C10.1622 3.3332 8.66616 4.8292 8.66616 6.66653V9.99985H15.3328ZM6.66617 10.0149V6.66653C6.66617 3.7252 9.05816 1.33321 11.9995 1.33321C14.9408 1.33321 17.3328 3.7252 17.3328 6.66653V10.0149C19.1997 10.1837 20.6661 11.7562 20.6661 13.6665V18.9998C20.6661 21.0225 19.0221 22.6665 16.9995 22.6665H6.9995C4.97684 22.6665 3.33284 21.0225 3.33284 18.9998V13.6665C3.33284 11.7562 4.79927 10.1837 6.66617 10.0149ZM11.9995 17.9998C12.5515 17.9998 12.9995 17.5518 12.9995 16.9998V15.6665C12.9995 15.1145 12.5515 14.6665 11.9995 14.6665C11.4475 14.6665 10.9995 15.1145 10.9995 15.6665V16.9998C10.9995 17.5518 11.4475 17.9998 11.9995 17.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/LogoutIcon.tsx
import { jsx as jsx99 } from "react/jsx-runtime";
var LogoutIcon = (props) => /* @__PURE__ */ jsx99(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx99(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.1506 2.0452C18.8666 1.78386 19.6653 1.89053 20.288 2.32653C20.9106 2.76253 21.2813 3.47719 21.2813 4.23719V19.7638C21.2813 20.5238 20.9093 21.2385 20.2866 21.6745C19.8893 21.9531 19.42 22.0971 18.9466 22.0971C18.6786 22.0971 18.4093 22.0505 18.1506 21.9571L13.484 20.2598C12.5653 19.9265 11.948 19.0452 11.948 18.0678V15.0012H13.6146C15.2693 15.0012 16.6146 13.6558 16.6146 12.0012C16.6146 10.3465 15.2693 9.00118 13.6146 9.00118H11.948V5.93452C11.948 4.95719 12.5653 4.07586 13.484 3.74253L18.1506 2.0452ZM6.02931 10.9999H13.616L13.6133 10.9985C14.1653 10.9985 14.6133 11.4465 14.6133 11.9985C14.6133 12.5505 14.1653 12.9985 13.6133 12.9985H6.02798L8.98798 15.9585C9.37864 16.3492 9.37864 16.9825 8.98798 17.3732C8.79331 17.5692 8.53731 17.6665 8.28131 17.6665C8.02531 17.6665 7.76931 17.5678 7.57465 17.3732L2.90799 12.7065C2.51732 12.3158 2.51732 11.6825 2.90799 11.2919L7.57465 6.6252C7.96531 6.23453 8.59864 6.23453 8.98931 6.6252C9.37997 7.01586 9.37997 7.64919 8.98931 8.03986L6.02931 10.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MaximizeIcon.tsx
import { jsx as jsx100 } from "react/jsx-runtime";
var MaximizeIcon = (props) => /* @__PURE__ */ jsx100(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx100(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.33327 9.99988C2.88527 9.99988 3.33327 9.55188 3.33327 8.99988V6.33322C3.33327 5.41455 4.08127 4.66655 4.99993 4.66655H7.6666C8.2186 4.66655 8.6666 4.21855 8.6666 3.66655C8.6666 3.11455 8.2186 2.66655 7.6666 2.66655H4.99993C2.97727 2.66655 1.33327 4.31055 1.33327 6.33322V8.99988C1.33327 9.55188 1.78127 9.99988 2.33327 9.99988ZM18.9999 2.66655H16.3333C15.7813 2.66655 15.3333 3.11455 15.3333 3.66655C15.3333 4.21855 15.7813 4.66655 16.3333 4.66655H18.9999C19.9186 4.66655 20.6666 5.41455 20.6666 6.33322V8.99988C20.6666 9.55188 21.1146 9.99988 21.6666 9.99988C22.2186 9.99988 22.6666 9.55188 22.6666 8.99988V6.33322C22.6666 4.31055 21.0226 2.66655 18.9999 2.66655ZM20.6666 14.9999C20.6666 14.4479 21.1146 13.9999 21.6666 13.9999C22.2186 13.9999 22.6666 14.4479 22.6666 14.9999V17.6665C22.6666 19.6892 21.0226 21.3332 18.9999 21.3332H16.3333C15.7813 21.3332 15.3333 20.8852 15.3333 20.3332C15.3333 19.7812 15.7813 19.3332 16.3333 19.3332H18.9999C19.9186 19.3332 20.6666 18.5852 20.6666 17.6665V14.9999ZM8.6666 20.3332C8.6666 19.7812 8.2186 19.3332 7.6666 19.3332H4.99993C4.08127 19.3332 3.33327 18.5852 3.33327 17.6665V14.9999C3.33327 14.4479 2.88527 13.9999 2.33327 13.9999C1.78127 13.9999 1.33327 14.4479 1.33327 14.9999V17.6665C1.33327 19.6892 2.97727 21.3332 4.99993 21.3332H7.6666C8.2186 21.3332 8.6666 20.8852 8.6666 20.3332ZM6.33327 6.66655H17.6666C18.2189 6.66655 18.6666 7.11426 18.6666 7.66655V16.3332C18.6666 16.8855 18.2189 17.3332 17.6666 17.3332H6.33327C5.78098 17.3332 5.33327 16.8855 5.33327 16.3332V7.66655C5.33327 7.11426 5.78098 6.66655 6.33327 6.66655Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MediaIcon.tsx
import { jsx as jsx101 } from "react/jsx-runtime";
var MediaIcon = (props) => /* @__PURE__ */ jsx101(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx101(
      "path",
      {
        d: "M17.6666 2.66653H6.33325C4.31059 2.66653 2.6666 4.31053 2.6666 6.33319V17.6665C2.6666 19.6892 4.31059 21.3332 6.33325 21.3332H17.6666C19.6892 21.3332 21.3332 19.6892 21.3332 17.6665V6.33319C21.3332 4.31053 19.6892 2.66653 17.6666 2.66653ZM15.0372 12.8638L10.1706 15.7025C10.0132 15.7945 9.83991 15.8412 9.66658 15.8412C9.49591 15.8412 9.32525 15.7958 9.16791 15.7065C8.85325 15.5265 8.66658 15.2025 8.66658 14.8398V9.16118C8.66658 8.79852 8.85458 8.47452 9.16791 8.29452C9.48258 8.11319 9.85724 8.11585 10.1692 8.29719L15.0359 11.1358C15.3466 11.3172 15.5332 11.6398 15.5332 11.9998C15.5332 12.3598 15.3492 12.6825 15.0372 12.8638Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/MegaphoneIcon.tsx
import { jsx as jsx102 } from "react/jsx-runtime";
var MegaphoneIcon = (props) => /* @__PURE__ */ jsx102(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx102(
      "path",
      {
        clipRule: "evenodd",
        d: "M-0.000305176 10.7022C-0.000305176 9.43425 0.793756 8.30207 1.9859 7.87025L18.8685 1.75508C20.4332 1.1883 22.0858 2.34737 22.0858 4.01161V17.3928C22.0858 19.057 20.4332 20.2161 18.8685 19.6493L15.811 18.5418C14.4792 21.615 10.9683 23.1614 7.77435 22.0088C4.57544 20.8545 2.86068 17.4137 3.80772 14.194L1.9859 13.5341C0.793757 13.1023 -0.000305176 11.9701 -0.000305176 10.7022ZM6.46266 15.1557C6.03897 16.9083 6.99436 18.7357 8.72951 19.3618C10.4619 19.987 12.3607 19.1943 13.156 17.5802L6.46266 15.1557Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MenuIcon.tsx
import { jsx as jsx103 } from "react/jsx-runtime";
var MenuIcon = (props) => /* @__PURE__ */ jsx103(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx103(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.66654 5.99986C1.66654 5.07939 2.41273 4.3332 3.3332 4.3332H20.6665C21.587 4.3332 22.3332 5.07939 22.3332 5.99986C22.3332 6.92033 21.587 7.66652 20.6665 7.66652H3.3332C2.41273 7.66652 1.66654 6.92033 1.66654 5.99986ZM1.66655 11.9998C1.66655 11.0794 2.41274 10.3332 3.33321 10.3332H20.6665C21.587 10.3332 22.3332 11.0794 22.3332 11.9998C22.3332 12.9203 21.587 13.6665 20.6665 13.6665H3.33321C2.41274 13.6665 1.66655 12.9203 1.66655 11.9998ZM3.33321 16.3332C2.41274 16.3332 1.66655 17.0794 1.66655 17.9998C1.66655 18.9203 2.41274 19.6665 3.33321 19.6665H20.6665C21.587 19.6665 22.3332 18.9203 22.3332 17.9998C22.3332 17.0794 21.587 16.3332 20.6665 16.3332H3.33321Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MicIcon.tsx
import { jsx as jsx104 } from "react/jsx-runtime";
var MicIcon = (props) => /* @__PURE__ */ jsx104(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx104(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.3331 10.6665C17.3331 13.6078 14.9411 15.9998 11.9998 15.9998C9.05846 15.9998 6.66646 13.6078 6.66646 10.6665V6.66653C6.66646 3.7252 9.05846 1.33321 11.9998 1.33321C14.9411 1.33321 17.3331 3.7252 17.3331 6.66653V10.6665ZM19.3331 10.6665C19.3331 10.1145 19.7811 9.66652 20.3331 9.66652C20.8851 9.66652 21.3331 10.1145 21.3331 10.6665C21.3331 15.4745 17.6771 19.4425 12.9998 19.9438V21.6665C12.9998 22.2185 12.5518 22.6665 11.9998 22.6665C11.4478 22.6665 10.9998 22.2185 10.9998 21.6665V19.9438C6.32246 19.4438 2.66647 15.4745 2.66647 10.6665C2.66647 10.1145 3.11447 9.66652 3.66647 9.66652C4.21847 9.66652 4.66647 10.1145 4.66647 10.6665C4.66647 14.7105 7.95579 17.9998 11.9998 17.9998C16.0438 17.9998 19.3331 14.7105 19.3331 10.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MicOffIcon.tsx
import { jsx as jsx105 } from "react/jsx-runtime";
var MicOffIcon = (props) => /* @__PURE__ */ jsx105(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx105(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.19441 14.391C7.25066 13.4303 6.66642 12.1168 6.66642 10.6665V6.66655C6.66642 3.72521 9.05842 1.33321 11.9998 1.33321C14.5062 1.33321 16.6137 3.07017 17.1822 5.40387L20.6264 1.95987C21.0171 1.56921 21.6504 1.56921 22.0411 1.95987C22.4317 2.35054 22.4317 2.98387 22.0411 3.37454L3.37308 22.0399C3.17841 22.2345 2.92241 22.3332 2.66641 22.3332C2.41041 22.3332 2.15441 22.2359 1.95975 22.0399C1.56908 21.6492 1.56908 21.0159 1.95975 20.6252L5.36927 17.2159C3.70118 15.5313 2.66642 13.2203 2.66642 10.6665C2.66642 10.1145 3.11442 9.66655 3.66642 9.66655C4.21842 9.66655 4.66642 10.1145 4.66642 10.6665C4.66642 12.6681 5.47593 14.481 6.7815 15.8038L8.19441 14.391ZM12.2691 15.9719C14.9918 15.8332 17.1664 13.6572 17.3051 10.9359L12.2691 15.9719ZM20.3331 9.66655C19.7811 9.66655 19.3331 10.1145 19.3331 10.6665C19.3331 14.7105 16.0437 17.9999 11.9997 17.9999C11.4557 17.9999 10.9277 17.9359 10.4184 17.8239L8.81441 19.4279C9.50908 19.6812 10.2397 19.8639 10.9997 19.9452V21.6679C10.9997 22.2199 11.4477 22.6679 11.9997 22.6679C12.5517 22.6679 12.9997 22.2199 12.9997 21.6679V19.9452C17.6771 19.4439 21.3331 15.4759 21.3331 10.6679C21.3331 10.1159 20.8851 9.66655 20.3331 9.66655Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MinimizeIcon.tsx
import { jsx as jsx106 } from "react/jsx-runtime";
var MinimizeIcon = (props) => /* @__PURE__ */ jsx106(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx106(
      "path",
      {
        clipRule: "evenodd",
        d: "M7.99976 3.66655C7.99976 3.11455 8.44776 2.66655 8.99976 2.66655C9.55176 2.66655 9.99976 3.11455 9.99976 3.66655V6.33322C9.99976 8.35588 8.35576 9.99988 6.33309 9.99988H3.66643C3.11443 9.99988 2.66643 9.55188 2.66643 8.99988C2.66643 8.44788 3.11443 7.99988 3.66643 7.99988H6.33309C7.25176 7.99988 7.99976 7.25188 7.99976 6.33322V3.66655ZM17.6664 7.99988H20.3331C20.8851 7.99988 21.3331 8.44788 21.3331 8.99988C21.3331 9.55188 20.8851 9.99988 20.3331 9.99988H17.6664C15.6438 9.99988 13.9998 8.35588 13.9998 6.33322V3.66655C13.9998 3.11455 14.4478 2.66655 14.9998 2.66655C15.5518 2.66655 15.9998 3.11455 15.9998 3.66655V6.33322C15.9998 7.25188 16.7478 7.99988 17.6664 7.99988ZM20.3331 13.9999H17.6664C15.6438 13.9999 13.9998 15.6439 13.9998 17.6665V20.3332C13.9998 20.8852 14.4478 21.3332 14.9998 21.3332C15.5518 21.3332 15.9998 20.8852 15.9998 20.3332V17.6665C15.9998 16.7479 16.7478 15.9999 17.6664 15.9999H20.3331C20.8851 15.9999 21.3331 15.5519 21.3331 14.9999C21.3331 14.4479 20.8851 13.9999 20.3331 13.9999ZM3.66643 13.9999H6.33309C8.35576 13.9999 9.99976 15.6439 9.99976 17.6665V20.3332C9.99976 20.8852 9.55176 21.3332 8.99976 21.3332C8.44776 21.3332 7.99976 20.8852 7.99976 20.3332V17.6665C7.99976 16.7479 7.25176 15.9999 6.33309 15.9999H3.66643C3.11443 15.9999 2.66643 15.5519 2.66643 14.9999C2.66643 14.4479 3.11443 13.9999 3.66643 13.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MinusIcon.tsx
import { jsx as jsx107 } from "react/jsx-runtime";
var MinusIcon = (props) => /* @__PURE__ */ jsx107(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx107(
      "path",
      {
        d: "M19.6667 12.9999H4.33344C3.7813 12.9999 3.33344 12.552 3.33344 11.9999C3.33344 11.4477 3.7813 10.9999 4.33344 10.9999H19.6667C20.2189 10.9999 20.6667 11.4477 20.6667 11.9999C20.6667 12.552 20.2189 12.9999 19.6667 12.9999Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/MinusCircleIcon.tsx
import { jsx as jsx108 } from "react/jsx-runtime";
var MinusCircleIcon = (props) => /* @__PURE__ */ jsx108(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx108(
      "path",
      {
        d: "M11.9998 1.33321C6.11842 1.33321 1.33309 6.11855 1.33309 11.9999C1.33309 17.8812 6.11842 22.6665 11.9998 22.6665C17.8811 22.6665 22.6664 17.8812 22.6664 11.9999C22.6664 6.11855 17.8811 1.33321 11.9998 1.33321ZM16.3331 12.9999H7.66642C7.11442 12.9999 6.66642 12.5519 6.66642 11.9999C6.66642 11.4479 7.11442 10.9999 7.66642 10.9999H16.3331C16.8851 10.9999 17.3331 11.4479 17.3331 11.9999C17.3331 12.5519 16.8851 12.9999 16.3331 12.9999Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/MobileIcon.tsx
import { jsx as jsx109 } from "react/jsx-runtime";
var MobileIcon = (props) => /* @__PURE__ */ jsx109(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx109(
      "path",
      {
        clipRule: "evenodd",
        d: "M7.66641 1.33321H16.3331C18.3557 1.33321 19.9997 2.9772 19.9997 4.99986V18.9998C19.9997 21.0225 18.3557 22.6665 16.3331 22.6665H7.66641C5.64374 22.6665 3.99975 21.0225 3.99975 18.9998V4.99986C3.99975 2.9772 5.64374 1.33321 7.66641 1.33321ZM16.3331 20.6665C17.2517 20.6665 17.9997 19.9185 17.9997 18.9998V4.99986C17.9997 4.0812 17.2517 3.3332 16.3331 3.3332H14.6664V3.66653C14.6664 4.21853 14.2184 4.66653 13.6664 4.66653H10.3331C9.78107 4.66653 9.33307 4.21853 9.33307 3.66653V3.3332H7.66641C6.74774 3.3332 5.99974 4.0812 5.99974 4.99986V18.9998C5.99974 19.9185 6.74774 20.6665 7.66641 20.6665H16.3331ZM13.3331 17.3332C13.3331 18.0695 12.7361 18.6665 11.9997 18.6665C11.2634 18.6665 10.6664 18.0695 10.6664 17.3332C10.6664 16.5968 11.2634 15.9998 11.9997 15.9998C12.7361 15.9998 13.3331 16.5968 13.3331 17.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MoreOptionsIcon.tsx
import { jsx as jsx110 } from "react/jsx-runtime";
var MoreOptionsIcon = (props) => /* @__PURE__ */ jsx110(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx110(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99968 2.66653H8.333C9.62167 2.66653 10.6663 3.7112 10.6663 4.99986V8.33319C10.6663 9.62185 9.62167 10.6665 8.333 10.6665H4.99968C3.71102 10.6665 2.66635 9.62185 2.66635 8.33319V4.99986C2.66635 3.7112 3.71102 2.66653 4.99968 2.66653ZM4.99968 13.3332H8.33301C9.62167 13.3332 10.6663 14.3778 10.6663 15.6665V18.9998C10.6663 20.2885 9.62167 21.3332 8.33301 21.3332H4.99968C3.71102 21.3332 2.66635 20.2885 2.66635 18.9998V15.6665C2.66635 14.3778 3.71102 13.3332 4.99968 13.3332ZM13.6663 7.99986H20.333C20.885 7.99986 21.333 7.55186 21.333 6.99986C21.333 6.44786 20.885 5.99986 20.333 5.99986H13.6663C13.1143 5.99986 12.6663 6.44786 12.6663 6.99986C12.6663 7.55186 13.1143 7.99986 13.6663 7.99986ZM13.6663 15.9998H20.333C20.885 15.9998 21.333 16.4478 21.333 16.9998C21.333 17.5518 20.885 17.9998 20.333 17.9998H13.6663C13.1143 17.9998 12.6663 17.5518 12.6663 16.9998C12.6663 16.4478 13.1143 15.9998 13.6663 15.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MoveLeftIcon.tsx
import { jsx as jsx111 } from "react/jsx-runtime";
var MoveLeftIcon = (props) => /* @__PURE__ */ jsx111(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx111(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.3333 2.66655H19.6667C20.9553 2.66655 22 3.71122 22 4.99988V18.9999C22 20.2885 20.9553 21.3332 19.6667 21.3332H18.3333C17.0447 21.3332 16 20.2885 16 18.9999V4.99988C16 3.71122 17.0447 2.66655 18.3333 2.66655ZM5.41467 10.9999H13L12.9987 10.9985C13.5507 10.9985 13.9987 11.4465 13.9987 11.9985C13.9987 12.5505 13.5507 12.9985 12.9987 12.9985H5.41334L8.04 15.6252C8.43067 16.0159 8.43067 16.6492 8.04 17.0399C7.84534 17.2359 7.58934 17.3332 7.33334 17.3332C7.07734 17.3332 6.82134 17.2345 6.62667 17.0399L2.29334 12.7065C1.90267 12.3159 1.90267 11.6825 2.29334 11.2919L6.62667 6.95854C7.01734 6.56788 7.65067 6.56788 8.04134 6.95854C8.432 7.34921 8.432 7.98254 8.04134 8.37321L5.41467 10.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MoveRightIcon.tsx
import { jsx as jsx112 } from "react/jsx-runtime";
var MoveRightIcon = (props) => /* @__PURE__ */ jsx112(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx112(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.66667 2.66655H4.33333C3.04467 2.66655 2 3.71122 2 4.99988V18.9999C2 20.2885 3.04467 21.3332 4.33333 21.3332H5.66667C6.95533 21.3332 8 20.2885 8 18.9999V4.99988C8 3.71122 6.95533 2.66655 5.66667 2.66655ZM18.5853 10.9999H11L11.0013 10.9985C10.4493 10.9985 10.0013 11.4465 10.0013 11.9985C10.0013 12.5505 10.4493 12.9985 11.0013 12.9985H18.5867L15.96 15.6252C15.5693 16.0159 15.5693 16.6492 15.96 17.0399C16.1547 17.2359 16.4107 17.3332 16.6667 17.3332C16.9227 17.3332 17.1787 17.2345 17.3733 17.0399L21.7067 12.7065C22.0973 12.3159 22.0973 11.6825 21.7067 11.2919L17.3733 6.95854C16.9827 6.56788 16.3493 6.56788 15.9587 6.95854C15.568 7.34921 15.568 7.98254 15.9587 8.37321L18.5853 10.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/MusicIcon.tsx
import { jsx as jsx113 } from "react/jsx-runtime";
var MusicIcon = (props) => /* @__PURE__ */ jsx113(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx113(
      "path",
      {
        d: "M17.9493 1.93853L8.61598 3.49453C7.48665 3.68253 6.66665 4.65053 6.66665 5.79586V15.0852C6.16398 14.8265 5.60265 14.6665 4.99999 14.6665C2.97732 14.6665 1.33333 16.3105 1.33333 18.3332C1.33333 20.3558 2.97732 21.9998 4.99999 21.9998C7.02265 21.9998 8.66664 20.3558 8.66664 18.3332V9.84651L18.6666 8.17985V13.0852C18.164 12.8265 17.6026 12.6665 17 12.6665C14.9773 12.6665 13.3333 14.3105 13.3333 16.3332C13.3333 18.3558 14.9773 19.9998 17 19.9998C19.0226 19.9998 20.6666 18.3558 20.6666 16.3332V4.2412C20.6666 3.5532 20.3653 2.9052 19.8413 2.4612C19.3159 2.0172 18.6306 1.82787 17.9493 1.93987V1.93853Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/OnAirIcon.tsx
import { jsx as jsx114 } from "react/jsx-runtime";
var OnAirIcon = (props) => /* @__PURE__ */ jsx114(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx114(
      "path",
      {
        d: "M12 22.6665C17.891 22.6665 22.6667 17.8909 22.6667 11.9999C22.6667 6.10884 17.891 1.33321 12 1.33321C6.10896 1.33321 1.33333 6.10884 1.33333 11.9999C1.33333 17.8909 6.10896 22.6665 12 22.6665Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/OpenNewIcon.tsx
import { jsx as jsx115 } from "react/jsx-runtime";
var OpenNewIcon = (props) => /* @__PURE__ */ jsx115(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx115(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.9852 2.66653H20.3319C20.8839 2.66653 21.3319 3.11453 21.3319 3.66653V11.0145C21.3319 11.5665 20.8839 12.0145 20.3319 12.0145C19.7799 12.0145 19.3319 11.5665 19.3319 11.0145V6.08119L11.0386 14.3745C10.8439 14.5705 10.5879 14.6678 10.3319 14.6678C10.0759 14.6678 9.81991 14.5692 9.62525 14.3745C9.23458 13.9838 9.23458 13.3505 9.62525 12.9598L17.9186 4.66653H12.9852C12.4332 4.66653 11.9852 4.21853 11.9852 3.66653C11.9852 3.11453 12.4332 2.66653 12.9852 2.66653ZM6.33325 19.3332H18.9999C19.5519 19.3332 19.9999 19.7812 19.9999 20.3332C19.9999 20.8852 19.5519 21.3332 18.9999 21.3332H6.33325C4.31059 21.3332 2.6666 19.6892 2.6666 17.6665V4.99986C2.6666 4.44786 3.11459 3.99986 3.66659 3.99986C4.21859 3.99986 4.66659 4.44786 4.66659 4.99986V17.6665C4.66659 18.5852 5.41459 19.3332 6.33325 19.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/OverviewIcon.tsx
import { jsx as jsx116 } from "react/jsx-runtime";
var OverviewIcon = (props) => /* @__PURE__ */ jsx116(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx116(
      "path",
      {
        clipRule: "evenodd",
        d: "M20.3025 9.43053C20.0598 9.92653 19.4625 10.1319 18.9638 9.88786L18.9652 9.89053C18.4705 9.64653 18.2652 9.04786 18.5092 8.55186C19.4665 6.60254 19.4878 5.32387 19.0838 4.91987C18.4558 4.29187 16.3292 4.79587 13.6718 6.5412C14.3478 7.10387 15.0172 7.69453 15.6625 8.3412C16.6758 9.35453 17.5798 10.4132 18.3665 11.4705C18.3701 11.476 18.3746 11.4808 18.379 11.4857C18.3843 11.4915 18.3895 11.4973 18.3932 11.5039C19.2758 12.6945 20.0038 13.8852 20.5105 15.0105C21.6065 17.4452 21.6011 19.3945 20.4972 20.4985C19.9412 21.0545 19.1772 21.3318 18.2452 21.3318C17.2092 21.3318 15.9652 20.9878 14.5705 20.3038C14.0758 20.0598 13.8705 19.4612 14.1145 18.9652C14.3572 18.4705 14.9558 18.2638 15.4532 18.5078C17.4012 19.4665 18.6825 19.4878 19.0838 19.0838C19.4238 18.7438 19.5238 17.6892 18.6865 15.8305C18.3745 15.1372 17.9425 14.4105 17.4598 13.6745C16.8972 14.3505 16.3065 15.0185 15.6625 15.6625C13.4985 17.8252 11.1305 19.5478 8.99318 20.5092C7.77985 21.0545 6.68652 21.3278 5.75986 21.3278C4.82519 21.3278 4.05853 21.0505 3.50519 20.4958C2.33053 19.3212 2.39853 17.2158 3.69986 14.5692C3.94253 14.0745 4.53986 13.8665 5.03852 14.1118C5.53319 14.3558 5.73852 14.9545 5.49452 15.4505C4.53719 17.3998 4.51586 18.6785 4.91986 19.0825C5.54786 19.7092 7.67318 19.2065 10.3305 17.4612C9.65451 16.8985 8.98518 16.3078 8.33985 15.6612C7.32652 14.6478 6.42252 13.5892 5.63586 12.5319C5.63223 12.5264 5.62779 12.5215 5.62339 12.5167C5.61808 12.5108 5.61283 12.5051 5.60919 12.4985C4.72652 11.3079 3.99853 10.1172 3.49186 8.99186C2.39586 6.5572 2.4012 4.60787 3.50519 3.50388C4.67852 2.33055 6.78385 2.39855 9.43185 3.69854C9.92651 3.94254 10.1318 4.54121 9.88784 5.03721C9.64518 5.53321 9.04651 5.73987 8.54918 5.49454C6.60119 4.53587 5.31986 4.51587 4.91852 4.91854C4.57852 5.25854 4.47852 6.3132 5.31586 8.17186C5.62786 8.8652 6.05985 9.59186 6.54252 10.3279C7.10385 9.65319 7.69318 8.98653 8.33985 8.33986C9.35051 7.3292 10.4065 6.42654 11.4625 5.6412C11.4694 5.63684 11.4754 5.6313 11.4814 5.62569C11.4883 5.61931 11.4953 5.61284 11.5038 5.60787C12.6945 4.72654 13.8838 3.99854 15.0092 3.49188C17.4465 2.39588 19.3945 2.39988 20.4972 3.50388C21.6718 4.67854 21.6038 6.78387 20.3025 9.43053ZM16.2398 12.0025C15.6345 11.2439 14.9825 10.4879 14.2478 9.75319C13.5198 9.02386 12.7652 8.36786 12.0012 7.75853C11.2665 8.34653 10.5185 8.98786 9.75318 9.75319C9.02518 10.4825 8.36918 11.2372 7.76118 11.9999C8.36652 12.7585 9.01851 13.5145 9.75318 14.2492C10.4812 14.9772 11.2372 15.6332 11.9998 16.2425C12.7345 15.6558 13.4825 15.0145 14.2478 14.2492C14.9732 13.5225 15.6292 12.7665 16.2398 12.0025ZM13.3332 11.9998C13.3332 12.7362 12.7362 13.3332 11.9998 13.3332C11.2635 13.3332 10.6665 12.7362 10.6665 11.9998C10.6665 11.2635 11.2635 10.6665 11.9998 10.6665C12.7362 10.6665 13.3332 11.2635 13.3332 11.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PageIcon.tsx
import { jsx as jsx117 } from "react/jsx-runtime";
var PageIcon = (props) => /* @__PURE__ */ jsx117(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx117(
      "path",
      {
        clipRule: "evenodd",
        d: "M23.1611 2.17988C22.2318 1.25055 20.7171 1.25055 19.7878 2.17988L14.8825 7.08787C15.6384 7.3852 16.3358 7.83053 16.9278 8.42386C17.5251 9.02253 17.9611 9.71986 18.2504 10.4625L23.1624 5.5532C24.0931 4.62254 24.0918 3.10921 23.1611 2.17988ZM18.9998 21.3332H4.9998C2.97714 21.3332 1.33315 19.6892 1.33315 17.6665V6.3332C1.33315 4.31054 2.97714 2.66654 4.9998 2.66654H14.2864C14.8384 2.66654 15.2864 3.11454 15.2864 3.66654C15.2864 4.21854 14.8384 4.66654 14.2864 4.66654H4.9998C4.08114 4.66654 3.33314 5.41453 3.33314 6.3332V17.6665C3.33314 18.5852 4.08114 19.3332 4.9998 19.3332H18.9998C19.9184 19.3332 20.6664 18.5852 20.6664 17.6665V11.0452C20.6664 10.4932 21.1144 10.0452 21.6664 10.0452C22.2184 10.0452 22.6664 10.4932 22.6664 11.0452V17.6665C22.6664 19.6892 21.0224 21.3332 18.9998 21.3332ZM5.66647 7.99986C6.21875 7.99986 6.66647 7.55215 6.66647 6.99986C6.66647 6.44758 6.21875 5.99987 5.66647 5.99987C5.11419 5.99987 4.66647 6.44758 4.66647 6.99986C4.66647 7.55215 5.11419 7.99986 5.66647 7.99986ZM9.99979 6.99986C9.99979 7.55215 9.55208 7.99986 8.99979 7.99986C8.44751 7.99986 7.9998 7.55215 7.9998 6.99986C7.9998 6.44758 8.44751 5.99987 8.99979 5.99987C9.55208 5.99987 9.99979 6.44758 9.99979 6.99986ZM12.7078 8.66653C13.7624 8.66653 14.7584 9.08253 15.5091 9.83586C17.0491 11.3812 17.0491 13.8945 15.5091 15.4398C14.1158 16.8665 12.5464 17.3398 10.9705 17.3398C9.75045 17.3398 8.52779 17.0558 7.37846 16.7105C6.99313 16.5945 6.71446 16.2585 6.6718 15.8572C6.63046 15.4572 6.83313 15.0692 7.18646 14.8758C8.27443 14.2812 8.36246 13.7692 8.52245 12.8386L8.52246 12.8385C8.66913 11.9785 8.85312 10.9105 9.91312 9.83852C10.6585 9.08386 11.6478 8.66786 12.6998 8.66653H12.7078Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PanelLeftIcon.tsx
import { jsx as jsx118 } from "react/jsx-runtime";
var PanelLeftIcon = (props) => /* @__PURE__ */ jsx118(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M18.9997 2.66653H4.99974C2.97708 2.66653 1.33309 4.31053 1.33309 6.33319V17.6665C1.33309 19.6892 2.97708 21.3332 4.99974 21.3332H18.9997C21.0224 21.3332 22.6664 19.6892 22.6664 17.6665V6.33319C22.6664 4.31053 21.0224 2.66653 18.9997 2.66653ZM7.33307 16.3332C7.33307 16.8852 6.88507 17.3332 6.33307 17.3332C5.78108 17.3332 5.33308 16.8852 5.33308 16.3332V7.66652C5.33308 7.11452 5.78108 6.66652 6.33307 6.66652C6.88507 6.66652 7.33307 7.11452 7.33307 7.66652V16.3332ZM17.6664 12.9998H12.0811L13.3744 14.2932C13.7651 14.6838 13.7651 15.3172 13.3744 15.7078C13.1797 15.9025 12.9237 16.0012 12.6677 16.0012C12.4117 16.0012 12.1557 15.9038 11.9611 15.7078L8.96107 12.7078C8.5704 12.3172 8.5704 11.6838 8.96107 11.2932L11.9611 8.29319C12.3517 7.90252 12.9851 7.90252 13.3757 8.29319C13.7664 8.68385 13.7664 9.31718 13.3757 9.70785L12.0824 11.0012H17.6677C18.2197 11.0012 18.6677 11.4492 18.6677 12.0012C18.6677 12.5532 18.2197 13.0012 17.6677 13.0012L17.6664 12.9998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PanelRightIcon.tsx
import { jsx as jsx119 } from "react/jsx-runtime";
var PanelRightIcon = (props) => /* @__PURE__ */ jsx119(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx119(
      "path",
      {
        d: "M18.9996 2.66653H4.99968C2.97702 2.66653 1.33302 4.31053 1.33302 6.33319V17.6665C1.33302 19.6892 2.97702 21.3332 4.99968 21.3332H18.9996C21.0223 21.3332 22.6663 19.6892 22.6663 17.6665V6.33319C22.6663 4.31053 21.0223 2.66653 18.9996 2.66653ZM7.33301 16.3332C7.33301 16.8852 6.88501 17.3332 6.33301 17.3332C5.78101 17.3332 5.33301 16.8852 5.33301 16.3332V7.66652C5.33301 7.11452 5.78101 6.66652 6.33301 6.66652C6.88501 6.66652 7.33301 7.11452 7.33301 7.66652V16.3332ZM18.373 12.7065L15.373 15.7065C15.1783 15.9012 14.9223 15.9998 14.6663 15.9998C14.4103 15.9998 14.1543 15.9025 13.9597 15.7065C13.569 15.3158 13.569 14.6825 13.9597 14.2918L15.253 12.9985H9.66634C9.11434 12.9985 8.66634 12.5505 8.66634 11.9985C8.66634 11.4465 9.11434 10.9985 9.66634 10.9985H15.2517L13.9583 9.70518C13.5677 9.31452 13.5677 8.68119 13.9583 8.29052C14.349 7.89985 14.9823 7.89985 15.373 8.29052L18.373 11.2905C18.7636 11.6812 18.7636 12.3158 18.373 12.7065Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PanelUpIcon.tsx
import { jsx as jsx120 } from "react/jsx-runtime";
var PanelUpIcon = (props) => /* @__PURE__ */ jsx120(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx120(
      "path",
      {
        d: "M18.9996 2.66653H4.99962C2.97696 2.66653 1.33296 4.31053 1.33296 6.33319V17.6665C1.33296 19.6892 2.97696 21.3332 4.99962 21.3332H18.9996C21.0222 21.3332 22.6662 19.6892 22.6662 17.6665V6.33319C22.6662 4.31053 21.0222 2.66653 18.9996 2.66653ZM15.5343 14.8678C15.3396 15.0625 15.0836 15.1612 14.8276 15.1612C14.5716 15.1612 14.3156 15.0638 14.1209 14.8678L12.9996 13.7465V16.6652C12.9996 17.2172 12.5516 17.6652 11.9996 17.6652C11.4476 17.6652 10.9996 17.2172 10.9996 16.6652V13.7465L9.87827 14.8678C9.48761 15.2585 8.85428 15.2585 8.46361 14.8678C8.07295 14.4772 8.07295 13.8438 8.46361 13.4532L11.2916 10.6252C11.6823 10.2345 12.3156 10.2345 12.7063 10.6252L15.5343 13.4532C15.9249 13.8438 15.9249 14.4772 15.5343 14.8678ZM17.6663 8.66652H6.33295C5.78095 8.66652 5.33295 8.21852 5.33295 7.66652C5.33295 7.11452 5.78095 6.66652 6.33295 6.66652H17.6663C18.2183 6.66652 18.6663 7.11452 18.6663 7.66652C18.6663 8.21852 18.2183 8.66652 17.6663 8.66652Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PasswordIcon.tsx
import { jsx as jsx121 } from "react/jsx-runtime";
var PasswordIcon = (props) => /* @__PURE__ */ jsx121(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx121(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.3329 7.99986V5.3332C15.3329 3.49587 13.8382 1.99987 11.9995 1.99987C10.1609 1.99987 8.66622 3.49587 8.66622 5.3332V7.99986H15.3329ZM6.66622 7.99986V5.3332C6.66622 2.39187 9.05822 -0.00012207 11.9995 -0.00012207C14.9409 -0.00012207 17.3329 2.39187 17.3329 5.3332V7.99986H17.6662C18.9529 7.99986 19.9995 9.04652 19.9995 10.3332V16.3332C19.9995 17.6198 18.9529 18.6665 17.6662 18.6665H6.33289C5.04623 18.6665 3.99956 17.6198 3.99956 16.3332V10.3332C3.99956 9.04652 5.04623 7.99986 6.33289 7.99986H6.66622ZM4.66623 22.3332C4.66623 23.2536 3.92004 23.9998 2.99957 23.9998C2.07909 23.9998 1.3329 23.2536 1.3329 22.3332C1.3329 21.4127 2.07909 20.6665 2.99957 20.6665C3.92004 20.6665 4.66623 21.4127 4.66623 22.3332ZM8.99955 23.9998C9.92002 23.9998 10.6662 23.2536 10.6662 22.3332C10.6662 21.4127 9.92002 20.6665 8.99955 20.6665C8.07908 20.6665 7.33289 21.4127 7.33289 22.3332C7.33289 23.2536 8.07908 23.9998 8.99955 23.9998ZM16.6662 22.3332C16.6662 23.2536 15.92 23.9998 14.9995 23.9998C14.0791 23.9998 13.3329 23.2536 13.3329 22.3332C13.3329 21.4127 14.0791 20.6665 14.9995 20.6665C15.92 20.6665 16.6662 21.4127 16.6662 22.3332ZM20.9995 23.9998C21.92 23.9998 22.6662 23.2536 22.6662 22.3332C22.6662 21.4127 21.92 20.6665 20.9995 20.6665C20.079 20.6665 19.3329 21.4127 19.3329 22.3332C19.3329 23.2536 20.079 23.9998 20.9995 23.9998ZM11.9995 14.9998C12.5515 14.9998 12.9995 14.5518 12.9995 13.9998V12.6665C12.9995 12.1145 12.5515 11.6665 11.9995 11.6665C11.4475 11.6665 10.9995 12.1145 10.9995 12.6665V13.9998C10.9995 14.5518 11.4475 14.9998 11.9995 14.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PauseIcon.tsx
import { jsx as jsx122 } from "react/jsx-runtime";
var PauseIcon = (props) => /* @__PURE__ */ jsx122(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx122(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99957 2.66654H6.99957C8.28823 2.66654 9.3329 3.71121 9.3329 4.99988V18.9999C9.3329 20.2885 8.28823 21.3332 6.99957 21.3332H4.99957C3.71091 21.3332 2.66624 20.2885 2.66624 18.9999V4.99988C2.66624 3.71121 3.71091 2.66654 4.99957 2.66654ZM16.9996 2.66654H18.9996C20.2882 2.66654 21.3329 3.71121 21.3329 4.99988V18.9999C21.3329 20.2885 20.2882 21.3332 18.9996 21.3332H16.9996C15.7109 21.3332 14.6662 20.2885 14.6662 18.9999V4.99988C14.6662 3.71121 15.7109 2.66654 16.9996 2.66654Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PencilIcon.tsx
import { jsx as jsx123 } from "react/jsx-runtime";
var PencilIcon = (props) => /* @__PURE__ */ jsx123(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx123(
      "path",
      {
        d: "M15.2186 3.7972L4.80665 14.2092C3.65599 15.3612 2.93866 18.7505 2.68266 20.1545C2.62399 20.4772 2.72799 20.8092 2.95999 21.0412C3.14932 21.2305 3.40399 21.3345 3.66666 21.3345C3.72532 21.3345 3.78532 21.3292 3.84532 21.3185C5.24932 21.0638 8.63864 20.3465 9.79064 19.1945L20.2026 8.78252C21.5759 7.40786 21.5759 5.1732 20.2026 3.79854C18.872 2.46787 16.5493 2.46787 15.2186 3.7972Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PeopleIcon.tsx
import { jsx as jsx124 } from "react/jsx-runtime";
var PeopleIcon = (props) => /* @__PURE__ */ jsx124(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx124(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.6666 4.99987C19.6666 7.0249 18.0249 8.66652 15.9999 8.66652C13.9749 8.66652 12.3332 7.0249 12.3332 4.99987C12.3332 2.97483 13.9749 1.33321 15.9999 1.33321C18.0249 1.33321 19.6666 2.97483 19.6666 4.99987ZM11.3332 8.33319C11.3332 10.3582 9.69163 11.9998 7.66659 11.9998C5.64155 11.9998 3.99993 10.3582 3.99993 8.33319C3.99993 6.30815 5.64155 4.66653 7.66659 4.66653C9.69163 4.66653 11.3332 6.30815 11.3332 8.33319ZM22.9279 14.7972C21.8465 11.9278 19.0626 9.99985 15.9999 9.99985C14.1106 9.99985 12.3306 10.7372 10.9879 11.9732C13.4599 12.9185 15.5012 14.8652 16.4666 17.4252C16.6159 17.8198 16.6826 18.2292 16.7092 18.6385C18.3239 18.5718 19.9212 18.2918 21.4625 17.7772C22.0705 17.5745 22.5745 17.1278 22.8505 16.5518C23.1172 15.9918 23.1452 15.3678 22.9292 14.7972H22.9279ZM7.66658 13.3332C10.7292 13.3332 13.5132 15.2612 14.5946 18.1305C14.8106 18.7012 14.7826 19.3252 14.5159 19.8852C14.2399 20.4612 13.7359 20.9078 13.1279 21.1105C11.3612 21.6998 9.52258 21.9998 7.66658 21.9998C5.81059 21.9998 3.97193 21.6998 2.20526 21.1105C1.59727 20.9078 1.09193 20.4612 0.817268 19.8852C0.550602 19.3252 0.522602 18.7025 0.738602 18.1305C1.81993 15.2612 4.60393 13.3332 7.66658 13.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PinIcon.tsx
import { jsx as jsx125 } from "react/jsx-runtime";
var PinIcon = (props) => /* @__PURE__ */ jsx125(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx125(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.3332 10.2785C17.8012 10.8119 18.2172 11.3879 18.5719 11.9959C19.3225 13.2825 19.7999 14.6985 19.9919 16.2065C20.0279 16.4919 19.9399 16.7772 19.7505 16.9932C19.5599 17.2079 19.2865 17.3319 18.9999 17.3319H12.9999V21.6665C12.9999 22.2185 12.5519 22.6665 11.9999 22.6665C11.4479 22.6665 10.9999 22.2185 10.9999 21.6665V17.3319H4.99988C4.71321 17.3319 4.43988 17.2092 4.24921 16.9932C4.05988 16.7785 3.97188 16.4919 4.00788 16.2065C4.19988 14.6985 4.67721 13.2825 5.42788 11.9959C5.78255 11.3879 6.19721 10.8119 6.66655 10.2785V4.99987C6.66655 2.97721 8.31055 1.33321 10.3332 1.33321H13.6665C15.6892 1.33321 17.3332 2.97721 17.3332 4.99987V10.2785Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PinSlashIcon.tsx
import { jsx as jsx126 } from "react/jsx-runtime";
var PinSlashIcon = (props) => /* @__PURE__ */ jsx126(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx126(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.2521 17.3332H4.99988V17.3319C4.71321 17.3319 4.43988 17.2092 4.24921 16.9932C4.05988 16.7785 3.97188 16.4919 4.00788 16.2065C4.19988 14.6985 4.67721 13.2825 5.42788 11.9959C5.78255 11.3879 6.19721 10.8119 6.66655 10.2785V4.99987C6.66655 2.97721 8.31055 1.33321 10.3332 1.33321H13.6665C15.6892 1.33321 17.3332 2.97721 17.3332 4.99987V5.25296L20.6265 1.95987C21.0172 1.5692 21.6505 1.5692 22.0412 1.95987C22.4319 2.35054 22.4319 2.98387 22.0412 3.37454L3.3732 22.0399C3.17854 22.2345 2.92254 22.3332 2.66654 22.3332C2.41054 22.3332 2.15454 22.2359 1.95987 22.0399C1.5692 21.6492 1.5692 21.0159 1.95987 20.6252L5.2521 17.3332ZM17.6119 10.6305L10.9999 17.3332V21.6665C10.9999 22.2185 11.4479 22.6665 11.9999 22.6665C12.5519 22.6665 12.9999 22.2185 12.9999 21.6665V17.3332H18.9999C19.2865 17.3332 19.5599 17.2092 19.7505 16.9945C19.9399 16.7785 20.0279 16.4932 19.9919 16.2079C19.7999 14.6999 19.3225 13.2839 18.5719 11.9972C18.2919 11.5172 17.9639 11.0652 17.6119 10.6305Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PipIcon.tsx
import { jsx as jsx127 } from "react/jsx-runtime";
var PipIcon = (props) => /* @__PURE__ */ jsx127(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx127(
      "path",
      {
        d: "M5 20.6665H19C21.0227 20.6665 22.6667 19.0225 22.6667 16.9999V6.99988C22.6667 4.97721 21.0227 3.33321 19 3.33321H5C2.97734 3.33321 1.33334 4.97721 1.33334 6.99988V16.9999C1.33334 19.0225 2.97734 20.6665 5 20.6665ZM14 13.6665C14 12.7465 14.7467 11.9999 15.6667 11.9999H19C19.92 11.9999 20.6667 12.7465 20.6667 13.6665V16.9999C20.6667 17.9199 19.92 18.6665 19 18.6665H15.6667C14.7467 18.6665 14 17.9199 14 16.9999V13.6665Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PlayIcon.tsx
import { jsx as jsx128 } from "react/jsx-runtime";
var PlayIcon = (props) => /* @__PURE__ */ jsx128(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx128(
      "path",
      {
        d: "M20.1332 9.97052L7.47719 2.96254C6.73986 2.55454 5.8692 2.56654 5.1452 2.9932C4.42787 3.41587 3.99987 4.16253 3.99987 4.99186V19.0078C3.99987 19.8372 4.42787 20.5838 5.1452 21.0065C5.5172 21.2252 5.92653 21.3358 6.33853 21.3358C6.7292 21.3358 7.11986 21.2358 7.47719 21.0385L20.1318 14.0305C20.8732 13.6212 21.3332 12.8425 21.3332 12.0012C21.3332 11.1598 20.8732 10.3799 20.1332 9.97052Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PlayerIcon.tsx
import { jsx as jsx129 } from "react/jsx-runtime";
var PlayerIcon = (props) => /* @__PURE__ */ jsx129(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx129(
      "path",
      {
        d: "M18.9998 3.99987H4.9998C2.97714 3.99987 1.33315 5.64386 1.33315 7.66653V16.3332C1.33315 18.3558 2.97714 19.9998 4.9998 19.9998H18.9998C21.0224 19.9998 22.6664 18.3558 22.6664 16.3332V7.66653C22.6664 5.64386 21.0224 3.99987 18.9998 3.99987ZM15.0371 12.8638L10.1705 15.7025C10.0131 15.7945 9.83979 15.8412 9.66646 15.8412C9.49579 15.8412 9.32513 15.7958 9.16779 15.7065C8.85313 15.5265 8.66646 15.2025 8.66646 14.8398V9.16119C8.66646 8.79852 8.85446 8.47452 9.16779 8.29452C9.48246 8.11319 9.85712 8.11586 10.1691 8.29719L15.0358 11.1359C15.3464 11.3172 15.5331 11.6398 15.5331 11.9998C15.5331 12.3598 15.3491 12.6825 15.0371 12.8638Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/PlusIcon.tsx
import { jsx as jsx130 } from "react/jsx-runtime";
var PlusIcon = (props) => /* @__PURE__ */ jsx130(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx130(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.9997 19.6665C10.9997 20.2185 11.4477 20.6665 11.9997 20.6665C12.5517 20.6665 12.9997 20.2185 12.9997 19.6665V12.9998H19.6664C20.2184 12.9998 20.6664 12.5518 20.6664 11.9998C20.6664 11.4478 20.2184 10.9998 19.6664 10.9998H12.9997V4.3332C12.9997 3.7812 12.5517 3.3332 11.9997 3.3332C11.4477 3.3332 10.9997 3.7812 10.9997 4.3332V10.9998H4.33308C3.78108 10.9998 3.33308 11.4478 3.33308 11.9998C3.33308 12.5518 3.78108 12.9998 4.33308 12.9998H10.9997V19.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PodcastIcon.tsx
import { jsx as jsx131 } from "react/jsx-runtime";
var PodcastIcon = (props) => /* @__PURE__ */ jsx131(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx131(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9997 -0.00012207C5.75035 -0.00012207 0.666359 5.08387 0.666359 11.3332C0.666359 14.6758 2.13302 17.8318 4.68768 19.9918C5.10635 20.3478 5.74101 20.2972 6.09701 19.8745C6.45434 19.4532 6.40234 18.8212 5.97968 18.4652C3.87435 16.6852 2.66769 14.0865 2.66769 11.3332C2.66769 6.18653 6.85568 1.99987 12.001 1.99987C17.1463 1.99987 21.3343 6.18653 21.3343 11.3332C21.3343 14.0852 20.1276 16.6852 18.0223 18.4652C17.601 18.8225 17.549 19.4532 17.905 19.8745C18.101 20.1078 18.3836 20.2292 18.6676 20.2292C18.897 20.2292 19.1263 20.1518 19.313 19.9932C21.8676 17.8318 23.3343 14.6772 23.3343 11.3345C23.333 5.08387 18.249 -0.00012207 11.9997 -0.00012207ZM11.9997 3.99987C16.0423 3.99987 19.333 7.28919 19.333 11.3332C19.333 12.9465 18.821 14.4758 17.849 15.7572C17.6517 16.0158 17.3543 16.1532 17.0503 16.1532C16.841 16.1532 16.629 16.0878 16.4477 15.9505C16.0063 15.6172 15.921 14.9892 16.2543 14.5492C16.961 13.6185 17.333 12.5065 17.333 11.3332C17.333 8.39186 14.941 5.99986 11.9997 5.99986C9.05834 5.99986 6.66635 8.39186 6.66635 11.3332C6.66635 12.5065 7.03968 13.6185 7.74501 14.5492C8.07834 14.9892 7.99167 15.6172 7.55168 15.9505C7.10768 16.2838 6.48235 16.1958 6.15035 15.7572C5.17968 14.4758 4.66635 12.9465 4.66635 11.3332C4.66635 7.28919 7.95701 3.99987 11.9997 3.99987ZM15.333 11.3332C15.333 13.1741 13.8406 14.6665 11.9997 14.6665C10.1587 14.6665 8.66634 13.1741 8.66634 11.3332C8.66634 9.49224 10.1587 7.99986 11.9997 7.99986C13.8406 7.99986 15.333 9.49224 15.333 11.3332ZM10.901 15.9998H13.097C13.9503 15.9998 14.765 16.3638 15.333 16.9998C15.9023 17.6358 16.173 18.4852 16.0783 19.3318L15.7903 21.9238C15.661 23.1078 14.665 23.9998 13.4717 23.9998H10.5263C9.33433 23.9998 8.33833 23.1065 8.20767 21.9225L7.91967 19.3305C7.825 18.4852 8.09567 17.6345 8.665 16.9998C9.233 16.3638 10.0477 15.9998 10.901 15.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PreviewIcon.tsx
import { jsx as jsx132 } from "react/jsx-runtime";
var PreviewIcon = (props) => /* @__PURE__ */ jsx132(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx132(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.94895 14.0598C3.41162 16.2785 6.66228 19.9998 11.9996 19.9998C17.3369 19.9998 20.5876 16.2772 22.0502 14.0585C22.8769 12.8065 22.8769 11.1892 22.0502 9.93851C20.5876 7.71985 17.3369 3.99853 11.9996 3.99853C6.66228 3.99853 3.41162 7.71985 1.94895 9.93851C1.12229 11.1905 1.12229 12.8078 1.94895 14.0598ZM3.61828 11.0398C4.85961 9.15851 7.59827 5.99985 11.9996 5.99985C16.4009 5.99985 19.1396 9.15851 20.3809 11.0412C20.7596 11.6145 20.7596 12.3852 20.3809 12.9598C19.1396 14.8412 16.4009 17.9998 11.9996 17.9998C7.59827 17.9998 4.85961 14.8412 3.61828 12.9585C3.23962 12.3852 3.23962 11.6145 3.61828 11.0398ZM16.3329 11.9999C16.3329 14.3931 14.3928 16.3332 11.9996 16.3332C9.60638 16.3332 7.66628 14.3931 7.66628 11.9999C7.66628 9.60663 9.60638 7.66653 11.9996 7.66653C14.3928 7.66653 16.3329 9.60663 16.3329 11.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PrivateChatIcon.tsx
import { jsx as jsx133 } from "react/jsx-runtime";
var PrivateChatIcon = (props) => /* @__PURE__ */ jsx133(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx133(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9998 19.6666V21.6666C11.9998 22.0092 12.0491 22.3399 12.1251 22.6599C12.1076 22.6605 12.0901 22.6617 12.0725 22.663C12.0485 22.6648 12.0244 22.6666 11.9998 22.6666C11.3385 22.6666 10.6745 22.6052 10.0291 22.4826C9.04115 22.2946 8.08782 21.9692 7.18782 21.5119C5.98115 22.1306 4.12782 22.6732 2.60115 22.6732C2.49315 22.6732 2.38515 22.6706 2.27982 22.6652C1.88648 22.6439 1.54115 22.3919 1.39982 22.0239C1.25848 21.6559 1.34782 21.2386 1.62648 20.9599C2.62248 19.9639 3.06648 17.8892 2.74915 17.2946C1.82782 15.7012 1.33315 13.8612 1.33315 11.9999C1.33315 6.11855 6.11848 1.33322 11.9998 1.33322C17.8811 1.33322 22.6665 6.11855 22.6665 11.9999C22.6665 12.3092 22.6451 12.6119 22.6198 12.9146C21.7078 11.9492 20.4291 11.3332 18.9998 11.3332C16.3465 11.3332 14.1691 13.4106 14.0091 16.0239C12.7958 16.8012 11.9998 18.1612 11.9998 19.6666ZM21.9998 16.3332V17.3666C23.1278 17.5306 23.9998 18.4946 23.9998 19.6666V21.6666C23.9998 22.9532 22.9531 23.9999 21.6665 23.9999H16.3331C15.0465 23.9999 13.9998 22.9532 13.9998 21.6666V19.6666C13.9998 18.4932 14.8718 17.5306 15.9998 17.3666V16.3332C15.9998 14.6786 17.3451 13.3332 18.9998 13.3332C20.6545 13.3332 21.9998 14.6786 21.9998 16.3332ZM19.9998 16.3332C19.9998 15.7812 19.5518 15.3332 18.9998 15.3332C18.4478 15.3332 17.9998 15.7812 17.9998 16.3332V17.3332H19.9998V16.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/PrivateUserSessionsIcon.tsx
import { jsx as jsx134 } from "react/jsx-runtime";
var PrivateUserSessionsIcon = (props) => /* @__PURE__ */ jsx134(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx134(
      "path",
      {
        clipRule: "evenodd",
        d: "M3.6529 5.39053C5.64756 2.85587 8.67022 1.33321 11.9995 1.33321C17.8809 1.33321 22.6662 6.1172 22.6662 11.9985C22.6662 17.8798 17.8809 22.6652 11.9995 22.6652C6.11822 22.6652 1.3329 17.8798 1.3329 11.9985C1.3329 11.4465 1.7809 10.9985 2.3329 10.9985C2.8849 10.9985 3.3329 11.4465 3.3329 11.9985C3.3329 14.6668 4.54515 17.0575 6.44781 18.6486C7.53031 16.6549 9.65117 15.3332 11.9995 15.3332C14.3438 15.3332 16.4602 16.6498 17.5485 18.6509C19.4527 17.0598 20.6662 14.6682 20.6662 11.9985C20.6662 7.21986 16.7782 3.33187 11.9995 3.33187C9.10221 3.33187 6.47689 4.73853 4.87289 7.07053L6.83955 6.79853C7.38222 6.73053 7.89155 7.10653 7.96755 7.65319C8.04222 8.19986 7.66089 8.70519 7.11289 8.78119L3.18756 9.32385C3.1409 9.33052 3.09423 9.33319 3.0489 9.33319C2.5569 9.33319 2.1289 8.97052 2.05957 8.47052L1.51557 4.54387C1.43957 3.99587 1.82223 3.49187 2.3689 3.41587C2.91556 3.33987 3.4209 3.72254 3.4969 4.2692L3.6529 5.39053ZM11.9995 13.9998C14.0246 13.9998 15.6662 12.3582 15.6662 10.3332C15.6662 8.30815 14.0246 6.66653 11.9995 6.66653C9.9745 6.66653 8.33289 8.30815 8.33289 10.3332C8.33289 12.3582 9.9745 13.9998 11.9995 13.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ProjectIcon.tsx
import { jsx as jsx135, jsxs as jsxs4 } from "react/jsx-runtime";
var ProjectIcon = (props) => /* @__PURE__ */ jsxs4(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx135(
        "path",
        {
          clipRule: "evenodd",
          d: "M3.60001 7.19985V5.39986C3.60001 4.40626 4.4064 3.59986 5.4 3.59986H8.95799C9.35879 3.59986 9.73439 3.80026 9.95639 4.13386L12 7.19985H18.6C19.5936 7.19985 20.4 8.00625 20.4 8.99984V16.7998C20.4 18.787 18.7872 20.3998 16.8 20.3998H7.2C5.2128 20.3998 3.60001 18.787 3.60001 16.7998V7.19986V7.19985Z",
          fill: "currentColor",
          fillRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx135(
        "path",
        {
          d: "M9.95639 4.13386L8.95738 4.79867L8.95787 4.79941L9.95639 4.13386ZM12 7.19985L11.0015 7.8654C11.224 8.19929 11.5987 8.39985 12 8.39985V7.19985ZM4.8 7.19985V5.39986H2.40001V7.19985H4.8ZM4.8 5.39986C4.8 5.069 5.06914 4.79986 5.4 4.79986V2.39987C3.74366 2.39987 2.40001 3.74352 2.40001 5.39986H4.8ZM5.4 4.79986H8.95799V2.39987H5.4V4.79986ZM8.95799 4.79986C8.95808 4.79986 8.95805 4.79986 8.95807 4.79987C8.95816 4.79989 8.95836 4.79996 8.95858 4.80007C8.95903 4.80031 8.95826 4.8 8.95738 4.79867L10.9554 3.46906C10.51 2.79971 9.75864 2.39987 8.95799 2.39987V4.79986ZM8.95787 4.79941L11.0015 7.8654L12.9985 6.5343L10.9549 3.46832L8.95787 4.79941ZM12 8.39985H18.6V5.99985H12V8.39985ZM18.6 8.39985C18.9308 8.39985 19.2 8.66899 19.2 8.99984H21.6C21.6 7.34351 20.2563 5.99985 18.6 5.99985V8.39985ZM19.2 8.99984V16.7998H21.6V8.99984H19.2ZM19.2 16.7998C19.2 18.1243 18.1244 19.1998 16.8 19.1998V21.5998C19.4499 21.5998 21.6 19.4498 21.6 16.7998H19.2ZM16.8 19.1998H7.2V21.5998H16.8V19.1998ZM7.2 19.1998C5.87554 19.1998 4.8 18.1243 4.8 16.7998H2.40001C2.40001 19.4498 4.55006 21.5998 7.2 21.5998V19.1998ZM4.8 16.7998V7.19986H2.40001V16.7998H4.8ZM4.8 7.19986V7.19985H2.40001V7.19986H4.8Z",
          fill: "currentColor"
        }
      )
    ]
  }
);

// src/components/Icon/icons/PropertiesIcon.tsx
import { jsx as jsx136 } from "react/jsx-runtime";
var PropertiesIcon = (props) => /* @__PURE__ */ jsx136(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx136(
      "path",
      {
        clipRule: "evenodd",
        d: "M4.99992 16.6665H10.7466C11.2986 16.6665 11.7466 17.1145 11.7466 17.6665C11.7466 18.2185 11.2986 18.6665 10.7466 18.6665H4.99992C2.97726 18.6665 1.33327 17.0225 1.33327 14.9998V6.3332C1.33327 4.31053 2.97726 2.66654 4.99992 2.66654H18.9999C21.0226 2.66654 22.6665 4.31053 22.6665 6.3332V11.9625C22.6665 12.5145 22.2185 12.9625 21.6665 12.9625C21.1146 12.9625 20.6666 12.5145 20.6666 11.9625V6.3332C20.6666 5.41453 19.9186 4.66653 18.9999 4.66653H4.99992C4.08126 4.66653 3.33326 5.41453 3.33326 6.3332V14.9998C3.33326 15.9185 4.08126 16.6665 4.99992 16.6665ZM21.4959 17.3332H22.6639C23.2159 17.3332 23.6639 17.7812 23.6639 18.3332C23.6639 18.8852 23.2159 19.3332 22.6639 19.3332H21.4932C21.4346 19.5172 21.3626 19.6932 21.2746 19.8625L22.1025 20.6905C22.4932 21.0812 22.4932 21.7145 22.1025 22.1052C21.9079 22.3012 21.6519 22.3985 21.3959 22.3985C21.1399 22.3985 20.8839 22.2998 20.6892 22.1052L19.8612 21.2772C19.6932 21.3652 19.5159 21.4372 19.3319 21.4958V22.6665C19.3319 23.2185 18.8839 23.6665 18.3319 23.6665C17.7799 23.6665 17.3319 23.2185 17.3319 22.6665V21.4958C17.1479 21.4372 16.9719 21.3652 16.8026 21.2772L15.9746 22.1052C15.7799 22.3012 15.5239 22.3985 15.2679 22.3985C15.0119 22.3985 14.7559 22.2998 14.5612 22.1052C14.1706 21.7145 14.1706 21.0812 14.5612 20.6905L15.3892 19.8625C15.3012 19.6945 15.2292 19.5172 15.1706 19.3332H13.9999C13.4479 19.3332 12.9999 18.8852 12.9999 18.3332C12.9999 17.7812 13.4479 17.3332 13.9999 17.3332H15.1706C15.2292 17.1492 15.3012 16.9732 15.3892 16.8038L14.5612 15.9758C14.1706 15.5852 14.1706 14.9518 14.5612 14.5612C14.9519 14.1705 15.5852 14.1705 15.9759 14.5612L16.8039 15.3892C16.9719 15.3012 17.1492 15.2292 17.3332 15.1705V13.9998C17.3332 13.4478 17.7812 12.9998 18.3332 12.9998C18.8852 12.9998 19.3332 13.4478 19.3332 13.9998V15.1705C19.5172 15.2292 19.6932 15.3012 19.8626 15.3892L20.6906 14.5612C21.0812 14.1705 21.7146 14.1705 22.1052 14.5612C22.4959 14.9518 22.4959 15.5852 22.1052 15.9758L21.2772 16.8038C21.3652 16.9718 21.4372 17.1492 21.4959 17.3332ZM18.3332 19.6665C19.0679 19.6665 19.6666 19.0678 19.6666 18.3332C19.6666 17.5985 19.0679 16.9998 18.3332 16.9998C17.5986 16.9998 16.9999 17.5985 16.9999 18.3332C16.9999 19.0678 17.5986 19.6665 18.3332 19.6665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/QuestionMarkIcon.tsx
import { jsx as jsx137 } from "react/jsx-runtime";
var QuestionMarkIcon = (props) => /* @__PURE__ */ jsx137(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx137(
      "path",
      {
        d: "M11.9998 1.33321C6.11853 1.33321 1.33321 6.11853 1.33321 11.9998C1.33321 17.8812 6.11853 22.6665 11.9998 22.6665C17.8812 22.6665 22.6665 17.8812 22.6665 11.9998C22.6665 6.11853 17.8812 1.33321 11.9998 1.33321ZM11.7212 18.0892C10.9852 18.0892 10.3879 17.4905 10.3879 16.7558C10.3879 16.0212 10.9852 15.4225 11.7212 15.4225C12.4572 15.4225 13.0545 16.0212 13.0545 16.7558C13.0545 17.4905 12.4572 18.0892 11.7212 18.0892ZM13.7598 12.2865C13.1505 12.7118 12.8532 12.9412 12.7532 13.5065C12.6692 13.9918 12.2465 14.3332 11.7705 14.3332C11.7132 14.3332 11.6545 14.3292 11.5958 14.3185C11.0518 14.2225 10.6878 13.7052 10.7852 13.1598C11.0332 11.7505 11.9478 11.1119 12.6172 10.6452C13.3198 10.1545 13.5638 9.94919 13.5638 9.24652C13.5638 8.13052 12.6332 7.83586 12.1412 7.83586C11.5892 7.83586 10.5985 8.00919 10.1719 9.16919C9.98118 9.68785 9.41052 9.95319 8.88785 9.76385C8.36919 9.57319 8.10386 8.99852 8.29452 8.48119C8.90252 6.82519 10.3399 5.83586 12.1412 5.83586C13.7998 5.83586 15.5638 7.03053 15.5638 9.24652C15.5638 11.0292 14.5225 11.7558 13.7598 12.2865Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ReactIcon.tsx
import { jsx as jsx138 } from "react/jsx-runtime";
var ReactIcon = (props) => /* @__PURE__ */ jsx138(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx138(
      "path",
      {
        d: "M11.9998 1.33321C6.11847 1.33321 1.33315 6.11853 1.33315 11.9998C1.33315 17.8812 6.11847 22.6665 11.9998 22.6665C17.8811 22.6665 22.6664 17.8812 22.6664 11.9998C22.6664 6.11853 17.8811 1.33321 11.9998 1.33321ZM6.66647 10.6665C6.66647 9.93052 7.2638 9.33319 7.9998 9.33319C8.73579 9.33319 9.33313 9.93052 9.33313 10.6665C9.33313 11.4025 8.73579 11.9998 7.9998 11.9998C7.2638 11.9998 6.66647 11.4025 6.66647 10.6665ZM11.9998 18.6665C9.95846 18.6665 8.18779 17.1465 7.88113 15.1318C7.8358 14.8372 7.93313 14.5398 8.13846 14.3345C8.33979 14.1345 8.61579 14.0452 8.90113 14.0878C10.9358 14.4078 13.0424 14.4078 15.0851 14.0892C15.3744 14.0465 15.6558 14.1372 15.8624 14.3412C16.0664 14.5452 16.1624 14.8385 16.1184 15.1265C15.8131 17.1438 14.0424 18.6652 11.9998 18.6652V18.6665ZM15.9998 11.9998C15.2638 11.9998 14.6664 11.4025 14.6664 10.6665C14.6664 9.93052 15.2638 9.33319 15.9998 9.33319C16.7358 9.33319 17.3331 9.93052 17.3331 10.6665C17.3331 11.4025 16.7358 11.9998 15.9998 11.9998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/RecordIcon.tsx
import { jsx as jsx139 } from "react/jsx-runtime";
var RecordIcon = (props) => /* @__PURE__ */ jsx139(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx139(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.33308 11.9998C1.33308 6.11853 6.11841 1.33321 11.9997 1.33321C17.881 1.33321 22.6664 6.11853 22.6664 11.9998C22.6664 17.8812 17.881 22.6665 11.9997 22.6665C6.11841 22.6665 1.33308 17.8812 1.33308 11.9998ZM3.33308 11.9998C3.33308 16.7785 7.22107 20.6665 11.9997 20.6665C16.7784 20.6665 20.6664 16.7785 20.6664 11.9998C20.6664 7.22119 16.7784 3.3332 11.9997 3.3332C7.22107 3.3332 3.33308 7.22119 3.33308 11.9998ZM18.6664 11.9998C18.6664 15.6817 15.6816 18.6665 11.9997 18.6665C8.31783 18.6665 5.33307 15.6817 5.33307 11.9998C5.33307 8.31796 8.31783 5.3332 11.9997 5.3332C15.6816 5.3332 18.6664 8.31796 18.6664 11.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/RecordGroupIcon.tsx
import { jsx as jsx140, jsxs as jsxs5 } from "react/jsx-runtime";
var RecordGroupIcon = (props) => /* @__PURE__ */ jsxs5(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx140(
        "path",
        {
          d: "M18.6361 12.1678C15.6787 12.1678 13.2724 14.6564 13.2724 17.715C13.2724 20.7735 15.6787 23.2621 18.6361 23.2621C21.5935 23.2621 23.9998 20.7735 23.9998 17.715C23.9998 14.6564 21.5935 12.1678 18.6361 12.1678ZM18.6361 22.2221C16.2331 22.2221 14.2781 20.2001 14.2781 17.715C14.2781 15.2298 16.2331 13.2079 18.6361 13.2079C21.039 13.2079 22.9941 15.2298 22.9941 17.715C22.9941 20.2001 21.039 22.2221 18.6361 22.2221Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx140(
        "path",
        {
          d: "M18.6361 20.8319C20.3006 20.8319 21.6499 19.4364 21.6499 17.715C21.6499 15.9936 20.3006 14.5981 18.6361 14.5981C16.9716 14.5981 15.6223 15.9936 15.6223 17.715C15.6223 19.4364 16.9716 20.8319 18.6361 20.8319Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx140(
        "path",
        {
          d: "M15.2836 1.74994C14.6131 1.74994 13.9896 1.93716 13.4331 2.23809C14.1666 3.27125 14.6131 4.53323 14.6131 5.91032C14.6131 7.2874 14.1666 8.54938 13.4331 9.58254C13.9896 9.88347 14.6131 10.0707 15.2836 10.0707C17.5015 10.0707 19.3064 8.20407 19.3064 5.91032C19.3064 3.61656 17.5015 1.74994 15.2836 1.74994Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx140(
        "path",
        {
          d: "M8.57911 10.0707C10.8008 10.0707 12.6019 8.20803 12.6019 5.91032C12.6019 3.6126 10.8008 1.74994 8.57911 1.74994C6.35739 1.74994 4.55632 3.6126 4.55632 5.91032C4.55632 8.20803 6.35739 10.0707 8.57911 10.0707Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx140(
        "path",
        {
          clipRule: "evenodd",
          d: "M12.8788 12.9146C11.5371 11.9582 9.92379 11.4101 8.23389 11.4101C4.84162 11.4101 1.75801 13.6186 0.5603 16.9054C0.321054 17.5607 0.352067 18.2739 0.647432 18.9154C0.951659 19.5752 1.51138 20.0869 2.18481 20.319C4.14161 20.9941 6.17815 21.3378 8.23389 21.3378C9.47978 21.3378 10.7186 21.2115 11.9369 20.9618C11.4888 19.9765 11.2384 18.876 11.2384 17.715C11.2384 15.8964 11.8529 14.2263 12.8788 12.9146Z",
          fill: "currentColor",
          fillRule: "evenodd"
        }
      )
    ]
  }
);

// src/components/Icon/icons/RedoIcon.tsx
import { jsx as jsx141 } from "react/jsx-runtime";
var RedoIcon = (props) => /* @__PURE__ */ jsx141(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx141(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.9987 13.3332C16.2547 13.3332 16.5107 13.2345 16.7053 13.0398L16.7067 13.0385L21.04 8.70518C21.4306 8.31452 21.4306 7.68119 21.04 7.29052L16.7067 2.9572C16.316 2.56653 15.6827 2.56653 15.292 2.9572C14.9013 3.34786 14.9013 3.98119 15.292 4.37186L17.9187 6.99852H14.6667C10.9907 6.99852 8.00001 9.98918 8.00001 13.6652C8.00001 14.2172 8.44801 14.6652 9.00001 14.6652C9.55201 14.6652 10 14.2172 10 13.6652C10 11.0918 12.0933 8.99852 14.6667 8.99852H17.9187L15.292 11.6252C14.9013 12.0158 14.9013 12.6492 15.292 13.0398C15.4867 13.2358 15.7427 13.3332 15.9987 13.3332ZM4.66535 14.3332C4.66535 13.7812 4.21735 13.3332 3.66535 13.3332C3.11335 13.3332 2.66536 13.7812 2.66536 14.3332V17.6665C2.66536 19.6892 4.30935 21.3332 6.33201 21.3332H20.332C20.884 21.3332 21.332 20.8852 21.332 20.3332C21.332 19.7812 20.884 19.3332 20.332 19.3332H6.33201C5.41335 19.3332 4.66535 18.5852 4.66535 17.6665V14.3332Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/RefreshIcon.tsx
import { jsx as jsx142 } from "react/jsx-runtime";
var RefreshIcon = (props) => /* @__PURE__ */ jsx142(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx142(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.3739 0.292771C13.7644 0.683295 13.7644 1.31646 13.3739 1.70698L12.4057 2.6752C17.3725 2.88752 21.3334 6.98082 21.3334 11.9999C21.3334 14.848 20.0553 17.4009 18.0454 19.1109C17.6248 19.4687 16.9937 19.4179 16.6358 18.9972C16.2779 18.5766 16.3288 17.9454 16.7495 17.5876C18.3316 16.2415 19.3334 14.2371 19.3334 11.9999C19.3334 7.9495 16.0505 4.66654 12.0001 4.66654C11.917 4.66654 11.8342 4.66793 11.7518 4.67067L13.3739 6.29277C13.7644 6.6833 13.7644 7.31646 13.3739 7.70698C12.9834 8.09751 12.3502 8.09751 11.9597 7.70698L8.95969 4.70698C8.56916 4.31646 8.56916 3.6833 8.95969 3.29277L11.9597 0.292771C12.3502 -0.0977532 12.9834 -0.0977532 13.3739 0.292771ZM7.25079 6.41219C7.67143 6.05431 7.72232 5.4232 7.36444 5.00255C7.00656 4.58191 6.37545 4.53102 5.9548 4.8889C3.94498 6.59882 2.66679 9.15176 2.66679 11.9999C2.66679 17.0189 6.62779 21.1122 11.5946 21.3246L10.6263 22.2928C10.2358 22.6833 10.2358 23.3165 10.6263 23.707C11.0169 24.0975 11.65 24.0975 12.0406 23.707L15.0406 20.707C15.4311 20.3165 15.4311 19.6833 15.0406 19.2928L12.0406 16.2928C11.65 15.9022 11.0169 15.9022 10.6263 16.2928C10.2358 16.6833 10.2358 17.3165 10.6263 17.707L12.2484 19.3291C12.166 19.3318 12.0832 19.3332 12.0001 19.3332C7.94975 19.3332 4.66679 16.0503 4.66679 11.9999C4.66679 9.76267 5.66861 7.75827 7.25079 6.41219Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ReplaceIcon.tsx
import { jsx as jsx143 } from "react/jsx-runtime";
var ReplaceIcon = (props) => /* @__PURE__ */ jsx143(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx143(
      "path",
      {
        clipRule: "evenodd",
        d: "M22.9836 5.24121C22.5662 4.88121 21.9342 4.93054 21.5729 5.34921L21.3289 5.63454C21.3102 3.26121 19.3769 1.33455 16.9996 1.33455H15.6663C15.1143 1.33455 14.6663 1.78255 14.6663 2.33455C14.6663 2.88655 15.1143 3.33455 15.6663 3.33455H16.9996C18.2716 3.33455 19.3063 4.35988 19.3289 5.62654L19.0929 5.35054C18.7329 4.93188 18.1023 4.88521 17.6823 5.24254C17.2636 5.60254 17.2143 6.23321 17.5743 6.65321L19.5743 8.98653C19.7649 9.20787 20.0422 9.33587 20.3329 9.33587C20.6236 9.33587 20.9022 9.20787 21.0916 8.98653L23.0916 6.65321C23.4516 6.23454 23.4022 5.60121 22.9836 5.24121ZM8.66628 15.3332C8.66628 11.6572 11.6569 8.66652 15.3329 8.66652C19.0089 8.66652 21.9996 11.6572 21.9996 15.3332C21.9996 19.0092 19.0089 21.9998 15.3329 21.9998C11.6569 21.9998 8.66628 19.0092 8.66628 15.3332ZM6.99963 20.6665H8.33296L8.33029 20.6652C8.88229 20.6652 9.33029 21.1132 9.33029 21.6652C9.33029 22.2172 8.88229 22.6652 8.33029 22.6652H6.99696C4.61963 22.6652 2.6863 20.7385 2.66764 18.3652L2.42364 18.6505C2.0623 19.0705 1.43164 19.1198 1.01297 18.7585C0.594308 18.3985 0.544975 17.7665 0.904974 17.3478L2.90497 15.0145C3.28497 14.5719 4.04363 14.5719 4.42363 15.0145L6.42363 17.3478C6.78363 17.7678 6.73429 18.3985 6.31563 18.7585C6.12763 18.9198 5.89563 18.9998 5.66496 18.9998C5.38363 18.9998 5.10363 18.8812 4.9063 18.6505L4.6703 18.3745C4.69296 19.6412 5.72763 20.6665 6.99963 20.6665ZM6.46095 14.8878C6.33028 14.8878 6.19828 14.8625 6.07162 14.8092C3.59696 13.7625 1.99963 11.3519 1.99963 8.66652C1.99963 4.99053 4.99029 1.99987 8.66628 1.99987C11.3503 1.99987 13.7623 3.59854 14.8076 6.07053C15.0249 6.57853 14.7863 7.16519 14.2769 7.38119C13.7743 7.59586 13.1836 7.35853 12.9676 6.85053C12.2343 5.11853 10.5463 3.99987 8.66628 3.99987C6.09295 3.99987 3.99962 6.0932 3.99962 8.66652C3.99962 10.5465 5.11829 12.2345 6.85028 12.9665C7.35828 13.1812 7.59695 13.7678 7.38095 14.2772C7.21961 14.6585 6.85028 14.8878 6.46095 14.8878Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ReplyIcon.tsx
import { jsx as jsx144 } from "react/jsx-runtime";
var ReplyIcon = (props) => /* @__PURE__ */ jsx144(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx144(
      "path",
      {
        clipRule: "evenodd",
        d: "M16.3329 14.6665C14.6795 14.6665 13.3329 16.0118 13.3329 17.6665H13.3342C13.3342 18.0185 13.4062 18.3518 13.5182 18.6665H8.35022L3.6249 22.4465C3.44357 22.5918 3.22223 22.6665 2.99957 22.6665C2.8529 22.6665 2.7049 22.6345 2.56623 22.5678C2.21957 22.4012 1.99957 22.0505 1.99957 21.6665V6.3332C1.99957 4.31053 3.64356 2.66654 5.66623 2.66654H18.3329C20.3555 2.66654 21.9995 4.31053 21.9995 6.3332V12.4238L21.7875 12.2118C21.2209 11.6452 20.4675 11.3332 19.6662 11.3332C18.8649 11.3332 18.1115 11.6452 17.5449 12.2118C16.9782 12.7785 16.6662 13.5318 16.6662 14.3332C16.6662 14.4452 16.6729 14.5558 16.6849 14.6665H16.3329ZM18.9582 13.6265C19.3489 13.2358 19.9822 13.2358 20.3729 13.6265L20.3715 13.6278L23.7048 16.9612C24.0955 17.3518 24.0955 17.9852 23.7048 18.3758L20.3715 21.7092C20.1769 21.9052 19.9209 22.0025 19.6649 22.0025C19.4089 22.0025 19.1529 21.9038 18.9582 21.7092C18.5675 21.3185 18.5675 20.6852 18.9582 20.2945L20.5849 18.6678H16.3329C15.7809 18.6678 15.3329 18.2198 15.3329 17.6678C15.3329 17.1158 15.7809 16.6678 16.3329 16.6678H20.5849L18.9582 15.0412C18.5675 14.6505 18.5675 14.0172 18.9582 13.6265Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/RequestVideoIcon.tsx
import { jsx as jsx145 } from "react/jsx-runtime";
var RequestVideoIcon = (props) => /* @__PURE__ */ jsx145(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx145(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.4282 1C8.04152 1 6.10677 2.93477 6.10677 5.32143C6.10677 7.70811 8.04152 9.64286 10.4282 9.64286C12.8149 9.64286 14.7496 7.70811 14.7496 5.32143C14.7496 2.93477 12.8149 1 10.4282 1ZM11.977 11.3383C11.4727 11.2567 10.9553 11.2143 10.4282 11.2143C5.24728 11.2143 0.999634 15.3112 0.999634 20.4219V20.9997L1.55518 21.1349C4.78947 21.9221 8.02549 22.2786 11.2614 22.2048C10.0135 20.8706 9.24963 19.0781 9.24963 17.1072C9.24963 14.7832 10.3117 12.7072 11.977 11.3383ZM17.1068 23C20.3613 23 22.9996 20.3617 22.9996 17.1072C22.9996 13.8526 20.3613 11.2143 17.1068 11.2143C13.8523 11.2143 11.2139 13.8526 11.2139 17.1072C11.2139 20.3617 13.8523 23 17.1068 23ZM17.8925 16.6469L19.4639 18.2183L20.5751 17.1072L17.1068 13.6389L13.6385 17.1072L14.7496 18.2183L16.3211 16.6469V20.25H17.8925V16.6469Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/RevertToOriginalIcon.tsx
import { jsx as jsx146 } from "react/jsx-runtime";
var RevertToOriginalIcon = (props) => /* @__PURE__ */ jsx146(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx146(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.64905 5.62299C7.27777 4.00086 9.52105 2.99994 12 2.99994C16.9706 2.99994 21 7.02938 21 11.9999C21 16.9705 16.9706 20.9999 12 20.9999C7.02944 20.9999 3 16.9705 3 11.9999V10.9999H1V11.9999C1 18.075 5.92487 22.9999 12 22.9999C18.0751 22.9999 23 18.075 23 11.9999C23 5.92481 18.0751 0.999939 12 0.999939C8.96073 0.999939 6.20777 2.23385 4.21795 4.22564L4.20135 4.24225L4 4.46368V0.999939H2V7.99994H9V5.99994H5.30628L5.64905 5.62299ZM13 5.99994V11.5857L17.4142 15.9999L16 17.4141L11 12.4141V5.99994H13Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/RewindIcon.tsx
import { jsx as jsx147 } from "react/jsx-runtime";
var RewindIcon = (props) => /* @__PURE__ */ jsx147(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx147(
      "path",
      {
        d: "M23.1796 5.02255C22.6636 4.71988 22.0489 4.71188 21.5262 4.99988L13.3329 9.53988V6.45855C13.3329 5.86255 13.0262 5.32521 12.5129 5.02255C11.9982 4.71988 11.3809 4.71055 10.8596 5.00121L0.859573 10.5412C0.330239 10.8345 -0.000427246 11.3932 -0.000427246 11.9999C-0.000427246 12.6065 0.328906 13.1652 0.859573 13.4585L10.8596 18.9985C11.1129 19.1399 11.3902 19.2105 11.6662 19.2105C11.9582 19.2105 12.2489 19.1319 12.5129 18.9772C13.0262 18.6745 13.3329 18.1372 13.3329 17.5412V14.4599L21.5262 18.9985C21.7796 19.1399 22.0569 19.2105 22.3329 19.2105C22.6249 19.2105 22.9156 19.1319 23.1796 18.9772C23.6929 18.6745 23.9996 18.1372 23.9996 17.5412V6.45855C23.9996 5.86255 23.6929 5.32521 23.1796 5.02255Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/SaveIcon.tsx
import { jsx as jsx148 } from "react/jsx-runtime";
var SaveIcon = (props) => /* @__PURE__ */ jsx148(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx148(
      "path",
      {
        d: "M21.3155 6.5692L17.4301 2.68387C16.9888 2.24254 16.4035 1.99987 15.7808 1.99987H5.87949C3.74083 1.99987 1.9995 3.7412 1.9995 5.87986V18.1198C1.9995 20.2585 3.74083 21.9998 5.87949 21.9998H18.1195C20.2581 21.9998 21.9995 20.2585 21.9995 18.1198V8.21852C21.9995 7.59586 21.7568 7.00919 21.3155 6.5692ZM6.66616 3.99987H14.6661V6.66653C14.6661 7.39986 14.0661 7.99986 13.3328 7.99986H7.99949C7.26616 7.99986 6.66616 7.39986 6.66616 6.66653V3.99987ZM17.3328 19.9998H6.66616V14.6665C6.66616 13.9332 7.26616 13.3332 7.99949 13.3332H15.9995C16.7328 13.3332 17.3328 13.9332 17.3328 14.6665V19.9998Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/SaveAsCopyIcon.tsx
import { jsx as jsx149 } from "react/jsx-runtime";
var SaveAsCopyIcon = (props) => /* @__PURE__ */ jsx149(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx149(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.7461 16.1438C2.81944 16.6398 3.24743 16.9972 3.7341 16.9972V16.9958C3.78343 16.9958 3.83277 16.9932 3.8821 16.9852C4.42743 16.9052 4.8061 16.3958 4.72476 15.8492L3.35277 6.61719C3.2861 6.17719 3.39543 5.73719 3.66077 5.37986C3.92477 5.02253 4.3141 4.7892 4.7541 4.72386L13.9861 3.35053C14.7527 3.23453 15.4874 3.6572 15.7767 4.3732C15.9847 4.88653 16.5701 5.13453 17.0781 4.92653C17.5914 4.71986 17.8381 4.1372 17.6314 3.6252C16.9954 2.0492 15.3714 1.12387 13.6927 1.3732L4.46077 2.7452C3.49143 2.8892 2.63677 3.40253 2.0541 4.1892C1.47011 4.97586 1.22877 5.94253 1.37277 6.91186L2.7461 16.1438ZM9.6661 5.99986H18.9994C21.0244 5.99986 22.6661 7.64148 22.6661 9.66652V18.9998C22.6661 21.0249 21.0244 22.6665 18.9994 22.6665H9.6661C7.64106 22.6665 5.99944 21.0249 5.99944 18.9998V9.66652C5.99944 7.64148 7.64106 5.99986 9.6661 5.99986Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ScissorsIcon.tsx
import { jsx as jsx150 } from "react/jsx-runtime";
var ScissorsIcon = (props) => /* @__PURE__ */ jsx150(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx150(
      "path",
      {
        d: "M8.03042 13.6605C7.73535 14.0006 7.25074 14.0917 6.8028 14.0461C6.54217 14.0195 6.27019 14.0236 5.96972 14.0236C3.76414 14.0236 2.01959 16.1038 1.97773 18.0156C1.93586 19.9273 3.23259 22.2035 5.96972 22.0076C8.70686 21.8116 9.96172 20.2211 9.96172 18.0156C9.96172 17.6742 9.91811 17.3462 9.83672 17.0336C9.73128 16.6286 9.79891 16.1824 10.0948 15.8865L11.2506 14.7307C11.6411 14.3401 12.2743 14.3401 12.6648 14.7307L13.8206 15.8865C14.1165 16.1824 14.1841 16.6286 14.0787 17.0336C13.9973 17.3462 13.9537 17.6742 13.9537 18.0156C13.9537 20.2211 15.1616 22.1277 17.9457 22.0076C20.1138 22.0076 21.8769 20.2813 21.9362 18.1275C21.9382 18.0533 21.9472 17.9794 21.9583 17.906C22.2072 16.2536 19.8847 13.8188 17.9457 14.0236C17.606 14.0236 17.2796 14.0667 16.9684 14.1474C16.561 14.2529 16.1121 14.1834 15.8164 13.8839L15.5341 13.5979C15.1988 13.2583 15.1485 12.7314 15.4153 12.3357C16.7051 10.4232 20.3129 5.04458 20.9214 3.82741C21.5171 2.63608 20.8216 2.27428 20.5213 2.17749C20.4397 2.15115 20.3538 2.15005 20.2692 2.16438C19.9626 2.21628 19.1552 2.39484 18.4294 2.91739C17.5596 3.54369 11.1772 10.0335 8.03042 13.6605ZM7.96572 18.0156C7.96572 19.1134 7.62753 20.2005 5.93739 20.2918C4.24725 20.3831 4.00273 19.3194 3.97372 18.0156C3.94472 16.7117 4.78907 16.3065 5.90361 16.2727C7.01816 16.2389 7.96572 16.9178 7.96572 18.0156ZM19.5954 17.9155C19.5954 19.0133 18.9851 19.6891 17.8773 19.6891C16.7695 19.6891 16.2146 19.1796 16.2146 18.0818C16.2146 16.984 16.7141 16.3637 17.8219 16.3637C18.9297 16.3637 19.5954 16.8177 19.5954 17.9155ZM3.59453 4.26526C3.95765 4.87977 7.97772 9.95022 8.7319 10.3133C9.48608 10.6765 11.7442 7.95976 11.3532 7.17765C10.9621 6.39553 4.78187 2.23266 3.97372 2.04758C3.45763 1.92939 3.19544 2.20587 3.07334 2.42113C2.99439 2.56032 2.99979 2.7259 3.0443 2.87961C3.14116 3.21412 3.3405 3.83537 3.59453 4.26526Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ScreenshareOffIcon.tsx
import { jsx as jsx151 } from "react/jsx-runtime";
var ScreenshareOffIcon = (props) => /* @__PURE__ */ jsx151(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx151(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.9999 2.39981H16.7999C17.6895 2.39981 18.5234 2.64278 19.2378 3.06489L19.5514 2.75128C20.02 2.28265 20.7798 2.28265 21.2485 2.75128C21.7171 3.21991 21.7171 3.97971 21.2485 4.44834L20.1937 5.50314L4.44846 21.2483C3.97983 21.717 3.22003 21.717 2.7514 21.2483C2.28278 20.7797 2.28278 20.0199 2.7514 19.5513L5.73159 16.5711C3.79875 15.9508 2.39993 14.1388 2.39993 11.9998C2.39993 11.3371 2.93719 10.7998 3.59993 10.7998C4.26267 10.7998 4.79993 11.3371 4.79993 11.9998C4.79993 13.3255 5.87427 14.3998 7.19993 14.3998H7.90288L17.4216 4.88104C17.2234 4.82801 17.015 4.79981 16.7999 4.79981H11.9999C11.3372 4.79981 10.7999 4.26255 10.7999 3.59981C10.7999 2.93707 11.3372 2.39981 11.9999 2.39981ZM21.5999 9.53861C21.5999 8.87587 21.0627 8.33861 20.3999 8.33861C19.7372 8.33861 19.1999 8.87587 19.1999 9.53861V11.9998C19.1999 13.3255 18.1256 14.3998 16.7999 14.3998H14.3387C13.676 14.3998 13.1387 14.9371 13.1387 15.5998C13.1387 16.2626 13.676 16.7998 14.3387 16.7998H16.7999C19.4511 16.7998 21.5999 14.6509 21.5999 11.9998V9.53861ZM3.59993 2.39981C3.43722 2.39981 3.28208 2.43219 3.14059 2.49087C3.0001 2.54899 2.86841 2.63495 2.75395 2.74874L2.74886 2.75383C2.5332 2.97078 2.39993 3.26973 2.39993 3.59981V7.19981C2.39993 7.86255 2.93719 8.39981 3.59993 8.39981C4.26267 8.39981 4.79993 7.86255 4.79993 7.19981V6.49687L8.18141 9.87834C8.65003 10.347 9.40983 10.347 9.87846 9.87834C10.3471 9.40971 10.3471 8.64991 9.87846 8.18128L6.49699 4.79981H7.19993C7.86267 4.79981 8.39993 4.26255 8.39993 3.59981C8.39993 2.93707 7.86267 2.39981 7.19993 2.39981H3.59993ZM13.1999 18.0665C14.5934 18.2215 15.9746 18.6455 17.3366 19.3265C17.9294 19.6229 18.1696 20.3437 17.8732 20.9365C17.5769 21.5292 16.856 21.7695 16.2633 21.4731C14.4046 20.5438 12.596 20.2313 10.8109 20.484C10.1547 20.5768 9.54744 20.1202 9.45457 19.464C9.36171 18.8078 9.81838 18.2005 10.4746 18.1076C10.5831 18.0923 10.6915 18.0786 10.7999 18.0665V17.9386C10.7999 17.2759 11.3372 16.7386 11.9999 16.7386C12.6627 16.7386 13.1999 17.2759 13.1999 17.9386V18.0665Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ScreenshareOnIcon.tsx
import { jsx as jsx152 } from "react/jsx-runtime";
var ScreenshareOnIcon = (props) => /* @__PURE__ */ jsx152(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx152(
      "path",
      {
        clipRule: "evenodd",
        d: "M16.7999 4.79981H11.9999C11.3372 4.79981 10.7999 4.26255 10.7999 3.59981C10.7999 2.93707 11.3372 2.39981 11.9999 2.39981H16.7999C19.4511 2.39981 21.5999 4.54867 21.5999 7.19981V11.9998C21.5999 14.651 19.4511 16.7998 16.7999 16.7998H13.1999V18.0668C14.583 18.2207 15.9641 18.6405 17.3364 19.3264C17.9292 19.6227 18.1696 20.3435 17.8733 20.9363C17.577 21.5291 16.8563 21.7695 16.2634 21.4732C13.4008 20.0424 10.5991 20.0424 7.73642 21.4732C7.1436 21.7695 6.42283 21.5291 6.12653 20.9363C5.83024 20.3435 6.07061 19.6227 6.66343 19.3264C8.03573 18.6405 9.41684 18.2207 10.7999 18.0668V16.7998H7.19993C4.54879 16.7998 2.39993 14.651 2.39993 11.9998C2.39993 11.3371 2.93719 10.7998 3.59993 10.7998C4.26267 10.7998 4.79993 11.3371 4.79993 11.9998C4.79993 13.3255 5.87427 14.3998 7.19993 14.3998H11.9999H16.7999C18.1256 14.3998 19.1999 13.3255 19.1999 11.9998V7.19981C19.1999 5.87415 18.1256 4.79981 16.7999 4.79981ZM3.14059 2.49087C3.28208 2.43219 3.43722 2.39981 3.59993 2.39981H7.19993C7.86267 2.39981 8.39993 2.93707 8.39993 3.59981C8.39993 4.26255 7.86267 4.79981 7.19993 4.79981H6.49699L10.4485 8.75128C10.9171 9.21991 10.9171 9.97971 10.4485 10.4483C9.97983 10.917 9.22003 10.917 8.75141 10.4483L4.79993 6.49687V7.19981C4.79993 7.86255 4.26267 8.39981 3.59993 8.39981C2.93719 8.39981 2.39993 7.86255 2.39993 7.19981V3.59981C2.39993 3.26973 2.5332 2.97078 2.74886 2.75383C2.75056 2.75213 2.75225 2.75043 2.75395 2.74874C2.86841 2.63495 3.0001 2.54899 3.14059 2.49087Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SearchIcon.tsx
import { jsx as jsx153 } from "react/jsx-runtime";
var SearchIcon = (props) => /* @__PURE__ */ jsx153(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx153(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.6666 10.3331C2.6666 14.5598 6.10659 17.9998 10.3332 17.9998C12.0872 17.9998 13.7056 17.4074 14.9991 16.4123L19.6266 21.0398C19.8212 21.2358 20.0772 21.3331 20.3332 21.3331H20.3346C20.5906 21.3331 20.8465 21.2344 21.0412 21.0398C21.4319 20.6491 21.4319 20.0158 21.0412 19.6251L16.4135 14.9974C17.408 13.7043 17.9999 12.0864 17.9999 10.3331C17.9999 6.10647 14.5599 2.66648 10.3332 2.66648C6.10659 2.66648 2.6666 6.10647 2.6666 10.3331ZM4.66659 10.3331C4.66659 7.20913 7.20925 4.66647 10.3332 4.66647C13.4572 4.66647 15.9999 7.20913 15.9999 10.3331C15.9999 13.4571 13.4572 15.9998 10.3332 15.9998C7.20925 15.9998 4.66659 13.4571 4.66659 10.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SendIcon.tsx
import { jsx as jsx154 } from "react/jsx-runtime";
var SendIcon = (props) => /* @__PURE__ */ jsx154(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M21.0892 10.5011L5.8012 2.56248C5.1972 2.25048 4.47187 2.32915 3.94787 2.75981C3.4252 3.19181 3.21187 3.89181 3.40387 4.54114L5.3172 10.9998H11.8478C12.3998 10.9998 12.8478 11.4478 12.8478 11.9998C12.8478 12.5518 12.3998 12.9998 11.8478 12.9998H5.3172L3.40387 19.4584C3.21054 20.1091 3.4252 20.8078 3.94787 21.2398C4.2572 21.4944 4.63853 21.6264 5.02253 21.6264C5.28787 21.6264 5.55453 21.5638 5.8012 21.4358L21.0892 13.4971C21.6505 13.2051 21.9998 12.6318 21.9998 11.9985C21.9998 11.3651 21.6505 10.7931 21.0892 10.5011Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/ShapesIcon.tsx
import { jsx as jsx155 } from "react/jsx-runtime";
var ShapesIcon = (props) => /* @__PURE__ */ jsx155(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx155(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.6118 8.16116C10.9118 8.67449 10.9131 9.31449 10.6171 9.83049C10.3198 10.3465 9.76648 10.6665 9.17182 10.6665H2.16115C1.56648 10.6665 1.01315 10.3465 0.715816 9.83049C0.419816 9.31449 0.42115 8.67449 0.72115 8.16116L4.22782 2.14782C4.83315 1.11449 6.50115 1.11449 7.10648 2.14782L10.6118 8.16116ZM17.9998 13.3331C20.9453 13.3331 23.3332 10.9453 23.3332 7.99982C23.3332 5.0543 20.9453 2.66648 17.9998 2.66648C15.0543 2.66648 12.6665 5.0543 12.6665 7.99982C12.6665 10.9453 15.0543 13.3331 17.9998 13.3331ZM12.3332 13.3331H7.66649C6.37782 13.3331 5.33315 14.3778 5.33315 15.6665V20.3331C5.33315 21.6218 6.37782 22.6665 7.66649 22.6665H12.3332C13.6218 22.6665 14.6665 21.6218 14.6665 20.3331V15.6665C14.6665 14.3778 13.6218 13.3331 12.3332 13.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ShareIcon.tsx
import { jsx as jsx156 } from "react/jsx-runtime";
var ShareIcon = (props) => /* @__PURE__ */ jsx156(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx156(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.2704 1.41448C15.6384 1.25714 16.0624 1.33314 16.3531 1.60647L22.3531 7.27446C22.5531 7.46246 22.6664 7.72646 22.6664 8.00113C22.6664 8.27579 22.5531 8.53846 22.3531 8.72779L16.3531 14.3944C16.0624 14.6691 15.6384 14.7451 15.2704 14.5864C14.9038 14.4278 14.6664 14.0664 14.6664 13.6678V11.0785C10.9838 11.6504 8.5198 15.1918 8.49313 15.2304C8.30246 15.5078 7.99046 15.6664 7.66646 15.6664C7.5678 15.6664 7.46913 15.6504 7.3718 15.6211C6.95313 15.4918 6.66647 15.1051 6.66647 14.6664C6.66647 11.4865 8.36913 5.60646 14.6664 5.0438V2.33314C14.6664 1.93447 14.9038 1.57314 15.2704 1.41448ZM19.3331 14.3331C19.3331 13.7811 19.7811 13.3331 20.3331 13.3331C20.8851 13.3331 21.3331 13.7811 21.3331 14.3331V17.6664C21.3331 19.6891 19.6891 21.3331 17.6664 21.3331H6.33313C4.31047 21.3331 2.66648 19.6891 2.66648 17.6664V6.33314C2.66648 4.31047 4.31047 2.66648 6.33313 2.66648H9.66646C10.2185 2.66648 10.6665 3.11448 10.6665 3.66648C10.6665 4.21847 10.2185 4.66647 9.66646 4.66647H6.33313C5.41447 4.66647 4.66647 5.41447 4.66647 6.33314V17.6664C4.66647 18.5851 5.41447 19.3331 6.33313 19.3331H17.6664C18.5851 19.3331 19.3331 18.5851 19.3331 17.6664V14.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SharingPermissionsIcon.tsx
import { jsx as jsx157 } from "react/jsx-runtime";
var SharingPermissionsIcon = (props) => /* @__PURE__ */ jsx157(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx157(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.6664 4.9998C19.6664 7.02484 18.0248 8.66646 15.9997 8.66646C13.9747 8.66646 12.3331 7.02484 12.3331 4.9998C12.3331 2.97476 13.9747 1.33315 15.9997 1.33315C18.0248 1.33315 19.6664 2.97476 19.6664 4.9998ZM11.3331 8.33313C11.3331 10.3582 9.69144 11.9998 7.6664 11.9998C5.64136 11.9998 3.99974 10.3582 3.99974 8.33313C3.99974 6.30809 5.64136 4.66647 7.6664 4.66647C9.69144 4.66647 11.3331 6.30809 11.3331 8.33313ZM22.9277 14.7971C21.8464 11.9278 19.0624 9.99979 15.9997 9.99979C14.1104 9.99979 12.3304 10.7371 10.9877 11.9731C13.4597 12.9184 15.501 14.8651 16.4664 17.4251C16.6157 17.8198 16.6824 18.2291 16.709 18.6384C18.3237 18.5718 19.921 18.2918 21.4624 17.7771C22.0704 17.5744 22.5744 17.1278 22.8504 16.5518C23.117 15.9918 23.145 15.3678 22.929 14.7971H22.9277ZM7.6664 13.3331C10.7291 13.3331 13.5131 15.2611 14.5944 18.1304C14.8104 18.7011 14.7824 19.3251 14.5157 19.8851C14.2397 20.4611 13.7357 20.9078 13.1277 21.1104C11.3611 21.6998 9.5224 21.9998 7.6664 21.9998C5.81041 21.9998 3.97174 21.6998 2.20508 21.1104C1.59708 20.9078 1.09175 20.4611 0.817085 19.8851C0.550419 19.3251 0.522419 18.7024 0.738419 18.1304C1.81975 15.2611 4.60374 13.3331 7.6664 13.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SimulcastIcon.tsx
import { jsx as jsx158 } from "react/jsx-runtime";
var SimulcastIcon = (props) => /* @__PURE__ */ jsx158(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx158(
      "path",
      {
        clipRule: "evenodd",
        d: "M19.5423 1.79047C19.1516 1.39981 18.5183 1.39981 18.1277 1.79047C17.737 2.18114 17.737 2.81447 18.1277 3.20514C19.765 4.84247 20.6663 7.01846 20.6663 9.33312C20.6663 11.6478 19.765 13.8251 18.1277 15.4611C17.737 15.8518 17.737 16.4851 18.1277 16.8758C18.3223 17.0704 18.5783 17.1691 18.8343 17.1691C19.0903 17.1691 19.3463 17.0718 19.541 16.8758C21.5556 14.8611 22.665 12.1824 22.665 9.33312C22.665 6.4838 21.557 3.80514 19.5423 1.79047ZM3.33302 9.33312C3.33302 7.01846 4.23435 4.84113 5.87168 3.20514C6.26234 2.81447 6.26234 2.18114 5.87168 1.79048C5.48101 1.39981 4.84768 1.39981 4.45702 1.79048C2.44235 3.80514 1.33302 6.4838 1.33302 9.33312C1.33302 12.1824 2.44235 14.8611 4.45702 16.8758C4.65168 17.0704 4.90768 17.1691 5.16368 17.1691C5.41968 17.1691 5.67568 17.0718 5.87034 16.8758C6.26101 16.4851 6.26101 15.8518 5.87034 15.4611C4.23302 13.8238 3.33302 11.6478 3.33302 9.33312ZM7.28502 14.0478C7.47968 14.2424 7.73568 14.3411 7.99168 14.3411C8.24768 14.3411 8.50368 14.2438 8.69835 14.0478C9.08901 13.6571 9.08901 13.0238 8.69835 12.6331C6.87835 10.8131 6.87835 7.85313 8.69835 6.03313C9.08901 5.64247 9.08901 5.00914 8.69835 4.61847C8.30768 4.2278 7.67435 4.2278 7.28368 4.61847C4.68369 7.21846 4.68369 11.4478 7.28368 14.0464L7.28502 14.0478ZM16.0063 14.3411C15.7503 14.3411 15.4943 14.2424 15.2996 14.0478L15.2983 14.0491C14.9076 13.6584 14.9076 13.0251 15.2983 12.6344C17.1183 10.8144 17.1183 7.85445 15.2983 6.03446C14.9076 5.64379 14.9076 5.01046 15.2983 4.61979C15.689 4.22913 16.3223 4.22913 16.713 4.61979C19.313 7.21845 19.313 11.4478 16.713 14.0478C16.5183 14.2438 16.2623 14.3411 16.0063 14.3411ZM12.9997 19.3331H15.9997C16.5517 19.3331 16.9997 19.7811 16.9997 20.3331C16.9997 20.8851 16.5517 21.3331 15.9997 21.3331H7.99967C7.44768 21.3331 6.99968 20.8851 6.99968 20.3331C6.99968 19.7811 7.44768 19.3331 7.99967 19.3331H10.9997V11.8011C10.0237 11.4051 9.333 10.4491 9.333 9.33313C9.333 7.86246 10.529 6.66647 11.9997 6.66647C13.4703 6.66647 14.6663 7.86246 14.6663 9.33313C14.6663 10.4491 13.9757 11.4038 12.9997 11.8011V19.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SortIcon.tsx
import { jsx as jsx159 } from "react/jsx-runtime";
var SortIcon = (props) => /* @__PURE__ */ jsx159(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx159(
      "path",
      {
        clipRule: "evenodd",
        d: "M1.6663 5.9998C1.6663 5.07933 2.41249 4.33314 3.33296 4.33314H20.6662C21.5867 4.33314 22.3329 5.07933 22.3329 5.9998C22.3329 6.92027 21.5867 7.66646 20.6662 7.66646H3.33296C2.41249 7.66646 1.6663 6.92027 1.6663 5.9998ZM1.6663 11.9998C1.6663 11.0793 2.41249 10.3331 3.33297 10.3331H15.3329C16.2534 10.3331 16.9996 11.0793 16.9996 11.9998C16.9996 12.9203 16.2534 13.6664 15.3329 13.6664H3.33297C2.41249 13.6664 1.6663 12.9203 1.6663 11.9998ZM3.33297 16.3331C2.41249 16.3331 1.6663 17.0793 1.6663 17.9998C1.6663 18.9202 2.41249 19.6664 3.33297 19.6664H9.99962C10.9201 19.6664 11.6663 18.9202 11.6663 17.9998C11.6663 17.0793 10.9201 16.3331 9.99962 16.3331H3.33297Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SparkleIcon.tsx
import { jsx as jsx160 } from "react/jsx-runtime";
var SparkleIcon = (props) => /* @__PURE__ */ jsx160(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx160(
      "path",
      {
        clipRule: "evenodd",
        d: "M17.66 6.34114L22.0333 8.07047C22.4159 8.22113 22.6653 8.59047 22.6653 8.9998C22.6653 9.40913 22.4133 9.77846 22.0319 9.92913L17.6586 11.6585L15.928 16.0331C15.7773 16.4144 15.408 16.6651 14.9986 16.6651C14.5893 16.6651 14.22 16.4144 14.0693 16.0331L12.3386 11.6585L7.96532 9.92913C7.58265 9.77846 7.33199 9.40913 7.33199 8.9998C7.33199 8.59047 7.58399 8.22113 7.96532 8.07047L12.3386 6.34114L14.0693 1.96648C14.372 1.20382 15.6266 1.20382 15.9293 1.96648L17.66 6.34114ZM8.23598 15.7625L10.7 16.7371C11.0826 16.8878 11.332 17.2571 11.332 17.6665C11.332 18.0758 11.08 18.4451 10.6986 18.5958L8.23465 19.5705L7.26132 22.0344C7.11065 22.4158 6.74132 22.6664 6.33199 22.6664C5.92266 22.6664 5.55332 22.4158 5.40266 22.0344L4.42933 19.5705L1.96533 18.5958C1.58267 18.4451 1.332 18.0758 1.332 17.6665C1.332 17.2571 1.584 16.8878 1.96533 16.7371L4.42933 15.7625L5.40266 13.2985C5.70532 12.5358 6.95999 12.5358 7.26265 13.2985L8.23598 15.7625Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SpeedIcon.tsx
import { jsx as jsx161 } from "react/jsx-runtime";
var SpeedIcon = (props) => /* @__PURE__ */ jsx161(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx161(
      "path",
      {
        clipRule: "evenodd",
        d: "M14.9998 0.66655H8.99982C8.44782 0.66655 7.99982 1.11455 7.99982 1.66655C7.99982 2.21855 8.44782 2.66655 8.99982 2.66655H10.9998V4.0532C7.46699 4.43212 4.52402 6.79523 3.29315 9.99988H4.99982C6.65315 9.99988 7.99982 11.3452 7.99982 12.9999C7.99982 13.3572 7.92648 13.6959 7.81048 14.0145C9.39582 14.0919 10.6665 15.3945 10.6665 16.9999C10.6665 18.6545 9.31982 19.9999 7.66648 19.9999H5.48115C7.16515 21.6465 9.46515 22.6665 11.9998 22.6665C17.1452 22.6665 21.3332 18.4799 21.3332 13.3332C21.3332 8.5244 17.6769 4.55368 12.9998 4.05309V2.66655H14.9998C15.5518 2.66655 15.9998 2.21855 15.9998 1.66655C15.9998 1.11455 15.5518 0.66655 14.9998 0.66655ZM15.7065 11.0399L12.7065 14.0399C12.5118 14.2345 12.2558 14.3332 11.9998 14.3332C11.7438 14.3332 11.4878 14.2359 11.2932 14.0399C10.9025 13.6492 10.9025 13.0159 11.2932 12.6252L14.2932 9.62522C14.6838 9.23455 15.3172 9.23455 15.7078 9.62522C16.0985 10.0159 16.0985 10.6492 15.7078 11.0399H15.7065ZM7.66648 17.9999H3.66648C3.11448 17.9999 2.66648 17.5519 2.66648 16.9999C2.66648 16.4479 3.11448 15.9999 3.66648 15.9999H7.66648C8.21848 15.9999 8.66648 16.4479 8.66648 16.9999C8.66648 17.5519 8.21848 17.9999 7.66648 17.9999ZM0.999817 13.9999H4.99982C5.55182 13.9999 5.99982 13.5519 5.99982 12.9999C5.99982 12.4479 5.55182 11.9999 4.99982 11.9999H0.999817C0.447817 11.9999 -0.000183105 12.4479 -0.000183105 12.9999C-0.000183105 13.5519 0.447817 13.9999 0.999817 13.9999ZM20.9598 7.03991C21.1545 7.23591 21.4105 7.33324 21.6665 7.33324C21.9225 7.33324 22.1798 7.23457 22.3745 7.03991C22.7652 6.64924 22.7652 6.01591 22.3745 5.62524L19.7078 2.95857C19.3172 2.56791 18.6838 2.56791 18.2932 2.95857C17.9025 3.34924 17.9025 3.98257 18.2932 4.37324L20.9598 7.03991Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SpinnerIcon.tsx
import { jsx as jsx162, jsxs as jsxs6 } from "react/jsx-runtime";
var SpinnerIcon = (props) => /* @__PURE__ */ jsxs6(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx162(
        "path",
        {
          clipRule: "evenodd",
          d: "M11.9999 5.33314C8.31802 5.33314 5.33326 8.31788 5.33326 11.9998C5.33326 15.6817 8.31802 18.6664 11.9999 18.6664C15.6818 18.6664 18.6666 15.6817 18.6666 11.9998C18.6666 11.0793 19.4127 10.3331 20.3332 10.3331C21.2537 10.3331 21.9999 11.0793 21.9999 11.9998C21.9999 17.5226 17.5227 21.9998 11.9999 21.9998C6.47708 21.9998 1.99994 17.5226 1.99994 11.9998C1.99994 6.47697 6.47708 1.99981 11.9999 1.99981C12.9204 1.99981 13.6666 2.74598 13.6666 3.66647C13.6666 4.58697 12.9204 5.33314 11.9999 5.33314Z",
          fill: "currentColor",
          fillRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx162(
        "animateTransform",
        {
          attributeName: "transform",
          dur: "1.5s",
          keyTimes: "0;1",
          repeatCount: "indefinite",
          type: "rotate",
          values: "0 0 0;360 0 0"
        }
      )
    ]
  }
);

// src/components/Icon/icons/StatsIcon.tsx
import { jsx as jsx163 } from "react/jsx-runtime";
var StatsIcon = (props) => /* @__PURE__ */ jsx163(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx163(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.3332 2.66647H11.6665C10.3779 2.66647 9.33319 3.71114 9.33319 4.9998V18.9998C9.33319 20.2884 10.3779 21.3331 11.6665 21.3331H12.3332C13.6218 21.3331 14.6665 20.2884 14.6665 18.9998V4.9998C14.6665 3.71114 13.6218 2.66647 12.3332 2.66647ZM4.99987 9.33312H4.33321C3.04454 9.33312 1.99988 10.3778 1.99988 11.6665V18.9998C1.99988 20.2884 3.04454 21.3331 4.33321 21.3331H4.99987C6.28853 21.3331 7.3332 20.2884 7.3332 18.9998V11.6665C7.3332 10.3778 6.28853 9.33312 4.99987 9.33312ZM18.9998 14.6664H19.6665C20.9552 14.6664 21.9998 15.7111 21.9998 16.9998V18.9998C21.9998 20.2884 20.9552 21.3331 19.6665 21.3331H18.9998C17.7112 21.3331 16.6665 20.2884 16.6665 18.9998V16.9998C16.6665 15.7111 17.7112 14.6664 18.9998 14.6664Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/SwitchAccountsIcon.tsx
import { jsx as jsx164 } from "react/jsx-runtime";
var SwitchAccountsIcon = (props) => /* @__PURE__ */ jsx164(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx164(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.0758 6.66646C13.0758 8.8756 11.2849 10.6665 9.0758 10.6665C6.86667 10.6665 5.07581 8.8756 5.07581 6.66646C5.07581 4.45733 6.86667 2.66647 9.0758 2.66647C11.2849 2.66647 13.0758 4.45733 13.0758 6.66646ZM21.0398 7.62645L23.7064 10.2931L23.7051 10.2944C24.0958 10.6851 24.0958 11.3184 23.7051 11.7091L21.0384 14.3758C20.8438 14.5718 20.5878 14.6691 20.3318 14.6691C20.0758 14.6691 19.8198 14.5704 19.6251 14.3758C19.2344 13.9851 19.2344 13.3518 19.6251 12.9611L20.5851 12.0011H16.3331C15.7811 12.0011 15.3331 11.5531 15.3331 11.0011C15.3331 10.4491 15.7811 10.0011 16.3331 10.0011H20.5851L19.6251 9.04112C19.2344 8.65045 19.2344 8.01712 19.6251 7.62645C20.0158 7.23579 20.6491 7.23579 21.0398 7.62645ZM12.8785 19.7878C12.3118 19.2211 11.9998 18.4678 11.9998 17.6664C11.9998 16.8651 12.3118 16.1118 12.8785 15.5451L14.4518 13.9718C12.9771 12.7211 11.0918 11.9998 9.07446 11.9998C5.9158 11.9998 3.06781 13.7531 1.64115 16.5758C1.26515 17.3184 1.23181 18.1931 1.54915 18.9771C1.87715 19.7864 2.53714 20.4158 3.35981 20.7011C5.20647 21.3411 7.12913 21.6664 9.07446 21.6664C10.7545 21.6664 12.4118 21.4104 14.0224 20.9318L12.8785 19.7878ZM17.4144 16.6664H21.6664L21.6651 16.6651C22.2171 16.6651 22.6651 17.1131 22.6651 17.6651C22.6651 18.2171 22.2171 18.6651 21.6651 18.6651H17.4131L18.3731 19.6251C18.7638 20.0158 18.7638 20.6491 18.3731 21.0398C18.1784 21.2358 17.9224 21.3331 17.6664 21.3331C17.4104 21.3331 17.1544 21.2344 16.9598 21.0398L14.2931 18.3731C13.9024 17.9824 13.9024 17.3491 14.2931 16.9584L16.9598 14.2918C17.3504 13.9011 17.9838 13.9011 18.3744 14.2918C18.7651 14.6824 18.7651 15.3158 18.3744 15.7064L17.4144 16.6664Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TagIcon.tsx
import { jsx as jsx165 } from "react/jsx-runtime";
var TagIcon = (props) => /* @__PURE__ */ jsx165(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx165(
      "path",
      {
        d: "M16.3331 1.33315H7.66641C5.64374 1.33315 3.99975 2.97714 3.99975 4.9998V21.6664C3.99975 22.0344 4.20241 22.3744 4.52775 22.5478C4.85308 22.7224 5.24775 22.7038 5.55441 22.4984L11.9997 18.2011L18.445 22.4984C18.6117 22.6091 18.8064 22.6664 18.9997 22.6664C19.1624 22.6664 19.3237 22.6278 19.4717 22.5478C19.797 22.3744 19.9997 22.0344 19.9997 21.6664V4.9998C19.9997 2.97714 18.3557 1.33315 16.3331 1.33315Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/TeleprompterIcon.tsx
import { jsx as jsx166 } from "react/jsx-runtime";
var TeleprompterIcon = (props) => /* @__PURE__ */ jsx166(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx166(
      "path",
      {
        clipRule: "evenodd",
        d: "M20.1186 6.66654H7.8506C7.18032 6.66654 6.63632 6.06921 6.63632 5.33321C6.63632 4.59721 7.18032 3.99988 7.8506 3.99988H20.1186C20.7889 3.99988 21.3329 4.59721 21.3329 5.33321C21.3329 6.06921 20.7889 6.66654 20.1186 6.66654ZM11.214 13.6665H20.1187C20.789 13.6665 21.333 13.0692 21.333 12.3332C21.333 11.5972 20.789 10.9999 20.1187 10.9999H11.214C10.5437 10.9999 9.99967 11.5972 9.99967 12.3332C9.99967 13.0692 10.5437 13.6665 11.214 13.6665ZM7.8506 20.0389H20.1186C20.7889 20.0389 21.3329 19.4416 21.3329 18.7056C21.3329 17.9696 20.7889 17.3723 20.1186 17.3723H7.8506C7.18032 17.3723 6.63632 17.9696 6.63632 18.7056C6.63632 19.4416 7.18032 20.0389 7.8506 20.0389ZM7.99957 11.9998C7.99957 12.3038 7.86157 12.5908 7.62464 12.7803L4.2913 15.4472C3.9905 15.6874 3.5797 15.7343 3.2333 15.5676C2.88624 15.4016 2.66624 15.0507 2.66624 14.6666L2.66624 9.33324C2.66624 8.94911 2.88624 8.59817 3.2333 8.43217C3.5797 8.26551 3.9905 8.31231 4.2913 8.55258L7.62464 11.2192C7.86157 11.4086 7.99957 11.6958 7.99957 11.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TextSizeIcon.tsx
import { jsx as jsx167 } from "react/jsx-runtime";
var TextSizeIcon = (props) => /* @__PURE__ */ jsx167(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx167(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.33291 2.66655H15.6662C16.2182 2.66655 16.6662 3.11455 16.6662 3.66655C16.6662 4.21855 16.2182 4.66655 15.6662 4.66655H9.99957V20.3332C9.99957 20.8852 9.55157 21.3332 8.99957 21.3332C8.44757 21.3332 7.99957 20.8852 7.99957 20.3332V4.66655H2.33291C1.78091 4.66655 1.33291 4.21855 1.33291 3.66655C1.33291 3.11455 1.78091 2.66655 2.33291 2.66655ZM13.6662 9.33322H21.6662C22.2182 9.33322 22.6662 9.78122 22.6662 10.3332C22.6662 10.8852 22.2182 11.3332 21.6662 11.3332H18.6662V20.3332C18.6662 20.8852 18.2182 21.3332 17.6662 21.3332C17.1142 21.3332 16.6662 20.8852 16.6662 20.3332V11.3332H13.6662C13.1142 11.3332 12.6662 10.8852 12.6662 10.3332C12.6662 9.78122 13.1142 9.33322 13.6662 9.33322Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ThumbnailIcon.tsx
import { jsx as jsx168 } from "react/jsx-runtime";
var ThumbnailIcon = (props) => /* @__PURE__ */ jsx168(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx168(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.9996 20.6664H4.99968C2.97702 20.6664 1.33302 19.0224 1.33302 16.9998V6.9998C1.33302 4.97714 2.97702 3.33315 4.99968 3.33315H18.9996C21.0223 3.33315 22.6663 4.97714 22.6663 6.9998V16.9998C22.6663 19.0224 21.0223 20.6664 18.9996 20.6664ZM4.99968 5.33314C4.08102 5.33314 3.33302 6.08114 3.33302 6.9998V16.9998C3.33302 17.8922 4.03889 18.6236 4.92137 18.6646L12.4063 11.1785C13.837 9.74913 16.1623 9.74913 17.5917 11.1785L20.6663 14.2521V6.9998C20.6663 6.08114 19.9183 5.33314 18.9996 5.33314H4.99968ZM9.333 9.66646C9.333 10.5869 8.58681 11.3331 7.66634 11.3331C6.74587 11.3331 5.99968 10.5869 5.99968 9.66646C5.99968 8.74599 6.74587 7.9998 7.66634 7.9998C8.58681 7.9998 9.333 8.74599 9.333 9.66646Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ThumbsDownIcon.tsx
import { jsx as jsx169 } from "react/jsx-runtime";
var ThumbsDownIcon = (props) => /* @__PURE__ */ jsx169(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx169(
      "path",
      {
        clipRule: "evenodd",
        d: "M3.66623 15.9998H5.66622C5.958 15.9998 6.23744 15.9459 6.49512 15.8477C6.60894 16.0437 6.74141 16.23 6.89156 16.4038L12.5769 22.9864C12.7715 23.2118 13.0502 23.3331 13.3342 23.3331C13.4849 23.3331 13.6382 23.2984 13.7809 23.2278C15.3769 22.4291 16.1155 20.5638 15.4995 18.8891L14.4355 15.9998H18.8702C20.0169 15.9998 21.0755 15.4784 21.7755 14.5691C22.4742 13.6598 22.7075 12.5038 22.4142 11.3945H22.4128L20.8249 5.39447C20.3995 3.78781 18.9409 2.66648 17.2795 2.66648H9.66622C8.81466 2.66648 8.02984 2.95826 7.40665 3.44721C6.97904 2.96837 6.35725 2.66647 5.66622 2.66647H3.66623C2.37957 2.66647 1.3329 3.71314 1.3329 4.9998V13.6664C1.3329 14.9531 2.37957 15.9998 3.66623 15.9998ZM3.3329 4.9998C3.3329 4.8158 3.48223 4.66647 3.66623 4.66647H5.66622C5.85022 4.66647 5.99956 4.8158 5.99956 4.9998V13.6664C5.99956 13.8504 5.85022 13.9998 5.66622 13.9998H3.66623C3.48223 13.9998 3.3329 13.8504 3.3329 13.6664V4.9998Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ThumbsUpIcon.tsx
import { jsx as jsx170 } from "react/jsx-runtime";
var ThumbsUpIcon = (props) => /* @__PURE__ */ jsx170(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx170(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.8689 7.9998C20.0156 7.9998 21.0742 8.52113 21.7742 9.43047C22.4729 10.3398 22.7062 11.4958 22.4129 12.6051L20.8249 18.6051C20.3983 20.2118 18.9409 21.3331 17.2796 21.3331H9.66628C8.81472 21.3331 8.02989 21.0413 7.40671 20.5524C6.9791 21.0312 6.35731 21.3331 5.66629 21.3331H3.66629C2.37963 21.3331 1.33296 20.2864 1.33296 18.9998V10.3331C1.33296 9.04646 2.37963 7.9998 3.66629 7.9998H5.66629C5.9579 7.9998 6.23718 8.05356 6.49475 8.1517C6.60835 7.95575 6.74054 7.76953 6.89029 7.5958L12.5756 1.01315C12.8743 0.666488 13.3703 0.567822 13.7796 0.771821C15.3769 1.57049 16.1156 3.43581 15.4983 5.11048L14.4343 7.9998H18.8689ZM5.99962 10.3331C5.99962 10.1491 5.85029 9.99979 5.66629 9.99979H3.66629C3.48229 9.99979 3.33296 10.1491 3.33296 10.3331V18.9998C3.33296 19.1838 3.48229 19.3331 3.66629 19.3331H5.66629C5.85029 19.3331 5.99962 19.1838 5.99962 18.9998L5.99962 17.6664L5.99962 10.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TranscriptIcon.tsx
import { jsx as jsx171 } from "react/jsx-runtime";
var TranscriptIcon = (props) => /* @__PURE__ */ jsx171(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx171(
      "path",
      {
        d: "M19.4132 7.98019L14.9401 3.57694C14.5618 3.20457 14.0601 2.99982 13.5264 2.99982H7.14237C5.40865 2.99982 3.99951 4.38694 3.99951 6.09357V17.9061C3.99951 19.6127 5.40865 20.9998 7.14237 20.9998H16.8567C18.5904 20.9998 19.9995 19.6127 19.9995 17.9061V9.37182C19.9995 8.84644 19.7915 8.35144 19.4132 7.98019ZM8.28523 8.62482H10.5709C11.0441 8.62482 11.4281 9.00282 11.4281 9.46857C11.4281 9.93432 11.0441 10.3123 10.5709 10.3123H8.28523C7.81208 10.3123 7.42808 9.93432 7.42808 9.46857C7.42808 9.00282 7.81208 8.62482 8.28523 8.62482ZM15.7138 17.0623H8.28523C7.81208 17.0623 7.42808 16.6843 7.42808 16.2186C7.42808 15.7528 7.81208 15.3748 8.28523 15.3748H15.7138C16.1869 15.3748 16.5709 15.7528 16.5709 16.2186C16.5709 16.6843 16.1869 17.0623 15.7138 17.0623ZM15.7138 13.6873H8.28523C7.81208 13.6873 7.42808 13.3093 7.42808 12.8436C7.42808 12.3778 7.81208 11.9998 8.28523 11.9998H15.7138C16.1869 11.9998 16.5709 12.3778 16.5709 12.8436C16.5709 13.3093 16.1869 13.6873 15.7138 13.6873ZM18.2075 9.18732H14.8567C14.2281 9.18732 13.7138 8.68107 13.7138 8.06232V4.77619L13.7287 4.77057L18.2109 9.18169L18.2075 9.18732Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/TransitionsIcon.tsx
import { jsx as jsx172 } from "react/jsx-runtime";
var TransitionsIcon = (props) => /* @__PURE__ */ jsx172(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx172(
      "path",
      {
        clipRule: "evenodd",
        d: "M9.66615 2.66647H14.3328C16.3578 2.66647 17.9995 4.30809 17.9995 6.33313V17.6664C17.9995 19.6915 16.3578 21.3331 14.3328 21.3331H9.66615C7.64111 21.3331 5.9995 19.6915 5.9995 17.6664V6.33313C5.9995 4.30809 7.64111 2.66647 9.66615 2.66647ZM3.66617 6.66646H3.9995C4.5515 6.66646 4.9995 6.21846 4.9995 5.66647C4.9995 5.11447 4.5515 4.66647 3.9995 4.66647H3.66617C2.01284 4.66647 0.666176 6.0118 0.666176 7.66646V16.3331C0.666176 17.9878 2.01284 19.3331 3.66617 19.3331H3.9995C4.5515 19.3331 4.9995 18.8851 4.9995 18.3331C4.9995 17.7811 4.5515 17.3331 3.9995 17.3331H3.66617C3.1155 17.3331 2.66617 16.8851 2.66617 16.3331V7.66646C2.66617 7.11446 3.1155 6.66646 3.66617 6.66646ZM20.3328 4.66647H19.9995C19.4475 4.66647 18.9995 5.11447 18.9995 5.66647C18.9995 6.21846 19.4475 6.66646 19.9995 6.66646H20.3328C20.8835 6.66646 21.3328 7.11446 21.3328 7.66646V16.3331C21.3328 16.8851 20.8835 17.3331 20.3328 17.3331H19.9995C19.4475 17.3331 18.9995 17.7811 18.9995 18.3331C18.9995 18.8851 19.4475 19.3331 19.9995 19.3331H20.3328C21.9861 19.3331 23.3328 17.9878 23.3328 16.3331V7.66646C23.3328 6.0118 21.9861 4.66647 20.3328 4.66647Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TransparencyIcon.tsx
import { jsx as jsx173 } from "react/jsx-runtime";
var TransparencyIcon = (props) => /* @__PURE__ */ jsx173(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx173(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.3864 6.1452C11.4331 5.95187 12.5664 5.95187 13.6131 6.1452C13.6744 6.15587 13.7358 6.1612 13.7958 6.1612C14.2678 6.1612 14.6891 5.82387 14.7771 5.3412C14.8771 4.79853 14.5184 4.2772 13.9744 4.1772C12.6878 3.9412 11.3104 3.9412 10.0238 4.1772C9.48108 4.2772 9.12108 4.79853 9.22108 5.3412C9.32108 5.8852 9.84509 6.24253 10.3864 6.1452ZM5.13175 8.7412C5.37042 8.7412 5.60908 8.6572 5.79975 8.4852C6.39708 7.9492 7.03175 7.49187 7.68508 7.12654C8.16642 6.8572 8.33842 6.24787 8.06908 5.7652C7.80108 5.28387 7.19575 5.11054 6.70775 5.37987C5.92508 5.8172 5.17042 6.3612 4.46242 6.9972C4.05042 7.36654 4.01708 7.99854 4.38642 8.4092C4.58375 8.6292 4.85842 8.7412 5.13175 8.7412ZM15.9304 5.7652C16.1984 5.28387 16.8064 5.11054 17.2918 5.37987C18.0744 5.8172 18.8291 6.3612 19.5358 6.9972C19.9478 7.36654 19.9811 7.99854 19.6118 8.4092C19.4131 8.6292 19.1411 8.7412 18.8678 8.7412C18.6291 8.7412 18.3904 8.6572 18.1998 8.4852C17.6024 7.9492 16.9678 7.49187 16.3144 7.12654C15.8331 6.85587 15.6611 6.24654 15.9304 5.7652ZM7.68509 16.8745C7.03176 16.5092 6.39709 16.0519 5.79976 15.5159C5.39176 15.1479 4.75976 15.1812 4.38776 15.5905C4.01843 16.0012 4.05176 16.6332 4.46376 17.0025C5.17043 17.6385 5.92643 18.1825 6.70909 18.6199C6.86376 18.7052 7.03176 18.7465 7.19576 18.7465C7.54776 18.7465 7.88776 18.5625 8.06909 18.2345C8.33843 17.7532 8.16643 17.1439 7.68509 16.8732V16.8745ZM3.59309 9.32654C4.04509 9.64388 4.15309 10.2679 3.83575 10.7199V10.7185L3.61975 11.0372C3.43175 11.3225 3.33309 11.6532 3.33309 11.9985C3.33309 12.3439 3.43175 12.6759 3.61842 12.9572L3.83575 13.2785C4.15309 13.7305 4.04509 14.3545 3.59309 14.6719C3.41709 14.7959 3.21575 14.8545 3.01842 14.8545C2.70375 14.8545 2.39442 14.7079 2.19975 14.4305C2.11042 14.3025 2.02642 14.1772 1.94775 14.0572C1.54642 13.4505 1.33309 12.7385 1.33309 11.9999C1.33309 11.2612 1.54642 10.5492 1.94909 9.93854C2.02509 9.82254 2.11042 9.69721 2.19975 9.56921C2.51442 9.11721 3.13575 9.00654 3.59309 9.32654ZM13.6131 17.8545C12.5664 18.0479 11.4331 18.0479 10.3864 17.8545C9.84375 17.7559 9.32108 18.1145 9.22241 18.6585C9.12241 19.2012 9.48108 19.7225 10.0251 19.8225C10.6677 19.9399 11.3331 19.9999 11.9997 19.9999C12.6664 19.9999 13.3317 19.9399 13.9744 19.8225C14.5171 19.7225 14.8771 19.2012 14.7771 18.6585C14.6771 18.1145 14.1571 17.7572 13.6131 17.8545ZM16.3144 16.8746C16.9678 16.5092 17.6024 16.0519 18.1998 15.5159C18.6078 15.1466 19.2398 15.1812 19.6118 15.5919C19.9811 16.0026 19.9478 16.6346 19.5358 17.0039C18.8278 17.6399 18.0731 18.1839 17.2904 18.6212C17.1358 18.7066 16.9678 18.7479 16.8038 18.7479C16.4531 18.7479 16.1131 18.5639 15.9304 18.2359C15.6611 17.7532 15.8331 17.1439 16.3144 16.8746ZM22.0518 9.94256C21.9731 9.82256 21.8891 9.69722 21.7998 9.56922C21.4851 9.11856 20.8638 9.00922 20.4064 9.32656C19.9544 9.64389 19.8464 10.2679 20.1638 10.7199L20.3811 11.0412C20.5678 11.3226 20.6664 11.6546 20.6664 11.9999C20.6664 12.3452 20.5678 12.6759 20.3798 12.9612L20.1638 13.2799C19.8464 13.7319 19.9544 14.3559 20.4064 14.6732C20.5824 14.7959 20.7824 14.8559 20.9811 14.8559C21.2958 14.8559 21.6064 14.7079 21.7998 14.4319C21.8891 14.3039 21.9744 14.1786 22.0504 14.0626C22.4531 13.4519 22.6664 12.7399 22.6664 12.0012C22.6664 11.2626 22.4531 10.5506 22.0518 9.94389V9.94256ZM15.9998 11.9999C15.9998 14.209 14.2089 15.9999 11.9998 15.9999C9.79062 15.9999 7.99975 14.209 7.99975 11.9999C7.99975 9.79073 9.79062 7.99987 11.9998 7.99987C14.2089 7.99987 15.9998 9.79073 15.9998 11.9999Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TrendsIcon.tsx
import { jsx as jsx174 } from "react/jsx-runtime";
var TrendsIcon = (props) => /* @__PURE__ */ jsx174(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx174(
      "path",
      {
        clipRule: "evenodd",
        d: "M12.1758 21.5609C9.68781 21.7629 5.43281 21.8659 3.63781 20.3389C2.05381 18.9909 2.04781 15.6979 2.04281 13.1579C2.04281 12.8189 2.04181 12.4939 2.03681 12.1879C2.03381 11.9329 2.02681 11.6679 2.02181 11.3939C1.96781 8.8919 1.90081 5.7639 3.30881 4.0369C4.87081 2.1219 10.1438 2.0269 11.8248 2.0269C11.9888 2.0269 12.1948 2.0219 12.4328 2.0169C14.2678 1.9739 18.0548 1.8869 19.9028 3.5619C21.9188 5.3889 21.9158 8.2579 21.9128 11.1129V11.4199C21.9128 11.9319 21.9178 12.4289 21.9228 12.9099C21.9538 16.1399 21.9788 18.6549 20.4878 20.0459C19.1998 21.2479 16.9248 21.3359 14.5408 21.4279C13.7538 21.4579 12.9548 21.4899 12.1758 21.5609ZM20.3158 10.0509L15.9598 8.5999C15.9029 8.32674 15.7894 8.06852 15.6267 7.84182C15.4641 7.61513 15.2557 7.42499 15.0152 7.28362C14.7746 7.14225 14.5071 7.05278 14.2299 7.02097C13.9527 6.98916 13.6719 7.0157 13.4056 7.0989C13.1392 7.18209 12.8932 7.3201 12.6834 7.50404C12.4736 7.68799 12.3046 7.9138 12.1873 8.16696C12.0699 8.42013 12.0069 8.69504 12.0022 8.97403C11.9974 9.25302 12.0511 9.52991 12.1598 9.7869L9.66481 13.1139C9.38814 13.0163 9.09338 12.9809 8.80147 13.0101C8.50956 13.0393 8.22767 13.1325 7.97581 13.2829L3.88481 11.5779C3.64002 11.4758 3.36469 11.4751 3.11939 11.576C2.87409 11.6769 2.67892 11.8711 2.57681 12.1159C2.4747 12.3607 2.47402 12.636 2.57492 12.8813C2.67581 13.1266 2.87002 13.3218 3.11481 13.4239L6.99981 15.0419C7.0061 15.3152 7.06834 15.5842 7.18269 15.8325C7.29703 16.0808 7.46105 16.303 7.66462 16.4854C7.86819 16.6678 8.10699 16.8065 8.36627 16.893C8.62555 16.9796 8.89982 17.012 9.17213 16.9884C9.44444 16.9648 9.70902 16.8856 9.94954 16.7557C10.19 16.6258 10.4014 16.448 10.5705 16.2333C10.7396 16.0186 10.8629 15.7714 10.9328 15.5072C11.0027 15.2429 11.0177 14.9672 10.9768 14.6969L13.7608 10.9859C14.0418 11.0202 14.3269 10.9942 14.5971 10.9098C14.8673 10.8253 15.1164 10.6842 15.3278 10.4959L19.6838 11.9489C19.8084 11.9904 19.94 12.0069 20.071 11.9976C20.2021 11.9882 20.3299 11.9532 20.4474 11.8944C20.5649 11.8356 20.6696 11.7543 20.7556 11.655C20.8417 11.5558 20.9073 11.4405 20.9488 11.3159C20.9903 11.1913 21.0069 11.0597 20.9975 10.9287C20.9881 10.7977 20.9531 10.6698 20.8943 10.5523C20.8355 10.4348 20.7542 10.3301 20.6549 10.2441C20.5557 10.158 20.4404 10.0924 20.3158 10.0509Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TrialUnlockClockIcon.tsx
import { jsx as jsx175 } from "react/jsx-runtime";
var TrialUnlockClockIcon = (props) => /* @__PURE__ */ jsx175(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx175(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.6649 22.3301C11.113 22.3301 10.665 21.8822 10.665 21.3303C10.665 20.7784 11.113 20.3304 11.6649 20.3304C16.4428 20.3304 20.3302 16.4431 20.3302 11.6651C20.3302 6.88721 16.4428 2.99981 11.6649 2.99981C11.113 2.99981 10.665 2.55188 10.665 1.99997C10.665 1.44805 11.113 1.00012 11.6649 1.00012C17.5453 1.00012 22.3299 5.78471 22.3299 11.6651C22.3299 17.5455 17.5453 22.3301 11.6649 22.3301ZM15.9962 15.6645C15.8016 15.6645 15.6016 15.6072 15.4283 15.4872L11.0956 12.4877C10.8263 12.301 10.665 11.9931 10.665 11.6651V5.99934C10.665 5.44743 11.113 4.9995 11.6649 4.9995C12.2168 4.9995 12.6647 5.44743 12.6647 5.99934V11.1412L16.5668 13.8421C17.0214 14.1567 17.1347 14.7793 16.8187 15.2326C16.6241 15.5125 16.3135 15.6632 15.9962 15.6632V15.6645ZM5.82983 18.5C5.82983 19.0522 5.38218 19.4998 4.82998 19.4998C4.27779 19.4998 3.83014 19.0522 3.83014 18.5C3.83014 17.9478 4.27779 17.5001 4.82998 17.5001C5.38218 17.5001 5.82983 17.9478 5.82983 18.5ZM1.99972 12.665C2.55192 12.665 2.99957 12.2173 2.99957 11.6651C2.99957 11.1129 2.55192 10.6653 1.99972 10.6653C1.44752 10.6653 0.999878 11.1129 0.999878 11.6651C0.999878 12.2173 1.44752 12.665 1.99972 12.665ZM5.82983 4.83029C5.82983 5.38248 5.38218 5.83013 4.82998 5.83013C4.27779 5.83013 3.83014 5.38248 3.83014 4.83029C3.83014 4.27809 4.27779 3.83044 4.82998 3.83044C5.38218 3.83044 5.82983 4.27809 5.82983 4.83029ZM7.96689 21.5942C8.51909 21.5942 8.96673 21.1466 8.96673 20.5944C8.96673 20.0422 8.51909 19.5945 7.96689 19.5945C7.41469 19.5945 6.96704 20.0422 6.96704 20.5944C6.96704 21.1466 7.41469 21.5942 7.96689 21.5942ZM3.73545 15.3633C3.73545 15.9155 3.2878 16.3631 2.7356 16.3631C2.1834 16.3631 1.73576 15.9155 1.73576 15.3633C1.73576 14.8111 2.1834 14.3634 2.7356 14.3634C3.2878 14.3634 3.73545 14.8111 3.73545 15.3633ZM2.7356 8.96683C3.2878 8.96683 3.73545 8.51919 3.73545 7.96699C3.73545 7.41479 3.2878 6.96714 2.7356 6.96714C2.1834 6.96714 1.73576 7.41479 1.73576 7.96699C1.73576 8.51919 2.1834 8.96683 2.7356 8.96683ZM8.96673 2.73587C8.96673 3.28807 8.51909 3.73571 7.96689 3.73571C7.41469 3.73571 6.96704 3.28807 6.96704 2.73587C6.96704 2.18367 7.41469 1.73603 7.96689 1.73603C8.51909 1.73603 8.96673 2.18367 8.96673 2.73587Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TrimIcon.tsx
import { jsx as jsx176 } from "react/jsx-runtime";
var TrimIcon = (props) => /* @__PURE__ */ jsx176(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx176(
      "path",
      {
        clipRule: "evenodd",
        d: "M5.93277 7.48913L10.5594 10.6198C11.0181 10.9305 11.2901 11.4465 11.2901 11.9998C11.2901 12.5531 11.0181 13.0691 10.5594 13.3798L5.93411 16.5104C5.65144 16.7011 5.32611 16.7971 4.99944 16.7971C4.73278 16.7971 4.46478 16.7331 4.21811 16.6024C3.67144 16.3131 3.33278 15.7478 3.33278 15.1304V8.86913C3.33278 8.2518 3.67144 7.68647 4.21811 7.39713C4.76478 7.1078 5.42211 7.14247 5.93277 7.48913ZM18.0647 7.48913C18.5794 7.14247 19.2354 7.1078 19.7807 7.39713C20.3274 7.68647 20.6661 8.2518 20.6661 8.86913V15.1331C20.6661 15.7504 20.3274 16.3158 19.7807 16.6051C19.5341 16.7344 19.2661 16.7998 18.9994 16.7998C18.6727 16.7998 18.3474 16.7038 18.0661 16.5131L13.4394 13.3811C12.9808 13.0705 12.7088 12.5545 12.7088 12.0011C12.7088 11.4478 12.9808 10.9318 13.4394 10.6211L18.0647 7.48913Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/TurnstileIcon.tsx
import { jsx as jsx177 } from "react/jsx-runtime";
var TurnstileIcon = (props) => /* @__PURE__ */ jsx177(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx177(
      "path",
      {
        clipRule: "evenodd",
        d: "M11.8426 16.6664H4.33333C3.41466 16.6664 2.66667 15.9184 2.66667 14.9997V8.99973C2.66667 8.08106 3.41466 7.33306 4.33333 7.33306H18.3333C19.252 7.33306 20 8.08106 20 8.99973V13.0597C20 13.6117 20.448 14.0597 21 14.0597C21.552 14.0597 22 13.6117 22 13.0597V8.99973C22 6.97706 20.356 5.33307 18.3333 5.33307H4.33333C2.31067 5.33307 0.666672 6.97706 0.666672 8.99973V14.9997C0.666672 17.0224 2.31067 18.6664 4.33333 18.6664H11.8426C12.3946 18.6664 12.8426 18.2184 12.8426 17.6664C12.8426 17.1144 12.3946 16.6664 11.8426 16.6664ZM7.99999 11.9997C7.99999 12.7361 7.40304 13.333 6.66666 13.333C5.93028 13.333 5.33333 12.7361 5.33333 11.9997C5.33333 11.2633 5.93028 10.6664 6.66666 10.6664C7.40304 10.6664 7.99999 11.2633 7.99999 11.9997ZM12.6666 11.9997C12.6666 12.7361 12.0697 13.333 11.3333 13.333C10.5969 13.333 9.99998 12.7361 9.99998 11.9997C9.99998 11.2633 10.5969 10.6664 11.3333 10.6664C12.0697 10.6664 12.6666 11.2633 12.6666 11.9997ZM15.1786 13.417L23.0986 16.3104H23.0999C23.6506 16.5117 24.0119 17.041 24.0013 17.6277C23.9893 18.2144 23.6066 18.729 23.0479 18.9077L19.912 19.9104L18.9093 23.045C18.7306 23.605 18.216 23.9877 17.6293 23.9997H17.6013C17.0266 23.9997 16.5093 23.641 16.312 23.0984L13.4186 15.1784C13.2333 14.6704 13.356 14.117 13.7373 13.7357C14.12 13.3544 14.6746 13.2317 15.1786 13.417Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/UndoIcon.tsx
import { jsx as jsx178 } from "react/jsx-runtime";
var UndoIcon = (props) => /* @__PURE__ */ jsx178(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx178(
      "path",
      {
        clipRule: "evenodd",
        d: "M7.99992 13.3331C7.74392 13.3331 7.48792 13.2344 7.29326 13.0397L7.29192 13.0384L2.9586 8.70506C2.56794 8.3144 2.56794 7.68107 2.9586 7.2904L7.29192 2.95708C7.68259 2.56641 8.31592 2.56641 8.70659 2.95708C9.09725 3.34774 9.09725 3.98107 8.70659 4.37174L6.07993 6.9984H9.33192C13.0079 6.9984 15.9986 9.98906 15.9986 13.6651C15.9986 14.217 15.5506 14.665 14.9986 14.665C14.4466 14.665 13.9986 14.217 13.9986 13.6651C13.9986 11.0917 11.9052 8.9984 9.33192 8.9984H6.07993L8.70659 11.6251C9.09725 12.0157 9.09725 12.6491 8.70659 13.0397C8.51192 13.2357 8.25592 13.3331 7.99992 13.3331ZM19.3332 14.3331C19.3332 13.7811 19.7812 13.3331 20.3332 13.3331C20.8852 13.3331 21.3332 13.7811 21.3332 14.3331V17.6664C21.3332 19.689 19.6892 21.333 17.6666 21.333H3.6666C3.1146 21.333 2.6666 20.885 2.6666 20.333C2.6666 19.781 3.1146 19.333 3.6666 19.333H17.6666C18.5852 19.333 19.3332 18.585 19.3332 17.6664V14.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/UnlockIcon.tsx
import { jsx as jsx179 } from "react/jsx-runtime";
var UnlockIcon = (props) => /* @__PURE__ */ jsx179(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx179(
      "path",
      {
        clipRule: "evenodd",
        d: "M8.66652 9.99973V6.6664C8.66652 4.82908 7.17053 3.33308 5.3332 3.33308C3.49587 3.33308 1.99987 4.82908 1.99987 6.6664V8.33307C1.99987 8.88507 1.55187 9.33306 0.999875 9.33306C0.447877 9.33306 -0.00012207 8.88507 -0.00012207 8.33307V6.6664C-0.00012207 3.72508 2.39187 1.33308 5.3332 1.33308C8.27452 1.33308 10.6665 3.72508 10.6665 6.6664V9.99973H17.6665C19.6892 9.99973 21.3332 11.6437 21.3332 13.6664V18.9997C21.3332 21.0224 19.6892 22.6664 17.6665 22.6664H7.66653C5.64386 22.6664 3.99987 21.0224 3.99987 18.9997V13.6664C3.99987 11.6437 5.64386 9.99973 7.66653 9.99973H8.66652ZM12.6665 17.9997C13.2185 17.9997 13.6665 17.5517 13.6665 16.9997V15.6664C13.6665 15.1144 13.2185 14.6664 12.6665 14.6664C12.1145 14.6664 11.6665 15.1144 11.6665 15.6664V16.9997C11.6665 17.5517 12.1145 17.9997 12.6665 17.9997Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/UploadIcon.tsx
import { jsx as jsx180 } from "react/jsx-runtime";
var UploadIcon = (props) => /* @__PURE__ */ jsx180(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx180(
      "path",
      {
        clipRule: "evenodd",
        d: "M10.9998 5.41307L8.37313 8.03973C7.98246 8.43039 7.34913 8.43173 6.95847 8.04106C6.5678 7.6504 6.5678 7.01706 6.95847 6.6264L11.2918 2.29308C11.6825 1.90241 12.3158 1.90241 12.7065 2.29308L17.0398 6.6264C17.4304 7.01706 17.4304 7.6504 17.0398 8.04106C16.8451 8.23706 16.5891 8.33439 16.3331 8.33439C16.0771 8.33439 15.8211 8.23573 15.6264 8.04106L12.9998 5.4144V12.9984C12.9998 13.5504 12.5518 13.9984 11.9998 13.9984C11.4478 13.9984 10.9998 13.5504 10.9998 12.9984V5.41307ZM4.9998 15.9997H18.9998C20.2884 15.9997 21.3331 17.0444 21.3331 18.333V19.6664C21.3331 20.955 20.2884 21.9997 18.9998 21.9997H4.9998C3.71114 21.9997 2.66647 20.955 2.66647 19.6664V18.333C2.66647 17.0444 3.71114 15.9997 4.9998 15.9997Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/UsersPermissionsIcon.tsx
import { jsx as jsx181 } from "react/jsx-runtime";
var UsersPermissionsIcon = (props) => /* @__PURE__ */ jsx181(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx181(
      "path",
      {
        clipRule: "evenodd",
        d: "M16.6664 5.99974C16.6664 8.57706 14.5771 10.6664 11.9997 10.6664C9.42241 10.6664 7.33307 8.57706 7.33307 5.99974C7.33307 3.42242 9.42241 1.33308 11.9997 1.33308C14.5771 1.33308 16.6664 3.42242 16.6664 5.99974ZM3.47308 17.5557C4.96775 14.1811 8.3144 11.9997 11.9997 11.9997H11.9971C15.6824 11.9997 19.029 14.1811 20.5237 17.5557C20.8664 18.329 20.8624 19.2157 20.513 19.989C20.157 20.777 19.477 21.3797 18.6477 21.641C16.4904 22.321 14.2531 22.6664 11.9984 22.6664C9.74373 22.6664 7.50641 22.321 5.34908 21.641C4.51975 21.3797 3.84108 20.777 3.48375 19.989C3.13442 19.2157 3.13042 18.329 3.47308 17.5557Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ViewStreamIcon.tsx
import { jsx as jsx182 } from "react/jsx-runtime";
var ViewStreamIcon = (props) => /* @__PURE__ */ jsx182(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx182(
      "path",
      {
        clipRule: "evenodd",
        d: "M18.333 2.6666H5.66635C3.64369 2.6666 1.99969 4.31059 1.99969 6.33325V14.9999C1.99969 17.0226 3.64369 18.6666 5.66635 18.6666H18.333C20.3556 18.6666 21.9996 17.0226 21.9996 14.9999V6.33325C21.9996 4.31059 20.3556 2.6666 18.333 2.6666ZM16.921 11.8546C16.201 12.7812 14.605 14.3332 11.9997 14.3332C9.39434 14.3332 7.79834 12.7812 7.07834 11.8559C6.52768 11.1452 6.52768 10.1906 7.07834 9.47991C7.79834 8.55325 9.39434 7.00125 11.9997 7.00125C14.605 7.00125 16.201 8.55325 16.921 9.47858C17.473 10.1906 17.473 11.1452 16.921 11.8559V11.8546ZM17.6663 22.6665H6.33301C5.78101 22.6665 5.33301 22.2185 5.33301 21.6666C5.33301 21.1146 5.78101 20.6666 6.33301 20.6666H17.6663C18.2183 20.6666 18.6663 21.1146 18.6663 21.6666C18.6663 22.2185 18.2183 22.6665 17.6663 22.6665ZM13.9997 10.6666C13.9997 11.7711 13.1042 12.6666 11.9997 12.6666C10.8951 12.6666 9.99967 11.7711 9.99967 10.6666C9.99967 9.56201 10.8951 8.66658 11.9997 8.66658C13.1042 8.66658 13.9997 9.56201 13.9997 10.6666Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/VolumeIcon.tsx
import { jsx as jsx183 } from "react/jsx-runtime";
var VolumeIcon = (props) => /* @__PURE__ */ jsx183(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx183(
      "path",
      {
        d: "M19.133 2.08909C18.5996 1.79709 17.9476 1.81843 17.4356 2.14776L10.3756 6.66643H5.66763C4.01297 6.66643 2.66763 8.01176 2.66763 9.66643V14.3331C2.66763 15.9878 4.01297 17.3331 5.66763 17.3331H10.3756L17.4356 21.8518C17.709 22.0264 18.021 22.1158 18.3356 22.1158C18.6103 22.1158 18.885 22.0478 19.1343 21.9118C19.669 21.6198 20.001 21.0584 20.001 20.4491V3.55176C20.001 2.94243 19.6676 2.38243 19.133 2.08909Z",
        fill: "currentColor"
      }
    )
  }
);

// src/components/Icon/icons/VolumeOffIcon.tsx
import { jsx as jsx184 } from "react/jsx-runtime";
var VolumeOffIcon = (props) => /* @__PURE__ */ jsx184(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx184(
      "path",
      {
        clipRule: "evenodd",
        d: "M6.02857 17.3331H4.99963C3.3463 17.3331 1.99963 15.9878 1.99963 14.3331V9.66643C1.99963 8.01176 3.3463 6.66643 4.99963 6.66643H9.70763L16.769 2.14776C17.2836 1.81843 17.933 1.79709 18.4663 2.08909C19.001 2.38243 19.333 2.94243 19.333 3.55176V4.50293L21.6383 2.27976C22.037 1.89843 22.669 1.90776 23.053 2.30643C23.4356 2.70376 23.4236 3.3371 23.0263 3.7211L4.36097 21.7198C4.16631 21.9064 3.91697 21.9998 3.66631 21.9998C3.40497 21.9998 3.14231 21.8971 2.94631 21.6944C2.56364 21.2971 2.57564 20.6638 2.97297 20.2798L6.02857 17.3331ZM10.9596 18.1344L16.7676 21.8518C17.041 22.0264 17.353 22.1158 17.6663 22.1158C17.941 22.1158 18.2156 22.0478 18.4663 21.9118C19.001 21.6198 19.333 21.0584 19.333 20.4491V10.0598L10.9596 18.1344Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/VolumeUpIcon.tsx
import { jsx as jsx185 } from "react/jsx-runtime";
var VolumeUpIcon = (props) => /* @__PURE__ */ jsx185(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx185(
      "path",
      {
        clipRule: "evenodd",
        d: "M15.133 2.08909C14.5996 1.79709 13.9476 1.81843 13.4356 2.14776L6.37563 6.66643H2.99963C1.34497 6.66643 -0.000366211 8.01176 -0.000366211 9.66643V14.3331C-0.000366211 15.9878 1.34497 17.3331 2.99963 17.3331H6.3743L13.4343 21.8518C13.7076 22.0264 14.0196 22.1158 14.3343 22.1158C14.609 22.1158 14.8836 22.0478 15.133 21.9118C15.6676 21.6198 15.9996 21.0584 15.9996 20.4491V3.55176C15.9996 2.94243 15.6676 2.38243 15.133 2.08909ZM19.2583 9.40778C18.8676 9.01711 18.233 9.01711 17.8436 9.40778C17.453 9.79844 17.453 10.4318 17.8436 10.8224C18.1583 11.1371 18.3316 11.5558 18.3316 12.0011C18.3316 12.4464 18.1583 12.8638 17.8436 13.1798C17.453 13.5691 17.453 14.2038 17.8436 14.5944C18.0383 14.7891 18.2943 14.8878 18.5503 14.8878C18.8063 14.8878 19.0623 14.7904 19.257 14.5944C19.9503 13.9024 20.3316 12.9824 20.3316 12.0024C20.3316 11.0224 19.9503 10.1024 19.257 9.41044L19.2583 9.40778ZM20.4383 8.2291C20.0478 7.83865 20.0476 7.20582 20.4376 6.8151C20.8283 6.4251 21.4612 6.42532 21.8516 6.81577C23.237 8.19977 23.9996 10.0424 23.9996 12.0011C23.9996 13.9598 23.237 15.8011 21.8516 17.1864C21.657 17.3824 21.401 17.4798 21.145 17.4798C20.889 17.4798 20.633 17.3811 20.4383 17.1864C20.0476 16.7958 20.0476 16.1624 20.4383 15.7718C22.5183 13.6931 22.5183 10.3091 20.4383 8.2291ZM20.4376 6.8151C20.4378 6.81488 20.4381 6.81466 20.4383 6.81443L20.437 6.81577C20.4372 6.81554 20.4374 6.81532 20.4376 6.8151Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/VolumeXIcon.tsx
import { jsx as jsx186 } from "react/jsx-runtime";
var VolumeXIcon = (props) => /* @__PURE__ */ jsx186(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx186(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.4356 2.14776C13.9476 1.81843 14.5996 1.79709 15.133 2.08909C15.6676 2.38243 15.9996 2.94243 15.9996 3.55176V20.4491C15.9996 21.0584 15.6676 21.6198 15.133 21.9118C14.8836 22.0478 14.609 22.1158 14.3343 22.1158C14.0196 22.1158 13.7076 22.0264 13.4343 21.8518L6.3743 17.3331H2.99963C1.34497 17.3331 -0.000366211 15.9878 -0.000366211 14.3331V9.66643C-0.000366211 8.01176 1.34497 6.66643 2.99963 6.66643H6.37563L13.4356 2.14776ZM23.7076 10.3731L22.0809 11.9998H22.0783L23.7049 13.6264C24.0956 14.0171 24.0956 14.6504 23.7049 15.0411C23.5103 15.2371 23.2543 15.3344 22.9983 15.3344C22.7423 15.3344 22.4863 15.2358 22.2916 15.0411L20.6649 13.4144L19.0383 15.0411C18.8436 15.2371 18.5876 15.3344 18.3316 15.3344C18.0756 15.3344 17.8196 15.2358 17.6249 15.0411C17.2343 14.6504 17.2343 14.0171 17.6249 13.6264L19.2516 11.9998L17.6249 10.3731C17.2343 9.98244 17.2343 9.3491 17.6249 8.95844C18.0156 8.56777 18.6489 8.56777 19.0396 8.95844L20.6663 10.5851L22.2929 8.95844C22.6836 8.56777 23.3169 8.56777 23.7076 8.95844C24.0983 9.3491 24.0983 9.98244 23.7076 10.3731Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/WandIcon.tsx
import { jsx as jsx187 } from "react/jsx-runtime";
var WandIcon = (props) => /* @__PURE__ */ jsx187(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx187(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.3333 2.33308C13.3333 2.88537 12.8856 3.33308 12.3333 3.33308C11.781 3.33308 11.3333 2.88537 11.3333 2.33308C11.3333 1.7808 11.781 1.33308 12.3333 1.33308C12.8856 1.33308 13.3333 1.7808 13.3333 2.33308ZM8.39598 4.23575L9.65731 4.65575C9.8613 4.72375 9.99864 4.91442 9.99864 5.13041C9.99864 5.34641 9.85997 5.53708 9.65597 5.60508L8.39464 6.02508L7.97331 7.28774C7.90664 7.49041 7.71464 7.62908 7.49998 7.62908C7.28531 7.62908 7.09465 7.49174 7.02665 7.28774L6.60531 6.02508L5.34398 5.60508C5.13998 5.53708 5.00132 5.34641 5.00132 5.13041C5.00132 4.91442 5.13998 4.72375 5.34398 4.65575L6.60531 4.23575L7.02665 2.97309C7.16264 2.56509 7.83864 2.56509 7.97464 2.97309L8.39598 4.23575ZM19.5239 3.36374L20.6373 4.47707C21.0786 4.91707 21.3213 5.50507 21.3213 6.12774C21.3213 6.7504 21.0786 7.33573 20.6373 7.77706L7.76398 20.6477C7.32398 21.089 6.73865 21.3317 6.11465 21.3317C5.49065 21.3317 4.90532 21.089 4.46399 20.6477L3.35065 19.5344C2.90932 19.0944 2.66666 18.5077 2.66666 17.885C2.66666 17.2624 2.90932 16.677 3.35065 16.2357L16.224 3.36374C17.1066 2.48241 18.6439 2.48108 19.5239 3.36374ZM16.8493 8.73573L19.2226 6.3624H19.2239C19.3079 6.27707 19.3213 6.17707 19.3213 6.1264C19.3213 6.07573 19.3079 5.97574 19.2239 5.89174L18.1093 4.77707C18.024 4.69174 17.9253 4.67974 17.8733 4.67974C17.8213 4.67974 17.7226 4.69307 17.6373 4.77707L15.264 7.1504L16.8493 8.73573ZM22.2106 15.9864L20.5266 15.4251L19.9653 13.7411C19.7826 13.1971 18.8826 13.1971 18.7 13.7411L18.1386 15.4251L16.4546 15.9864C16.1826 16.077 15.9986 16.3317 15.9986 16.6184C15.9986 16.905 16.1826 17.1597 16.4546 17.2504L18.1386 17.8117L18.7 19.4957C18.7906 19.7677 19.0466 19.9517 19.3333 19.9517C19.6199 19.9517 19.8746 19.7677 19.9666 19.4957L20.5279 17.8117L22.2119 17.2504C22.4839 17.1597 22.6679 16.905 22.6679 16.6184C22.6679 16.3317 22.4826 16.077 22.2106 15.9864Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/WaveformIcon.tsx
import { jsx as jsx188 } from "react/jsx-runtime";
var WaveformIcon = (props) => /* @__PURE__ */ jsx188(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx188(
      "path",
      {
        clipRule: "evenodd",
        d: "M13.6666 21.3333C13.1146 21.3333 12.6666 20.8853 12.6666 20.3333V3.6666C12.6666 3.1146 13.1146 2.6666 13.6666 2.6666C14.2186 2.6666 14.6666 3.1146 14.6666 3.6666V20.3333C14.6666 20.8853 14.2186 21.3333 13.6666 21.3333ZM5.66661 19.9999C5.11461 19.9999 4.66661 19.5519 4.66661 18.9999V4.99993C4.66661 4.44793 5.11461 3.99993 5.66661 3.99993C6.21861 3.99993 6.66661 4.44793 6.66661 4.99993V18.9999C6.66661 19.5519 6.21861 19.9999 5.66661 19.9999ZM0.666611 12.9999C0.666611 13.5519 1.11461 13.9999 1.66661 13.9999C2.21861 13.9999 2.66661 13.5519 2.66661 12.9999V10.9999C2.66661 10.4479 2.21861 9.99993 1.66661 9.99993C1.11461 9.99993 0.666611 10.4479 0.666611 10.9999V12.9999ZM20.6666 12.9999C20.6666 13.5519 21.1146 13.9999 21.6666 13.9999C22.2186 13.9999 22.6666 13.5519 22.6666 12.9999V10.9999C22.6666 10.4479 22.2186 9.99993 21.6666 9.99993C21.1146 9.99993 20.6666 10.4479 20.6666 10.9999V12.9999ZM8.66661 16.3333C8.66661 16.8853 9.11461 17.3333 9.66661 17.3333C10.2186 17.3333 10.6666 16.8853 10.6666 16.3333V7.6666C10.6666 7.1146 10.2186 6.6666 9.66661 6.6666C9.11461 6.6666 8.66661 7.1146 8.66661 7.6666V16.3333ZM16.6666 16.3333C16.6666 16.8853 17.1146 17.3333 17.6666 17.3333C18.2186 17.3333 18.6666 16.8853 18.6666 16.3333V7.6666C18.6666 7.1146 18.2186 6.6666 17.6666 6.6666C17.1146 6.6666 16.6666 7.1146 16.6666 7.6666V16.3333Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ZoomInIcon.tsx
import { jsx as jsx189 } from "react/jsx-runtime";
var ZoomInIcon = (props) => /* @__PURE__ */ jsx189(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx189(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.6666 10.3331C2.6666 6.1064 6.10659 2.66641 10.3332 2.66641C14.5599 2.66641 17.9999 6.1064 17.9999 10.3331C17.9999 12.0863 17.408 13.7042 16.4135 14.9974L21.0412 19.625C21.4319 20.0157 21.4319 20.649 21.0412 21.0397C20.8465 21.2344 20.5906 21.333 20.3346 21.333H20.3332C20.0772 21.333 19.8212 21.2357 19.6266 21.0397L14.9991 16.4122C13.7057 17.4073 12.0872 17.9997 10.3332 17.9997C6.10659 17.9997 2.6666 14.5597 2.6666 10.3331ZM11.3332 11.3331H13.3332C13.8852 11.3331 14.3332 10.8851 14.3332 10.3331C14.3332 9.78106 13.8852 9.33306 13.3332 9.33306H11.3332V7.33307C11.3332 6.78107 10.8852 6.33307 10.3332 6.33307C9.78124 6.33307 9.33325 6.78107 9.33325 7.33307V9.33306H7.33325C6.78125 9.33306 6.33325 9.78106 6.33325 10.3331C6.33325 10.8851 6.78125 11.3331 7.33325 11.3331H9.33325V13.3331C9.33325 13.8851 9.78124 14.333 10.3332 14.333C10.8852 14.333 11.3332 13.8851 11.3332 13.3331V11.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/icons/ZoomOutIcon.tsx
import { jsx as jsx190 } from "react/jsx-runtime";
var ZoomOutIcon = (props) => /* @__PURE__ */ jsx190(
  "svg",
  {
    ...props,
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx190(
      "path",
      {
        clipRule: "evenodd",
        d: "M2.66653 10.3331C2.66653 6.1064 6.10653 2.66641 10.3332 2.66641C14.5598 2.66641 17.9998 6.1064 17.9998 10.3331C17.9998 12.0863 17.4079 13.7042 16.4135 14.9974L21.0412 19.625C21.4318 20.0157 21.4318 20.649 21.0412 21.0397C20.8465 21.2344 20.5905 21.333 20.3345 21.333H20.3332C20.0772 21.333 19.8212 21.2357 19.6265 21.0397L14.999 16.4122C13.7056 17.4073 12.0871 17.9997 10.3332 17.9997C6.10653 17.9997 2.66653 14.5597 2.66653 10.3331ZM6.33319 10.3331C6.33319 10.8851 6.78119 11.3331 7.33319 11.3331H13.3332C13.8852 11.3331 14.3332 10.8851 14.3332 10.3331C14.3332 9.78106 13.8852 9.33306 13.3332 9.33306H7.33319C6.78119 9.33306 6.33319 9.78106 6.33319 10.3331Z",
        fill: "currentColor",
        fillRule: "evenodd"
      }
    )
  }
);

// src/components/Icon/iconMap.ts
var iconMap = {
  "ab-test": AbTestIcon,
  accessibility: AccessibilityIcon,
  activity: ActivityIcon,
  "add-layout": AddLayoutIcon,
  "add-media": AddMediaIcon,
  "annotation-link": AnnotationLinkIcon,
  appearance: AppearanceIcon,
  archive: ArchiveIcon,
  "arrow-down": ArrowDownIcon,
  "arrow-left": ArrowLeftIcon,
  "arrow-right": ArrowRightIcon,
  "arrow-up": ArrowUpIcon,
  "arrow-up-right": ArrowUpRightIcon,
  asterisk: AsteriskIcon,
  "audio-description": AudioDescriptionIcon,
  "auto-scroll": AutoScrollIcon,
  background: BackgroundIcon,
  bell: BellIcon,
  bolt: BoltIcon,
  calendar: CalendarIcon,
  "call-to-action": CallToActionIcon,
  camera: CameraIcon,
  "camera-off": CameraOffIcon,
  "caret-down": CaretDownIcon,
  "caret-down-strong": CaretDownStrongIcon,
  "caret-left": CaretLeftIcon,
  "caret-left-strong": CaretLeftStrongIcon,
  "caret-right": CaretRightIcon,
  "caret-right-strong": CaretRightStrongIcon,
  "caret-up": CaretUpIcon,
  "caret-up-strong": CaretUpStrongIcon,
  channel: ChannelIcon,
  chapters: ChaptersIcon,
  checkmark: CheckmarkIcon,
  "checkmark-circle": CheckmarkCircleIcon,
  "checkmark-circle-outline": CheckmarkCircleOutlineIcon,
  "checkmark-three-quaters-circle": CheckmarkThreeQuatersCircleIcon,
  "circle-slashed": CircleSlashedIcon,
  clapboard: ClapboardIcon,
  close: CloseIcon,
  "close-octagon": CloseOctagonIcon,
  "closed-captions": ClosedCaptionsIcon,
  collapse: CollapseIcon,
  "collapse-diagonal": CollapseDiagonalIcon,
  comments: CommentsIcon,
  company: CompanyIcon,
  compare: CompareIcon,
  "connection-okay": ConnectionOkayIcon,
  "connection-poor": ConnectionPoorIcon,
  "connection-strong": ConnectionStrongIcon,
  "content-library": ContentLibraryIcon,
  controls: ControlsIcon,
  "cursor-click": CursorClickIcon,
  delete: DeleteIcon,
  desktop: DesktopIcon,
  "dots-horizontal": DotsHorizontalIcon,
  download: DownloadIcon,
  "edit-transcript": EditTranscriptIcon,
  "ellipses-vertical": EllipsesVerticalIcon,
  embed: EmbedIcon,
  envelope: EnvelopeIcon,
  error: ErrorIcon,
  expand: ExpandIcon,
  "expand-diagonal": ExpandDiagonalIcon,
  "expand-horizontal": ExpandHorizontalIcon,
  "fast-forward": FastForwardIcon,
  favorite: FavoriteIcon,
  "favorite-outline": FavoriteOutlineIcon,
  "file-tree": FileTreeIcon,
  fill: FillIcon,
  "film-strip": FilmStripIcon,
  filter: FilterIcon,
  fit: FitIcon,
  fonts: FontsIcon,
  "full-screen": FullScreenIcon,
  gear: GearIcon,
  "getting-started": GettingStartedIcon,
  grid: GridIcon,
  "grip-dots-vertical": GripDotsVerticalIcon,
  "help-center": HelpCenterIcon,
  hide: HideIcon,
  home: HomeIcon,
  hourglass: HourglassIcon,
  import: ImportIcon,
  info: InfoIcon,
  integrations: IntegrationsIcon,
  leave: LeaveIcon,
  link: LinkIcon,
  list: ListIcon,
  live: LiveIcon,
  localization: LocalizationIcon,
  lock: LockIcon,
  logout: LogoutIcon,
  maximize: MaximizeIcon,
  media: MediaIcon,
  megaphone: MegaphoneIcon,
  menu: MenuIcon,
  mic: MicIcon,
  "mic-off": MicOffIcon,
  minimize: MinimizeIcon,
  minus: MinusIcon,
  "minus-circle": MinusCircleIcon,
  mobile: MobileIcon,
  "more-options": MoreOptionsIcon,
  "move-left": MoveLeftIcon,
  "move-right": MoveRightIcon,
  music: MusicIcon,
  "on-air": OnAirIcon,
  "open-new": OpenNewIcon,
  overview: OverviewIcon,
  page: PageIcon,
  "panel-left": PanelLeftIcon,
  "panel-right": PanelRightIcon,
  "panel-up": PanelUpIcon,
  password: PasswordIcon,
  pause: PauseIcon,
  pencil: PencilIcon,
  people: PeopleIcon,
  pin: PinIcon,
  "pin-slash": PinSlashIcon,
  pip: PipIcon,
  play: PlayIcon,
  player: PlayerIcon,
  plus: PlusIcon,
  podcast: PodcastIcon,
  preview: PreviewIcon,
  "private-chat": PrivateChatIcon,
  "private-user-sessions": PrivateUserSessionsIcon,
  project: ProjectIcon,
  properties: PropertiesIcon,
  "question-mark": QuestionMarkIcon,
  react: ReactIcon,
  record: RecordIcon,
  "record-group": RecordGroupIcon,
  redo: RedoIcon,
  refresh: RefreshIcon,
  replace: ReplaceIcon,
  reply: ReplyIcon,
  "request-video": RequestVideoIcon,
  "revert-to-original": RevertToOriginalIcon,
  rewind: RewindIcon,
  save: SaveIcon,
  "save-as-copy": SaveAsCopyIcon,
  scissors: ScissorsIcon,
  "screenshare-off": ScreenshareOffIcon,
  "screenshare-on": ScreenshareOnIcon,
  search: SearchIcon,
  send: SendIcon,
  shapes: ShapesIcon,
  share: ShareIcon,
  "sharing-permissions": SharingPermissionsIcon,
  simulcast: SimulcastIcon,
  sort: SortIcon,
  sparkle: SparkleIcon,
  speed: SpeedIcon,
  spinner: SpinnerIcon,
  stats: StatsIcon,
  "switch-accounts": SwitchAccountsIcon,
  tag: TagIcon,
  teleprompter: TeleprompterIcon,
  "text-size": TextSizeIcon,
  thumbnail: ThumbnailIcon,
  "thumbs-down": ThumbsDownIcon,
  "thumbs-up": ThumbsUpIcon,
  transcript: TranscriptIcon,
  transitions: TransitionsIcon,
  transparency: TransparencyIcon,
  trends: TrendsIcon,
  "trial-unlock-clock": TrialUnlockClockIcon,
  trim: TrimIcon,
  turnstile: TurnstileIcon,
  undo: UndoIcon,
  unlock: UnlockIcon,
  upload: UploadIcon,
  "users-permissions": UsersPermissionsIcon,
  "view-stream": ViewStreamIcon,
  volume: VolumeIcon,
  "volume-off": VolumeOffIcon,
  "volume-up": VolumeUpIcon,
  "volume-x": VolumeXIcon,
  wand: WandIcon,
  waveform: WaveformIcon,
  "zoom-in": ZoomInIcon,
  "zoom-out": ZoomOutIcon
};

// src/private/hooks/useResponsiveProp/useResponsiveProp.ts
import { isNotUndefined as isNotUndefined2, isRecord as isRecord2, isUndefined } from "@wistia/type-guards";
import { useMemo as useMemo2 } from "react";
var isResponsiveObject = (values) => {
  return typeof values === "object" && values !== null && !Array.isArray(values) && "base" in values;
};
var useResponsiveProp = (values) => {
  const activeMediaQueries = useActiveMq();
  return useMemo2(() => {
    if (isRecord2(values) && isResponsiveObject(values)) {
      const mq2 = activeMediaQueries.find((key) => key in values);
      return isNotUndefined2(mq2) && isNotUndefined2(values[mq2]) ? values[mq2] : values.base;
    }
    return values;
  }, [activeMediaQueries, values]);
};

// src/components/Icon/Icon.tsx
import { jsx as jsx191 } from "react/jsx-runtime";
var iconSizeMap = {
  sm: "12",
  md: "16",
  lg: "24",
  xl: "32",
  xxl: "48"
};
var StyledIcon = styled3.svg`
  ${({ $colorScheme }) => getColorScheme($colorScheme ?? "inherit")}
`;
var Icon = ({
  invertColor,
  colorScheme = "inherit",
  size = "lg",
  style,
  type,
  ...props
}) => {
  const responsiveSize = useResponsiveProp(size);
  if (isNil5(type)) {
    throw new Error("An Icon component requires a `type` prop to be provided");
  }
  if (isNil5(iconMap[type])) {
    throw new Error(`Type "${type}" does not exist, please update type prop in Icon component.`);
  }
  if (isNil5(iconSizeMap[responsiveSize])) {
    throw new Error(
      `Size "${responsiveSize}" does not exist, please update size prop in Icon component.`
    );
  }
  const IconElement = iconMap[type];
  const defaultStyle = {
    verticalAlign: "middle",
    ...style
  };
  const iconColor = invertColor ? "var(--wui-color-icon-on-fill)" : "var(--wui-color-icon)";
  return /* @__PURE__ */ jsx191(
    StyledIcon,
    {
      $colorScheme: colorScheme,
      "aria-hidden": "true",
      as: IconElement,
      height: `${iconSizeMap[responsiveSize]}px`,
      role: "presentation",
      style: defaultStyle,
      viewBox: "0 0 24 24",
      width: `${iconSizeMap[responsiveSize]}px`,
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      color: iconColor
    }
  );
};
Icon.displayName = "Icon";

// src/components/Link/Link.tsx
import { forwardRef } from "react";
import styled4 from "styled-components";
import { Link as RouterLink, useInRouterContext } from "react-router-dom";
import { isFunction as isFunction2, isNil as isNil6, isNotNil as isNotNil5, isNotUndefined as isNotUndefined3, isUndefined as isUndefined2 } from "@wistia/type-guards";
import { jsx as jsx192, jsxs as jsxs7 } from "react/jsx-runtime";
var generateHref = (href, type, disabled) => {
  if (disabled || isNil6(href)) {
    return void 0;
  }
  let modifiedHref = href;
  if (type === "phone") {
    modifiedHref = `tel:${href}`;
  }
  if (type === "email") {
    modifiedHref = `mailto:${href}`;
  }
  return modifiedHref;
};
var isButton = (props) => isUndefined2(props.href);
var isLink = (props) => isNotUndefined3(props.href);
var StyledLink = styled4.a`
  ${({ href }) => isNil6(href) && buttonResetCss};
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  cursor: ${({ $disabled }) => $disabled ? "not-allowed" : "pointer"};
  font-family: var(--wui-typography-family-default);
  color: ${({ $disabled }) => $disabled ? "var(--wui-color-text-disabled)" : "var(--wui-color-text-link)"};
  text-decoration: ${({ $underline }) => $underline ? "underline" : "none"};
  display: inline-flex;
  align-items: baseline;
  gap: var(--wui-space-01);

  && {
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;
var Link = forwardRef(
  ({
    beforeAction,
    children,
    type,
    disabled = false,
    colorScheme = "inherit",
    underline = false,
    leftIcon,
    rightIcon,
    "aria-disabled": ariaDisabled,
    role,
    ...props
  }, ref) => {
    const inRouterContext = useInRouterContext();
    const to = generateHref(props.href, type, disabled);
    const shouldUseReactRouterLink = inRouterContext && type !== "external" && !to?.startsWith("http");
    const handleClick = async (event) => {
      if (disabled) {
        event.preventDefault();
      } else if (beforeAction) {
        if (!shouldUseReactRouterLink) {
          event.preventDefault();
        }
        try {
          await beforeAction();
        } catch (error) {
          console.error(error);
        } finally {
          if (isFunction2(props.onClick)) {
            props.onClick(event);
          }
          if (!shouldUseReactRouterLink && isNotNil5(to)) {
            window.location.assign(to);
          }
        }
      } else if (isFunction2(props.onClick)) {
        props.onClick(event);
      } else {
      }
    };
    const commonProps = {
      $colorScheme: colorScheme,
      $disabled: disabled,
      $underline: underline,
      onClick: handleClick,
      "aria-disabled": ariaDisabled ?? (disabled ? "true" : void 0)
    };
    if (isButton(props)) {
      return /* @__PURE__ */ jsx192(
        StyledLink,
        {
          ref,
          as: "button",
          type: "button",
          ...props,
          ...commonProps,
          children
        }
      );
    }
    if (isLink(props)) {
      const externalLinkProps = type === "external" ? { target: "_blank", rel: "noopener noreferrer" } : null;
      const routerSpecificProps = shouldUseReactRouterLink ? {
        // TODO: Using 'any' here due to styled-components typing limitations with RouterLink
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        as: RouterLink,
        to: to ?? ""
      } : {
        as: "a",
        href: to
      };
      const combinedProps = {
        ref,
        ...props,
        ...commonProps,
        ...externalLinkProps,
        ...routerSpecificProps
      };
      return /* @__PURE__ */ jsxs7(
        StyledLink,
        {
          ...combinedProps,
          role: role ?? (disabled ? "link" : void 0),
          children: [
            leftIcon ?? null,
            children,
            rightIcon ?? null
          ]
        }
      );
    }
    return null;
  }
);
Link.displayName = "Link";

// src/components/Button/Button.tsx
import { Fragment, jsx as jsx193, jsxs as jsxs8 } from "react/jsx-runtime";
var isLink2 = (props) => isNotUndefined4(props.href);
var StyledButton = styled5.button`
  ${buttonResetCss}
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  ${({ $size }) => buttonSizeStyles[$size]}
  ${({ $variant }) => buttonVariantsStyles[$variant]}
  ${({ $unstyled }) => !$unstyled && buttonBaseStyles}
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
  text-align: center;
`;
var StyledButtonContent = styled5.div`
  align-items: center;
  display: inline-flex;
  flex-grow: 1;
  justify-content: center;
  opacity: ${({ $isLoading }) => $isLoading ? 0 : 1};
  padding: 0 var(--button-label-padding-horizontal);
  gap: var(--button-label-gap);

  ${({ $hasLeftIcon }) => $hasLeftIcon && "padding-left: 0;"}
  ${({ $hasRightIcon }) => $hasRightIcon && "padding-right: 0;"}
`;
var StyledButtonContentLabel = styled5.span`
  flex: ${({ $fullWidth }) => $fullWidth ? "initial" : "1"};
`;
var StyledButtonLoading = styled5.div`
  position: absolute;
  display: flex;
`;
var ButtonContent = ({
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  fullWidth = false
}) => {
  return /* @__PURE__ */ jsxs8(Fragment, { children: [
    isLoading ? /* @__PURE__ */ jsx193(StyledButtonLoading, { children: /* @__PURE__ */ jsx193(Icon, { type: "spinner" }) }) : null,
    /* @__PURE__ */ jsxs8(
      StyledButtonContent,
      {
        $hasLeftIcon: isNotNil6(leftIcon),
        $hasRightIcon: isNotNil6(rightIcon),
        $isLoading: isLoading,
        children: [
          leftIcon ?? null,
          /* @__PURE__ */ jsx193(StyledButtonContentLabel, { $fullWidth: fullWidth, children }),
          rightIcon ?? null
        ]
      }
    )
  ] });
};
var Button = forwardRef2(
  ({
    children,
    forceState,
    className,
    leftIcon,
    rightIcon,
    colorScheme = "inherit",
    disabled = false,
    isLoading = false,
    unstyled = false,
    onClick,
    fullWidth = false,
    size = "md",
    variant = "solid",
    ...props
  }, ref) => {
    const responsiveFullWidth = useResponsiveProp(fullWidth);
    const responsiveSize = useResponsiveProp(size);
    const isAriaDisabled = () => isLoading || disabled;
    const commonProps = {
      "aria-disabled": isAriaDisabled() || void 0,
      $colorScheme: colorScheme,
      $fullWidth: responsiveFullWidth,
      $size: responsiveSize,
      $unstyled: unstyled,
      $variant: variant,
      className
    };
    if (forceState) {
      commonProps.className = `${className ?? ""} force-state-${forceState}`;
    }
    if (isLink2(props)) {
      return /* @__PURE__ */ jsx193(
        StyledButton,
        {
          ref,
          as: Link,
          disabled: isAriaDisabled(),
          ...props,
          ...commonProps,
          children: unstyled ? children : /* @__PURE__ */ jsx193(
            ButtonContent,
            {
              fullWidth: responsiveFullWidth,
              isLoading,
              leftIcon,
              rightIcon,
              children
            }
          )
        }
      );
    }
    const handleClick = (event) => {
      if (isAriaDisabled()) {
        event.preventDefault();
        return;
      }
      if (isNotNil6(onClick)) {
        onClick(event);
      }
    };
    return /* @__PURE__ */ jsx193(
      StyledButton,
      {
        ref,
        onClick: handleClick,
        type: "button",
        ...props,
        ...commonProps,
        children: unstyled ? children : /* @__PURE__ */ jsx193(
          ButtonContent,
          {
            fullWidth: responsiveFullWidth,
            isLoading,
            leftIcon,
            rightIcon,
            children
          }
        )
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ActionButton/ActionButton.tsx
import { jsx as jsx194, jsxs as jsxs9 } from "react/jsx-runtime";
var StyledActionButton = styled6(Button)`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  display: grid;
  grid-template-columns: 24px 1fr;
  grid-template-rows: 24px auto;
  gap: var(--wui-space-02);
  width: fit-content;
  min-width: 96px;
  padding: var(--wui-space-03);
  background: var(--wui-color-bg-surface-secondary);
  grid-row-gap: var(--wui-space-02);
  border-radius: var(--wui-border-radius-03);
  flex: 0 0 auto;
  transition: background-color var(--wui-motion-duration-02) var(--wui-motion-ease);

  &:focus {
    outline: none;
  }

  &:hover,
  :focus-visible {
    ${({ $hoverColorScheme }) => getColorScheme($hoverColorScheme)};
    background: var(--wui-color-bg-surface-tertiary);
  }

  &:focus-visible {
    outline: 2px solid var(--wui-color-focus-ring);
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    background: var(--wui-color-bg-surface-disabled);
    color: var(--wui-color-text-disabled);
    outline-color: var(--wui-color-focus-ring-disabled);
    cursor: not-allowed;

    && {
      svg {
        color: var(--wui-color-icon-disabled);
      }
    }
  }
`;
var StyledMainIcon = styled6.div`
  grid-column: 1;
  grid-row: 1;
  width: 24px;
  height: 24px;
  transition: color var(--wui-motion-duration-02) var(--wui-motion-ease);

  && {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;
var StyledSecondaryIcon = styled6.div`
  grid-column: 2;
  grid-row: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  transition: all var(--wui-motion-duration-02) var(--wui-motion-ease-in);

  ${({ $hideUntilHover }) => $hideUntilHover && css17`
      will-change: transform;
      opacity: 0;
      transform: translateX(-16px);
      ${StyledActionButton}:hover:not([aria-disabled="true"]) &,
  ${StyledActionButton}:focus-visible:not([aria-disabled="true"]) & {
        transform: translateX(0);
        opacity: 1;
      }
    `};

  && {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;
var StyledLabel = styled6.span`
  font-family: var(--wui-typography-heading-4-family);
  font-size: var(--wui-typography-heading-4-size);
  font-weight: var(--wui-typography-heading-4-weight);
  line-height: var(--wui-typography-heading-4-line-height);
  color: ${({ $disabled }) => $disabled ? "var(--wui-color-text-disabled)" : "var(--wui-color-text)"};
  grid-column: 1 / -1;
  grid-row: 2;
  text-align: left;
`;
var ActionButton = forwardRef3(
  ({
    icon,
    colorScheme = "default",
    disabled = false,
    hoverColorScheme,
    variant = "default",
    children,
    ...props
  }, ref) => {
    const resolvedHoverColorScheme = hoverColorScheme ?? (variant === "gated" ? "purple" : "inherit");
    const secondaryIconMap = {
      default: "arrow-right",
      gated: "sparkle",
      "menu-up": "caret-up",
      "menu-down": "caret-down"
    };
    return /* @__PURE__ */ jsxs9(
      StyledActionButton,
      {
        ...props,
        ref,
        $colorScheme: colorScheme,
        $hoverColorScheme: resolvedHoverColorScheme,
        disabled,
        unstyled: true,
        children: [
          /* @__PURE__ */ jsx194(StyledMainIcon, { children: icon }),
          /* @__PURE__ */ jsx194(StyledSecondaryIcon, { $hideUntilHover: variant === "default", children: /* @__PURE__ */ jsx194(
            Icon,
            {
              colorScheme: variant === "gated" ? "purple" : "inherit",
              type: secondaryIconMap[variant]
            }
          ) }),
          /* @__PURE__ */ jsx194(StyledLabel, { $disabled: disabled, children })
        ]
      }
    );
  }
);
ActionButton.displayName = "ActionButton";

// src/components/Avatar/Avatar.tsx
import { useState as useState8, useMemo as useMemo3, useEffect as useEffect7 } from "react";
import { isNil as isNil8, isNotNil as isNotNil8 } from "@wistia/type-guards";
import styled9 from "styled-components";

// src/components/Avatar/formatNameForDisplay.tsx
import { isNil as isNil7, isString, isEmptyString } from "@wistia/type-guards";
var containsEmojiCharacter = (char) => {
  const emojiRegex = /<a?:.+?:\d{18}>|\p{Extended_Pictographic}/gu;
  return emojiRegex.test(char);
};
var formatNameForDisplay = (name) => {
  if (isNil7(name) || !isString(name) || isEmptyString(name) || containsEmojiCharacter(name)) {
    return "U";
  }
  const firstChar = name.trim().substring(0, 1);
  return firstChar.toUpperCase();
};

// src/components/Image/Image.tsx
import styled7 from "styled-components";
import { isNotNil as isNotNil7 } from "@wistia/type-guards";
import { jsx as jsx195 } from "react/jsx-runtime";
var getFillStyle = (fillContainer) => {
  if (fillContainer === "horizontal") {
    return "width: 100%;";
  }
  if (fillContainer === "vertical") {
    return "height: 100%;";
  }
  if (fillContainer === true) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return void 0;
};
var StyledImage = styled7.img`
  border-radius: ${({ $borderRadius }) => isNotNil7($borderRadius) ? `var(--wui-${$borderRadius})` : void 0};
  ${({ $fillContainer }) => getFillStyle($fillContainer)};
  object-fit: ${({ $objFit }) => $objFit};
  object-position: ${({ $objPosition }) => $objPosition};
`;
var Image = ({
  src,
  alt,
  borderRadius,
  fill: fillContainer = false,
  fit: objFit,
  loading = "lazy",
  position: objPosition,
  onLoad,
  onError,
  ...props
}) => /* @__PURE__ */ jsx195(
  StyledImage,
  {
    $borderRadius: borderRadius,
    $fillContainer: fillContainer,
    $objFit: objFit,
    $objPosition: objPosition,
    alt,
    loading,
    onError,
    onLoad,
    src,
    ...props
  }
);
Image.displayName = "Image";

// src/components/ColorSchemeWrapper/ColorSchemeWrapper.tsx
import styled8 from "styled-components";
import { jsx as jsx196 } from "react/jsx-runtime";
var defaultColorSchemeOptions = ["default", "inherit"];
var semanticColorSchemeOptions = ["error", "info", "success", "warning"];
var brandColorSchemeOptions = [
  "blue",
  "green",
  "orange",
  "pink",
  "purple",
  "yellow"
];
var vendorColorSchemeOptions = [
  "vendor-hubspot",
  "vendor-marketo",
  "vendor-pardot"
];
var colorSchemeOptions = [
  ...defaultColorSchemeOptions,
  ...semanticColorSchemeOptions,
  ...brandColorSchemeOptions,
  ...vendorColorSchemeOptions
];
var StyledColorSchemeWrapper = styled8.div`
  ${({ $colorScheme, $nav }) => getColorScheme($nav ? "nav" : $colorScheme)};
`;
var ColorSchemeWrapper = ({
  __nav = false,
  colorScheme = "inherit",
  children,
  ...props
}) => /* @__PURE__ */ jsx196(
  StyledColorSchemeWrapper,
  {
    $colorScheme: colorScheme,
    $nav: __nav,
    ...props,
    children
  }
);
ColorSchemeWrapper.displayName = "ColorSchemeWrapper";

// src/components/Avatar/Avatar.tsx
import { jsx as jsx197, jsxs as jsxs10 } from "react/jsx-runtime";
var avatarSizeMap = {
  sm: 16,
  md: 24,
  lg: 32
};
var Initial = ({ initial }) => /* @__PURE__ */ jsxs10(
  "svg",
  {
    "data-wui-avatar-initial": true,
    style: { width: "100%", height: "100%", color: "var(--wui-color-text-on-fill)" },
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx197(
        "path",
        {
          d: "M0 13.5h24",
          id: "P"
        }
      ),
      /* @__PURE__ */ jsx197("text", { children: /* @__PURE__ */ jsx197(
        "textPath",
        {
          dominantBaseline: "middle",
          fill: "currentColor",
          fontSize: "16px",
          href: "#P",
          startOffset: "50%",
          textAnchor: "middle",
          children: initial
        }
      ) })
    ]
  }
);
var AvatarWrapper = styled9.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  width: ${({ $heightAndWidth }) => $heightAndWidth}px;
  height: ${({ $heightAndWidth }) => $heightAndWidth}px;
  place-content: center;
  overflow: hidden;
  background-color: var(--wui-color-bg-fill);
  border-radius: var(--wui-border-radius-rounded);
  display: grid;
  user-select: none;

  > * {
    grid-area: 1 / 1;
  }

  --wui-avatar-inset-opacity: 10%;

  outline: 2px solid rgb(0 0 0 / var(--wui-avatar-inset-opacity));
  outline-offset: -2px;

  [data-wui-avatar-initial] {
    font-family: var(--wui-typography-family-default);
    font-weight: var(--wui-typography-weight-label-bold);
    padding: min(12.5%, 4px);
  }

  &:only-child {
    a:hover & {
      --wui-avatar-inset-opacity: 20%;

      background-color: var(--wui-color-bg-fill-hover);
    }

    a:active & {
      --wui-avatar-inset-opacity: 30%;

      background-color: var(--wui-color-bg-fill-active);
    }
  }
`;
var chooseColorScheme = (name) => {
  if (isNil8(name) || name === "Anonymous") {
    return "blue";
  }
  const multiplier = 31;
  const hashCode = (str) => Array.from(str).reduce((sum, char) => Math.imul(multiplier, sum) + char.charCodeAt(0) | 0, 0);
  const index = Math.abs(hashCode(name)) % brandColorSchemeOptions.length;
  return brandColorSchemeOptions[index] ?? "blue";
};
var Avatar = ({
  imageUrl,
  heightAndWidth,
  name,
  size = "md",
  onImageLoad,
  ...props
}) => {
  const [imageLoadState, setImageLoadState] = useState8("loading");
  useEffect7(() => {
    setImageLoadState("loading");
  }, [imageUrl]);
  const handleImageLoad = () => {
    setImageLoadState("loaded");
    onImageLoad?.({ state: "loaded", type: "image" });
  };
  const handleImageLoadError = () => {
    setImageLoadState("error");
    onImageLoad?.({ state: "error", type: "initials" });
  };
  const avatarSize = heightAndWidth ?? avatarSizeMap[size];
  const avatarColor = useMemo3(() => chooseColorScheme(name), [name]);
  return /* @__PURE__ */ jsxs10(
    AvatarWrapper,
    {
      $colorScheme: avatarColor,
      $heightAndWidth: avatarSize,
      ...props,
      children: [
        /* @__PURE__ */ jsx197(Initial, { initial: formatNameForDisplay(name) }),
        isNotNil8(imageUrl) && imageLoadState !== "error" && /* @__PURE__ */ jsx197(
          Image,
          {
            alt: isNotNil8(name) ? name : "",
            "data-state": imageLoadState,
            fit: "cover",
            onError: handleImageLoadError,
            onLoad: handleImageLoad,
            src: imageUrl
          }
        )
      ]
    }
  );
};
Avatar.displayName = "Avatar";

// src/components/Badge/Badge.tsx
import { forwardRef as forwardRef4 } from "react";
import styled10 from "styled-components";
import { isNotNil as isNotNil9 } from "@wistia/type-guards";
import { jsx as jsx198, jsxs as jsxs11 } from "react/jsx-runtime";
var StyledBadge = styled10.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  align-items: center;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-rounded);
  color: var(--wui-color-text);
  display: inline-flex;
  font-size: var(--wui-typography-label-4-size);
  font-weight: var(--wui-typography-label-4-weight);
  gap: var(--wui-space-01);
  justify-content: center;
  padding: var(--wui-space-01) var(--wui-space-02);
  width: fit-content;

  /* Icon component size */
  svg {
    height: 12px;
    width: 12px;
  }
`;
var Badge = forwardRef4(
  ({ colorScheme = "inherit", label, icon, ...props }, ref) => {
    const hasIcon = isNotNil9(icon);
    return /* @__PURE__ */ jsxs11(
      StyledBadge,
      {
        ref,
        ...props,
        $colorScheme: colorScheme,
        $hasIcon: hasIcon,
        children: [
          hasIcon ? icon : null,
          /* @__PURE__ */ jsx198("span", { children: label })
        ]
      }
    );
  }
);
Badge.displayName = "Badge";

// src/components/Box/Box.tsx
import { Children, cloneElement as cloneElement2, forwardRef as forwardRef5 } from "react";
import styled11, { css as css18 } from "styled-components";
import { isNotNil as isNotNil10, isNil as isNil9, isRecord as isRecord3, isNotUndefined as isNotUndefined5 } from "@wistia/type-guards";

// src/private/helpers/makePolymorphic/makePolymorphic.tsx
var makePolymorphic = (component) => {
  return component;
};

// src/components/Box/Box.tsx
import { jsx as jsx199 } from "react/jsx-runtime";
var isDev = process.env["NODE_ENV"] === "development" || process.env["NODE_ENV"] === "test";
var getGapStyle = (gap) => {
  if (isNotNil10(gap)) {
    if (isRecord3(gap)) {
      return Object.entries(gap).map(([key, value]) => {
        return `${key}-gap: var(--wui-${value})};`;
      }).join("");
    }
    return `gap: var(--wui-${gap.toString()})};`;
  }
  return void 0;
};
var getFillStyle2 = (fill) => {
  if (fill === "horizontal") {
    return "width: 100%;";
  }
  if (fill === "vertical") {
    return "height: 100%;";
  }
  if (fill) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return void 0;
};
var getFillViewportStyle = (fillViewport) => {
  if (fillViewport === "horizontal") {
    return "width: 100vw;";
  }
  if (fillViewport === "vertical") {
    return "height: 100vh;";
  }
  if (fillViewport) {
    return `
      width: 100vw;
      height: 100vh;
    `;
  }
  return void 0;
};
var getFlexStyle = (flexMode) => {
  if (!flexMode) return null;
  switch (flexMode) {
    // grows to fill space, won't shrink, starts at 0
    case "grow":
      return css18`
        flex: 1 0 0;
      `;
    // default behavior, can shrink but won't grow
    case "initial":
      return css18`
        flex: 0 1 auto;
      `;
    // equal distribution of space
    case "equal":
      return css18`
        flex: 1;
      `;
    // won't grow or shrink
    case "rigid":
      return css18`
        flex: 0 0 auto;
      `;
    // can grow and shrink from content size
    case "fluid":
      return css18`
        flex: 1 1 auto;
      `;
    default:
      return null;
  }
};
var StyledBoxComponent = styled11.div`
  align-content: ${({ $alignContent }) => $alignContent};
  align-items: ${({ $alignItems }) => $alignItems};
  align-self: ${({ $alignSelf }) => $alignSelf ?? null};
  display: ${({ $inline }) => isNotNil10($inline) && $inline ? "inline-flex" : "flex"};
  flex-basis: ${({ $basis }) => isNotNil10($basis) ? $basis : null};
  flex-direction: ${({ $flexDirection }) => $flexDirection};
  ${({ $fillBox: fill }) => getFillStyle2(fill)};
  ${({ $fillViewport }) => getFillViewportStyle($fillViewport)};
  ${({ $flexMode }) => getFlexStyle($flexMode)};
  ${({ $gap }) => getGapStyle($gap)};
  height: ${({ $height }) => $height ?? null};
  width: ${({ $width }) => $width ?? null};

  /* Box children styles */
  flex-grow: ${({ $grow }) => isNotNil10($grow) ? $grow : null};
  flex-shrink: ${({ $shrink }) => isNotNil10($shrink) ? $shrink : null};
  flex-wrap: ${({ $wrapItems }) => $wrapItems ? "wrap" : "nowrap"};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  order: ${({ $order }) => isNotNil10($order) ? $order : null};
`;
var wrapChildren = (children) => {
  if (isNotNil10(children)) {
    if (typeof children === "object" && isDev) {
      return Children.map(children, (child) => {
        if (isNil9(child)) return null;
        const elementParams = {};
        if (child.type?.displayName === "Box" || child.type?.displayName === "Box_UI") {
          elementParams.hasBoxParent = true;
        }
        return cloneElement2(child, elementParams);
      });
    }
    return children;
  }
  return null;
};
var DEFAULT_ELEMENT = "div";
var BoxComponent = forwardRef5(
  ({
    alignContent = "stretch",
    alignItems = "flex-start",
    alignSelf,
    basis,
    children,
    direction = "row",
    fill = false,
    fillViewport = false,
    gap,
    grow,
    hasBoxParent = false,
    // eslint-disable-line @typescript-eslint/no-unused-vars
    height,
    inline = false,
    justifyContent = "flex-start",
    order,
    renderAs,
    shrink,
    width,
    wrapItems = false,
    flexMode,
    ...props
  }, ref) => {
    const responsiveAlignContent = useResponsiveProp(alignContent);
    const responsiveAlignItems = useResponsiveProp(alignItems);
    const responsiveDirection = useResponsiveProp(direction);
    const responsiveFill = useResponsiveProp(fill);
    const responsiveGap = useResponsiveProp(gap);
    const responsiveJustifyContent = useResponsiveProp(justifyContent);
    if (isNotUndefined5(height)) {
      if (responsiveFill === true || responsiveFill === "vertical") {
        throw new Error('Cannot use height prop with fill="vertical" or fill={true}');
      }
      if (fillViewport === true || fillViewport === "vertical") {
        throw new Error(
          'Cannot use height prop with fillViewport="vertical" or fillViewport={true}'
        );
      }
    }
    if (isNotUndefined5(width)) {
      if (responsiveFill === true || responsiveFill === "horizontal") {
        throw new Error('Cannot use width prop with fill="horizontal" or fill={true}');
      }
      if (fillViewport === true || fillViewport === "horizontal") {
        throw new Error(
          'Cannot use width prop with fillViewport="horizontal" or fillViewport={true}'
        );
      }
    }
    return /* @__PURE__ */ jsx199(
      StyledBoxComponent,
      {
        ref,
        $alignContent: responsiveAlignContent,
        $alignItems: responsiveAlignItems,
        $alignSelf: alignSelf,
        $basis: basis,
        $fillBox: responsiveFill,
        $fillViewport: fillViewport,
        $flexDirection: responsiveDirection,
        $flexMode: flexMode,
        $gap: responsiveGap,
        $grow: grow,
        $height: height,
        $inline: inline,
        $justifyContent: responsiveJustifyContent,
        $order: order,
        $shrink: shrink,
        $width: width,
        $wrapItems: wrapItems,
        as: renderAs ?? DEFAULT_ELEMENT,
        ...props,
        children: wrapChildren(children)
      }
    );
  }
);
BoxComponent.displayName = "Box";
var Box = makePolymorphic(BoxComponent);

// src/components/Breadcrumbs/Breadcrumbs.tsx
import { Children as Children2 } from "react";
import styled12 from "styled-components";
import { Fragment as Fragment2, jsx as jsx200, jsxs as jsxs12 } from "react/jsx-runtime";
var StyledBreadcrumbs = styled12.nav`
  display: flex;
  align-items: center;
  gap: var(--wui-space-01);
  overflow: hidden;

  > [data-wui-breadcrumb-divider]:last-of-type {
    display: none;
  }
`;
var BUFFER_WIDTH = 10;
var Breadcrumbs = ({ children, ...props }) => {
  const { isXsAndDown } = useMq();
  let crumbs = Children2.toArray(children);
  if (isXsAndDown) {
    crumbs = crumbs.slice(-1);
  }
  const crumbMaxWidth = Math.floor(100 / crumbs.length) - BUFFER_WIDTH;
  return /* @__PURE__ */ jsxs12(Fragment2, { children: [
    /* @__PURE__ */ jsx200("style", { type: "text/css", children: `
       [data-wui-breadcrumbs] {
          container-type: inline-size;
          container-name: breadcrumbs;
        }

        @container breadcrumbs (width < 100vw) {
          [data-wui-breadcrumb] {
              max-width: ${crumbMaxWidth}cqw;
          }
        }
     ` }),
    /* @__PURE__ */ jsx200(
      StyledBreadcrumbs,
      {
        "aria-label": "Breadcrumbs",
        "data-wui-breadcrumbs": true,
        ...props,
        children: crumbs
      }
    )
  ] });
};
Breadcrumbs.displayName = "Breadcrumbs";

// src/components/Breadcrumbs/Breadcrumb.tsx
import styled13 from "styled-components";
import { Fragment as Fragment3, jsx as jsx201, jsxs as jsxs13 } from "react/jsx-runtime";
var BreadcrumbContent = styled13.span`
  overflow: hidden;
  white-space: nowrap;
  display: block;
  text-overflow: ellipsis;
  text-align: left;
  flex: 0;
`;
var Breadcrumb = ({ icon, href, children, ...props }) => {
  return /* @__PURE__ */ jsxs13(Fragment3, { children: [
    /* @__PURE__ */ jsx201(
      Button,
      {
        ...props,
        href,
        leftIcon: icon,
        variant: "ghost",
        children: /* @__PURE__ */ jsx201(BreadcrumbContent, { "data-wui-breadcrumb": true, children })
      }
    ),
    /* @__PURE__ */ jsx201("div", { "data-wui-breadcrumb-divider": true, children: /* @__PURE__ */ jsx201(
      Icon,
      {
        size: "sm",
        style: { flex: "0 1 auto" },
        type: "caret-right"
      }
    ) })
  ] });
};

// src/components/ButtonGroup/ButtonGroup.tsx
import styled14 from "styled-components";
import { isNil as isNil10 } from "@wistia/type-guards";
import { jsx as jsx202 } from "react/jsx-runtime";
var getAlignment = (align) => {
  if (align === "center") {
    return "center";
  }
  if (align === "left") {
    return "flex-start";
  }
  if (align === "right") {
    return "flex-end";
  }
  return "flex-start";
};
var ButtonGroupComponent = styled14.div`
  display: flex;

  /* this helps ensure that primary buttons appear at the top of the column */
  flex-direction: ${({ $collapse, $align }) => {
  if (!$collapse) {
    return "row";
  }
  return $align === "right" ? "column-reverse" : "column";
}};
  gap: var(--wui-space-02);
  align-items: center;
  justify-content: ${({ $align }) => getAlignment($align)};
  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};

  ${mq.smAndUp} {
    flex-direction: row;
  }

  :not([data-wistia-ui-icon-button]) {
    ${mq.smAndDown} {
      width: 100%;
    }
  }
`;
var ButtonGroup = ({
  children,
  align = "left",
  fullWidth = false,
  collapseOnSmallScreens = true,
  ...props
}) => {
  if (isNil10(children)) {
    return null;
  }
  return /* @__PURE__ */ jsx202(
    ButtonGroupComponent,
    {
      $align: align,
      $collapse: collapseOnSmallScreens,
      $fullWidth: fullWidth,
      ...props,
      children
    }
  );
};
ButtonGroup.displayName = "ButtonGroup";

// src/components/Card/Card.tsx
import styled15 from "styled-components";
import { jsx as jsx203 } from "react/jsx-runtime";
var StyledCard = styled15(Box)`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  box-sizing: border-box;
  padding: ${({ $paddingSize }) => `var(--wui-${$paddingSize})`};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  outline: 2px solid ${({ $borderColor }) => $borderColor};
  outline-offset: -2px;
  border-radius: ${({ $borderRadius }) => `var(--wui-${$borderRadius})`};
  display: flex;
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
`;
var prominenceMap = {
  default: {
    backgroundColor: "var(--wui-color-bg-surface)",
    borderColor: "var(--wui-color-border)"
  },
  secondary: {
    backgroundColor: "var(--wui-color-bg-surface-secondary)",
    borderColor: "var(--wui-color-border-secondary)"
  },
  tertiary: {
    backgroundColor: "var(--wui-color-bg-surface-tertiary)",
    borderColor: "var(--wui-color-border-secondary)"
  }
};
var Card = ({
  children,
  border = false,
  borderRadius = "border-radius-04",
  colorScheme = "inherit",
  direction = "column",
  gap = "space-03",
  height,
  paddingSize = "space-04",
  prominence = "secondary",
  width,
  ...props
}) => /* @__PURE__ */ jsx203(
  StyledCard,
  {
    $backgroundColor: prominenceMap[prominence].backgroundColor,
    $borderColor: border ? prominenceMap[prominence].borderColor : "transparent",
    $borderRadius: borderRadius,
    $colorScheme: colorScheme,
    $height: height,
    $paddingSize: paddingSize,
    $width: width,
    direction,
    gap,
    ...props,
    children
  }
);
Card.displayName = "Card";

// src/components/Center/Center.tsx
import { forwardRef as forwardRef6 } from "react";
import styled16 from "styled-components";
import { jsx as jsx204 } from "react/jsx-runtime";
var StyledCenter = styled16.div`
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  max-width: ${({ $maxWidth }) => $maxWidth};
  ${({ $gutterWidth }) => $gutterWidth && `padding: 0 var(--wui-${$gutterWidth.toString()});`}
  ${({ $intrinsic }) => $intrinsic && `
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;
var Center = forwardRef6(
  ({ maxWidth = "100%", gutterWidth = "space-00", intrinsic = false, children, ...props }, ref) => /* @__PURE__ */ jsx204(
    StyledCenter,
    {
      ref,
      $gutterWidth: gutterWidth,
      $intrinsic: intrinsic,
      $maxWidth: maxWidth,
      ...props,
      children
    }
  )
);
Center.displayName = "Center";

// src/components/Checkbox/Checkbox.tsx
import { forwardRef as forwardRef7, useId } from "react";
import styled20, { css as css21 } from "styled-components";
import { isNonEmptyString as isNonEmptyString2 } from "@wistia/type-guards";

// src/private/components/FormControlLabel/FormControlLabel.tsx
import styled19, { css as css20 } from "styled-components";
import { isNil as isNil12, isNotNil as isNotNil12 } from "@wistia/type-guards";

// src/private/components/FormControlLabel/FormControlLabelDescription.tsx
import styled17, { css as css19 } from "styled-components";
import { isNil as isNil11 } from "@wistia/type-guards";
import { jsx as jsx205 } from "react/jsx-runtime";
var disabledStyle = css19`
  color: var(--wui-color-text-disabled);
`;
var StyledFormControlLabelDescription = styled17.div`
  color: var(--wui-color-text-secondary);
  display: block;
  font-size: var(--wui-typography-body-4-size);
  font-weight: var(--wui-typography-body-4-weight);
  line-height: var(--wui-typography-body-4-line-height);
  margin-top: var(--wui-space-01);

  ${({ $disabled }) => $disabled && disabledStyle}
`;
var FormControlLabelDescription = ({
  children,
  disabled = false,
  ...props
}) => {
  if (isNil11(children)) {
    return null;
  }
  return /* @__PURE__ */ jsx205(
    StyledFormControlLabelDescription,
    {
      ...props,
      $disabled: disabled,
      children
    }
  );
};
FormControlLabelDescription.displayName = "FormControlLabelDescription";

// src/components/ScreenReaderOnly/ScreenReaderOnly.tsx
import styled18 from "styled-components";
import { isNotNil as isNotNil11 } from "@wistia/type-guards";
import { jsx as jsx206 } from "react/jsx-runtime";
var VisuallyHidden = styled18.div({ ...visuallyHiddenStyle });
var VisuallyHiddenButFocusable = styled18.div({
  "&:not(:focus-within)": visuallyHiddenStyle
});
var ScreenReaderOnly = ({
  text,
  children,
  focusable = false,
  ...props
}) => {
  const accessibleText = isNotNil11(text) ? text : children;
  if (focusable) {
    return /* @__PURE__ */ jsx206(VisuallyHiddenButFocusable, { ...props, children: accessibleText });
  }
  return /* @__PURE__ */ jsx206(VisuallyHidden, { ...props, children: accessibleText });
};
ScreenReaderOnly.displayName = "ScreenReaderOnly";

// src/private/components/FormControlLabel/FormControlLabel.tsx
import { jsx as jsx207, jsxs as jsxs14 } from "react/jsx-runtime";
var disabledStyle2 = css20`
  cursor: not-allowed;
  color: var(--wui-color-text-disabled);
`;
var StyledLabelWrapper = styled19.div`
  display: flex;
  flex-direction: column;
`;
var StyledFormControlLabel = styled19.label`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--wui-space-01);
  width: 100%;
  color: var(--wui-color-tex);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  line-height: var(--wui-typography-label-3-line-height);

  ${({ $disabled }) => $disabled && disabledStyle2}
`;
var FormControlLabel = ({
  label,
  description,
  disabled = false,
  controlSlot,
  htmlFor,
  hideLabel = false,
  ...props
}) => {
  if (isNil12(label)) {
    return null;
  }
  const labelContent = hideLabel ? /* @__PURE__ */ jsx207(ScreenReaderOnly, { children: label }) : /* @__PURE__ */ jsxs14(StyledLabelWrapper, { children: [
    label,
    /* @__PURE__ */ jsx207(FormControlLabelDescription, { children: description })
  ] });
  return /* @__PURE__ */ jsxs14(
    StyledFormControlLabel,
    {
      $disabled: disabled,
      htmlFor,
      ...props,
      children: [
        isNotNil12(controlSlot) ? controlSlot : null,
        labelContent
      ]
    }
  );
};
FormControlLabel.displayName = "FormControlLabel";

// src/components/Checkbox/Checkbox.tsx
import { jsx as jsx208, jsxs as jsxs15 } from "react/jsx-runtime";
var CheckIcon = () => /* @__PURE__ */ jsx208(
  "svg",
  {
    fill: "inherit",
    height: "8",
    viewBox: "0 0 10 8",
    width: "10",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx208(
      "path",
      {
        d: "M3.47281 7.19303L0.377565 4.07999C0.191609 3.89297 0.191609 3.58973 0.377565 3.40268L1.05098 2.72537C1.23694 2.53833 1.53847 2.53833 1.72442 2.72537L3.80953 4.82245L8.27558 0.330729C8.46154 0.143704 8.76306 0.143704 8.94902 0.330729L9.62244 1.00804C9.8084 1.19506 9.8084 1.49831 9.62244 1.68535L4.14624 7.19305C3.96027 7.38008 3.65876 7.38008 3.47281 7.19303Z",
        fill: "currentColor"
      }
    )
  }
);
var sizeSmall = css21`
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;

  svg {
    width: 7px;
    height: 7px;
  }
`;
var sizeMedium = css21`
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;

  svg {
    width: 9px;
    height: 9px;
  }
`;
var sizeLarge = css21`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;

  svg {
    width: 11px;
    height: 11px;
  }
`;
var getSizeCss = (size) => {
  if (size === "sm") return sizeSmall;
  if (size === "lg") return sizeLarge;
  return sizeMedium;
};
var StyledCheckboxWrapper = styled20.div`
  display: flex;
  margin: 0;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;
var StyledCheckboxInput = styled20.div`
  ${({ $size }) => getSizeCss($size)}
  line-height: 0;
  margin: 0;
  border-radius: var(--wui-border-radius-01);
  display: grid;
  place-content: center;
  align-self: flex-start;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $disabled }) => $disabled ? "var(--wui-color-border-disabled)" : "var(--wui-color-border)"};
  background-color: ${({ $disabled }) => $disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-surface)"};

  /* checkmark icon */
  svg {
    display: none;

    path {
      fill: ${({ $disabled }) => $disabled ? "var(--wui-color-icon-disabled)" : "var(--wui-color-bg-fill-white)"};
    }
  }
`;
var StyledHiddenCheckboxInput = styled20.input`
  ${visuallyHiddenStyle}

  /* toggles display of faux-input background-color */
  &:checked + label [data-wui-faux-input='true'] {
    border-color: ${({ disabled }) => disabled ? void 0 : "var(--wui-color-bg-fill)"};
    background-color: ${({ disabled }) => disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-fill)"};
  }

  /* toggles display of CheckIcon */
  &:checked + label svg {
    display: block;
  }
`;
var Checkbox = forwardRef7(
  ({
    checked,
    disabled = false,
    id,
    label,
    description,
    name,
    onChange,
    size = "md",
    value,
    required = false,
    hideLabel = false,
    ...props
  }, ref) => {
    const generatedId = useId();
    const computedId = isNonEmptyString2(id) ? id : `wistia-ui-checkbox-${generatedId}`;
    return /* @__PURE__ */ jsxs15(StyledCheckboxWrapper, { disabled, children: [
      /* @__PURE__ */ jsx208(
        StyledHiddenCheckboxInput,
        {
          ...props,
          ref,
          checked,
          disabled,
          id: computedId,
          name,
          onChange,
          required,
          type: "checkbox",
          value
        }
      ),
      /* @__PURE__ */ jsx208(
        FormControlLabel,
        {
          controlSlot: /* @__PURE__ */ jsx208(
            StyledCheckboxInput,
            {
              $disabled: disabled,
              $size: size,
              "data-wui-faux-input": "true",
              children: /* @__PURE__ */ jsx208(CheckIcon, {})
            }
          ),
          description,
          disabled,
          hideLabel,
          htmlFor: computedId,
          label
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";

// src/components/ClickRegion/ClickRegion.tsx
import { Children as Children3, cloneElement as cloneElement3, useCallback as useCallback8, useEffect as useEffect8 } from "react";
var ClickRegion = ({ children, targetRef }) => {
  useEffect8(() => {
    if (targetRef.current && targetRef.current.tagName === "A") {
      targetRef.current.setAttribute("data-click-region-target-link", "");
    } else if (targetRef.current && targetRef.current.tagName === "BUTTON") {
      targetRef.current.setAttribute("data-click-region-target-button", "");
    } else {
    }
  }, [targetRef]);
  const handleClick = useCallback8(
    (event) => {
      const node = targetRef.current;
      if (event.target instanceof HTMLAnchorElement && !event.target.hasAttribute("data-click-region-target-link") || event.target instanceof HTMLButtonElement && !event.target.hasAttribute("data-click-region-target-button")) {
        event.stopPropagation();
        return;
      }
      if (node === null || event.defaultPrevented || !node.hasAttribute("data-click-region-target-button")) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      node.focus();
      node.click();
    },
    [targetRef]
  );
  return cloneElement3(Children3.only(children), {
    "data-click-region": true,
    onClick: handleClick
  });
};
ClickRegion.displayName = "ClickRegion";

// src/components/Collapsible/Collapsible.tsx
import { Root as CollapsibleRoot } from "@radix-ui/react-collapsible";
import { isNotNil as isNotNil13 } from "@wistia/type-guards";
import styled21 from "styled-components";
import { jsx as jsx209 } from "react/jsx-runtime";
var StyledRoot = styled21(CollapsibleRoot)`
  &[data-state='closed'] [data-wui-collapsible-content] {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--wui-collapsible-content-clamp-lines);
    overflow: hidden;
  }
`;
var Collapsible = ({
  children,
  isOpen = false,
  onOpenChange
}) => {
  const controlProps = {
    ...isNotNil13(onOpenChange) && isNotNil13(isOpen) ? { open: isOpen, onOpenChange } : {}
  };
  return /* @__PURE__ */ jsx209(StyledRoot, { ...controlProps, children });
};
Collapsible.displayName = "Collapsible";

// src/components/Collapsible/CollapsibleTrigger.tsx
import { Children as Children4 } from "react";
import { Trigger } from "@radix-ui/react-collapsible";
import { jsx as jsx210 } from "react/jsx-runtime";
var CollapsibleTrigger = ({ children }) => {
  Children4.only(children);
  return /* @__PURE__ */ jsx210(Trigger, { asChild: true, children });
};

// src/components/Collapsible/CollapsibleContent.tsx
import styled22 from "styled-components";
import { Content } from "@radix-ui/react-collapsible";
import { isNotUndefined as isNotUndefined6 } from "@wistia/type-guards";
import { Fragment as Fragment4, jsx as jsx211, jsxs as jsxs16 } from "react/jsx-runtime";
var ClampedContent = styled22.div`
  --wui-collapsible-content-clamp-lines: ${({ $clamp }) => $clamp};
`;
var CollapsibleContent = ({ clamp, children }) => {
  if (isNotUndefined6(clamp)) {
    return /* @__PURE__ */ jsx211(
      ClampedContent,
      {
        $clamp: clamp,
        "data-wui-collapsible-content": "true",
        children
      }
    );
  }
  return /* @__PURE__ */ jsx211(Content, { children: /* @__PURE__ */ jsxs16(Fragment4, { children: [
    children,
    " "
  ] }) });
};

// src/components/DataCards/DataCard.tsx
import styled24, { keyframes } from "styled-components";
import { isNotNull } from "@wistia/type-guards";

// src/components/Heading/Heading.tsx
import { forwardRef as forwardRef8 } from "react";
import styled23, { css as css22 } from "styled-components";
import { isNotNil as isNotNil14 } from "@wistia/type-guards";
import { jsx as jsx212 } from "react/jsx-runtime";
var heroStyle = css22`
  --font-family: var(--wui-typography-heading-hero-family);
  --font-size: var(--wui-typography-heading-hero-size);
  --font-weight: var(--wui-typography-heading-hero-weight);
  --line-height: var(--wui-typography-heading-hero-line-height);
`;
var heading1TextStyle = css22`
  --font-family: var(--wui-typography-heading-1-family);
  --font-size: var(--wui-typography-heading-1-size);
  --font-weight: var(--wui-typography-heading-1-weight);
  --line-height: var(--wui-typography-heading-1-line-height);
`;
var heading2TextStyle = css22`
  --font-family: var(--wui-typography-heading-2-family);
  --font-size: var(--wui-typography-heading-2-size);
  --font-weight: var(--wui-typography-heading-2-weight);
  --line-height: var(--wui-typography-heading-2-line-height);
`;
var heading3TextStyle = css22`
  --font-family: var(--wui-typography-heading-3-family);
  --font-size: var(--wui-typography-heading-3-size);
  --font-weight: var(--wui-typography-heading-3-weight);
  --line-height: var(--wui-typography-heading-3-line-height);
`;
var heading4TextStyle = css22`
  --font-family: var(--wui-typography-heading-4-family);
  --font-size: var(--wui-typography-heading-4-size);
  --font-weight: var(--wui-typography-heading-4-weight);
  --line-height: var(--wui-typography-heading-4-line-height);
`;
var heading5TextStyle = css22`
  --font-family: var(--wui-typography-heading-5-family);
  --font-size: var(--wui-typography-heading-5-size);
  --font-weight: var(--wui-typography-heading-5-weight);
  --line-height: var(--wui-typography-heading-5-line-height);
`;
var heading6TextStyle = css22`
  --font-family: var(--wui-typography-heading-6-family);
  --font-size: var(--wui-typography-heading-6-size);
  --font-weight: var(--wui-typography-heading-6-weight);
  --line-height: var(--wui-typography-heading-6-line-height);
`;
var variantStyleMap = {
  hero: heroStyle,
  heading1: heading1TextStyle,
  heading2: heading2TextStyle,
  heading3: heading3TextStyle,
  heading4: heading4TextStyle,
  heading5: heading5TextStyle,
  heading6: heading6TextStyle
};
var DEFAULT_ELEMENT2 = "h1";
var StyledHeading = styled23.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  line-height: var(--line-height);
  color: var(--wui-color-text);
  ${({ $variant }) => variantStyleMap[$variant]}
  ${({ $truncate }) => {
  if (isNotNil14($truncate)) {
    return css22`
        ${ellipsisStyle};
        ${lineClampCss($truncate)};
      `;
  }
  return void 0;
}}
  ${({ $inline }) => $inline && css22`
      display: inline-block;
    `}
  ${({ $disabled }) => $disabled && css22`
      color: var(--wui-color-text-disabled);
    `}
  ${({ $preventUserSelect }) => $preventUserSelect && css22`
      user-select: none;
    `}
  ${({ $align }) => css22`
    text-align: ${$align};
  `}
  margin: 0;
`;
var variantElementMap = {
  hero: DEFAULT_ELEMENT2,
  heading1: DEFAULT_ELEMENT2,
  heading2: "h2",
  heading3: "h3",
  heading4: "h4",
  heading5: "h5",
  heading6: "h6"
};
var HeadingComponent = forwardRef8(
  ({
    align = "left",
    colorScheme = "inherit",
    disabled = false,
    inline = false,
    preventUserSelect = false,
    truncate,
    variant = "heading1",
    renderAs,
    ...props
  }, ref) => /* @__PURE__ */ jsx212(
    StyledHeading,
    {
      ref,
      $align: align,
      $colorScheme: colorScheme,
      $disabled: disabled,
      $inline: inline,
      $preventUserSelect: preventUserSelect,
      $truncate: truncate,
      $variant: variant,
      as: renderAs ?? variantElementMap[variant],
      ...props
    }
  )
);
HeadingComponent.displayName = "Heading";
var Heading = makePolymorphic(HeadingComponent);

// src/components/DataCards/DataCard.tsx
import { jsx as jsx213, jsxs as jsxs17 } from "react/jsx-runtime";
var StyledDataCard = styled24.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  display: grid;
  grid-template-areas: 'label slot' 'value value';
  grid-template-rows: auto auto;
  grid-template-columns: auto auto;
  gap: var(--wui-space-01);
  padding: var(--wui-space-03);
  background: var(--wui-color-bg-surface-secondary);
  flex: 1;
  border-radius: var(--wui-border-radius-02);
  position: relative;
`;
var shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;
var LoadingBackground = styled24.div`
  background: linear-gradient(
    90deg,
    var(--wui-color-bg-surface-tertiary) 25%,
    var(--wui-color-bg-surface-secondary) 37%,
    var(--wui-color-bg-surface-tertiary) 63%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: var(--wui-border-radius-01);
`;
var StyledLoadingLabel = styled24(LoadingBackground)`
  width: 80px;
  height: var(--wui-typography-heading-6-line-height);
`;
var StyledLoadingValue = styled24(LoadingBackground)`
  width: 90%;
  height: var(--wui-typography-heading-3-line-height);
`;
var StyledLabel2 = styled24(Heading)`
  grid-area: label;
`;
var StyledValue = styled24(Heading)`
  grid-area: value;
`;
var StyledSlot = styled24.div`
  display: flex;
  justify-content: flex-end;
  grid-area: slot;
  align-self: center;
`;
var StyledDataCardTrendContainer = styled24.div`
  position: absolute;
  bottom: var(--wui-space-01);
  right: var(--wui-space-01);
`;
var DataCard = ({
  label,
  value,
  upperRightSlot,
  colorScheme = "inherit",
  isLoading = false,
  trend,
  ...props
}) => /* @__PURE__ */ jsxs17(
  StyledDataCard,
  {
    $colorScheme: colorScheme,
    ...props,
    children: [
      /* @__PURE__ */ jsx213(
        StyledLabel2,
        {
          renderAs: "dt",
          variant: "heading6",
          children: isLoading ? /* @__PURE__ */ jsx213(StyledLoadingLabel, {}) : label
        }
      ),
      /* @__PURE__ */ jsx213(
        StyledValue,
        {
          renderAs: "dd",
          variant: "heading3",
          children: isLoading ? /* @__PURE__ */ jsx213(StyledLoadingValue, {}) : value
        }
      ),
      isNotNull(upperRightSlot) && !isLoading && /* @__PURE__ */ jsx213(StyledSlot, { children: upperRightSlot }),
      isNotNull(trend) && !isLoading && /* @__PURE__ */ jsx213(StyledDataCardTrendContainer, { children: trend })
    ]
  }
);
DataCard.displayName = "DataCard";

// src/components/DataCards/DataCards.tsx
import styled25 from "styled-components";
import { jsx as jsx214 } from "react/jsx-runtime";
var StyledDataCards = styled25(Box)`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  margin-top: 0; /* Remove default <dl> style */
  > * {
    min-width: 120px;
    max-width: ${({ $cardMaxWidth }) => $cardMaxWidth};
  }
`;
var DataCards = ({
  children,
  cardMaxWidth = "none",
  colorScheme = "inherit",
  ...props
}) => {
  return /* @__PURE__ */ jsx214(
    StyledDataCards,
    {
      ...props,
      $cardMaxWidth: cardMaxWidth,
      $colorScheme: colorScheme,
      direction: "row",
      fill: "horizontal",
      gap: "space-02",
      renderAs: "dl",
      wrapItems: true,
      children
    }
  );
};
DataCards.displayName = "DataCards";

// src/components/DataCards/DataCardTrend.tsx
import styled27 from "styled-components";

// src/components/Text/Text.tsx
import { forwardRef as forwardRef9 } from "react";
import styled26, { css as css23 } from "styled-components";
import { isNotNil as isNotNil15 } from "@wistia/type-guards";
import { jsx as jsx215 } from "react/jsx-runtime";
var bodyBoldStyles = css23`
  > strong,
  b {
    font-weight: var(--wui-typography-weight-body-bold);
  }
`;
var labelBoldStyles = css23`
  > strong,
  b {
    font-weight: var(--wui-typography-weight-label-bold);
  }
`;
var body1TextStyle = css23`
  --font-family: var(--wui-typography-body-1-family);
  --font-size: var(--wui-typography-body-1-size);
  --font-weight: var(--wui-typography-body-1-weight);
  --line-height: var(--wui-typography-body-1-line-height);
  --paragraph-spacing: var(--wui-typography-body-1-paragraph-spacing);
  ${bodyBoldStyles}
`;
var body2TextStyle = css23`
  --font-family: var(--wui-typography-body-2-family);
  --font-size: var(--wui-typography-body-2-size);
  --font-weight: var(--wui-typography-body-2-weight);
  --line-height: var(--wui-typography-body-2-line-height);
  --paragraph-spacing: var(--wui-typography-body-2-paragraph-spacing);
  ${bodyBoldStyles}
`;
var body3TextStyle = css23`
  --font-family: var(--wui-typography-body-3-family);
  --font-size: var(--wui-typography-body-3-size);
  --font-weight: var(--wui-typography-body-3-weight);
  --line-height: var(--wui-typography-body-3-line-height);
  --paragraph-spacing: var(--wui-typography-body-3-paragraph-spacing);
  ${bodyBoldStyles}
`;
var body4TextStyle = css23`
  --font-family: var(--wui-typography-body-4-family);
  --font-size: var(--wui-typography-body-4-size);
  --font-weight: var(--wui-typography-body-4-weight);
  --line-height: var(--wui-typography-body-4-line-height);
  --paragraph-spacing: var(--wui-typography-body-4-paragraph-spacing);
  ${bodyBoldStyles}
`;
var label1TextStyle = css23`
  --font-family: var(--wui-typography-label-1-family);
  --font-size: var(--wui-typography-label-1-size);
  --font-weight: var(--wui-typography-label-1-weight);
  --line-height: var(--wui-typography-label-1-line-height);
  ${labelBoldStyles}
`;
var label2TextStyle = css23`
  --font-family: var(--wui-typography-label-2-family);
  --font-size: var(--wui-typography-label-2-size);
  --font-weight: var(--wui-typography-label-2-weight);
  --line-height: var(--wui-typography-label-2-line-height);
  ${labelBoldStyles}
`;
var label3TextStyle = css23`
  --font-family: var(--wui-typography-label-3-family);
  --font-size: var(--wui-typography-label-3-size);
  --font-weight: var(--wui-typography-label-3-weight);
  --line-height: var(--wui-typography-label-3-line-height);
  ${labelBoldStyles}
`;
var label4TextStyle = css23`
  --font-family: var(--wui-typography-label-4-family);
  --font-size: var(--wui-typography-label-4-size);
  --font-weight: var(--wui-typography-label-4-weight);
  --line-height: var(--wui-typography-label-4-line-height);
  ${labelBoldStyles}
`;
var body1MonoTextStyle = css23`
  ${body1TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;
var body2MonoTextStyle = css23`
  ${body2TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;
var body3MonoTextStyle = css23`
  ${body3TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;
var body4MonoTextStyle = css23`
  ${body4TextStyle};
  --font-family: var(--wui-typography-family-mono);
`;
var variantStyleMap2 = {
  body1: body1TextStyle,
  body2: body2TextStyle,
  body3: body3TextStyle,
  body4: body4TextStyle,
  body1Mono: body1MonoTextStyle,
  body2Mono: body2MonoTextStyle,
  body3Mono: body3MonoTextStyle,
  body4Mono: body4MonoTextStyle,
  label1: label1TextStyle,
  label2: label2TextStyle,
  label3: label3TextStyle,
  label4: label4TextStyle
};
var bodyElement = "p";
var labelElement = "span";
var DEFAULT_ELEMENT3 = bodyElement;
var variantElementMap2 = {
  body1: DEFAULT_ELEMENT3,
  body2: DEFAULT_ELEMENT3,
  body3: DEFAULT_ELEMENT3,
  body4: DEFAULT_ELEMENT3,
  body1Mono: DEFAULT_ELEMENT3,
  body2Mono: DEFAULT_ELEMENT3,
  body3Mono: DEFAULT_ELEMENT3,
  body4Mono: DEFAULT_ELEMENT3,
  label1: labelElement,
  label2: labelElement,
  label3: labelElement,
  label4: labelElement
};
var StyledText = styled26.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  --text-color: ${({ $prominence, $disabled }) => {
  if ($disabled) {
    return "var(--wui-color-text-disabled)";
  }
  if ($prominence === "secondary") {
    return "var(--wui-color-text-secondary)";
  }
  if ($prominence === "button") {
    return "var(--wui-color-text-button)";
  }
  return "var(--wui-color-text)";
}};

  color: var(--text-color);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  line-height: var(--line-height);
  font-style: normal;
  margin: 0;
  ${({ $variant }) => variantStyleMap2[$variant]}
  ${({ $truncate }) => {
  if (isNotNil15($truncate)) {
    return css23`
        ${ellipsisStyle};
        ${lineClampCss($truncate)};
      `;
  }
  return void 0;
}}
  ${({ $inline }) => $inline && css23`
      display: inline-block;
    `}
  ${({ $disabled }) => $disabled && css23`
      --text-color: var(--wui-color-text-disabled);
    `}
  ${({ $preventUserSelect }) => $preventUserSelect && css23`
      user-select: none;
    `}
  ${({ $align }) => css23`
    text-align: ${$align};
  `}
  ${({ as }) => as === "p" && css23`
      display: block;

      + p {
        margin-top: var(--paragraph-spacing, var(--wui-space-04));
      }
    `}
`;
var TextComponent = forwardRef9(
  ({
    align = "left",
    colorScheme = "inherit",
    disabled = false,
    inline = false,
    preventUserSelect = false,
    prominence = "primary",
    truncate,
    variant = "body2",
    renderAs,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx215(
      StyledText,
      {
        ref,
        $align: align,
        $colorScheme: colorScheme,
        $disabled: disabled,
        $inline: inline,
        $preventUserSelect: preventUserSelect,
        $prominence: prominence,
        $truncate: truncate,
        $variant: variant,
        as: renderAs ?? variantElementMap2[variant],
        ...props
      }
    );
  }
);
TextComponent.displayName = "Text";
var Text = makePolymorphic(TextComponent);

// src/components/DataCards/DataCardTrend.tsx
import { jsx as jsx216, jsxs as jsxs18 } from "react/jsx-runtime";
var StyledDataCardTrend = styled27.div`
  ${({ $outlook }) => getColorScheme($outlook === "positive" ? "success" : "error")};
  background: var(--wui-color-bg-app);
  border-radius: var(--wui-border-radius-rounded);
  padding: var(--wui-space-01);
  display: flex;
  align-items: center;
  gap: 2px;
`;
var DataCardTrend = ({
  direction = "up",
  outlook = "positive",
  children,
  ...props
}) => {
  return /* @__PURE__ */ jsxs18(StyledDataCardTrend, { $outlook: outlook, children: [
    /* @__PURE__ */ jsx216(
      Icon,
      {
        size: "md",
        type: direction === "up" ? "arrow-up" : "arrow-down",
        ...props
      }
    ),
    /* @__PURE__ */ jsx216(
      Text,
      {
        prominence: "secondary",
        variant: "label4",
        children
      }
    )
  ] });
};

// src/components/Divider/Divider.tsx
import styled28, { css as css24 } from "styled-components";
import { jsx as jsx217 } from "react/jsx-runtime";
var horizontalBorderCss = css24`
  border-top-color: var(--wui-color-border);
  border-top-style: solid;
  border-top-width: 1px;
  clear: both; /* for horizontal dividers, ensure it clears any floats */
  height: 0;
`;
var verticalBorderCss = css24`
  background-color: var(--wui-color-border);
  max-width: 1px;
  min-height: 100%;
  width: 1px;
`;
var DividerComponent = styled28.div`
  ${({ $orientation }) => {
  switch ($orientation) {
    case "vertical":
      return verticalBorderCss;
    case "horizontal":
    default:
      return horizontalBorderCss;
  }
}}
`;
var Divider = ({ orientation = "horizontal", ...props }) => {
  return /* @__PURE__ */ jsx217(
    DividerComponent,
    {
      $orientation: orientation,
      "aria-orientation": orientation,
      role: "separator",
      ...props
    }
  );
};
Divider.displayName = "Divider";

// src/components/EditableHeading/EditableHeading.tsx
import styled31, { css as css27 } from "styled-components";
import { useState as useState9, useRef as useRef7 } from "react";

// src/components/Tooltip/Tooltip.tsx
import {
  Root as TooltipRoot,
  Content as TooltipContent,
  Trigger as TooltipTrigger,
  Portal as TooltipPortal,
  Arrow as TooltipArrow
} from "@radix-ui/react-tooltip";
import styled29, { keyframes as keyframes2 } from "styled-components";
import { jsx as jsx218, jsxs as jsxs19 } from "react/jsx-runtime";
var hide = keyframes2`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;
var slideDownAndFade = keyframes2`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
var slideLeftAndFade = keyframes2`
  from {
    opacity: 0;
    transform: translateX(6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
var slideUpAndFade = keyframes2`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
var slideRightAndFade = keyframes2`
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
var StyledContent = styled29(TooltipContent)`
  --tooltip-font-family: var(--wui-typography-family-default);
  --tooltip-border-radius: var(--wui-border-radius-05);
  --tooltip-bg: var(--wui-color-bg-tooltip);
  --tooltip-color: var(--wui-color-text-on-fill);
  --tooltip-font-size: var(--wui-typography-label-4-size);
  --tooltip-line-height: var(--wui-typography-label-4-line-height);
  --tooltip-font-weight: var(--wui-typography-label-4-weight);
  --tooltip-horizontal-padding: var(--wui-space-02);
  --tooltip-vertical-padding: var(--wui-space-03);
  --tooltip-animation-duration: var(--wui-motion-duration-04);
  --tooltip-animation-timing-function: var(--wui-motion-ease-out);

  font-family: var(--tooltip-font-family);
  font-size: var(--tooltip-font-size);
  border-radius: var(--tooltip-border-radius);
  padding: var(--tooltip-horizontal-padding) var(--tooltip-vertical-padding);
  background-color: var(--tooltip-bg);
  color: var(--tooltip-color);
  user-select: none;
  animation-duration: var(--tooltip-animation-duration);
  animation-timing-function: var(--tooltip-animation-timing-function);
  will-change: transform, opacity;
  max-width: 30em;
  z-index: var(--wui-zindex-tooltip);

  &[data-state='closed'] {
    animation-name: ${hide};
  }

  &[data-state='delayed-open'][data-side='top'] {
    animation-name: ${slideUpAndFade};
  }

  &[data-state='delayed-open'][data-side='right'] {
    animation-name: ${slideRightAndFade};
  }

  &[data-state='delayed-open'][data-side='bottom'] {
    animation-name: ${slideDownAndFade};
  }

  &[data-state='delayed-open'][data-side='left'] {
    animation-name: ${slideLeftAndFade};
  }
`;
var VISUAL_OFFSET = 6;
var Tooltip = ({
  delay = 500,
  direction = "top",
  content,
  children,
  forceOpen,
  hideArrow = false
}) => {
  const rootProps = {};
  if (content === "" || content === null) {
    rootProps.open = false;
  }
  if (forceOpen === true) {
    rootProps.open = true;
  }
  return /* @__PURE__ */ jsxs19(
    TooltipRoot,
    {
      delayDuration: delay,
      ...rootProps,
      children: [
        /* @__PURE__ */ jsx218(TooltipTrigger, { asChild: true, children }),
        /* @__PURE__ */ jsx218(TooltipPortal, { children: /* @__PURE__ */ jsxs19(
          StyledContent,
          {
            side: direction,
            sideOffset: hideArrow ? VISUAL_OFFSET : VISUAL_OFFSET - 2,
            children: [
              content,
              !hideArrow ? /* @__PURE__ */ jsx218(TooltipArrow, { asChild: true, children: /* @__PURE__ */ jsx218(
                "svg",
                {
                  fill: "var(--tooltip-bg)",
                  height: "8",
                  style: { transform: "translateY(-2px)" },
                  viewBox: "0 0 12 8",
                  width: "12",
                  xmlns: "http://www.w3.org/2000/svg",
                  children: /* @__PURE__ */ jsx218("path", { d: "M6.8 6.93333C6.4 7.46667 5.6 7.46667 5.2 6.93333L-2.54292e-07 -1.04907e-06L12 0L6.8 6.93333Z" })
                }
              ) }) : null
            ]
          }
        ) })
      ]
    }
  );
};
Tooltip.displayName = "Tooltip";

// src/components/Input/Input.tsx
import { forwardRef as forwardRef10, cloneElement as cloneElement4, useRef as useRef6 } from "react";
import styled30, { css as css26 } from "styled-components";
import { isNil as isNil13, isNotNil as isNotNil16, isRecord as isRecord4 } from "@wistia/type-guards";

// src/css/sharedStyles/inputCssVariables.ts
import { css as css25 } from "styled-components";
var inputCssVariables = css25`
  /* Colors */
  --wui-input-color-bg: var(--wui-color-bg-surface);
  --wui-input-color-bg-disabled: var(--wui-color-bg-surface-disabled);
  --wui-input-color-border: var(--wui-color-border-secondary);
  --wui-input-color-border-focus: var(--wui-color-focus-ring);
  --wui-input-color-border-error: var(--wui-color-invalid-indicator);
  --wui-input-color-border-disabled: var(--wui-color-border-disabled);
  --wui-input-color-text: var(--wui-color-text);

  /* Border */
  --wui-input-border-radius: var(--wui-border-radius-03);
  --wui-input-multiline-border-radius: var(--wui-border-radius-02);

  /* Padding */
  --wui-input-vertical-padding: var(--wui-space-02);
  --wui-input-horizontal-padding: var(--wui-space-03);

  /* Typography */
  --wui-input-placeholder: var(--wui-gray-10);
  --wui-input-font-size: var(--wui-typography-body-3-size);
  --wui-input-line-height: 16px;
  --wui-input-font-weight: var(--wui-typography-weight-body);
`;

// src/components/Input/Input.tsx
import { jsx as jsx219, jsxs as jsxs20 } from "react/jsx-runtime";
var inputStyles = css26`
  ${inputCssVariables}
  input,
  textarea {
    padding: var(--wui-input-vertical-padding) var(--wui-input-horizontal-padding);
    background-color: var(--wui-input-color-bg);
    border: none;
    outline: 1px solid var(--wui-input-color-border);
    outline-offset: -1px;
    border-radius: var(--wui-input-border-radius);
    font-size: var(--wui-input-font-size);
    line-height: var(--wui-input-line-height);
    font-weight: var(--wui-input-font-weight);
    color: var(--wui-input-color-text);

    &::placeholder {
      color: var(--wui-input-placeholder);
      opacity: 1; /* Firefox */
    }

    &:focus {
      outline: 2px solid var(--wui-input-color-border-focus);
      outline-offset: -2px;
    }

    &[aria-invalid='true'] {
      outline-color: var(--wui-input-color-border-error);
    }

    &:disabled {
      outline-color: var(--wui-color-border-disabled);
      background-color: var(--wui-color-bg-surface-disabled);
    }
  }
`;
var StyledInputContainer = styled30.div`
  display: inline-flex;
  position: relative;
  ${inputStyles};
  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};

  input,
  textarea {
    width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};
    ${({ $monospace }) => $monospace && "font-family: var(--wui-typography-family-mono);"}
  }

  textarea {
    border-radius: var(--wui-input-multiline-border-radius);

    --wui-input-line-height: var(--wui-typography-body-3-line-height);
  }

  [type='search'] {
    &::-webkit-search-cancel-button {
      appearance: none;
      height: 16px;
      width: 16px;
      margin-right: -4px;

      /* css vars don't work in data URLs, so the stroke color value is hardcoded. if we support dark mode, we'll need to swap the background image based on the users preferred color scheme */
      background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 4L4 12M4 4L12 12' stroke='%2364646a' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-position: center;
      background-repeat: no-repeat;
      cursor: pointer;
      background-color: var(--wui-color-bg-surface-secondary);
      border-radius: var(--wui-border-radius-rounded);

      &:hover {
        background-color: var(--wui-color-bg-surface-secondary-hover);
      }
    }
  }

  .wui-input-left-icon {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    left: var(--wui-input-horizontal-padding);
    transform: translateY(-50%);
  }

  :has(.wui-input-left-icon) input,
  :has(.wui-input-left-icon) textarea {
    padding-left: 32px;
  }

  .wui-input-right-icon {
    display: flex;
    align-items: center;
    position: absolute;
    top: 50%;
    right: var(--wui-input-horizontal-padding);
    transform: translateY(-50%);
  }

  :has(.wui-input-right-icon) input,
  :has(.wui-input-right-icon) textarea {
    padding-right: 32px;
  }
`;
var Input = forwardRef10(
  ({
    fullWidth = true,
    monospace = false,
    type = "text",
    autoSelect = false,
    leftIcon,
    rightIcon,
    ...props
  }, externalRef) => {
    const internalRef = useRef6();
    const ref = (
      // eslint-disable-next-line react-compiler/react-compiler
      isNotNil16(externalRef) && isRecord4(externalRef) && "current" in externalRef ? externalRef : internalRef
    );
    let leftIconToDisplay = leftIcon;
    if (isNil13(leftIcon) && type === "search") {
      leftIconToDisplay = /* @__PURE__ */ jsx219(Icon, { type: "search" });
    }
    if (isNotNil16(leftIconToDisplay)) {
      leftIconToDisplay = cloneElement4(leftIconToDisplay, {
        size: "md",
        className: "wui-input-left-icon"
      });
    }
    let rightIconToDisplay = rightIcon;
    if (isNotNil16(rightIconToDisplay)) {
      rightIconToDisplay = cloneElement4(rightIconToDisplay, {
        size: "md",
        className: "wui-input-right-icon"
      });
    }
    const handleFocus2 = (event) => {
      if (isNotNil16(props.onFocus)) {
        props.onFocus(event);
      }
      if (autoSelect && isValidRef(ref) && "current" in ref) {
        requestAnimationFrame(() => {
          ref.current?.select();
        });
      }
    };
    return /* @__PURE__ */ jsxs20(
      StyledInputContainer,
      {
        $fullWidth: fullWidth,
        $monospace: monospace,
        children: [
          leftIconToDisplay ?? null,
          type === "multiline" ? /* @__PURE__ */ jsx219(
            "textarea",
            {
              ...props,
              ref,
              onFocus: handleFocus2
            }
          ) : /* @__PURE__ */ jsx219(
            "input",
            {
              ...props,
              ref,
              onFocus: handleFocus2,
              type
            }
          ),
          rightIconToDisplay ?? null
        ]
      }
    );
  }
);
Input.displayName = "Input";

// src/components/EditableHeading/EditableHeading.tsx
import { Fragment as Fragment5, jsx as jsx220, jsxs as jsxs21 } from "react/jsx-runtime";
var StyledInput = styled31(Input)`
  :not([rows]) {
    min-height: unset;
  }

  && {
    --wui-input-font-size: var(--wui-typography-heading-1-size);
    --wui-input-font-weight: var(--wui-typography-heading-1-weight);
    --wui-input-line-height: var(--wui-typography-heading-1-line-height);

    font-family: var(--wui-typography-heading-1-family);
    width: 100%;
    padding: var(--wui-space-02);
    border: none;
    height: ${({ $height }) => `${$height}px`};
  }
`;
var editableStyles = css27`
  &:has(+ :focus-within) {
    background: var(--wui-color-bg-surface-hover);
  }

  &:hover {
    background: var(--wui-color-bg-surface-hover);
    cursor: pointer;
  }
`;
var StyledHeading2 = styled31(Heading)`
  width: 100%;
  border-radius: var(--wui-border-radius-02);
  padding: var(--wui-space-02);
  overflow-wrap: anywhere;
  transition: all var(--wui-motion-duration-01) var(--wui-motion-ease);
  ${({ $editingDisabled }) => !$editingDisabled && editableStyles}
`;
var EditableHeading = ({
  children,
  onValueChange,
  onEditingChange,
  tooltipText = "Click to edit title",
  ariaLabel = "Edit title",
  __forceEditing = false,
  editingDisabled = false
}) => {
  const [isEditing, setIsEditing] = useState9(false);
  const [value, setValue] = useState9(children);
  const [previousValue, setPreviousValue] = useState9(children);
  const [headingHeight, setHeadingHeight] = useState9("60");
  const headingRef = useRef7(null);
  const handleSetEditing = (editing) => {
    if (editingDisabled) return;
    if (editing && headingRef.current) {
      setHeadingHeight(`${headingRef.current.offsetHeight}`);
    }
    if (editing) {
      setPreviousValue(value);
    }
    setIsEditing(editing);
    onEditingChange?.(editing);
  };
  const handleFinishEditing = () => {
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setValue(previousValue);
    } else if (trimmedValue !== previousValue && onValueChange) {
      onValueChange(trimmedValue);
    } else {
      setValue(trimmedValue);
    }
    handleSetEditing(false);
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleFinishEditing();
    }
    if (event.key === "Escape") {
      setValue(previousValue);
      handleSetEditing(false);
    }
  };
  const HeadingComponent2 = /* @__PURE__ */ jsx220(
    StyledHeading2,
    {
      ref: headingRef,
      $editingDisabled: editingDisabled,
      onClick: () => handleSetEditing(true),
      variant: "heading1",
      children: value
    }
  );
  if (editingDisabled) {
    return HeadingComponent2;
  }
  if (isEditing || __forceEditing) {
    return /* @__PURE__ */ jsx220(
      StyledInput,
      {
        $height: headingHeight,
        autoFocus: true,
        fullWidth: true,
        onBlur: handleFinishEditing,
        onChange: (event) => setValue(event.target.value),
        onFocus: (event) => {
          const { length } = event.currentTarget.value;
          event.currentTarget.setSelectionRange(length, length);
        },
        onKeyDown: handleKeyDown,
        type: "multiline",
        value
      }
    );
  }
  return /* @__PURE__ */ jsxs21(Fragment5, { children: [
    /* @__PURE__ */ jsx220(Tooltip, { content: tooltipText, children: HeadingComponent2 }),
    /* @__PURE__ */ jsx220(ScreenReaderOnly, { children: /* @__PURE__ */ jsx220(
      "button",
      {
        "aria-label": ariaLabel,
        onClick: () => handleSetEditing(true),
        type: "button"
      }
    ) })
  ] });
};

// src/components/Form/Form.tsx
import { forwardRef as forwardRef12, useRef as useRef8, useMemo as useMemo4, createContext as createContext3, useState as useState10, useId as useId2 } from "react";
import styled33 from "styled-components";
import { isNotUndefined as isNotUndefined7, isUndefined as isUndefined3 } from "@wistia/type-guards";

// src/components/Stack/Stack.tsx
import { forwardRef as forwardRef11 } from "react";
import styled32 from "styled-components";
import { jsx as jsx221 } from "react/jsx-runtime";
var DEFAULT_ELEMENT4 = "div";
var StyledStack = styled32.div`
  display: flex;
  flex-direction: ${({ $direction }) => $direction === "horizontal" ? "row" : "column"};
  gap: ${({ $gap }) => `var(--wui-${$gap})`};
  align-items: ${({ $alignItems }) => $alignItems};
`;
var StackComponent = forwardRef11(
  ({ renderAs, direction = "vertical", gap = "space-02", alignItems = "stretch", ...props }, ref) => {
    const responsiveGap = useResponsiveProp(gap);
    const responsiveDirection = useResponsiveProp(direction);
    const responsiveAlignItems = useResponsiveProp(alignItems);
    return /* @__PURE__ */ jsx221(
      StyledStack,
      {
        ref,
        $alignItems: responsiveAlignItems,
        $direction: responsiveDirection,
        $gap: responsiveGap,
        as: renderAs ?? DEFAULT_ELEMENT4,
        ...props
      }
    );
  }
);
StackComponent.displayName = "Stack";
var Stack = makePolymorphic(StackComponent);

// src/components/Form/Form.tsx
import { jsx as jsx222 } from "react/jsx-runtime";
var StyledForm = styled33.form`
  --form-default-width: 690px;

  max-width: ${({ $fullWidth }) => $fullWidth ? "auto" : "var(--form-default-width)"};
  align-items: ${({ $fullWidth }) => $fullWidth ? "stretch" : "flex-start"};
`;
var FormContext = createContext3({
  values: {},
  errors: {},
  hasSubmitted: false,
  formId: "NO_FORM",
  labelPosition: "block"
});
var FormComponent = ({
  children,
  action,
  values = {},
  labelPosition = "block",
  validate,
  fullWidth = false,
  ...props
}, forwardedRef) => {
  const [errors, setErrors] = useState10({});
  const [hasSubmitted, setHasSubmitted] = useState10(false);
  const innerRef = useRef8();
  const ref = forwardedRef ?? innerRef;
  const autoId = useId2();
  const id = props.id ?? autoId;
  const handleValidate = (nextFormData) => {
    const nextData = Object.fromEntries(nextFormData.entries());
    if (isUndefined3(validate)) {
      return {};
    }
    return validate(nextData);
  };
  const handleBlur2 = (event) => {
    if (props.onBlur) {
      props.onBlur(event);
    }
    const formData = new FormData(event.currentTarget);
    if (isNotUndefined7(validate)) {
      handleValidate(formData);
    }
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const nextErrors = handleValidate(formData);
    const isValid = Object.keys(nextErrors).length === 0;
    setErrors(nextErrors);
    setHasSubmitted(true);
    if (isValid && props.onSubmit) {
      props.onSubmit(event);
    }
    if (isValid && action) {
      void action(formData);
    }
  };
  const handleReset = (event) => {
    setErrors({});
    setHasSubmitted(false);
    if (props.onReset) {
      props.onReset(event);
    }
    if (action) {
      void action(null);
    }
  };
  const context = useMemo4(() => {
    return {
      values,
      errors,
      hasSubmitted,
      formId: id,
      labelPosition
    };
  }, [labelPosition, values, errors, id, hasSubmitted]);
  return (
    // @ts-expect-error - generic context providers are a giant pain
    /* @__PURE__ */ jsx222(FormContext.Provider, { value: context, children: /* @__PURE__ */ jsx222(
      Stack,
      {
        ...props,
        ref,
        $fullWidth: fullWidth,
        as: StyledForm,
        gap: "space-05",
        id,
        onBlur: handleBlur2,
        onReset: handleReset,
        onSubmit: handleSubmit,
        children
      }
    ) })
  );
};
FormComponent.displayName = "Form";
var Form = forwardRef12(FormComponent);

// src/components/Form/useFormState.tsx
import { useCallback as useCallback9, useState as useState11 } from "react";
var useFormState = (action, initialData = {}) => {
  const [data, setData] = useState11(initialData);
  const [isPending, setIsPending] = useState11(false);
  const [error, setError] = useState11(null);
  const formAction = useCallback9(
    async (nextFormData) => {
      if (nextFormData === null) {
        setData(initialData);
        setError(null);
        return;
      }
      const nextData = Object.fromEntries(nextFormData.entries());
      setIsPending(true);
      try {
        const result = await action(data, nextData);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsPending(false);
      }
    },
    [action, initialData, data]
  );
  return [
    {
      data,
      error
    },
    formAction,
    isPending
  ];
};

// src/components/Form/FormErrorSummary.tsx
import { useContext as useContext3, useRef as useRef9 } from "react";
import { isArray, isNotUndefined as isNotUndefined8 } from "@wistia/type-guards";
import { jsx as jsx223, jsxs as jsxs22 } from "react/jsx-runtime";
var ErrorItem = ({ name, error, formId }) => {
  return /* @__PURE__ */ jsx223("li", { children: /* @__PURE__ */ jsx223(Link, { href: `#${formId}-${name}`, children: error }) }, name);
};
var FormErrorSummary = ({ description }) => {
  const ref = useRef9(null);
  const { formId, errors, hasSubmitted } = useContext3(FormContext);
  const isValid = Object.keys(errors).length === 0;
  if (isValid || !hasSubmitted) {
    return null;
  }
  return /* @__PURE__ */ jsxs22("div", { ref, children: [
    /* @__PURE__ */ jsx223("p", { children: description }),
    /* @__PURE__ */ jsx223("ul", { children: Object.entries(errors).filter(([, error]) => isNotUndefined8(error)).map(
      ([name, error]) => isArray(error) ? error.map((err) => /* @__PURE__ */ jsx223(
        ErrorItem,
        {
          error: err,
          formId,
          name
        },
        err
      )) : /* @__PURE__ */ jsx223(
        ErrorItem,
        {
          error: error ?? "",
          formId,
          name
        },
        error
      )
    ) })
  ] });
};

// src/components/FormField/FormField.tsx
import { Children as Children5, cloneElement as cloneElement5, useContext as useContext4 } from "react";
import styled36 from "styled-components";
import { isArray as isArray2, isNotNil as isNotNil17, isNotUndefined as isNotUndefined9, isUndefined as isUndefined4 } from "@wistia/type-guards";

// src/components/Label/Label.tsx
import styled34, { css as css28 } from "styled-components";
import { jsx as jsx224 } from "react/jsx-runtime";
var requiredStyle = css28`
  &::after {
    color: var(--wui-color-text-error);
    display: inline;
    padding-left: var(--wui-space-01);
    content: '*';
  }
`;
var disabledStyle3 = css28`
  color: var(--wui-color-text-disabled);
`;
var StyledLabel3 = styled34.label`
  display: block;
  width: 100%;
  color: var(--wui-color-text);
  font-family: var(--wui-typography-heading-6-family);
  line-height: var(--wui-typography-heading-6-line-height);
  font-size: var(--wui-typography-heading-6-size);
  font-weight: var(--wui-typography-heading-6-weight);

  /* if label is wrapping an input this ensures it appears beneath the label with a nice margin */
  > input,
  > textarea,
  > select {
    display: block;
  }

  > label {
    font-weight: normal;
  }

  ${({ $screenReaderOnly }) => $screenReaderOnly && visuallyHiddenStyle}
  ${({ $disabled }) => $disabled && disabledStyle3}
  ${({ $required }) => $required && requiredStyle}
`;
var Label = ({
  children,
  disabled = false,
  htmlFor,
  required = false,
  screenReaderOnly = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx224(
    StyledLabel3,
    {
      ...props,
      $disabled: disabled,
      $required: required,
      $screenReaderOnly: screenReaderOnly,
      htmlFor,
      children
    }
  );
};
Label.displayName = "Label";

// src/components/FormGroup/CheckboxGroup.tsx
import { createContext as createContext4, useMemo as useMemo5 } from "react";

// src/components/FormGroup/FormGroup.tsx
import styled35 from "styled-components";
import { useRef as useRef10 } from "react";
import { jsx as jsx225, jsxs as jsxs23 } from "react/jsx-runtime";
var StyledFieldset = styled35.fieldset`
  border: 0;
`;
var StyledLegend = styled35.legend`
  margin-bottom: var(--space-01);
`;
var FormGroup = ({ children, label, ...props }) => {
  const ref = useRef10();
  return /* @__PURE__ */ jsxs23(
    Stack,
    {
      ...props,
      ref,
      renderAs: StyledFieldset,
      children: [
        /* @__PURE__ */ jsx225(
          Heading,
          {
            renderAs: StyledLegend,
            variant: "heading5",
            children: label
          }
        ),
        children
      ]
    }
  );
};
FormGroup.displayName = "FormGroup";

// src/components/FormGroup/CheckboxGroup.tsx
import { jsx as jsx226 } from "react/jsx-runtime";
var CheckboxGroupContext = createContext4(null);
var CheckboxGroup = ({
  children,
  name,
  onChange,
  value,
  ...props
}) => {
  const context = useMemo5(() => {
    return {
      name,
      onChange
    };
  }, [name, onChange]);
  return /* @__PURE__ */ jsx226(CheckboxGroupContext.Provider, { value: context, children: /* @__PURE__ */ jsx226(FormGroup, { ...props, children }) });
};
CheckboxGroup.displayName = "CheckboxGroup";

// src/components/FormField/FormField.tsx
import { Fragment as Fragment6, jsx as jsx227, jsxs as jsxs24 } from "react/jsx-runtime";
var StyledFormField = styled36.div`
  --form-field-spacing: var(--wui-space-01);
  --form-field-spacing-inline: var(--wui-space-02);
  --form-field-error-color: var(--wui-color-text-secondary-error);

  display: grid;
  gap: var(--form-field-spacing);
  justify-content: flex-start;
  align-items: center;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;

  &[data-label-position='inline-compact'] {
    gap: var(--form-field-spacing-inline);
    grid-template-columns: auto 1fr;
    grid-template-rows: 1fr auto;
  }

  &[data-label-position='inline'] {
    gap: var(--form-field-spacing-inline);
    grid-template-columns: minmax(auto, 1fr) 1fr;
    grid-template-rows: 1fr auto;
  }

  &[data-label-position='block'] {
    gap: var(--form-field-spacing);
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }
`;
var StyledErrorList = styled36.ul`
  margin: 0;
  padding: 0;
  padding-left: var(--wui-space-04);
  display: flex;
  flex-direction: column;
  gap: var(--wui-space-01);
`;
var ErrorMessages = ({ errors, id }) => {
  const isMultipleErrors = isArray2(errors);
  return isMultipleErrors ? /* @__PURE__ */ jsx227(StyledErrorList, { children: errors.map((error, index) => /* @__PURE__ */ jsx227(
    Text,
    {
      colorScheme: "error",
      id: `${id}-${index}`,
      renderAs: "li",
      variant: "label4",
      children: error
    },
    `${id}-${index}`
  )) }) : /* @__PURE__ */ jsx227(
    Text,
    {
      colorScheme: "error",
      id,
      prominence: "secondary",
      variant: "label4",
      children: errors
    },
    id
  );
};
var FormField = ({
  children,
  description,
  error,
  id,
  label,
  labelPosition,
  name,
  value,
  ...props
}) => {
  const formState = useContext4(FormContext);
  const checkboxGroup = useContext4(CheckboxGroupContext);
  const defaultValue = formState.values[name];
  const isIntegratedLabel = children.type === Checkbox;
  const computedId = id ?? `${formState.formId}-${name}`;
  const computedError = error ?? formState.errors[name];
  const descriptionId = `${computedId}-description`;
  const errorId = `${computedId}-error`;
  const ariaDescribedby = [descriptionId, errorId].filter(Boolean).join(" ") || void 0;
  let childProps = {
    name,
    id: computedId,
    label: isIntegratedLabel ? label : void 0,
    "aria-describedby": ariaDescribedby,
    ...props
  };
  if (isUndefined4(value) && isNotUndefined9(defaultValue)) {
    childProps = {
      ...childProps,
      defaultValue
    };
  }
  if (isNotNil17(checkboxGroup)) {
    const computedName = isNotNil17(checkboxGroup.name) ? `${checkboxGroup.name}[${name}]` : name;
    const handleChange = (event) => {
      if (isNotUndefined9(props.onChange)) {
        props.onChange(event);
      }
      if (isNotUndefined9(checkboxGroup.onChange)) {
        checkboxGroup.onChange(event);
      }
    };
    childProps = {
      ...childProps,
      name: computedName,
      onChange: handleChange,
      "aria-invalid": isNotNil17(error)
    };
  }
  Children5.only(children);
  return /* @__PURE__ */ jsxs24(
    StyledFormField,
    {
      ...props,
      "data-label-position": labelPosition ?? formState.labelPosition,
      children: [
        !isIntegratedLabel && /* @__PURE__ */ jsx227(Label, { htmlFor: computedId, children: label }),
        isNotNil17(description) ? /* @__PURE__ */ jsx227(FormControlLabelDescription, { id: descriptionId, children: description }) : null,
        cloneElement5(children, childProps),
        isNotNil17(computedError) ? /* @__PURE__ */ jsxs24(Fragment6, { children: [
          /* @__PURE__ */ jsx227("div", {}),
          /* @__PURE__ */ jsx227(
            ErrorMessages,
            {
              errors: computedError,
              id: errorId
            }
          )
        ] }) : null
      ]
    }
  );
};
FormField.displayName = "FormField";

// src/components/FormGroup/RadioGroup.tsx
import { createContext as createContext5, useMemo as useMemo6 } from "react";
import { jsx as jsx228 } from "react/jsx-runtime";
var RadioGroupContext = createContext5(null);
var RadioGroup = ({
  children,
  name,
  onChange,
  value,
  ...props
}) => {
  const context = useMemo6(() => {
    return {
      name,
      onChange
    };
  }, [name, onChange]);
  return /* @__PURE__ */ jsx228(RadioGroupContext.Provider, { value: context, children: /* @__PURE__ */ jsx228(FormGroup, { ...props, children }) });
};
RadioGroup.displayName = "RadioGroup";

// src/components/IconButton/IconButton.tsx
import { Children as Children6, cloneElement as cloneElement6, forwardRef as forwardRef13 } from "react";
import styled37 from "styled-components";
import { jsx as jsx229 } from "react/jsx-runtime";
var StyledButton2 = styled37(Button)`
  --icon-button-size-sm: 24px;
  --icon-button-size-md: 32px;
  --icon-button-size-lg: 40px;
  --icon-button-size-xl: 48px;

  border-radius: 50%;
  height: ${({ size }) => `var(--icon-button-size-${size})`};
  width: ${({ size }) => `var(--icon-button-size-${size})`};
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
`;
var IconButton = forwardRef13(
  ({ children, label, size = "md", ...props }, ref) => {
    const responsiveSize = useResponsiveProp(size);
    return /* @__PURE__ */ jsx229(
      StyledButton2,
      {
        ...props,
        ref,
        "aria-label": label,
        "data-wistia-ui-icon-button": true,
        size: responsiveSize,
        children: cloneElement6(Children6.only(children), {
          size: responsiveSize
        })
      }
    );
  }
);
IconButton.displayName = "IconButton";

// src/components/InputClickToCopy/InputClickToCopy.tsx
import styled38 from "styled-components";
import { forwardRef as forwardRef14, useEffect as useEffect9, useState as useState12 } from "react";
import { isFunction as isFunction3 } from "@wistia/type-guards";
import { jsx as jsx230 } from "react/jsx-runtime";
var StyledInput2 = styled38(Input)`
  &:not([aria-disabled='true']) {
    cursor: pointer;
  }

  &::selection {
    background-color: transparent;
  }
`;
var COPY_SUCCESS_DURATION = 2e3;
var InputClickToCopy = forwardRef14(
  ({ value, onCopy, ...props }, ref) => {
    const [isCopied, setIsCopied] = useState12(false);
    useEffect9(() => {
      if (isCopied) {
        const timeout = setTimeout(() => {
          setIsCopied(false);
        }, COPY_SUCCESS_DURATION);
        return () => {
          clearTimeout(timeout);
        };
      }
      return () => null;
    }, [isCopied]);
    const handleClick = (event) => {
      event.preventDefault();
      if (isFunction3(props.onClick)) {
        props.onClick(event);
      }
      void copyToClipboard(value).then(() => {
        setIsCopied(true);
        if (isFunction3(onCopy)) {
          onCopy(value);
        }
        return value;
      });
    };
    return /* @__PURE__ */ jsx230(
      StyledInput2,
      {
        "aria-label": "Click to Copy",
        ...props,
        ref,
        onClick: handleClick,
        readOnly: true,
        rightIcon: isCopied ? /* @__PURE__ */ jsx230(
          Icon,
          {
            colorScheme: "success",
            type: "checkmark-circle"
          }
        ) : /* @__PURE__ */ jsx230(Icon, { type: "save-as-copy" }),
        value
      }
    );
  }
);
InputClickToCopy.displayName = "InputClickToCopy";

// src/components/KeyboardShortcut/KeyboardShortcut.tsx
import styled39 from "styled-components";
import { isNotNil as isNotNil18 } from "@wistia/type-guards";
import { jsx as jsx231, jsxs as jsxs25 } from "react/jsx-runtime";
var StyledKeyboardShortcut = styled39.div`
  align-items: center;
  display: flex;
  gap: var(--wui-space-02);
  ${({ $fullWidth }) => $fullWidth && "width: 100%; justify-content: space-between;"}
`;
var StyledKey = styled39.kbd`
  align-items: center;
  background: var(--wui-color-bg-surface-secondary);
  border-bottom: 1px solid var(--wui-color-border-secondary);
  border-radius: 4px;
  color: var(--wui-color-text);
  display: flex;
  font-family: InterVariable, sans-serif;

  /* Adds serif to uppercase "I" to distinguish it from "l": https://rsms.me/inter/glyphs/?g=I.1 */
  font-feature-settings: 'cv08' on;
  font-size: 10px;

  /* Adds a slash to the zero to distinguish it from the letter "O": https://rsms.me/inter/glyphs/?g=zero.tf.slash */

  /* Adds tabular numbers to ensure the width of the key is consistent */
  font-variant-numeric: slashed-zero tabular-nums;
  height: 20px;
  justify-content: center;
  line-height: 20px;
  min-width: 20px;
  padding: 0 var(--wui-space-01);
`;
var Label2 = styled39.span`
  color: var(--wui-color-text);
  font-size: 12px;
`;
var KeysContainer = styled39.div`
  display: flex;
  gap: var(--wui-space-01);
`;
var keyToString = (key) => {
  switch (key) {
    // Arrow Keys
    case "ArrowDown":
      return "\u2193";
    case "ArrowLeft":
      return "\u2190";
    case "ArrowRight":
      return "\u2192";
    case "ArrowUp":
      return "\u2191";
    // Modifier Keys
    case "Alt":
      return "Alt";
    case "Cmd":
    case "Meta":
      if (/Mac|iPhone|iPad/.test(window.navigator.userAgent)) {
        return "\u2318";
      }
      if (window.navigator.userAgent.includes("Win")) {
        return "\u229E";
      }
      return "^";
    case "Ctrl":
      return "Ctrl";
    case "Option":
      return "\u2325";
    case "Shift":
      return "\u21E7";
    // Whitespace Keys
    case "Enter":
      return "\u21B5";
    case "Space":
      return "Space";
    case "Tab":
      return "Tab";
    case "Backspace":
      return "Del";
    case "Esc":
      return "Esc";
    default:
      return key.toUpperCase();
  }
};
var KeyboardShortcut = ({
  label,
  keyboardKeys,
  fullWidth = false,
  ...otherProps
}) => /* @__PURE__ */ jsxs25(
  StyledKeyboardShortcut,
  {
    $fullWidth: fullWidth,
    ...otherProps,
    children: [
      isNotNil18(label) && /* @__PURE__ */ jsx231(Label2, { children: label }),
      /* @__PURE__ */ jsx231(KeysContainer, { children: (Array.isArray(keyboardKeys) ? keyboardKeys : [keyboardKeys]).map((keyboardKey, index) => /* @__PURE__ */ jsx231(
        StyledKey,
        {
          children: keyToString(keyboardKey)
        },
        index
      )) })
    ]
  }
);
KeyboardShortcut.displayName = "KeyboardShortcut";

// src/components/Menu/Menu.tsx
import styled40, { css as css29, keyframes as keyframes3 } from "styled-components";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent
} from "@radix-ui/react-dropdown-menu";
import { isNotNil as isNotNil19, isNotUndefined as isNotUndefined10 } from "@wistia/type-guards";
import { useMemo as useMemo7 } from "react";

// src/components/Menu/MenuContext.tsx
import { createContext as createContext6, useContext as useContext5 } from "react";
var MenuContext = createContext6({ compact: false });
var useMenuContext = () => useContext5(MenuContext);

// src/components/Menu/Menu.tsx
import { jsx as jsx232, jsxs as jsxs26 } from "react/jsx-runtime";
var open = keyframes3`
  from {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;
var close = keyframes3`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(.97) translateY(-8px);
  }
`;
var menuItemCss = css29`
  --menu-label-description-gap: var(--wui-space-01);
  --menu-item-inner-gap: var(--wui-space-03);
  --menu-item-left-icon-size: 24px;
  --menu-item-right-icon-size: 16px;
  --menu-item-padding: var(--wui-space-02);
  --menu-label-font-size: var(--wui-typography-label-3-size);
  --menu-label-font-weight: var(--wui-typography-label-3-weight);
  --menu-label-line-height: var(--wui-typography-label-3-line-height);
  --menu-divider-margin: var(--wui-space-02);
`;
var compactMenuItemCss = css29`
  --menu-label-description-gap: 0;
  --menu-item-inner-gap: var(--wui-space-02);
  --menu-item-left-icon-size: 16px;
  --menu-item-right-icon-size: 12px;
  --menu-item-padding: 6px;
  --menu-label-font-size: var(--wui-typography-label-4-size);
  --menu-label-font-weight: var(--wui-typography-label-4-weight);
  --menu-label-line-height: var(--wui-typography-label-4-line-height);
  --menu-divider-margin: var(--wui-space-01);
`;
var menuContentCss = css29`
  --menu-font-family: var(--wui-typography-family-default);
  --menu-bg: var(--wui-color-bg-surface);
  --menu-shadow: var(--wui-elevation-01);
  --menu-border-radius: var(--wui-border-radius-02);
  --menu-padding: var(--wui-space-02);
  --menu-min-width: 120px;
  --menu-max-width: 284px;
  --menu-animation-duration: var(--wui-motion-duration-03);

  ${({ $compact }) => $compact ? compactMenuItemCss : menuItemCss};

  animation: var(--menu-animation-duration) var(--wui-motion-ease-out) ${open};
  font-family: var(--menu-font-family);
  background: var(--menu-bg);
  border-radius: var(--menu-border-radius);
  box-shadow: var(--menu-shadow); /* TODO - Need a box-shadow token */
  max-height: var(--radix-dropdown-menu-content-available-height);
  min-width: var(--menu-min-width);
  max-width: var(--menu-max-width);
  overflow: auto;
  padding: var(--menu-padding);
  transform-origin: var(--radix-dropdown-menu-content-transform-origin);
  z-index: var(--wui-zindex-menu);
  border: 1px solid var(--wui-color-border);

  &[data-state='closed'] {
    animation: var(--menu-animation-duration) var(--wui-motion-ease-in) ${close}; /* TODO - need easing tokens */
  }

  ${mq.smAndUp} {
    bottom: 0;
    padding: var(--menu-padding);
    right: 0;
    top: 0;
    transform: initial;
  }

  [role='separator'] {
    margin: var(--menu-divider-margin) 0;
  }
`;
var MenuContent = styled40(DropdownMenuContent)`
  ${menuContentCss}
`;
var Menu = ({
  align = "start",
  children,
  disabled = false,
  compact = false,
  trigger,
  label,
  isOpen,
  triggerProps = {},
  onOpenChange,
  onInteractOutside,
  ...props
}) => {
  const contextValue = useMemo7(() => ({ compact }), [compact]);
  let controlProps = {
    ...isNotNil19(onOpenChange) && isNotNil19(isOpen) ? { open: isOpen, onOpenChange } : {}
  };
  if (disabled) {
    controlProps = {
      open: false,
      onOpenChange: () => null
    };
  }
  return /* @__PURE__ */ jsx232(MenuContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsxs26(
    DropdownMenu,
    {
      modal: false,
      ...controlProps,
      children: [
        /* @__PURE__ */ jsx232(DropdownMenuTrigger, { asChild: true, children: isNotUndefined10(trigger) ? trigger : /* @__PURE__ */ jsx232(
          Button,
          {
            "aria-expanded": isOpen,
            disabled,
            forceState: isOpen ? "active" : void 0,
            rightIcon: /* @__PURE__ */ jsx232(Icon, { type: "caret-down" }),
            ...triggerProps,
            children: label
          }
        ) }),
        /* @__PURE__ */ jsx232(DropdownMenuPortal, { children: /* @__PURE__ */ jsx232(
          MenuContent,
          {
            ...props,
            $compact: compact,
            align,
            collisionPadding: 8,
            sideOffset: 6,
            onFocusOutside: (event) => {
              event.preventDefault();
            },
            children
          }
        ) })
      ]
    }
  ) });
};
Menu.displayName = "Menu";

// src/components/Menu/MenuLabel.tsx
import styled41 from "styled-components";
import { DropdownMenuLabel } from "@radix-ui/react-dropdown-menu";
import { jsx as jsx233 } from "react/jsx-runtime";
var StyledMenuLabel = styled41(DropdownMenuLabel)`
  padding: var(--wui-space-02);
`;
var MenuLabel = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx233(
    StyledMenuLabel,
    {
      asChild: true,
      ...props,
      children: /* @__PURE__ */ jsx233(
        Heading,
        {
          renderAs: "span",
          variant: "heading6",
          ...props,
          children
        }
      )
    }
  );
};
MenuLabel.displayName = "MenuLabel";

// src/components/Menu/SubMenu.tsx
import { useState as useState13 } from "react";
import styled44 from "styled-components";
import {
  DropdownMenuPortal as DropdownMenuPortal2,
  DropdownMenuSub,
  DropdownMenuGroup,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuItem
} from "@radix-ui/react-dropdown-menu";
import { isNotNil as isNotNil21 } from "@wistia/type-guards";

// src/components/Menu/MenuItemButton.tsx
import { forwardRef as forwardRef15 } from "react";
import styled42 from "styled-components";
import { isNotNil as isNotNil20, isNotUndefined as isNotUndefined11 } from "@wistia/type-guards";
import { jsx as jsx234, jsxs as jsxs27 } from "react/jsx-runtime";
var StyledButton3 = styled42(Button)`
  ${({ colorScheme }) => getColorScheme(colorScheme)};

  display: flex;
  flex-direction: row;
  gap: var(--menu-item-inner-gap);
  padding: var(--menu-item-padding);
  border-radius: var(--wui-border-radius-01);
  text-align: left;
  color: var(--wui-color-text-button);
  font-size: var(--menu-label-font-size);
  font-weight: var(--menu-label-font-weight);
  line-height: var(--menu-label-line-height);
  transition: background-color var(--wui-motion-duration-01) var(--wui-motion-ease);

  :active {
    background-color: var(--wui-color-bg-surface-active);
  }

  &[data-highlighted] {
    outline: none;
    ${({ $isGated }) => $isGated && getColorScheme("purple")};
    background-color: var(--wui-color-bg-surface-hover);
  }

  &[aria-disabled='true'] {
    --wui-color-text-button: var(--wui-color-text-disabled);
    --wui-color-text-secondary: var(--wui-color-text-disabled);
    --wui-color-icon: var(--wui-color-icon-disabled);

    &[data-highlighted] {
      background-color: transparent;
    }
  }

  &[role='menuitemradio'] {
    padding-left: var(--wui-space-04);
  }
`;
var StyledLeftIconContainer = styled42.div`
  height: var(--menu-item-left-icon-size);
  width: var(--menu-item-left-icon-size);

  && {
    svg {
      height: var(--menu-item-left-icon-size);
      width: var(--menu-item-left-icon-size);
    }
  }
`;
var StyledRightIconContainer = styled42.div`
  height: var(--menu-item-right-icon-size);
  width: var(--menu-item-right-icon-size);

  && {
    svg {
      height: var(--menu-item-right-icon-size);
      width: var(--menu-item-right-icon-size);
    }
  }
`;
var StyledLabelAndDescriptionContainer = styled42.div`
  display: flex;
  flex-direction: column;
  gap: var(--menu-label-description-gap);
  flex-basis: 100%;
`;
var StyledBadgeContainer = styled42.div`
  align-self: start;
  justify-self: end;
  font-size: var(--wui-typography-label-4-size);
  color: var(--wui-color-text-secondary);
`;
var MenuItemButton = forwardRef15(({ children, appearance, command, icon, ...props }, ref) => {
  let { colorScheme, badge } = props;
  if (appearance === "dangerous") {
    if (isNotUndefined11(colorScheme)) {
      console.warn("colorScheme prop is ignored when appearance is dangerous");
    }
    colorScheme = "error";
  }
  if (appearance === "gated") {
    badge = /* @__PURE__ */ jsx234(
      Icon,
      {
        colorScheme: "purple",
        style: {
          height: "16px",
          width: "16px"
        },
        type: "sparkle"
      }
    );
  }
  return /* @__PURE__ */ jsxs27(
    StyledButton3,
    {
      ...props,
      ref,
      $isGated: appearance === "gated",
      colorScheme: colorScheme ?? "default",
      fullWidth: true,
      unstyled: true,
      children: [
        props.leftIcon ? /* @__PURE__ */ jsx234(StyledLeftIconContainer, { children: props.leftIcon }) : null,
        /* @__PURE__ */ jsx234(StyledLabelAndDescriptionContainer, { children }),
        isNotNil20(badge) || isNotNil20(command) ? /* @__PURE__ */ jsx234(StyledBadgeContainer, { children: badge ?? command }) : null,
        props.rightIcon ? /* @__PURE__ */ jsx234(StyledRightIconContainer, { children: props.rightIcon }) : null
      ]
    }
  );
});
MenuItemButton.displayName = "MenuItemButton";

// src/components/Menu/MenuItemLabelDescription.tsx
import styled43 from "styled-components";
import { jsx as jsx235 } from "react/jsx-runtime";
var StyledMenuItemLabel = styled43.span``;
var StyledMenuItemDescription = styled43(Text)``;
var MenuItemLabel = ({ children }) => {
  return /* @__PURE__ */ jsx235(StyledMenuItemLabel, { children });
};
var MenuItemDescription = ({ children }) => {
  return /* @__PURE__ */ jsx235(
    StyledMenuItemDescription,
    {
      prominence: "secondary",
      variant: "body4",
      children
    }
  );
};

// src/components/Menu/SubMenu.tsx
import { jsx as jsx236, jsxs as jsxs28 } from "react/jsx-runtime";
var SubMenuContent = styled44(DropdownMenuSubContent)`
  ${menuContentCss}

  ${mq.smAndDown} {
    transform: translateX(-100%) !important;
  }
`;
var StyledMobileSubMenuItems = styled44.div`
  margin-left: var(--wui-space-04);
`;
var StyledSubTrigger = styled44(DropdownMenuSubTrigger)`
  outline: none;

  &[data-state='open'],
  &[data-highlighted] {
    background-color: var(--wui-color-bg-surface-hover);
  }
`;
var SubMenuTrigger = ({ icon, ...props }) => {
  const { isSmAndUp } = useMq();
  const Trigger4 = isSmAndUp ? StyledSubTrigger : DropdownMenuItem;
  return /* @__PURE__ */ jsx236(Trigger4, { asChild: true, children: /* @__PURE__ */ jsx236(
    MenuItemButton,
    {
      ...props,
      leftIcon: icon,
      rightIcon: /* @__PURE__ */ jsx236(Icon, { type: "caret-right" })
    }
  ) });
};
var SubMenu = ({
  label,
  description,
  children,
  onOpenChange = () => null,
  ...props
}) => {
  const { isSmAndUp } = useMq();
  const [isExpanded, setIsExpanded] = useState13(false);
  const { compact } = useMenuContext();
  return isSmAndUp ? /* @__PURE__ */ jsxs28(DropdownMenuSub, { onOpenChange, children: [
    /* @__PURE__ */ jsxs28(SubMenuTrigger, { ...props, children: [
      /* @__PURE__ */ jsx236(MenuItemLabel, { children: label }),
      isNotNil21(description) ? /* @__PURE__ */ jsx236(MenuItemDescription, { children: description }) : null
    ] }),
    /* @__PURE__ */ jsx236(DropdownMenuPortal2, { children: /* @__PURE__ */ jsx236(SubMenuContent, { $compact: compact, children }) })
  ] }) : /* @__PURE__ */ jsxs28(DropdownMenuGroup, { children: [
    /* @__PURE__ */ jsxs28(
      SubMenuTrigger,
      {
        ...props,
        onClick: (event) => {
          event.preventDefault();
          setIsExpanded((prev) => !prev);
        },
        children: [
          /* @__PURE__ */ jsx236(MenuItemLabel, { children: label }),
          /* @__PURE__ */ jsx236(MenuItemDescription, { children: description })
        ]
      }
    ),
    /* @__PURE__ */ jsx236(StyledMobileSubMenuItems, { children: isExpanded ? children : null })
  ] });
};
SubMenu.displayName = "SubMenu";

// src/components/Menu/MenuItem.tsx
import { forwardRef as forwardRef16 } from "react";
import { DropdownMenuItem as DropdownMenuItem2 } from "@radix-ui/react-dropdown-menu";
import { jsx as jsx237 } from "react/jsx-runtime";
var MenuItem = forwardRef16(
  ({ onSelect = () => null, ...props }, ref) => {
    return /* @__PURE__ */ jsx237(
      DropdownMenuItem2,
      {
        asChild: true,
        onSelect,
        children: /* @__PURE__ */ jsx237(
          MenuItemButton,
          {
            ...props,
            ref,
            leftIcon: props.icon
          }
        )
      }
    );
  }
);
MenuItem.displayName = "MenuItem";

// src/components/Menu/MenuRadioGroup.tsx
import {
  DropdownMenuRadioGroup
} from "@radix-ui/react-dropdown-menu";
import { jsx as jsx238, jsxs as jsxs29 } from "react/jsx-runtime";
var MenuRadioGroup = ({ children, label, ...props }) => {
  return /* @__PURE__ */ jsxs29(DropdownMenuRadioGroup, { ...props, children: [
    /* @__PURE__ */ jsx238(MenuLabel, { children: label }),
    children
  ] });
};
MenuRadioGroup.displayName = "MenuRadioGroup";

// src/components/Menu/RadioMenuItem.tsx
import {
  DropdownMenuItemIndicator,
  DropdownMenuRadioItem
} from "@radix-ui/react-dropdown-menu";
import { jsx as jsx239 } from "react/jsx-runtime";
var RadioMenuItem = ({
  onSelect,
  value,
  indicator = /* @__PURE__ */ jsx239(
    Icon,
    {
      size: "sm",
      type: "checkmark"
    }
  ),
  ...props
}) => {
  const extraProps = onSelect ? { onSelect } : {};
  return /* @__PURE__ */ jsx239(
    DropdownMenuRadioItem,
    {
      ...extraProps,
      asChild: true,
      value,
      children: /* @__PURE__ */ jsx239(
        MenuItemButton,
        {
          ...props,
          rightIcon: /* @__PURE__ */ jsx239(DropdownMenuItemIndicator, { children: indicator })
        }
      )
    }
  );
};
RadioMenuItem.displayName = "RadioMenuItem";

// src/components/Menu/CheckboxMenuItem.tsx
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItemIndicator as DropdownMenuItemIndicator2
} from "@radix-ui/react-dropdown-menu";
import { isNotNil as isNotNil22 } from "@wistia/type-guards";
import { jsx as jsx240, jsxs as jsxs30 } from "react/jsx-runtime";
var CheckboxIndicator = ({ checked, ...props }) => {
  return checked ? /* @__PURE__ */ jsxs30(
    "svg",
    {
      ...props,
      fill: "none",
      height: "24",
      viewBox: "0 0 24 24",
      width: "24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx240(
          "path",
          {
            d: "m4 8c0-2.20914 1.79086-4 4-4h8c2.2091 0 4 1.79086 4 4v8c0 2.2091-1.7909 4-4 4h-8c-2.20914 0-4-1.7909-4-4z",
            fill: "#2949e5"
          }
        ),
        /* @__PURE__ */ jsx240(
          "path",
          {
            d: "m10.4728 15.1931-3.09523-3.1131c-.18596-.187-.18596-.4902 0-.6773l.67341-.6773c.18596-.187.48749-.187.67344 0l2.08508 2.0971 4.4661-4.49174c.1859-.18703.4875-.18703.6734 0l.6734.67731c.186.18703.186.49027 0 .67731l-5.4762 5.50772c-.1859.187-.4874.187-.6734 0z",
            fill: "#fff"
          }
        )
      ]
    }
  ) : /* @__PURE__ */ jsxs30(
    "svg",
    {
      ...props,
      fill: "none",
      height: "24",
      viewBox: "0 0 24 24",
      width: "24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx240(
          "path",
          {
            d: "m8 4.5h8c1.933 0 3.5 1.567 3.5 3.5v8c0 1.933-1.567 3.5-3.5 3.5h-8c-1.933 0-3.5-1.567-3.5-3.5v-8c0-1.933 1.567-3.5 3.5-3.5z",
            fill: "#fcfcfd"
          }
        ),
        /* @__PURE__ */ jsx240(
          "path",
          {
            d: "m8 4.5h8c1.933 0 3.5 1.567 3.5 3.5v8c0 1.933-1.567 3.5-3.5 3.5h-8c-1.933 0-3.5-1.567-3.5-3.5v-8c0-1.933 1.567-3.5 3.5-3.5z",
            stroke: "#bbbbc4"
          }
        )
      ]
    }
  );
};
var CheckboxMenuItem = ({
  onSelect = () => null,
  checked,
  onCheckedChange,
  ...props
}) => {
  return /* @__PURE__ */ jsx240(
    DropdownMenuCheckboxItem,
    {
      asChild: true,
      checked,
      onCheckedChange,
      onSelect,
      children: /* @__PURE__ */ jsx240(
        MenuItemButton,
        {
          ...props,
          leftIcon: isNotNil22(props.icon) ? props.icon : /* @__PURE__ */ jsx240(CheckboxIndicator, { checked }),
          rightIcon: isNotNil22(props.icon) ? /* @__PURE__ */ jsx240(DropdownMenuItemIndicator2, { children: /* @__PURE__ */ jsx240(Icon, { type: "checkmark" }) }) : void 0
        }
      )
    }
  );
};
CheckboxMenuItem.displayName = "CheckboxMenuItem";

// src/components/Modal/Modal.tsx
import { forwardRef as forwardRef19 } from "react";
import styled49 from "styled-components";
import { Root as DialogRoot, Portal as DialogPortal } from "@radix-ui/react-dialog";
import { isNotNil as isNotNil24 } from "@wistia/type-guards";

// src/components/Modal/ModalHeader.tsx
import styled46 from "styled-components";
import { Title as DialogTitle } from "@radix-ui/react-dialog";

// src/components/Modal/ModalCloseButton.tsx
import styled45 from "styled-components";
import { Close as DialogClose } from "@radix-ui/react-dialog";
import { jsx as jsx241 } from "react/jsx-runtime";
var CloseButton = styled45(DialogClose)`
  align-self: start;
`;
var ModalCloseButton = () => {
  return /* @__PURE__ */ jsx241(CloseButton, { asChild: true, children: /* @__PURE__ */ jsx241(
    IconButton,
    {
      label: "Dismiss modal",
      size: "sm",
      variant: "ghost",
      children: /* @__PURE__ */ jsx241(Icon, { type: "close" })
    }
  ) });
};

// src/components/Modal/ModalHeader.tsx
import { jsx as jsx242, jsxs as jsxs31 } from "react/jsx-runtime";
var Header = styled46.header`
  display: flex;
  order: 1;
  gap: var(--wui-space-01);
  justify-content: ${({ $hideTitle }) => $hideTitle ? "flex-end" : "space-between"}; /* ensure ModalCloseButton is right-aligned */

  /* DialogTitle creates an h2 element which inherits some margin */
  h2 {
    margin: 0 0 var(--wui-space-03);
  }
`;
var Title = styled46(DialogTitle)`
  font-family: var(--wui-typography-heading-2-family);
  line-height: var(--wui-typography-heading-2-line-height);
  font-size: var(--wui-typography-heading-2-size);
  font-weight: var(--wui-typography-heading-2-weight);
`;
var ModalHeader = ({
  title,
  hideTitle,
  hideCloseButton
}) => {
  const TitleComponent = hideTitle ? /* @__PURE__ */ jsx242(ScreenReaderOnly, { children: /* @__PURE__ */ jsx242(Title, { children: title }) }) : /* @__PURE__ */ jsx242(Title, { children: title });
  return /* @__PURE__ */ jsxs31(Header, { $hideTitle: hideTitle, children: [
    TitleComponent,
    hideCloseButton ? null : /* @__PURE__ */ jsx242(ModalCloseButton, {})
  ] });
};

// src/components/Modal/ModalContent.tsx
import { forwardRef as forwardRef17 } from "react";
import styled47, { css as css30 } from "styled-components";
import { Content as DialogContent } from "@radix-ui/react-dialog";

// src/private/hooks/useFocusRestore/useFocusRestore.ts
import { useRef as useRef11, useEffect as useEffect10 } from "react";
import { isNotNil as isNotNil23 } from "@wistia/type-guards";
var useFocusRestore = () => {
  const previouslyFocusedRef = useRef11(null);
  useEffect10(() => {
    previouslyFocusedRef.current = document.activeElement;
  }, []);
  useEffect10(() => {
    return () => {
      if (isNotNil23(previouslyFocusedRef.current)) {
        setTimeout(() => {
          previouslyFocusedRef.current?.focus();
        }, 0);
      }
    };
  }, []);
};

// src/components/Modal/ModalContent.tsx
import { jsx as jsx243 } from "react/jsx-runtime";
var alignTopStyles = css30`
  --wui-modal-translate-y: 0;

  top: 0;
`;
var alignVerticalCenterStyles = css30`
  --wui-modal-translate-y: -50%;

  top: 50%;
`;
var alignBottomStyles = css30`
  --wui-modal-translate-y: 0;

  bottom: 0;
`;
var alignHorizontalCenterStyles = css30`
  --wui-modal-translate-x: -50%;

  left: 50%;
`;
var alignRightStyles = css30`
  --wui-modal-translate-x: 0;

  right: 0;
`;
var alignLeftStyles = css30`
  --wui-modal-translate-x: 0;

  left: 0;
`;
var StyledModalContent = styled47(DialogContent)`
  background-color: var(--wui-color-bg-app);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100vh;
  left: 0;
  overflow: auto;
  padding: var(--wui-space-05);
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: var(--wui-zindex-modal);

  ${mq.smAndUp} {
    --wui-modal-screen-offset: var(--wui-space-05);
    --wui-modal-max-height: calc(100vh - var(--wui-modal-screen-offset) * 2);
    --wui-modal-max-width: calc(100vw - var(--wui-modal-screen-offset) * 2);

    top: unset;
    left: unset;
    ${({ $alignVertical }) => $alignVertical === "top" && alignTopStyles}
    ${({ $alignVertical }) => $alignVertical === "center" && alignVerticalCenterStyles}
    ${({ $alignVertical }) => $alignVertical === "bottom" && alignBottomStyles}
    ${({ $alignHorizontal }) => $alignHorizontal === "center" && alignHorizontalCenterStyles}
    ${({ $alignHorizontal }) => $alignHorizontal === "right" && alignRightStyles}
    ${({ $alignHorizontal }) => $alignHorizontal === "left" && alignLeftStyles}
    animation: contentShow var(--wui-motion-duration-03) var(--wui-motion-ease-out);
    border-radius: var(--wui-border-radius-03);
    height: ${({ $fullHeight }) => $fullHeight ? "min-content" : "auto"};
    max-height: var(--wui-modal-max-height);
    ${({ $fullHeight }) => $fullHeight ? "min-height: calc(100vh - 48px);" : ""}
    max-width: var(--wui-modal-max-width);
    translate: var(--wui-modal-translate-x) var(--wui-modal-translate-y);
    min-width: ${({ $width }) => $width ?? DEFAULT_MODAL_WIDTH};
    width: ${({ $width }) => $width ?? DEFAULT_MODAL_WIDTH};
  }

  @keyframes contentShow {
    from {
      scale: 0.96;
    }

    to {
      scale: 1;
    }
  }
`;
var ModalContent = forwardRef17(
  ({ fullHeight, width, alignHorizontal, alignVertical, children, ...props }, ref) => {
    useFocusRestore();
    return /* @__PURE__ */ jsx243(
      StyledModalContent,
      {
        ref,
        $alignHorizontal: alignHorizontal,
        $alignVertical: alignVertical,
        $fullHeight: fullHeight,
        $width: width,
        "aria-describedby": void 0,
        ...props,
        children
      }
    );
  }
);

// src/private/components/Backdrop/Backdrop.tsx
import { forwardRef as forwardRef18 } from "react";
import styled48 from "styled-components";
import { jsx as jsx244 } from "react/jsx-runtime";
var alignVerticalMap = {
  stretch: "normal",
  top: "start",
  center: "center",
  bottom: "end"
};
var alignHorizontalMap = {
  left: "start",
  center: "center",
  right: "end"
};
var BackdropComponent = styled48.div`
  align-items: ${({ $alignVertical }) => alignVerticalMap[$alignVertical]};
  animation-name: backdropShow var(--wui-motion-duration-03) var(--wui-motion-ease-out);
  background: var(--wui-color-backdrop);
  display: flex;
  inset: 0;
  justify-content: ${({ $alignHorizontal }) => alignHorizontalMap[$alignHorizontal]};
  overflow-y: auto;
  position: fixed;
  z-index: var(--wui-zindex-backdrop);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }

  @keyframes backdropShow {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;
var Backdrop = forwardRef18(
  ({ alignHorizontal = "center", alignVertical = "center", children, ...otherProps }, ref) => /* @__PURE__ */ jsx244(
    BackdropComponent,
    {
      ref,
      $alignHorizontal: alignHorizontal,
      $alignVertical: alignVertical,
      ...otherProps,
      children
    }
  )
);
Backdrop.displayName = "Backdrop";

// src/components/Modal/Modal.tsx
import { jsx as jsx245, jsxs as jsxs32 } from "react/jsx-runtime";
var DEFAULT_MODAL_WIDTH = "532px";
var ModalBody = styled49.div`
  flex-direction: column;
  display: flex;
  flex: 1 0 0;
  order: 2;
`;
var Modal = forwardRef19(
  ({
    children,
    fullHeight = false,
    hideCloseButton = false,
    hideTitle = false,
    initialFocusRef,
    isOpen,
    onRequestClose,
    title,
    width = DEFAULT_MODAL_WIDTH,
    alignHorizontal = "center",
    alignVertical = "center",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx245(
      DialogRoot,
      {
        onOpenChange: (open2) => {
          if (!open2 && isNotNil24(onRequestClose)) {
            onRequestClose();
          }
        },
        open: isOpen,
        children: /* @__PURE__ */ jsxs32(DialogPortal, { children: [
          /* @__PURE__ */ jsx245(Backdrop, {}),
          /* @__PURE__ */ jsxs32(
            ModalContent,
            {
              ref,
              alignHorizontal,
              alignVertical,
              fullHeight,
              onOpenAutoFocus: (event) => {
                if (isNotNil24(initialFocusRef) && initialFocusRef.current) {
                  event.preventDefault();
                  requestAnimationFrame(() => {
                    initialFocusRef.current?.focus();
                  });
                }
              },
              width,
              ...props,
              children: [
                /* @__PURE__ */ jsx245(ModalBody, { children }),
                /* @__PURE__ */ jsx245(
                  ModalHeader,
                  {
                    hideCloseButton,
                    hideTitle,
                    title
                  }
                )
              ]
            }
          )
        ] })
      }
    );
  }
);
Modal.displayName = "Modal";

// src/components/Popover/Popover.tsx
import { Root, Trigger as Trigger2, Portal, Content as Content2, Close } from "@radix-ui/react-popover";
import styled51, { css as css32 } from "styled-components";

// src/private/helpers/getControls/getControlProps.tsx
import { isNotNil as isNotNil25 } from "@wistia/type-guards";
var getControlProps = (isOpen, onOpenChange) => {
  return isNotNil25(onOpenChange) && isNotNil25(isOpen) ? { open: isOpen, onOpenChange } : {};
};

// src/components/Popover/PopoverArrow.tsx
import { PopoverArrow as RadixPopoverArrow } from "@radix-ui/react-popover";
import styled50, { css as css31, keyframes as keyframes4 } from "styled-components";
import { jsx as jsx246, jsxs as jsxs33 } from "react/jsx-runtime";
var StyledPath = styled50.path`
  fill: var(--wui-color-border-secondary);
`;
var circleAnimation = keyframes4`
  0% {
    opacity: var(--wui-popover-arrow-circle-starting-opacity);
  }
  100% {
    opacity: 0;
  }
`;
var StyledCircle = styled50.circle`
  stroke: var(--wui-color-border-active);
  animation-duration: 2s;
  animation-iteration-count: infinite;
  animation-direction: alternate;
  animation-timing-function: ease-in-out;

  &[data-wui-popover-arrow-inner-circle] {
    --wui-popover-arrow-circle-starting-opacity: 0.36;

    opacity: var(--wui-popover-arrow-circle-starting-opacity);
  }

  &[data-wui-popover-arrow-outer-circle] {
    --wui-popover-arrow-circle-starting-opacity: 0.2;

    animation-direction: alternate-reverse;
    opacity: 0.11;
  }

  @media (prefers-reduced-motion: no-preference) {
    ${({ $isAnimated }) => $isAnimated && css31`
        animation-name: ${circleAnimation};
      `}
  }
`;
var PopoverArrow = ({ isAnimated }) => {
  return /* @__PURE__ */ jsx246(RadixPopoverArrow, { asChild: true, children: /* @__PURE__ */ jsxs33(
    "svg",
    {
      fill: "none",
      height: "56",
      viewBox: "0 0 48 56",
      width: "48",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx246(StyledPath, { d: "M24 26.6667C21.0545 26.6667 18.6667 29.0545 18.6667 32C18.6667 34.9455 21.0545 37.3333 24 37.3333C26.9455 37.3333 29.3333 34.9455 29.3333 32C29.3333 29.0545 26.9455 26.6667 24 26.6667ZM23 0V32H25V0H23Z" }),
        /* @__PURE__ */ jsx246(
          StyledCircle,
          {
            $isAnimated: isAnimated,
            cx: "24",
            cy: "32",
            "data-wui-popover-arrow-inner-circle": true,
            r: "10",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx246(
          StyledCircle,
          {
            $isAnimated: isAnimated,
            cx: "24",
            cy: "32",
            "data-wui-popover-arrow-outer-circle": true,
            r: "20",
            strokeWidth: "8"
          }
        )
      ]
    }
  ) });
};
PopoverArrow.displayName = "PopoverArrow";

// src/components/Popover/Popover.tsx
import { jsx as jsx247, jsxs as jsxs34 } from "react/jsx-runtime";
var StyledContent2 = styled51(Content2)`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  ${({ $unstyled }) => !$unstyled && css32`
      border-radius: var(--wui-border-radius-02);
      padding: var(--wui-space-04);
      max-width: var(--wui-popover-max-width, 320px);
      max-height: var(--wui-popover-max-height, auto);
      background-color: var(--wui-color-bg-surface);
      box-shadow: var(--wui-elevation-01);
      border: 1px solid var(--wui-color-border);
      overflow: auto;
    `}

  [data-wui-popover-close] {
    position: absolute;
    top: var(--wui-space-02);
    right: var(--wui-space-02);
  }
`;
var Popover = ({
  children,
  trigger,
  isOpen = false,
  hideCloseButton = false,
  maxWidth = "320px",
  maxHeight = "auto",
  onOpenChange,
  unstyled = false,
  withArrow = false,
  isAnimated = false,
  colorScheme = "inherit",
  ...props
}) => {
  const sideOffset = withArrow ? -24 : 8;
  const style = {
    "--wui-popover-max-width": maxWidth,
    "--wui-popover-max-height": maxHeight
  };
  return /* @__PURE__ */ jsxs34(Root, { ...getControlProps(isOpen, onOpenChange), children: [
    /* @__PURE__ */ jsx247(Trigger2, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx247(Portal, { children: /* @__PURE__ */ jsxs34(
      StyledContent2,
      {
        $colorScheme: colorScheme,
        sideOffset,
        ...props,
        $unstyled: unstyled,
        style,
        children: [
          !hideCloseButton && /* @__PURE__ */ jsx247(Close, { asChild: true, children: /* @__PURE__ */ jsx247(
            IconButton,
            {
              "data-wui-popover-close": true,
              label: "Close",
              variant: "ghost",
              children: /* @__PURE__ */ jsx247(Icon, { type: "close" })
            }
          ) }),
          withArrow ? /* @__PURE__ */ jsx247(PopoverArrow, { isAnimated }) : null,
          /* @__PURE__ */ jsx247("div", { children })
        ]
      }
    ) })
  ] });
};
Popover.displayName = "Popover";

// src/components/ProgressBar/ProgressBar.tsx
import styled52 from "styled-components";
import { Root as ProgressRoot, Indicator as ProgressIndicator } from "@radix-ui/react-progress";
import { isNotNil as isNotNil26 } from "@wistia/type-guards";
import { jsx as jsx248, jsxs as jsxs35 } from "react/jsx-runtime";
var ProgressBarWrapper = styled52.div`
  --progress-bar-height: 8px;
  --progress-bar-indicator-color: var(--wui-color-bg-fill);
  --progress-bar-background-color: var(--wui-color-bg-surface-secondary);

  display: flex;
  align-items: center;
  gap: var(--wui-space-02);
`;
var getTranslateValue = (progress, max) => {
  if (progress === 0) {
    return "translateX(-101%)";
  }
  const progressPercentage = progress / max * 100;
  return `translateX(-${100 - progressPercentage}%)`;
};
var ProgressBarLabel = styled52.div`
  display: flex;
  line-height: var(--wui-typography-label-3-line-height);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-weight-label);
  color: var(--wui-color-text-secondary);
  flex-shrink: 0;
`;
var StyledProgressIndicator = styled52(ProgressIndicator)`
  background-color: var(--progress-bar-indicator-color);
  width: 100%;
  height: 100%;
  border-top-right-radius: var(--wui-border-radius-rounded);
  border-bottom-right-radius: var(--wui-border-radius-rounded);
  transition: transform 660ms cubic-bezier(0.65, 0, 0.35, 1);
  transform: ${({ $progress, $max }) => getTranslateValue($progress, $max)};
`;
var StyledProgressBar = styled52(ProgressRoot)`
  position: relative;
  overflow: hidden;
  background: var(--progress-bar-background-color);
  border-radius: var(--wui-border-radius-rounded);
  width: 100%;
  height: var(--progress-bar-height);

  /* Fix overflow clipping in Safari: https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0 */
  transform: translateZ(0);
`;
var ProgressBar = ({
  progress,
  max = 100,
  children,
  labelLeft,
  labelRight,
  ...props
}) => {
  return /* @__PURE__ */ jsxs35(ProgressBarWrapper, { children: [
    isNotNil26(labelLeft) ? /* @__PURE__ */ jsx248(ProgressBarLabel, { children: labelLeft }) : null,
    /* @__PURE__ */ jsx248(
      StyledProgressBar,
      {
        max,
        value: progress,
        ...props,
        children: /* @__PURE__ */ jsx248(
          StyledProgressIndicator,
          {
            $max: max,
            $progress: progress
          }
        )
      }
    ),
    isNotNil26(labelRight) ? /* @__PURE__ */ jsx248(ProgressBarLabel, { children: labelRight }) : null
  ] });
};
ProgressBar.displayName = "ProgressBar";

// src/components/Radio/Radio.tsx
import { forwardRef as forwardRef20, useId as useId3 } from "react";
import styled53, { css as css33 } from "styled-components";
import { isNonEmptyString as isNonEmptyString3 } from "@wistia/type-guards";
import { jsx as jsx249, jsxs as jsxs36 } from "react/jsx-runtime";
var RadioIcon = () => /* @__PURE__ */ jsx249(
  "svg",
  {
    fill: "inherit",
    height: "1",
    viewBox: "0 0 1 1",
    width: "1",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx249(
      "circle",
      {
        cx: "0.5",
        cy: "0.5",
        fill: "currentColor",
        r: "0.5"
      }
    )
  }
);
var sizeSmall2 = css33`
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;

  svg {
    width: 7px;
    height: 7px;
  }
`;
var sizeMedium2 = css33`
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;

  svg {
    width: 8px;
    height: 8px;
  }
`;
var sizeLarge2 = css33`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;

  svg {
    width: 10px;
    height: 10px;
  }
`;
var getSizeCss2 = (size) => {
  if (size === "sm") return sizeSmall2;
  if (size === "lg") return sizeLarge2;
  return sizeMedium2;
};
var StyledRadioWrapper = styled53.div`
  display: flex;
  margin: 0;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;
var StyledRadioInput = styled53.div`
  ${({ $size }) => getSizeCss2($size)}
  line-height: 0;
  margin: 0;
  border-radius: 50%; /* TODO --wui-border-radius-rounded */
  display: grid;
  place-content: center;
  align-self: flex-start;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $disabled }) => $disabled ? "var(--wui-color-border-disabled)" : "var(--wui-color-border)"};
  background-color: ${({ $disabled }) => $disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-surface)"};

  /* radio button circle icon */
  svg {
    display: none;

    circle {
      fill: ${({ $disabled }) => $disabled ? "var(--wui-color-icon-disabled)" : "var(--wui-color-bg-fill)"};
    }
  }
`;
var StyledHiddenRadioInput = styled53.input`
  ${visuallyHiddenStyle}

  /* toggles display of faux-input border-color */
  &:checked + label [data-wui-faux-input='true'] {
    border-color: ${({ disabled }) => disabled ? "var(--wui-color-border-disabled)" : "var(--wui-color-bg-fill)"};
  }

  /* toggles display of RadioIcon */
  &:checked + label svg {
    display: block;
  }
`;
var Radio = forwardRef20(
  ({
    checked,
    disabled = false,
    id,
    label,
    description,
    name,
    onChange,
    size = "md",
    value = "false",
    required = false,
    hideLabel = false,
    ...props
  }, ref) => {
    const generatedId = useId3();
    const computedId = isNonEmptyString3(id) ? id : `wistia-ui-radio-${generatedId}`;
    return /* @__PURE__ */ jsxs36(
      StyledRadioWrapper,
      {
        $disabled: disabled,
        "aria-invalid": props["aria-invalid"],
        children: [
          /* @__PURE__ */ jsx249(
            StyledHiddenRadioInput,
            {
              ...props,
              ref,
              checked,
              disabled,
              id: computedId,
              name,
              onChange,
              required,
              type: "radio",
              value
            }
          ),
          /* @__PURE__ */ jsx249(
            FormControlLabel,
            {
              controlSlot: /* @__PURE__ */ jsx249(
                StyledRadioInput,
                {
                  $disabled: disabled,
                  $size: size,
                  "data-wui-faux-input": "true",
                  children: /* @__PURE__ */ jsx249(RadioIcon, {})
                }
              ),
              description,
              disabled,
              hideLabel,
              htmlFor: computedId,
              label
            }
          )
        ]
      }
    );
  }
);
Radio.displayName = "Radio";

// src/components/SegmentedControl/SegmentedControl.tsx
import { forwardRef as forwardRef21 } from "react";
import styled55, { css as css34 } from "styled-components";
import { Root as ToggleGroupRoot } from "@radix-ui/react-toggle-group";
import { isNil as isNil14 } from "@wistia/type-guards";

// src/components/SegmentedControl/useSelectedItemStyle.tsx
import { createContext as createContext7, useContext as useContext6, useMemo as useMemo8, useState as useState14 } from "react";
import { jsx as jsx250 } from "react/jsx-runtime";
var SelectedItemStyleContext = createContext7(null);
var SelectedItemStyleProvider = ({
  children
}) => {
  const [selectedItemMeasurements, setSelectedItemMeasurements] = useState14(null);
  const selectedItemIndicatorStyle = useMemo8(
    () => selectedItemMeasurements != null ? {
      height: `${selectedItemMeasurements.offsetHeight}px`,
      transform: `translateX(${selectedItemMeasurements.offsetLeft}px) translateY(-50%)`,
      width: `${selectedItemMeasurements.offsetWidth}px`
    } : {
      transform: "translateY(-50%)"
    },
    [selectedItemMeasurements]
  );
  const contextValue = useMemo8(
    () => ({
      setSelectedItemMeasurements,
      selectedItemIndicatorStyle
    }),
    [selectedItemIndicatorStyle]
  );
  return /* @__PURE__ */ jsx250(SelectedItemStyleContext.Provider, { value: contextValue, children });
};
var useSelectedItemStyle = () => {
  const context = useContext6(SelectedItemStyleContext);
  if (context === null) {
    throw new Error("useSelectedItemStyle must be used within a SelectedItemStyleProvider");
  }
  return context;
};

// src/components/SegmentedControl/SelectedItemIndicator.tsx
import styled54 from "styled-components";

// src/components/SegmentedControl/useSegmentedControlValue.tsx
import { createContext as createContext8, useContext as useContext7 } from "react";
var SegmentedControlValueContext = createContext8(null);
var SegmentedControlValueProvider = SegmentedControlValueContext.Provider;
var useSegmentedControlValue = () => {
  const context = useContext7(SegmentedControlValueContext);
  if (context === null) {
    throw new Error("useSegmentedControlValue must be used within a SegmentedControlValueProvider");
  }
  return context;
};

// src/components/SegmentedControl/SelectedItemIndicator.tsx
import { jsx as jsx251 } from "react/jsx-runtime";
var SelectedItemIndicatorDiv = styled54.div`
  background-color: var(--wui-color-bg-fill-white);
  border-radius: var(--wui-border-radius-rounded);
  left: 0;
  position: absolute;
  top: 50%;
  box-shadow: var(--wui-elevation-01);
  transition:
    transform var(--wui-motion-duration-02) var(--wui-motion-ease-out),
    width var(--wui-motion-duration-02) var(--wui-motion-ease-out);
  z-index: var(--wui-zindex-under);
`;
var SelectedItemIndicator = () => {
  const selectedValue = useSegmentedControlValue();
  const { selectedItemIndicatorStyle } = useSelectedItemStyle();
  if (!selectedValue) {
    return null;
  }
  return /* @__PURE__ */ jsx251(SelectedItemIndicatorDiv, { style: selectedItemIndicatorStyle });
};

// src/components/SegmentedControl/SegmentedControl.tsx
import { jsx as jsx252, jsxs as jsxs37 } from "react/jsx-runtime";
var segmentedControlStyles = css34`
  display: inline-flex;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-rounded);
  gap: var(--wui-space-01);
  isolation: isolate;
  overflow: hidden;
  padding: 2px;
  position: relative;
  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};
`;
var StyledSegmentedControl = styled55(ToggleGroupRoot)`
  ${segmentedControlStyles}
`;
var SegmentedControl = forwardRef21(
  ({
    children,
    disabled = false,
    fullWidth = false,
    selectedValue,
    onSelectedValueChange,
    ...props
  }, ref) => {
    if (isNil14(children)) {
      return null;
    }
    return /* @__PURE__ */ jsx252(
      StyledSegmentedControl,
      {
        ref,
        $fullWidth: fullWidth,
        disabled,
        loop: true,
        onValueChange: onSelectedValueChange,
        rovingFocus: true,
        type: "single",
        value: selectedValue,
        ...props,
        children: /* @__PURE__ */ jsx252(SegmentedControlValueProvider, { value: selectedValue, children: /* @__PURE__ */ jsxs37(SelectedItemStyleProvider, { children: [
          /* @__PURE__ */ jsx252(SelectedItemIndicator, {}),
          children
        ] }) })
      }
    );
  }
);
SegmentedControl.displayName = "SegmentedControl";

// src/components/SegmentedControl/SegmentedControlItem.tsx
import { forwardRef as forwardRef22, useEffect as useEffect11, useRef as useRef12 } from "react";
import styled56, { css as css35 } from "styled-components";
import { Item as ToggleGroupItem } from "@radix-ui/react-toggle-group";
import { isNotNil as isNotNil27 } from "@wistia/type-guards";
import { jsxs as jsxs38 } from "react/jsx-runtime";
var segmentedControlItemStyles = css35`
  all: unset; /* ToggleGroupItem is a button element */
  align-items: center;
  border-radius: var(--wui-border-radius-rounded);
  color: var(--wui-color-text-secondary);
  cursor: pointer;
  display: flex;
  gap: 2px;
  flex-grow: 1;
  flex-shrink: 1;
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-label-3-weight);
  line-height: var(--wui-typography-label-3-line-height);
  justify-content: center;
  padding: 6px ${({ $hasLabel }) => $hasLabel ? "12px" : "6px"};
  user-select: none;
  transition: background-color var(--wui-motion-duration-01) var(--wui-motion-ease);

  /* Icon component */
  svg {
    height: 16px;
    width: 16px;

    /* add right margin if there's a label and an icon */
    ${({ $hasLabel }) => $hasLabel ? "margin-right: var(--wui-space-01)" : ""}
  }

  &:focus-visible {
    outline: 2px solid var(--wui-color-focus-ring);
  }

  &:hover:not([data-state='on'], [data-state='active']) {
    background-color: var(--wui-color-bg-surface-secondary-hover);
  }

  /* note: react-toggle-group uses "on" and react-tabs uses "active" */
  &[data-state='on'],
  &[data-state='active'] {
    color: var(--wui-color-text-selected);
    cursor: default;
    transition: none;

    svg path {
      fill: var(--wui-color-icon-selected);
    }
  }

  &[data-disabled] {
    cursor: not-allowed;
    color: var(--wui-color-text-disabled);

    svg path {
      fill: var(--wui-color-icon-disabled);
    }

    &:hover {
      background-color: inherit;
    }
  }
`;
var StyledSegmentedControlItem = styled56(ToggleGroupItem)`
  ${segmentedControlItemStyles}
`;
var SegmentedControlItem = forwardRef22(
  ({ disabled, icon, label, "aria-label": ariaLabel, value }, forwardedRef) => {
    const selectedValue = useSegmentedControlValue();
    const { setSelectedItemMeasurements } = useSelectedItemStyle();
    const buttonRef = useRef12(null);
    const combinedRef = mergeRefs([buttonRef, forwardedRef]);
    const handleClick = (event) => {
      const target = event.target;
      const { state } = target.dataset;
      if (state === "on") {
        event.preventDefault();
      }
    };
    useEffect11(() => {
      const buttonElem = buttonRef.current;
      if (!buttonElem) {
        return void 0;
      }
      if (selectedValue !== value) {
        return void 0;
      }
      setSelectedItemMeasurements({
        offsetLeft: buttonElem.offsetLeft,
        offsetWidth: buttonElem.offsetWidth,
        offsetHeight: buttonElem.offsetHeight
      });
      const resizeObserver = new ResizeObserver(() => {
        setSelectedItemMeasurements({
          offsetLeft: buttonElem.offsetLeft,
          offsetWidth: buttonElem.offsetWidth,
          offsetHeight: buttonElem.offsetHeight
        });
      });
      resizeObserver.observe(buttonElem);
      return () => {
        resizeObserver.disconnect();
      };
    }, [selectedValue, setSelectedItemMeasurements, value]);
    return /* @__PURE__ */ jsxs38(
      StyledSegmentedControlItem,
      {
        ref: combinedRef,
        $hasLabel: isNotNil27(label),
        "aria-label": isNotNil27(label) ? void 0 : ariaLabel,
        disabled,
        onClick: handleClick,
        value,
        children: [
          icon,
          label
        ]
      }
    );
  }
);
SegmentedControlItem.displayName = "SegmentedControlItem";

// src/components/Select/Select.tsx
import {
  Root as Root2,
  Trigger as Trigger3,
  Portal as Portal2,
  Content as Content3,
  ScrollUpButton,
  Viewport,
  Value,
  ScrollDownButton
} from "@radix-ui/react-select";
import { forwardRef as forwardRef23 } from "react";
import styled57 from "styled-components";
import { jsx as jsx253, jsxs as jsxs39 } from "react/jsx-runtime";
var StyledTrigger = styled57(Trigger3)`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  ${inputCssVariables};
  padding: var(--wui-input-vertical-padding) var(--wui-input-horizontal-padding);
  background-color: var(--wui-input-color-bg);
  border: none;
  outline: 1px solid var(--wui-input-color-border);
  outline-offset: -2px;
  border-radius: var(--wui-input-border-radius);
  min-width: 200px;
  text-align: left;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  width: ${({ $fullWidth = false }) => $fullWidth ? "100%" : "auto"};
  font-size: var(--wui-input-font-size);
  line-height: var(--wui-input-line-height);
  color: var(--wui-input-color-text);

  > span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &[aria-expanded='true'],
  &:focus {
    outline: 2px solid var(--wui-color-focus-ring);
  }

  &[aria-invalid='true'] {
    outline: 2px solid var(--wui-input-color-border-error);
  }

  &:disabled {
    outline-color: var(--wui-color-border-disabled);
    background-color: var(--wui-color-bg-surface-disabled);
  }

  &[data-placeholder] {
    color: var(--wui-input-placeholder);
  }
`;
var StyledContent3 = styled57(Content3)`
  --wui-select-content-border: var(--wui-color-border);
  --wui-select-content-bg: var(--wui-color-bg-surface);
  --wui-select-content-border-radius: var(--wui-border-radius-02);
  --wui-select-option-padding: var(--wui-space-02);
  --wui-select-option-inner-gap: var(--wui-space-03);
  --wui-select-option-bg-hover: var(--wui-color-bg-surface-hover);

  overflow: hidden;
  transform: translateY(8px);
  background-color: var(--wui-select-content-bg);
  border: 1px solid var(--wui-select-content-border);
  border-radius: var(--wui-select-content-border-radius);
  padding: var(--wui-select-option-padding);
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
  z-index: var(--wui-zindex-select);
`;
var Select = forwardRef23(
  ({
    colorScheme = "inherit",
    children,
    onChange = () => null,
    placeholder = "Select...",
    fullWidth = false,
    ...props
  }, forwardedRef) => {
    const responsiveFullWidth = useResponsiveProp(fullWidth);
    return /* @__PURE__ */ jsxs39(
      Root2,
      {
        ...props,
        onValueChange: onChange,
        children: [
          /* @__PURE__ */ jsxs39(
            StyledTrigger,
            {
              ref: forwardedRef,
              $colorScheme: colorScheme,
              $fullWidth: responsiveFullWidth,
              ...props,
              children: [
                /* @__PURE__ */ jsx253(Value, { placeholder }),
                /* @__PURE__ */ jsx253(
                  Icon,
                  {
                    size: "md",
                    type: "caret-down"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx253(Portal2, { children: /* @__PURE__ */ jsxs39(StyledContent3, { position: "popper", children: [
            /* @__PURE__ */ jsx253(ScrollUpButton, { children: /* @__PURE__ */ jsx253(Icon, { type: "caret-up" }) }),
            /* @__PURE__ */ jsx253(Viewport, { children }),
            /* @__PURE__ */ jsx253(ScrollDownButton, { children: /* @__PURE__ */ jsx253(Icon, { type: "caret-down" }) })
          ] }) })
        ]
      }
    );
  }
);
Select.displayName = "Select";

// src/components/Select/SelectOption.tsx
import { Item, ItemText, ItemIndicator } from "@radix-ui/react-select";
import { forwardRef as forwardRef24 } from "react";
import styled58 from "styled-components";
import { isNotNil as isNotNil28 } from "@wistia/type-guards";
import { jsx as jsx254, jsxs as jsxs40 } from "react/jsx-runtime";
var StyledItem = styled58(Item)`
  ${variantStyleMap2.body3};
  display: flex;
  padding: var(--wui-select-option-padding);
  gap: var(--wui-select-option-inner-gap);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  border-radius: var(--wui-border-radius-01);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: var(--wui-select-option-bg-hover);
    outline: none;
  }

  &[aria-disabled='true'] {
    color: var(--wui-color-text-disabled);
  }

  &[aria-disabled='true']:hover {
    background-color: transparent;
  }
`;
var StyledIconContainer = styled58.span`
  width: 12px;
`;
var SelectOption = forwardRef24(
  ({ children, selectedDisplayValue, ...props }, forwardedRef) => {
    return /* @__PURE__ */ jsxs40(
      StyledItem,
      {
        ...props,
        ref: forwardedRef,
        children: [
          /* @__PURE__ */ jsx254(StyledIconContainer, { "data-indicator": true, children: /* @__PURE__ */ jsx254(ItemIndicator, { children: /* @__PURE__ */ jsx254(
            Icon,
            {
              size: "sm",
              type: "checkmark"
            }
          ) }) }),
          isNotNil28(selectedDisplayValue) ? /* @__PURE__ */ jsxs40("div", { children: [
            children,
            /* @__PURE__ */ jsx254("div", { style: { display: "none" }, children: /* @__PURE__ */ jsx254(ItemText, { children: selectedDisplayValue }) })
          ] }) : /* @__PURE__ */ jsx254(ItemText, { children })
        ]
      }
    );
  }
);
SelectOption.displayName = "SelectOption";

// src/components/Select/SelectOptionGroup.tsx
import { Group, Label as Label3 } from "@radix-ui/react-select";
import styled59 from "styled-components";
import { jsx as jsx255, jsxs as jsxs41 } from "react/jsx-runtime";
var StyledLabel4 = styled59(Label3)`
  padding: var(--wui-select-option-padding);
`;
var SelectOptionGroup = ({ children, label, ...props }) => {
  return /* @__PURE__ */ jsxs41(Group, { ...props, children: [
    /* @__PURE__ */ jsx255(StyledLabel4, { children: /* @__PURE__ */ jsx255(
      Text,
      {
        prominence: "secondary",
        variant: "body3",
        children: label
      }
    ) }),
    children
  ] });
};

// src/components/Switch/Switch.tsx
import { forwardRef as forwardRef25, useId as useId4 } from "react";
import styled60, { css as css36 } from "styled-components";
import { isNonEmptyString as isNonEmptyString4 } from "@wistia/type-guards";
import { jsx as jsx256, jsxs as jsxs42 } from "react/jsx-runtime";
var sizeSmall3 = css36`
  --wui-size-small-width: 24px;
  --wui-size-small-height: 14px;

  width: var(--wui-size-small-width);
  height: var(--wui-size-small-height);
  min-width: var(--wui-size-small-width);
  min-height: var(--wui-size-small-height);
  border-radius: calc(var(--wui-size-small-height) / 2);

  &::after {
    width: calc(var(--wui-size-small-width) / 2);
    height: calc(var(--wui-size-small-width) / 2);
  }
`;
var sizeMedium3 = css36`
  --wui-size-medium-width: 32px;
  --wui-size-medium-height: 18px;

  width: var(--wui-size-medium-width);
  height: var(--wui-size-medium-height);
  min-width: var(--wui-size-medium-width);
  min-height: var(--wui-size-medium-height);
  border-radius: calc(var(--wui-size-medium-height) / 2);

  &::after {
    width: calc(var(--wui-size-medium-width) / 2);
    height: calc(var(--wui-size-medium-width) / 2);
  }
`;
var sizeLarge3 = css36`
  --wui-size-large-width: 40px;
  --wui-size-large-height: 22px;

  width: var(--wui-size-large-width);
  height: var(--wui-size-large-height);
  min-width: var(--wui-size-large-width);
  min-height: var(--wui-size-large-height);
  border-radius: calc(var(--wui-size-large-height) / 2);

  &::after {
    width: calc(var(--wui-size-large-width) / 2);
    height: calc(var(--wui-size-large-width) / 2);
  }
`;
var getSizeCss3 = (size) => {
  if (size === "sm") return sizeSmall3;
  if (size === "lg") return sizeLarge3;
  return sizeMedium3;
};
var StyledSwitchWrapper = styled60.div`
  display: flex;
  margin: 0;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;
var StyledSwitchInput = styled60.div`
  ${({ $size }) => getSizeCss3($size)}
  line-height: 0;
  margin: 0;
  display: grid;
  place-content: center;
  align-self: flex-start;
  position: relative;
  transition: all 0.2s ease-in-out;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $disabled }) => $disabled ? "var(--wui-color-border-disabled)" : "var(--wui-color-border)"}; /* "off state" border */

  background-color: ${({ $disabled }) => $disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-surface-secondary)"}; /* "off state" background */

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    box-shadow: 1px 0 1px rgb(0 0 0 / 10%); /* "off state" shadow - cast horizontally right */
    background-color: ${({ $disabled }) => $disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-fill-white)"}; /* "off state" dot color */

    transition: all 0.2s ease-in-out;
  }
`;
var StyledHiddenSwitchInput = styled60.input`
  ${visuallyHiddenStyle}

  /* toggles display of faux-input background-color and toggle position */
  &:checked + label [data-wui-faux-input='true'] {
    border-color: ${({ disabled }) => disabled ? void 0 : "var(--wui-color-bg-fill)"}; /* "on state" border */

    background-color: ${({ disabled }) => disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-fill)"}; /* "on state" background */

    &::after {
      transform: translate(calc(100% - 2px), -50%); /* 2px is the border width */
      box-shadow: -1px 0 1px rgb(0 0 0 / 10%); /* "on state" shadow - cast horizontally left */
      background-color: ${({ disabled }) => disabled ? "var(--wui-color-bg-surface-disabled)" : "var(--wui-color-bg-fill-white)"}; /* "on state" dot color */
    }
  }
`;
var Switch = forwardRef25(
  ({
    checked,
    disabled = false,
    id,
    label,
    description,
    name,
    onChange,
    size = "md",
    value,
    required = false,
    hideLabel = false,
    ...props
  }, ref) => {
    const generatedId = useId4();
    const computedId = isNonEmptyString4(id) ? id : `wistia-ui-switch-${generatedId}`;
    return /* @__PURE__ */ jsxs42(StyledSwitchWrapper, { $disabled: disabled, children: [
      /* @__PURE__ */ jsx256(
        StyledHiddenSwitchInput,
        {
          ...props,
          ref,
          checked,
          disabled,
          id: computedId,
          name,
          onChange,
          required,
          type: "checkbox",
          value
        }
      ),
      /* @__PURE__ */ jsx256(
        FormControlLabel,
        {
          controlSlot: /* @__PURE__ */ jsx256(
            StyledSwitchInput,
            {
              $disabled: disabled,
              $size: size,
              "data-testid": "wui-faux-input",
              "data-wui-faux-input": "true"
            }
          ),
          description,
          disabled,
          hideLabel,
          htmlFor: computedId,
          label
        }
      )
    ] });
  }
);
Switch.displayName = "Switch";

// src/components/Table/Table.tsx
import styled61, { css as css37 } from "styled-components";
import { jsx as jsx257 } from "react/jsx-runtime";
var StyledTable = styled61.table`
  width: 100%;
  border-collapse: collapse;

  ${({ $divided }) => $divided && css37`
      tr {
        border-bottom: 1px solid var(--wui-color-border);
      }
    `}

  ${({ $striped }) => $striped && css37`
      tbody tr:nth-child(even) {
        background-color: var(--wui-color-bg-surface-secondary);
      }
    `}

  ${({ $visuallyHiddenHeader }) => $visuallyHiddenHeader && css37`
      thead {
        ${visuallyHiddenStyle}
      }
    `}
`;
var Table = ({
  children,
  divided = false,
  striped = false,
  visuallyHiddenHeader = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx257(
    StyledTable,
    {
      $divided: divided,
      $striped: striped,
      $visuallyHiddenHeader: visuallyHiddenHeader,
      ...props,
      children
    }
  );
};

// src/components/Table/TableBody.tsx
import styled62 from "styled-components";

// src/components/Table/TableSectionContext.ts
import { createContext as createContext9 } from "react";
var TableSectionContext = createContext9(null);

// src/components/Table/TableBody.tsx
import { jsx as jsx258 } from "react/jsx-runtime";
var StyledTableBody = styled62.tbody``;
var TableBody = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx258(TableSectionContext.Provider, { value: "body", children: /* @__PURE__ */ jsx258(StyledTableBody, { ...props, children }) });
};

// src/components/Table/TableCell.tsx
import { useContext as useContext8 } from "react";
import styled63, { css as css38 } from "styled-components";
import { jsx as jsx259 } from "react/jsx-runtime";
var sharedStyles = css38`
  color: var(--wui-color-text);
  padding: var(--wui-space-02);
  text-align: left;
`;
var StyledTh = styled63.th`
  ${sharedStyles}
  font-size: var(--wui-typography-body-4-size);
  font-weight: var(--wui-typography-weight-label-bold);
  line-height: var(--wui-typography-body-4-line-height);
`;
var StyledTd = styled63.td`
  ${sharedStyles}
  font-size: var(--wui-typography-body-2-size);
  font-weight: var(--wui-typography-body-2-weight);
  line-height: var(--wui-typography-body-2-line-height);
`;
var TableCell = ({ children, ...props }) => {
  const section = useContext8(TableSectionContext);
  if (section === "head") {
    return /* @__PURE__ */ jsx259(StyledTh, { ...props, children });
  }
  return /* @__PURE__ */ jsx259(StyledTd, { ...props, children });
};

// src/components/Table/TableFoot.tsx
import styled64 from "styled-components";
import { jsx as jsx260 } from "react/jsx-runtime";
var StyledTableFoot = styled64.tfoot``;
var TableFoot = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx260(TableSectionContext.Provider, { value: "footer", children: /* @__PURE__ */ jsx260(StyledTableFoot, { ...props, children }) });
};

// src/components/Table/TableHead.tsx
import styled65 from "styled-components";
import { jsx as jsx261 } from "react/jsx-runtime";
var StyledThead = styled65.thead``;
var TableHead = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx261(TableSectionContext.Provider, { value: "head", children: /* @__PURE__ */ jsx261(StyledThead, { ...props, children }) });
};

// src/components/Table/TableRow.tsx
import styled66 from "styled-components";
import { jsx as jsx262 } from "react/jsx-runtime";
var StyledTableRow = styled66.tr``;
var TableRow = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx262(StyledTableRow, { ...props, children });
};

// src/components/Tabs/Tabs.tsx
import { forwardRef as forwardRef27, useState as useState15 } from "react";
import { Root as TabsRoot } from "@radix-ui/react-tabs";
import { isNotNil as isNotNil30, isNil as isNil15 } from "@wistia/type-guards";
import styled71 from "styled-components";

// src/components/Tabs/TabPanel.tsx
import styled67 from "styled-components";
import { Content as TabsContent } from "@radix-ui/react-tabs";
import { jsx as jsx263 } from "react/jsx-runtime";
var StyledTabPanel = styled67(TabsContent)`
  display: flex;
  flex-direction: column;
`;
var TabPanel = ({ children, value, ...props }) => {
  return /* @__PURE__ */ jsx263(
    StyledTabPanel,
    {
      value,
      ...props,
      children
    }
  );
};
TabPanel.displayName = "TabPanel";

// src/components/Tabs/TabList.tsx
import styled68 from "styled-components";
import { List as RadixTabList } from "@radix-ui/react-tabs";
import { jsx as jsx264 } from "react/jsx-runtime";
var StyledTabList = styled68(RadixTabList)`
  ${segmentedControlStyles}

  margin-bottom: var(--wui-space-04);
  width: ${({ $fullWidth }) => $fullWidth ? "100%" : "auto"};
  align-self: ${({ $fullWidth }) => $fullWidth ? "auto" : "flex-start"};
`;
var TabList = ({
  children,
  fullWidth,
  ariaLabel,
  ...props
}) => {
  return /* @__PURE__ */ jsx264(
    StyledTabList,
    {
      $fullWidth: fullWidth,
      "aria-label": ariaLabel,
      ...props,
      children
    }
  );
};
TabList.displayName = "TabList";

// src/components/Tabs/TabItem.tsx
import { forwardRef as forwardRef26, useEffect as useEffect12, useRef as useRef13 } from "react";
import styled69 from "styled-components";
import { Trigger as RadixTab } from "@radix-ui/react-tabs";
import { isNotNil as isNotNil29 } from "@wistia/type-guards";

// src/components/Tabs/useTabsValue.tsx
import { createContext as createContext10, useContext as useContext9 } from "react";
var TabsValueContext = createContext10(null);
var TabsValueProvider = TabsValueContext.Provider;
var useTabsValue = () => {
  const context = useContext9(TabsValueContext);
  if (context === null) {
    throw new Error("useTabsValue must be used within a TabsValueProvider");
  }
  return context;
};

// src/components/Tabs/TabItem.tsx
import { jsxs as jsxs43 } from "react/jsx-runtime";
var StyledTabItem = styled69(RadixTab)`
  ${segmentedControlItemStyles}

  &:focus-visible {
    outline: none;
  }
`;
var TabItem = forwardRef26(
  ({ disabled = false, icon, label, "aria-label": ariaLabel, value }, forwardedRef) => {
    const selectedValue = useTabsValue();
    const { setSelectedItemMeasurements } = useSelectedItemStyle();
    const buttonRef = useRef13(null);
    const combinedRef = mergeRefs([buttonRef, forwardedRef]);
    useEffect12(() => {
      const buttonElem = buttonRef.current;
      if (!buttonElem) {
        return void 0;
      }
      if (selectedValue !== value) {
        return void 0;
      }
      setSelectedItemMeasurements({
        offsetLeft: buttonElem.offsetLeft,
        offsetWidth: buttonElem.offsetWidth,
        offsetHeight: buttonElem.offsetHeight
      });
      const resizeObserver = new ResizeObserver(() => {
        setSelectedItemMeasurements({
          offsetLeft: buttonElem.offsetLeft,
          offsetWidth: buttonElem.offsetWidth,
          offsetHeight: buttonElem.offsetHeight
        });
      });
      resizeObserver.observe(buttonElem);
      return () => {
        resizeObserver.disconnect();
      };
    }, [selectedValue, setSelectedItemMeasurements, value]);
    return /* @__PURE__ */ jsxs43(
      StyledTabItem,
      {
        ref: combinedRef,
        $hasLabel: isNotNil29(label),
        "aria-label": isNotNil29(label) ? void 0 : ariaLabel,
        disabled,
        value,
        children: [
          icon,
          label
        ]
      }
    );
  }
);
TabItem.displayName = "TabItem";

// src/components/Tabs/extractTabItems.ts
import { Children as Children7, Fragment as Fragment7, isValidElement as isValidElement2 } from "react";
var extractTabItems = (children) => {
  const tabItems = [];
  Children7.forEach(children, (child) => {
    if (!isValidElement2(child)) {
      return;
    }
    if (typeof child.type !== "string" && child.type.displayName === "Tab") {
      tabItems.push(child);
    } else if (child.type === Fragment7) {
      const fragmentElement = child;
      tabItems.push(...extractTabItems(fragmentElement.props.children));
    } else if ((child.props.children ?? null) != null) {
      const childChildren = child.props.children;
      tabItems.push(...extractTabItems(childChildren));
    } else {
    }
  });
  return tabItems;
};

// src/components/Tabs/SelectedItemIndicator.tsx
import styled70 from "styled-components";
import { jsx as jsx265 } from "react/jsx-runtime";
var TabsSelectedItemIndicatorDiv = styled70(SelectedItemIndicatorDiv)`
  &:has(~ ${StyledTabItem}:focus-visible) {
    outline: 2px solid var(--wui-color-focus-ring);
  }
`;
var SelectedItemIndicator2 = () => {
  const { selectedItemIndicatorStyle } = useSelectedItemStyle();
  const selectedValue = useTabsValue();
  if (selectedValue == null) {
    return null;
  }
  return /* @__PURE__ */ jsx265(TabsSelectedItemIndicatorDiv, { style: selectedItemIndicatorStyle });
};

// src/components/Tabs/Tabs.tsx
import { jsx as jsx266, jsxs as jsxs44 } from "react/jsx-runtime";
var TabsContainer = styled71.div`
  display: flex;
  flex-direction: column;
  height: ${({ $stickyHeaders }) => $stickyHeaders ? "100%" : "auto"};
  overflow: ${({ $stickyHeaders }) => $stickyHeaders ? "hidden" : "visible"};
`;
var StickyTabContent = styled71.div`
  flex: ${({ $stickyHeaders }) => $stickyHeaders ? "1" : "initial"};
  overflow-y: ${({ $stickyHeaders }) => $stickyHeaders ? "auto" : "visible"};
  overflow-x: ${({ $stickyHeaders }) => $stickyHeaders ? "hidden" : "visible"};
`;
var StyledTabsRoot = styled71(TabsRoot)`
  display: flex;
  flex-direction: column;
  height: ${({ $stickyHeaders }) => $stickyHeaders ? "100%" : "auto"};
`;
var Tabs = forwardRef27(
  ({
    children,
    fullWidth = true,
    "aria-label": ariaLabel,
    defaultSelectedValue,
    selectedValue,
    onSelectedValueChange,
    stickyHeaders = true,
    ...props
  }, ref) => {
    const tabItems = extractTabItems(children);
    const [internalSelectedValue, setInternalSelectedValue] = useState15(defaultSelectedValue);
    const modeProps = defaultSelectedValue !== void 0 ? {
      defaultValue: defaultSelectedValue,
      onValueChange: setInternalSelectedValue
    } : { value: selectedValue, onValueChange: onSelectedValueChange };
    const currentValue = defaultSelectedValue !== void 0 ? internalSelectedValue : selectedValue;
    return /* @__PURE__ */ jsx266(TabsContainer, { $stickyHeaders: stickyHeaders, children: /* @__PURE__ */ jsx266(
      StyledTabsRoot,
      {
        ref,
        $stickyHeaders: stickyHeaders,
        activationMode: "automatic",
        ...props,
        ...modeProps,
        children: /* @__PURE__ */ jsx266(TabsValueProvider, { value: currentValue, children: /* @__PURE__ */ jsxs44(SelectedItemStyleProvider, { children: [
          /* @__PURE__ */ jsxs44(
            TabList,
            {
              ariaLabel,
              fullWidth,
              children: [
                /* @__PURE__ */ jsx266(SelectedItemIndicator2, {}),
                tabItems.map((tab) => {
                  const {
                    value,
                    disabled = false,
                    icon,
                    label,
                    "aria-label": ariaLabelForTab
                  } = tab.props;
                  if (isNil15(icon)) {
                    return /* @__PURE__ */ jsx266(
                      TabItem,
                      {
                        disabled,
                        label,
                        value
                      },
                      value
                    );
                  }
                  if (isNotNil30(label)) {
                    return /* @__PURE__ */ jsx266(
                      TabItem,
                      {
                        disabled,
                        icon,
                        label,
                        value
                      },
                      value
                    );
                  }
                  if (isNotNil30(ariaLabelForTab)) {
                    return /* @__PURE__ */ jsx266(
                      TabItem,
                      {
                        "aria-label": ariaLabelForTab,
                        disabled,
                        icon,
                        value
                      },
                      value
                    );
                  }
                  throw new Error("A `Tab` with an icon must have either label or aria-label");
                })
              ]
            }
          ),
          /* @__PURE__ */ jsx266(StickyTabContent, { $stickyHeaders: stickyHeaders, children: tabItems.map((tab) => /* @__PURE__ */ jsx266(
            TabPanel,
            {
              value: tab.props.value,
              children: tab.props.children
            },
            tab.props.value
          )) })
        ] }) })
      }
    ) });
  }
);
Tabs.displayName = "Tabs";

// src/components/Tabs/Tab.tsx
import { forwardRef as forwardRef28 } from "react";
import { jsx as jsx267 } from "react/jsx-runtime";
var Tab = forwardRef28(({ children }, ref) => {
  return /* @__PURE__ */ jsx267("div", { ref, children });
});
Tab.displayName = "Tab";

// src/components/Tag/Tag.tsx
import { forwardRef as forwardRef29 } from "react";
import styled72 from "styled-components";
import { isNil as isNil16, isNotNil as isNotNil31, isNonEmptyString as isNonEmptyString5 } from "@wistia/type-guards";
import { Fragment as Fragment8, jsx as jsx268, jsxs as jsxs45 } from "react/jsx-runtime";
var TagLabel = styled72.a`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  font-size: var(--wui-typography-label-4-size);
  font-weight: var(--wui-typography-label-4-weight);
  line-height: var(--wui-typography-label-4-line-height);
  border-radius: var(--wui-border-radius-01);
  display: flex;
  align-items: center;
  color: var(--wui-color-text);
  text-decoration: none;
  padding: var(--wui-space-01);
  gap: var(--wui-space-01);

  svg {
    height: 12px;
    width: 12px;
  }

  :not(:only-child) {
    padding-right: var(--wui-space-01);
  }

  :is(a):hover {
    background: var(--wui-color-bg-surface-secondary-hover);
  }

  :is(a):active {
    background: var(--wui-color-bg-surface-secondary-active);
  }

  :focus-visible {
    outline: unset;
    box-shadow: inset 0 0 0 2px var(--wui-color-border-secondary);
  }
`;
var TagDivider = styled72.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  border-left: 1px solid var(--wui-color-border);
  display: flex;
  height: 14px;
  width: 1px;
`;
var StyledRemoveButton = styled72.button`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  all: unset;
  cursor: pointer;
  display: flex;
  height: 22px;
  width: 12px;
  align-items: center;
  padding: 0 var(--wui-space-01);
  border-radius: var(--wui-border-radius-01);

  svg path {
    fill: var(--wui-color-icon);
  }

  :hover {
    background: var(--wui-color-bg-surface-secondary-hover);
  }

  :active {
    background: var(--wui-color-bg-surface-secondary-active);
  }

  :focus-visible {
    outline: unset;
    box-shadow: inset 0 0 0 2px var(--wui-color-border-secondary);
  }
`;
var StyledTag = styled72.div`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  align-items: center;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-01);
  display: inline-flex;
  overflow: hidden;

  > * {
    transition: background var(--wui-duration-01) var(--wui-motion-ease);
  }
`;
var RemoveButton = ({ onClickRemove, onClickRemoveLabel, colorScheme }) => {
  if (isNil16(onClickRemove)) {
    return null;
  }
  if (isNil16(onClickRemoveLabel)) {
    throw new Error(
      "for accessibility, onClickRemoveLabel must be provided if onClickRemove is provided"
    );
  }
  return /* @__PURE__ */ jsxs45(Fragment8, { children: [
    /* @__PURE__ */ jsx268(TagDivider, { $colorScheme: colorScheme }),
    /* @__PURE__ */ jsxs45(
      StyledRemoveButton,
      {
        $colorScheme: colorScheme,
        onClick: onClickRemove,
        type: "button",
        children: [
          /* @__PURE__ */ jsx268(
            Icon,
            {
              size: "sm",
              type: "close"
            }
          ),
          /* @__PURE__ */ jsx268(ScreenReaderOnly, { children: onClickRemoveLabel })
        ]
      }
    )
  ] });
};
var Tag = forwardRef29(
  ({ onClickRemove, colorScheme = "inherit", href, icon, label, onClickRemoveLabel, ...props }, ref) => {
    const hasIcon = isNotNil31(icon);
    const labelProps = isNotNil31(href) && isNonEmptyString5(href) ? { href, as: "a" } : { as: "span" };
    return /* @__PURE__ */ jsxs45(
      StyledTag,
      {
        ref,
        $colorScheme: colorScheme,
        ...props,
        children: [
          /* @__PURE__ */ jsxs45(
            TagLabel,
            {
              ...labelProps,
              $colorScheme: colorScheme,
              children: [
                hasIcon ? icon : null,
                label
              ]
            }
          ),
          /* @__PURE__ */ jsx268(
            RemoveButton,
            {
              colorScheme,
              onClickRemove,
              onClickRemoveLabel
            }
          )
        ]
      }
    );
  }
);
Tag.displayName = "Tag";

// src/components/Thumbnail/ThumbnailBadge.tsx
import styled73 from "styled-components";
import { isNotNil as isNotNil32 } from "@wistia/type-guards";
import { jsx as jsx269, jsxs as jsxs46 } from "react/jsx-runtime";
var StyledThumbnailBadge = styled73.div`
  align-items: center;
  background-color: rgb(0 0 0 / 50%);
  border-radius: var(--wui-border-radius-01);
  bottom: var(--wui-space-01);
  color: var(--wui-color-text-on-fill);
  display: flex;
  font-size: var(--wui-typography-body-4-size);
  font-weight: var(--wui-typography-weight-body-bold);
  gap: var(--wui-space-01);
  padding: 0 var(--wui-space-01);
  position: absolute;
  right: var(--wui-space-01);
  z-index: 1;

  svg {
    height: 12px;
    width: 12px;

    path {
      color: var(--wui-color-icon-on-fill);
    }
  }
`;
var ThumbnailBadge = ({ icon, label, ...props }) => {
  return /* @__PURE__ */ jsxs46(StyledThumbnailBadge, { ...props, children: [
    isNotNil32(icon) && icon,
    /* @__PURE__ */ jsx269("span", { children: label })
  ] });
};
ThumbnailBadge.displayName = "ThumbnailBadge";

// src/components/Thumbnail/Thumbnail.tsx
import { forwardRef as forwardRef30 } from "react";
import styled74 from "styled-components";
import { isNil as isNil17, isNotNil as isNotNil34 } from "@wistia/type-guards";

// src/private/helpers/getBackgroundGradient/getBackgroundGradient.ts
import { isNotNil as isNotNil33 } from "@wistia/type-guards";
import { css as css39 } from "styled-components";
var gradients = {
  defaultDarkOne: css39`
    background-color: #222d66;
    background-image:
      radial-gradient(farthest-corner at top right, #222d66, transparent 70%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #6b84ff, transparent 57%),
      radial-gradient(farthest-corner at top left, #2949e5, transparent 68%);
  `,
  defaultDarkTwo: css39`
    background-color: #222d66;
    background-image:
      radial-gradient(farthest-corner at top left, #6b84ff, transparent 100%),
      radial-gradient(farthest-corner at bottom left, #222d66, transparent 57%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  defaultLightOne: css39`
    background-color: #ccd5ff;
    background-image:
      radial-gradient(farthest-corner at bottom right, #ccd5ff, transparent 55%),
      radial-gradient(farthest-corner at top left, #ccd5ff, transparent 65%),
      radial-gradient(farthest-corner at top right, #6b84ff, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #f7f8ff, transparent 50%);
  `,
  defaultLightTwo: css39`
    background-color: #ccd5ff;
    background-image:
      radial-gradient(ellipse at top, #ccd5ff, transparent),
      radial-gradient(ellipse at bottom, #6b84ff, transparent);
  `,
  defaultMidOne: css39`
    background-color: #6b84ff;
    background-image:
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top left, #6b84ff, transparent 80%),
      radial-gradient(farthest-corner at bottom left, #222d66, transparent 57%);
  `,
  defaultMidTwo: css39`
    background-color: #6b84ff;
    background-image:
      radial-gradient(ellipse at top, #2949e5, transparent),
      radial-gradient(ellipse at bottom, #ccd5ff, transparent);
  `,
  green: css39`
    background-color: #fafffa;
    background-image:
      radial-gradient(farthest-corner at bottom left, #b0e5a5, transparent 50%),
      radial-gradient(farthest-corner at top right, #268713, transparent 50%),
      radial-gradient(farthest-corner at top left, #44b62d, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #44b62d, transparent 55%);
  `,
  greenWithPop: css39`
    background-color: #fafffa;
    background-image:
      radial-gradient(farthest-corner at bottom left, #b0e5a5, transparent 50%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at top left, #44b62d, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #44b62d, transparent 55%);
  `,
  pink: css39`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #ffc7e8, transparent 50%),
      radial-gradient(farthest-corner at top right, #e0128e, transparent 70%),
      radial-gradient(farthest-corner at top left, #ff40b3, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #ff40b3, transparent 55%);
  `,
  pinkWithPop: css39`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at top right, #e0128e, transparent 70%),
      radial-gradient(farthest-corner at top left, #ff40b3, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #ff40b3, transparent 55%),
      radial-gradient(farthest-corner at bottom left, #2949e5, transparent 50%);
  `,
  playfulGradientOne: css39`
    background-color: #f7f8ff;
    background-image:
      radial-gradient(farthest-corner at top left, #d65cff, transparent 68%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #ffc7e8, transparent 57%),
      radial-gradient(farthest-corner at top right, #ffcaba, transparent 70%);
  `,
  playfulGradientTwo: css39`
    background-color: #f7f8ff;
    background-image:
      radial-gradient(farthest-corner at top left, #44b62d, transparent 68%),
      radial-gradient(farthest-corner at bottom right, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at bottom left, #ccd5ff, transparent 57%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  purple: css39`
    background-color: #f2caff;
    background-image:
      radial-gradient(ellipse at 0% 100%, #f9e5ff, transparent 50%),
      radial-gradient(ellipse at 100% 0%, #e093fa, transparent 70%);
  `,
  purpleWithPop: css39`
    background-color: #f2caff;
    background-image:
      radial-gradient(farthest-corner at bottom left, #f2caff, transparent 50%),
      radial-gradient(farthest-corner at top left, #d65cff, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #d65cff, transparent 55%),
      radial-gradient(farthest-corner at top right, #2949e5, transparent 70%);
  `,
  yellow: css39`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at top right, #bcbf19, transparent 70%),
      radial-gradient(farthest-corner at top left, #e8ec1e, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #e8ec1e, transparent 55%);
  `,
  yellowWithPop: css39`
    background-color: #fffff0;
    background-image:
      radial-gradient(farthest-corner at bottom left, #eff18d, transparent 50%),
      radial-gradient(farthest-corner at top right, #bcbf19, transparent 70%),
      radial-gradient(farthest-corner at top left, #e8ec1e, transparent 65%),
      radial-gradient(farthest-corner at bottom right, #2949e5, transparent 55%);
  `
};
var gradientMap = Object.keys(gradients);
var getBackgroundGradient = (gradientName = void 0) => {
  return isNotNil33(gradientName) ? gradients[gradientName] : gradients.defaultDarkOne;
};

// src/components/Thumbnail/Thumbnail.tsx
import { jsx as jsx270, jsxs as jsxs47 } from "react/jsx-runtime";
var WideThumbnailImage = styled74.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;
var SquareThumbnailImage = styled74.img`
  backdrop-filter: blur(8px);
  object-fit: contain;
  width: 100%;
`;
var ThumbnailImage = ({ thumbnailImageType, thumbnailUrl }) => {
  if (thumbnailImageType === "wide") {
    return /* @__PURE__ */ jsx270(
      WideThumbnailImage,
      {
        alt: "",
        role: "img",
        src: thumbnailUrl
      }
    );
  }
  return /* @__PURE__ */ jsx270(
    SquareThumbnailImage,
    {
      alt: "",
      role: "img",
      src: thumbnailUrl
    }
  );
};
var StyledThumbnail = styled74.div`
  background-image: ${({ $backgroundUrl }) => isNotNil34($backgroundUrl) ? `url('${$backgroundUrl}')` : void 0};
  ${({ $backgroundUrl, $gradientBackground }) => (
  // if we don't have $backgroundUrl show a gradient
  isNil17($backgroundUrl) ? getBackgroundGradient($gradientBackground) : void 0
)};
  background-position: center center;
  background-size: cover;
  aspect-ratio: 16 / 9;
  display: flex;
  overflow: hidden;
  position: relative;
  width: ${({ $width }) => $width};

  &,
  img {
    border-radius: calc(8% * (9 / 16)) / 8%;
  }
`;
var Thumbnail = forwardRef30(
  ({
    gradientBackground = "defaultMidOne",
    thumbnailImageType = "square",
    thumbnailUrl,
    width = "100%",
    children,
    ...props
  }, ref) => {
    const backgroundUrl = thumbnailImageType === "square" && isNotNil34(thumbnailUrl) ? thumbnailUrl : void 0;
    return /* @__PURE__ */ jsxs47(
      StyledThumbnail,
      {
        ref,
        ...props,
        $backgroundUrl: backgroundUrl,
        $gradientBackground: gradientBackground,
        $width: width,
        role: "presentation",
        children: [
          isNotNil34(children) ? children : null,
          isNotNil34(thumbnailUrl) ? /* @__PURE__ */ jsx270(
            ThumbnailImage,
            {
              thumbnailImageType,
              thumbnailUrl
            }
          ) : null
        ]
      }
    );
  }
);
Thumbnail.displayName = "Thumbnail";

// src/components/ThumbnailCollage/ThumbnailCollage.tsx
import React2 from "react";
import styled75 from "styled-components";
import { isNonEmptyArray } from "@wistia/type-guards";
import { jsx as jsx271 } from "react/jsx-runtime";
var StyledThumbnailCollage = styled75.div`
  display: grid;
  gap: var(--wui-space-01);
  width: 100%;
  grid-template-columns: 3fr 2fr;
  grid-template-rows: 1fr 1fr;
  aspect-ratio: 16 / 9;

  &:has(:nth-child(1)) {
    --wui-collage-thumbnail-first-border-radius: calc(8% * (9 / 16)) / 8%;

    grid-template-areas:
      'a a'
      'a a';
  }

  &:has(:nth-child(2)) {
    --wui-collage-thumbnail-first-border-radius: 7.5% / 8%;
    --wui-collage-thumbnail-second-border-radius: 11.5% / 8%;

    grid-template-areas:
      'a b'
      'a b';
  }

  &:has(:nth-child(3)) {
    --wui-collage-thumbnail-first-border-radius: 7.5% / 8%;
    --wui-collage-thumbnail-second-border-radius: 11.5% / 16%;
    --wui-collage-thumbnail-third-border-radius: 11.5% / 16%;

    grid-template-areas:
      'a b'
      'a c';
  }

  > :nth-child(1) {
    --wui-collage-thumbnail-border-radius: var(--wui-collage-thumbnail-first-border-radius);

    grid-area: a;
  }

  > :nth-child(2) {
    --wui-collage-thumbnail-border-radius: var(--wui-collage-thumbnail-second-border-radius);

    grid-area: b;
  }

  > :nth-child(3) {
    --wui-collage-thumbnail-border-radius: var(--wui-collage-thumbnail-third-border-radius);

    grid-area: c;
  }

  > * {
    aspect-ratio: unset;
    height: 100%;
    width: 100%;
    border-radius: var(--wui-collage-thumbnail-border-radius);

    img {
      border-radius: var(--wui-collage-thumbnail-border-radius);
    }

    > :not(img) {
      display: none;
    }
  }
`;
var ThumbnailCollage = ({
  children = [],
  gradientBackground = "defaultMidOne",
  ...props
}) => {
  const thumbnailArray = React2.Children.toArray(children);
  const truncatedThumbnails = thumbnailArray.slice(0, 3);
  const thumbnails = isNonEmptyArray(thumbnailArray) ? truncatedThumbnails.map((child) => {
    return React2.cloneElement(child, {
      ...child.props,
      children: void 0
    });
  }) : [
    // ThumbnailCollage will fallback to a Thumbnail with a gradient background if no children are provided
    /* @__PURE__ */ jsx271(
      Thumbnail,
      {
        gradientBackground,
        thumbnailUrl: null
      },
      "fallback"
    )
  ];
  return /* @__PURE__ */ jsx271(
    StyledThumbnailCollage,
    {
      $gradientBackground: gradientBackground,
      "data-wui-thumbnail-collage": true,
      ...props,
      children: thumbnails
    }
  );
};

// src/components/WistiaLogo/WistiaLogo.tsx
import styled76, { css as css40 } from "styled-components";
import { isNotNil as isNotNil35 } from "@wistia/type-guards";
import { jsx as jsx272, jsxs as jsxs48 } from "react/jsx-runtime";
var renderBrandmark = (brandmarkColor, iconOnly) => {
  if (iconOnly) {
    return /* @__PURE__ */ jsx272(
      "g",
      {
        "data-testid": "ui-wistia-logo-brandmark",
        fill: brandmarkColor,
        children: /* @__PURE__ */ jsx272("path", { d: "M16.09 17.1h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 26.53c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87ZM32.14 0c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" })
      }
    );
  }
  return /* @__PURE__ */ jsx272(
    "g",
    {
      "data-testid": "ui-wistia-logo-brandmark",
      fill: brandmarkColor,
      children: /* @__PURE__ */ jsx272("path", { d: "M16.09 21.37h-5.2c-1.58 0-3.08.68-4.11 1.87L.21 30.8c4.78.25 9.78.25 13.3.25 18.31 0 20.89-11.27 20.89-16.55-1.59 1.93-6.06 6.87-18.32 6.87Zm16.05-17.1c-.08.92-.59 4.69-11.31 4.69-8.72 0-12.24 0-20.83-.17l6.44 7.4a6.657 6.657 0 0 0 4.96 2.3c2.13.03 5.05.06 5.53.06 11.01 0 17.19-5.05 17.19-9.89 0-2.01-.67-3.44-1.97-4.4Z" })
    }
  );
};
var renderLogotype = (logotypeColor, iconOnly) => {
  if (iconOnly) {
    return null;
  }
  return /* @__PURE__ */ jsx272(
    "g",
    {
      "data-testid": "ui-wistia-logo-logotype",
      fill: logotypeColor,
      children: /* @__PURE__ */ jsx272("path", { d: "M70.16 8.66v15.18c0 1.68-.35 3.09-1.05 4.23a6.612 6.612 0 0 1-2.85 2.54c-1.19.55-2.52.82-4.01.82s-2.8-.28-4.01-.85a6.655 6.655 0 0 1-3.11-2.96c-.08.15-.16.29-.24.42a6.552 6.552 0 0 1-2.87 2.54c-1.2.56-2.54.85-4.01.85s-2.8-.27-3.94-.82a6.214 6.214 0 0 1-2.71-2.52c-.67-1.14-1.01-2.56-1.02-4.25l-.22-15.18h7.34V22.3c0 .82.19 1.37.56 1.67.39.28.85.42 1.38.42s1.02-.15 1.45-.45c.43-.3.65-.85.65-1.65V8.65h7.3v13.64c0 .8.22 1.35.65 1.65.43.3.91.45 1.45.45s.99-.14 1.36-.42c.39-.3.58-.85.58-1.67V8.66h7.34Zm2.45 0v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89A3.43 3.43 0 0 0 78.28.44c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.13 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm8.86 1.96c-1.42.4-2.6 1.11-3.54 2.14-.93 1.02-1.4 2.4-1.4 4.12 0 1.11.17 2.09.51 2.94.36.85.82 1.62 1.38 2.34.22.28.55.65.98 1.11.37.4.64.72.8.96.18.24.27.47.27.69 0 .5-.4.87-1.2 1.09-.8.21-1.62.31-2.47.31-.1-.01-.22-.02-.33-.02l1.02 6.94c.42.07.92.11 1.51.11 1.9 0 3.6-.28 5.1-.85 1.5-.56 2.68-1.42 3.54-2.56.88-1.14 1.31-2.55 1.31-4.23 0-.68-.07-1.31-.22-1.87-.13-.58-.32-1.09-.56-1.54a6.64 6.64 0 0 0-.8-1.27c-.3-.37-.74-.82-1.34-1.36-.39-.33-.67-.59-.85-.8-.18-.22-.27-.45-.27-.67 0-.46.26-.79.78-.98.53-.19 1.17-.29 1.91-.29.25 0 .51.01.78.04l-.71-6.88a10.4 10.4 0 0 0-1.56-.11c-1.66 0-3.21.21-4.65.62Zm19.54 15.71c-.99 0-1.71-.23-2.14-.69-.42-.47-.62-1.18-.62-2.11v-6.57h4.21V8.66h-4.21V3.38l-7.34 1.85V24.1c0 2.45.47 4.29 1.4 5.52.95 1.22 2.45 1.83 4.49 1.83.95 0 1.86-.07 2.74-.22.88-.13 1.62-.34 2.25-.62l1.38-6.3c-.55.1-1.27.16-2.16.16Zm4.13-15.8v22.26h7.34V8.66h-7.34Zm5.67-1.87c.61-.3 1.08-.71 1.42-1.25.36-.55.53-1.19.53-1.94s-.18-1.34-.53-1.89a3.43 3.43 0 0 0-1.42-1.27c-.59-.3-1.25-.45-1.98-.45s-1.42.15-2.02.45c-.59.3-1.07.72-1.42 1.27-.36.55-.53 1.18-.53 1.89 0 1.1.38 1.97 1.14 2.63.76.65 1.71.98 2.85.98.73 0 1.39-.14 1.98-.42Zm27.51 1.87v22.26h-7.34v-2.28c-.41.43-.85.83-1.34 1.19-1.44 1.07-3.12 1.6-5.05 1.6s-3.61-.52-5.1-1.56c-1.48-1.05-2.63-2.47-3.45-4.25-.82-1.78-1.22-3.73-1.22-5.85s.41-4.07 1.22-5.83c.82-1.78 1.97-3.19 3.45-4.23 1.48-1.05 3.18-1.58 5.1-1.58s3.61.53 5.05 1.6c.48.36.93.75 1.34 1.19V8.66h7.34Zm-7.1 11.11c0-.8-.19-1.53-.56-2.18-.37-.67-.88-1.19-1.54-1.58-.64-.39-1.34-.58-2.09-.58s-1.45.19-2.09.58c-.64.39-1.15.91-1.54 1.58-.37.67-.56 1.39-.56 2.18s.19 1.51.56 2.18c.39.67.9 1.19 1.54 1.58.64.39 1.34.58 2.09.58s1.45-.19 2.09-.58c.65-.39 1.16-.91 1.54-1.56.37-.67.56-1.4.56-2.2Z" })
    }
  );
};
var computedViewBox = (iconOnly) => {
  if (iconOnly) {
    return "0 0 34.4 26.78";
  }
  return "0 0 144 31.47";
};
var WistiaLogoComponent = styled76.svg`
  height: ${({ height }) => `${height}px`};

  /* ensure it will always fit on mobile */
  max-height: 22vw;
  max-width: 100%;

  &:hover {
    path {
      ${({ $hoverColor }) => $hoverColor !== void 0 ? `fill: ${$hoverColor}` : null};
    }
  }

  ${({ $opticallyCentered, $iconOnly }) => {
  if ($opticallyCentered) {
    if ($iconOnly) {
      return css40`
          aspect-ratio: 1;
          padding: 11.85% 3.12% 13.91%;
        `;
    }
    return css40`
        aspect-ratio: 127 / 32;
      `;
  }
  return null;
}}
`;
var WistiaLogo = ({
  description = void 0,
  height = 100,
  hoverColor = void 0,
  href = void 0,
  iconOnly = false,
  opticallyCentered = false,
  title = "Wistia Logo",
  variant = "standard",
  ...props
}) => {
  const primaryColor = "#2949e5";
  const darkColor = "#000934";
  const lightColor = "#ffffff";
  const VARIANT_COLORS = {
    standard: {
      brandmark: primaryColor,
      logotype: darkColor
    },
    dark: {
      brandmark: darkColor,
      logotype: darkColor
    },
    light: {
      brandmark: lightColor,
      logotype: lightColor
    }
  };
  const brandmarkColor = VARIANT_COLORS[variant].brandmark;
  const logotypeColor = VARIANT_COLORS[variant].logotype;
  const Logo = /* @__PURE__ */ jsxs48(
    WistiaLogoComponent,
    {
      $hoverColor: hoverColor,
      $iconOnly: iconOnly,
      $opticallyCentered: opticallyCentered,
      height,
      preserveAspectRatio: opticallyCentered ? "xMidYMin" : void 0,
      role: "img",
      viewBox: computedViewBox(iconOnly),
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsx272("title", { children: title }),
        isNotNil35(description) ? /* @__PURE__ */ jsx272("desc", { children: description }) : null,
        renderBrandmark(brandmarkColor, iconOnly),
        renderLogotype(logotypeColor, iconOnly)
      ]
    }
  );
  return href !== void 0 ? /* @__PURE__ */ jsx272("a", { href, children: Logo }) : Logo;
};
WistiaLogo.displayName = "WistiaLogo";
export {
  ActionButton,
  Avatar,
  Badge,
  Box,
  Breadcrumb,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Card,
  Center,
  Checkbox,
  CheckboxGroup,
  CheckboxMenuItem,
  ClickRegion,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ColorSchemeWrapper,
  DataCard,
  DataCardTrend,
  DataCards,
  Divider,
  EditableHeading,
  Ellipsis,
  FileAmountLimitValidator,
  FileSizeValidator,
  FileTypeValidator,
  Form,
  FormErrorSummary,
  FormField,
  FormGroup,
  Heading,
  Icon,
  IconButton,
  Image,
  ImageDimensionsValidator,
  Input,
  InputClickToCopy,
  KeyboardShortcut,
  Label,
  Link,
  Menu,
  MenuItem,
  MenuItemDescription,
  MenuItemLabel,
  MenuLabel,
  MenuRadioGroup,
  Modal,
  PersistentFileAmountLimitValidator,
  Popover,
  ProgressBar,
  Radio,
  RadioGroup,
  RadioMenuItem,
  ScreenReaderOnly,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  SelectOptionGroup,
  Stack,
  SubMenu,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFoot,
  TableHead,
  TableRow,
  Tabs,
  Tag,
  Text,
  Thumbnail,
  ThumbnailBadge,
  ThumbnailCollage,
  Tooltip,
  UIProvider,
  WistiaLogo,
  colorSchemeOptions,
  copyToClipboard,
  dateTime,
  iconSizeMap,
  mergeRefs,
  mq,
  useActiveMq,
  useAriaLive,
  useBoolean,
  useClipboard,
  useFilePicker,
  useFocusTrap,
  useForceUpdate,
  useFormState,
  useImperativeFilePicker,
  useIsHovered,
  useKey,
  useLocalStorage,
  useLockBodyScroll,
  useMq,
  useOnClickOutside,
  usePreviousValue,
  useToast,
  useWindowSize
};
//# sourceMappingURL=index.mjs.map